import { makeAutoObservable } from "mobx";
import { LanguageCodes } from "../types";
import { monitorLabelDescriptors } from "./LangStoreMonitor";
import { systemErrorMessages } from "./LangStoreErrors";
import { customLabelDescriptors, customErrorMessages } from "./LangStoreCustom";

// Audiencias: {origin} es el host de la audiencia del usuario, {name} su nombre.
const AUDIENCE_TEXTS: Record<
  "WRONG_AUDIENCE" | "AUDIENCE_FORBIDDEN" | "AUTHOR_WELCOME",
  { [key: string]: string }
> = {
  WRONG_AUDIENCE: {
    es: "Tu cuenta no entra por acá. Entrá por {origin}.",
    en: "Your account doesn't sign in here. Use {origin}.",
    pt: "Sua conta não entra por aqui. Use {origin}.",
  },
  AUDIENCE_FORBIDDEN: {
    es: "Esa operación no está disponible en esta aplicación.",
    en: "That operation is not available in this application.",
    pt: "Essa operação não está disponível neste aplicativo.",
  },
  AUTHOR_WELCOME: {
    es: "Hola, {name}. Tu portal está en construcción.",
    en: "Hello, {name}. Your portal is under construction.",
    pt: "Olá, {name}. Seu portal está em construção.",
  },
};

// Los nombres de las audiencias (el select de Role.audience).
const AUDIENCE_LABELS: Record<
  "platform" | "publisher" | "author",
  { [key: string]: string }
> = {
  platform: { es: "Plataforma", en: "Platform", pt: "Plataforma" },
  publisher: { es: "Editorial", en: "Publisher", pt: "Editora" },
  author: { es: "Autor", en: "Author", pt: "Autor" },
};

// Los códigos que getApiError traduce. WRONG_AUDIENCE sin origen sugerido (el
// usuario sin rol): solo la primera oración.
function audienceErrorMessages(): Record<string, { [key: string]: string }> {
  return {
    WRONG_AUDIENCE: {
      es: "Tu cuenta no entra por acá.",
      en: "Your account doesn't sign in here.",
      pt: "Sua conta não entra por aqui.",
    },
    AUDIENCE_FORBIDDEN: AUDIENCE_TEXTS.AUDIENCE_FORBIDDEN,
    // Los services de Role (alta, edición y borrado de un rol de otra audiencia;
    // compartir un rol con todas las empresas; cambiar la audiencia de un rol).
    AUDIENCE_NOT_ALLOWED: {
      es: "No podés crear ni cambiar roles de otra audiencia.",
      en: "You can't create or change roles of another audience.",
      pt: "Você não pode criar nem alterar papéis de outro público.",
    },
    GLOBAL_ROLE_PLATFORM_ONLY: {
      es: "Solo la plataforma puede crear roles compartidos por todas las empresas.",
      en: "Only the platform can create roles shared by all companies.",
      pt: "Somente a plataforma pode criar papéis compartilhados por todas as empresas.",
    },
    AUDIENCE_IMMUTABLE: {
      es: "La audiencia de un rol no se cambia: creá otro rol.",
      en: "A role's audience can't be changed: create another role.",
      pt: "O público de um papel não muda: crie outro papel.",
    },
  };
}

const hostOf = (origin: string) => {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
};

function replaceEmbeddedJSON(
  input: string,
  replaceFn: (obj: any) => string
): string {
  // Expresión regular para encontrar fragmentos JSON embebidos en el formato {{...}}
  const jsonRegex = /{{(.+?)}}/g;

  return input.replace(jsonRegex, (match) => {
    try {
      // Eliminar las llaves exteriores para parsear el JSON
      const jsonString = match.slice(2, -2);
      // Parsear el fragmento JSON
      const jsonObject = JSON.parse(`{${jsonString}}`);
      // Aplicar la función de reemplazo al objeto JSON y devolver el resultado
      return replaceFn(jsonObject);
    } catch (error) {
      // Si ocurre un error, devolver el fragmento original
      console.error("Error parsing JSON fragment:", error);
      return match;
    }
  });
}

export class LangStore {
  language: LanguageCodes = "es";
  timeZones = {
    es: "America/Argentina/Buenos_Aires",
    en: "America/Argentina/Buenos_Aires",
    pt: "America/Argentina/Buenos_Aires",
  };
  timeFormats = { es: "es-AR", en: "en-US", pt: "pt-BR" };

  languageLabels = { es: "Español", en: "English", pt: "Português" };

  constructor() {
    Object.defineProperties(this, monitorLabelDescriptors());
    Object.defineProperties(this, customLabelDescriptors());
    Object.assign(this._apiServerErrors, systemErrorMessages());
    Object.assign(this._apiServerErrors, audienceErrorMessages());
    Object.assign(this._apiServerErrors, customErrorMessages());
    makeAutoObservable(this);
  }

  setLanguage(language: LanguageCodes) {
    this.language = language;
  }

  displayIdByLang(
    displayId?: string,
    lang: undefined | "en" | "es" | "pt" = undefined
  ) {
    if (displayId && displayId.includes('{{"')) {
      return replaceEmbeddedJSON(
        displayId,
        (x) => x[!lang ? this.language : lang]
      );
    }
    return displayId || "";
  }

  fieldByLang(baseFieldName: string) {
    return `${baseFieldName}_${this.language}`;
  }

  gridFilterLabels(operator: string) {
    const opLabel: Record<string, any> = {
      equal: { es: "Igual a", en: "Equal to", pt: "Igual a" },
      notEqual: { es: "Distinto", en: "Not equal", pt: "Distinto" },
      equalStr: { es: "Igual a", en: "Equal to", pt: "Igual a" },
      notEqualStr: { es: "Distinto", en: "Not equal", pt: "Distinto" },
      greaterThan: { es: "Mayor que", en: "Greater than", pt: "Maior que" },
      greaterThanOrEqual: {
        es: "Mayor o igual a",
        en: "Greater than or equal to",
        pt: "Maior ou igual a",
      },
      lessThan: { es: "Menor que", en: "Less than", pt: "Menor que" },
      lessThanOrEqual: {
        es: "Menor o igual a",
        en: "Less than or equal to",
        pt: "Menor ou igual a",
      },
      contains: { es: "Contiene", en: "Contains", pt: "Contém" },
      notContains: { es: "No contiene", en: "Not contains", pt: "Não contém" },
      startsWith: { es: "Empieza con", en: "Starts with", pt: "Começa com" },
      endsWith: { es: "Termina con", en: "Ends with", pt: "Termina com" },
      day: { es: "Año-Mes-Día", en: "Year-Month-Day", pt: "Ano-Mês-Dia" },
      "year-month": { es: "Año-Mes", en: "Year-Month", pt: "Ano-Mês" },
      year: { es: "Año", en: "Year", pt: "Ano" },
      "from-to": {
        es: "Año-Mes-Día , Año-Mes-Día",
        en: "Year-Month-Day , Year-Month-Day",
        pt: "Ano-Mês-Dia , Ano-Mês-Dia",
      },
      all: { es: "Todos", en: "All", pt: "Todos" },
      yes: { es: "Sí", en: "Yes", pt: "Sim" },
      no: { es: "No", en: "No", pt: "Não" },
    };
    return opLabel[operator] ? opLabel[operator][this.language] : "OP";
  }

  get timeZone() {
    return this.timeZones[this.language];
  }

  get timeFormat() {
    return this.timeFormats[this.language];
  }

  bestContentByLang(
    instance: any,
    baseFieldName: string,
    lang: string | null = null
  ) {
    const language = lang ? lang : this.language;
    let fieldName = `${baseFieldName}_${language}`;
    let content = instance[fieldName];
    if (!(!content || (Array(content) && content.length === 0))) {
      return { fieldName, content };
    }
    content = null;
    for (const lang of Object.keys(this.languageLabels).filter(
      (x) => x !== language
    )) {
      fieldName = `${baseFieldName}_${lang}`;
      const currentContent = instance[fieldName];
      if (
        !(
          !currentContent ||
          (Array(currentContent) && currentContent.length === 0)
        )
      ) {
        content = currentContent;
        break;
      }
    }
    if (content) {
      return { fieldName, content };
    } else {
      return { fieldName: `${baseFieldName}_${language}` };
    }
  }

  nextPage: (pageSize: number) => string = () => "";

  projectTitles = { en: "Jupiter", es: "Jupiter", pt: "Jupiter" };
  welcomedText = (name: string) => {
    return `${name}, ${
      {
        en: "wellcome to",
        es: "te damos la bienvenida al sistema",
        pt: "bemvindo ao sistema",
      }[this.language]
    } "${this.projectTitles[this.language]}"`;
  };

  listFormTitle(entityLabelPlural: string) {
    return {
      es: `Listado de ${entityLabelPlural}`,
      en: `${entityLabelPlural} list`,
      pt: `Lista de ${entityLabelPlural}`,
    }[this.language];
  }

  get projectTitle() {
    return this.projectTitles[this.language];
  }

  _apiServerErrors: Record<string, { [key: string]: string }> = {};

  hasApiServerError(code: string): boolean {
    return code in this._apiServerErrors;
  }

  apiServerError(code: string) {
    if (code in this._apiServerErrors) {
      return this._apiServerErrors[code][this.language];
    } else {
      return {
        es: "No se pudo completar la operación",
        en: "Could not complete the operation",
        pt: "Não foi possível completar a operação",
      }[this.language];
    }
  }

  getFieldLabel(entityName: string, fieldName: string): string {
    if (!entityName || !fieldName) {
      return fieldName || "";
    }

    const firstUpper = (str: string) =>
      str.charAt(0).toUpperCase() + str.slice(1);

    const fieldUpper = firstUpper(fieldName);
    const methodName = `${entityName}${fieldUpper}Label`;

    if (
      typeof (this as any)[methodName] === "function" ||
      (this as any)[methodName]
    ) {
      const label = (this as any)[methodName];
      return typeof label === "function" ? label() : label;
    }

    return fieldName;
  }

  uniqueConstraintMessage(fields: string[]) {
    const fieldList = fields.join(", ");
    const isPlural = fields.length > 1;
    return {
      es: isPlural
        ? `Ya existe un registro con esos valores en los campos ${fieldList}`
        : `Ya existe un registro con ese valor en el campo ${fieldList}`,
      en: isPlural
        ? `A record with those values already exists in the fields ${fieldList}`
        : `A record with that value already exists in the field ${fieldList}`,
      pt: isPlural
        ? `Já existe um registro com esses valores nos campos ${fieldList}`
        : `Já existe um registro com esse valor no campos ${fieldList}`,
    }[this.language];
  }

  jsonFieldError(fieldName: string) {
    return {
      es: `El campo "${fieldName}" contiene JSON inválido. Por favor, verificá la sintaxis.`,
      en: `Field "${fieldName}" contains invalid JSON. Please check the syntax.`,
      pt: `O campo "${fieldName}" contém JSON inválido. Por favor, verifique a sintaxe.`,
    }[this.language];
  }

  constraintMessage(constraint?: string): string {
    if (!constraint) {
      return {
        es: "no es válido",
        en: "is not valid",
        pt: "não é válido",
      }[this.language];
    }

    const constraints: Record<string, { es: string; en: string; pt: string }> =
      {
        required: {
          es: "es obligatorio",
          en: "is required",
          pt: "é obrigatório",
        },
        versionMismatch: {
          es: "versión desactualizada",
          en: "outdated version",
          pt: "versão desatualizada",
        },
        sum_equals_100: {
          es: "la suma debe ser 100%",
          en: "sum must equal 100%",
          pt: "a soma deve ser 100%",
        },
        min: {
          es: "valor por debajo del mínimo",
          en: "value below minimum",
          pt: "valor abaixo do mínimo",
        },
        max: {
          es: "valor por encima del máximo",
          en: "value above maximum",
          pt: "valor acima do máximo",
        },
        minLength: {
          es: "longitud menor a la mínima",
          en: "length below minimum",
          pt: "comprimento abaixo do mínimo",
        },
        maxLength: {
          es: "longitud mayor a la máxima",
          en: "length above maximum",
          pt: "comprimento acima do máximo",
        },
        pattern: {
          es: "formato no válido",
          en: "invalid format",
          pt: "formato inválido",
        },
        email: {
          es: "email no válido",
          en: "invalid email",
          pt: "email inválido",
        },
        unique: {
          es: "ya existe",
          en: "already exists",
          pt: "já existe",
        },
      };

    return (
      constraints[constraint]?.[this.language] ||
      {
        es: "no es válido",
        en: "is not valid",
        pt: "não é válido",
      }[this.language]
    );
  }

  validationErrorMessage(meta?: any, entityName?: string): string {
    if (!meta) {
      return {
        es: "Error de validación",
        en: "Validation error",
        pt: "Erro de validação",
      }[this.language];
    }

    if (meta.validationErrors && meta.validationErrors.length > 0) {
      const errorList = meta.validationErrors
        .map((err: any) => {
          const fieldLabel = entityName
            ? this.getFieldLabel(entityName, err.field)
            : err.field;

          const constraintMsg = this.constraintMessage(err.constraint);

          if (
            err.expectedValue !== undefined &&
            err.actualValue !== undefined
          ) {
            return {
              es: `${fieldLabel}: ${constraintMsg} (esperado: ${err.expectedValue}, actual: ${err.actualValue})`,
              en: `${fieldLabel}: ${constraintMsg} (expected: ${err.expectedValue}, actual: ${err.actualValue})`,
              pt: `${fieldLabel}: ${constraintMsg} (esperado: ${err.expectedValue}, atual: ${err.actualValue})`,
            }[this.language];
          }

          return {
            es: `${fieldLabel}: ${constraintMsg}`,
            en: `${fieldLabel}: ${constraintMsg}`,
            pt: `${fieldLabel}: ${constraintMsg}`,
          }[this.language];
        })
        .join("; ");

      const prefix = {
        es: `${meta.validationErrors.length} error${
          meta.validationErrors.length > 1 ? "es" : ""
        } de validación`,
        en: `${meta.validationErrors.length} validation error${
          meta.validationErrors.length > 1 ? "s" : ""
        }`,
        pt: `${meta.validationErrors.length} erro${
          meta.validationErrors.length > 1 ? "s" : ""
        } de validação`,
      }[this.language];

      let message = `${prefix}: ${errorList}`;

      if (meta.suggestedAction) {
        message += {
          es: `. ${meta.suggestedAction}`,
          en: `. ${meta.suggestedAction}`,
          pt: `. ${meta.suggestedAction}`,
        }[this.language];
      }

      return message;
    }

    if (meta.field) {
      const fieldLabel = entityName
        ? this.getFieldLabel(entityName, meta.field)
        : meta.field;

      const constraintMsg = meta.constraint
        ? this.constraintMessage(meta.constraint)
        : {
            es: "no es válido",
            en: "is not valid",
            pt: "não é válido",
          }[this.language];

      let message = {
        es: `${fieldLabel}: ${constraintMsg}`,
        en: `${fieldLabel}: ${constraintMsg}`,
        pt: `${fieldLabel}: ${constraintMsg}`,
      }[this.language];

      if (meta.expectedValue !== undefined && meta.actualValue !== undefined) {
        message += {
          es: ` (esperado: ${meta.expectedValue}, actual: ${meta.actualValue})`,
          en: ` (expected: ${meta.expectedValue}, actual: ${meta.actualValue})`,
          pt: ` (esperado: ${meta.expectedValue}, atual: ${meta.actualValue})`,
        }[this.language];
      }

      if (meta.suggestedAction) {
        message += {
          es: `. ${meta.suggestedAction}`,
          en: `. ${meta.suggestedAction}`,
          pt: `. ${meta.suggestedAction}`,
        }[this.language];
      }

      return message;
    }

    if (meta.suggestedAction) {
      return meta.suggestedAction;
    }

    return {
      es: "Error de validación",
      en: "Validation error",
      pt: "Erro de validação",
    }[this.language];
  }

  notFoundErrorMessage(meta?: any): string {
    if (!meta) {
      return {
        es: "No se encontró el recurso",
        en: "Resource not found",
        pt: "Recurso não encontrado",
      }[this.language];
    }

    let resourceInfo = "";

    if (meta.resourceType && meta.resourceId) {
      resourceInfo = {
        es: `${meta.resourceType} con ID ${meta.resourceId}`,
        en: `${meta.resourceType} with ID ${meta.resourceId}`,
        pt: `${meta.resourceType} com ID ${meta.resourceId}`,
      }[this.language];
    } else if (meta.resourceType) {
      resourceInfo = meta.resourceType;
    } else {
      resourceInfo = {
        es: "El recurso",
        en: "The resource",
        pt: "O recurso",
      }[this.language];
    }

    let message = {
      es: `${resourceInfo} no fue encontrado`,
      en: `${resourceInfo} was not found`,
      pt: `${resourceInfo} não foi encontrado`,
    }[this.language];

    if (meta.hint) {
      message += {
        es: `. ${meta.hint}`,
        en: `. ${meta.hint}`,
        pt: `. ${meta.hint}`,
      }[this.language];
    }

    if (
      meta.availableAlternatives !== undefined &&
      meta.availableAlternatives > 0
    ) {
      message += {
        es: `. Hay ${meta.availableAlternatives} recurso${
          meta.availableAlternatives > 1 ? "s" : ""
        } disponible${meta.availableAlternatives > 1 ? "s" : ""}`,
        en: `. There ${meta.availableAlternatives > 1 ? "are" : "is"} ${
          meta.availableAlternatives
        } available resource${meta.availableAlternatives > 1 ? "s" : ""}`,
        pt: `. Há ${meta.availableAlternatives} recurso${
          meta.availableAlternatives > 1 ? "s" : ""
        } disponível${meta.availableAlternatives > 1 ? "eis" : ""}`,
      }[this.language];
    }

    return message;
  }

  unknownErrorMessage(meta?: any): string {
    if (!meta) {
      return this.apiServerError("SYSTEM_UNKNOWN_ERROR");
    }

    let message = "";

    if (meta.operation) {
      message = {
        es: `Error en la operación "${meta.operation}"`,
        en: `Error in operation "${meta.operation}"`,
        pt: `Erro na operação "${meta.operation}"`,
      }[this.language];
    } else {
      message = {
        es: "Error inesperado",
        en: "Unexpected error",
        pt: "Erro inesperado",
      }[this.language];
    }

    if (meta.stage) {
      message += {
        es: ` durante ${meta.stage}`,
        en: ` during ${meta.stage}`,
        pt: ` durante ${meta.stage}`,
      }[this.language];
    }

    message += {
      es: ". Por favor, volvé a intentar o contactá al soporte",
      en: ". Please try again or contact support",
      pt: ". Por favor, tente novamente ou contacte o suporte",
    }[this.language];

    if (meta.timestamp) {
      message += {
        es: ` (Referencia: ${meta.timestamp})`,
        en: ` (Reference: ${meta.timestamp})`,
        pt: ` (Referência: ${meta.timestamp})`,
      }[this.language];
    }

    return message;
  }

  get emptyStringSymbol() {
    return {
      es: "∅",
      en: "∅",
      pt: "∅",
    }[this.language];
  }

  get initializingLabel() {
    return {
      es: "Inicializando...",
      en: "Initializing...",
      pt: "Inicializando...",
    }[this.language];
  }

  get loadingLabel() {
    return {
      es: "Cargando...",
      en: "Loading...",
      pt: "Carregando...",
    }[this.language];
  }

  wrongAudienceMessage(origin: string | null) {
    if (!origin) {
      return this.apiServerError("WRONG_AUDIENCE");
    }
    return AUDIENCE_TEXTS.WRONG_AUDIENCE[this.language].replace(
      "{origin}",
      hostOf(origin)
    );
  }

  // El texto partido donde va el enlace al origen (LoginForm).
  get wrongAudienceParts(): [string, string] {
    const [before, after = ""] =
      AUDIENCE_TEXTS.WRONG_AUDIENCE[this.language].split("{origin}");
    return [before, after];
  }

  authorWelcome(name: string) {
    return AUDIENCE_TEXTS.AUTHOR_WELCOME[this.language].replace("{name}", name);
  }

  // Las opciones del select de Role.audience.
  get audienceOptions(): { id: string; displayId: string }[] {
    return (["platform", "publisher", "author"] as const).map((id) => ({
      id,
      displayId: AUDIENCE_LABELS[id][this.language],
    }));
  }

  confirmDeleteDialogTitle = (entityLabel: string, displayId: string) => {
    return {
      es: `${entityLabel} ${displayId}`,
      en: `${entityLabel} ${displayId}`,
      pt: `${entityLabel} ${displayId}`,
    }[this.language];
  };

  get confirmDeleteDialogDescription() {
    return {
      es: "¿Borrar el registro?",
      en: "Do you confirm that you want to delete the record?",
      pt: "Você confirma que deseja excluir o registro?",
    }[this.language];
  }

  get multiSelectButton() {
    return {
      es: "Selección múltiple",
      en: "Multiple selection",
      pt: "Seleção múltipla",
    }[this.language];
  }

  get multiSelectCancel() {
    return {
      es: "Cancelar selección",
      en: "Cancel selection",
      pt: "Cancelar seleção",
    }[this.language];
  }

  selectedCount = (n: number) => {
    return {
      es: `${n} seleccionado${n !== 1 ? "s" : ""}`,
      en: `${n} selected`,
      pt: `${n} selecionado${n !== 1 ? "s" : ""}`,
    }[this.language];
  };

  confirmMultiDeleteTitle = (n: number) => {
    return {
      es: `¿Eliminar ${n} elemento${n !== 1 ? "s" : ""}?`,
      en: `Delete ${n} item${n !== 1 ? "s" : ""}?`,
      pt: `Excluir ${n} ite${n !== 1 ? "ns" : "m"}?`,
    }[this.language];
  };

  get confirmMultiDeleteDescription() {
    return {
      es: "Esta acción no se puede deshacer.",
      en: "This action cannot be undone.",
      pt: "Esta ação não pode ser desfeita.",
    }[this.language];
  }

  get batchCreateCommand() {
    return {
      es: "Crear en Lote",
      en: "Batch Create",
      pt: "Criar em Lote",
    }[this.language];
  }

  get batchCreateTooltip() {
    return {
      es: "Crear múltiples registros desde archivos",
      en: "Create multiple records from files",
      pt: "Criar múltiplos registros a partir de arquivos",
    }[this.language];
  }

  batchCreateConfirm = (n: number) => {
    return {
      es: `Se crearán ${n} registro${n !== 1 ? "s" : ""}`,
      en: `${n} record${n !== 1 ? "s" : ""} will be created`,
      pt: `${n} registro${n !== 1 ? "s" : ""} serão criados`,
    }[this.language];
  };

  batchCreateSuccess = (n: number) => {
    return {
      es: `${n} registro${n !== 1 ? "s" : ""} creado${
        n !== 1 ? "s" : ""
      } exitosamente`,
      en: `${n} record${n !== 1 ? "s" : ""} created successfully`,
      pt: `${n} registro${n !== 1 ? "s" : ""} criado${
        n !== 1 ? "s" : ""
      } com sucesso`,
    }[this.language];
  };

  get batchCreateUploadingFiles() {
    return {
      es: "Subiendo archivos...",
      en: "Uploading files...",
      pt: "Enviando arquivos...",
    }[this.language];
  }

  get batchCreateCreatingRecords() {
    return {
      es: "Creando registros...",
      en: "Creating records...",
      pt: "Criando registros...",
    }[this.language];
  }

  get batchCreateSelectFiles() {
    return {
      es: "Seleccionar o arrastrar archivos",
      en: "Select or drag files",
      pt: "Selecione ou arraste arquivos",
    }[this.language];
  }

  get batchCreateRemoveFile() {
    return {
      es: "Quitar",
      en: "Remove",
      pt: "Remover",
    }[this.language];
  }

  get multiDeleteButton() {
    return {
      es: "Eliminar seleccionados",
      en: "Delete selected",
      pt: "Excluir selecionados",
    }[this.language];
  }

  downloadOverwriteDescriptionWithName = (folderName: string) => {
    return {
      es: `Ya existe una carpeta con el nombre "${folderName}". ¿Sobrescribirla? Se borrarán todos los archivos existentes.`,
      en: `A folder named "${folderName}" already exists. Do you want to overwrite it? All existing files will be deleted.`,
      pt: `Já existe uma pasta com o nome "${folderName}". Deseja sobrescrevê-la? Todos os arquivos existentes serão excluídos.`,
    }[this.language];
  };

  uploadFailedMessage = (fileName: string, reason: string) => {
    return {
      es: `No se pudo subir "${fileName}"${
        reason ? `: ${reason}` : ""
      }. Volvé a adjuntarlo.`,
      en: `Could not upload "${fileName}"${
        reason ? `: ${reason}` : ""
      }. Please attach it again.`,
      pt: `Não foi possível enviar "${fileName}"${
        reason ? `: ${reason}` : ""
      }. Anexe-o novamente.`,
    }[this.language];
  };

  uploadingFileMessage = (fileName: string) => {
    return {
      es: `Subiendo "${fileName}"…`,
      en: `Uploading "${fileName}"…`,
      pt: `Enviando "${fileName}"…`,
    }[this.language];
  };

  uploadNotFinishedMessage = (fileName: string) => {
    return {
      es: `El archivo "${fileName}" todavía no terminó de subirse. Esperá a que termine o volvé a adjuntarlo.`,
      en: `The file "${fileName}" has not finished uploading yet. Wait for it to finish or attach it again.`,
      pt: `O arquivo "${fileName}" ainda não terminou de ser enviado. Aguarde ou anexe-o novamente.`,
    }[this.language];
  };

  invalidFileTypeMessage = (extensions: string) => {
    return {
      es: `Tipo de archivo no permitido. Extensiones aceptadas: ${extensions}`,
      en: `File type not allowed. Accepted extensions: ${extensions}`,
      pt: `Tipo de arquivo não permitido. Extensões aceitas: ${extensions}`,
    }[this.language];
  };

  satelliteTokenDescription = (satelliteName: string) => {
    return {
      es: `Se generó un nuevo token para "${satelliteName}". Copialo ahora, no vas a poder verlo de nuevo.`,
      en: `A new token has been generated for "${satelliteName}". Copy it now, you won't be able to see it again.`,
      pt: `Um novo token foi gerado para "${satelliteName}". Copie-o agora, você não poderá vê-lo novamente.`,
    }[this.language];
  };

  quickCreateConfirmTitle = (entityLabel: string) => {
    return {
      es: `Agregar a ${entityLabel}`,
      en: `Add to ${entityLabel}`,
      pt: `Adicionar a ${entityLabel}`,
    }[this.language];
  };

  quickCreateConfirmDescription = (value: string, entityLabel: string) => {
    return {
      es: `¿Agregar "${value}" a ${entityLabel}?`,
      en: `Are you sure you want to add "${value}" to ${entityLabel}?`,
      pt: `Tem certeza que deseja adicionar "${value}" a ${entityLabel}?`,
    }[this.language];
  };

  get logInLabel() {
    return { es: "Iniciar sesión", en: "Log in", pt: "Conecte-se" }[
      this.language
    ];
  }

  get logOutLabel() {
    return { es: "Cerrar sesión", en: "Log out", pt: "Sair" }[this.language];
  }

  get changePasswordLabel() {
    return {
      es: "Cambiar la contraseña",
      en: "Change password",
      pt: "Alterar a senha",
    }[this.language];
  }

  get openUserMenu() {
    return { es: "Menú de Usuario", en: "User menu", pt: "Menú de usuário" }[
      this.language
    ];
  }

  get beginOfListing() {
    return {
      es: "Inicio del listado",
      en: "Start of listing",
      pt: "Início da listagem",
    }[this.language];
  }

  get filterListing() {
    return {
      es: "Filtrado del listado",
      en: "Listing filtering",
      pt: "Filtragem de listagem",
    }[this.language];
  }

  get errorTitle() {
    return { es: "Error", en: "Error", pt: "Erro" }[this.language];
  }

  get loginPathLabel() {
    return { es: "Inicio sesión", en: "Login", pt: "Conecte-Se" }[
      this.language
    ];
  }

  get changePasswordPathLabel() {
    return {
      es: "Cambio de contraseña",
      en: "Change password",
      pt: "Mudança de senha",
    }[this.language];
  }

  get newPasswordLabel() {
    return { es: "Contraseña nueva", en: "New password", pt: "Senha nova" }[
      this.language
    ];
  }

  get homePathLabel() {
    return { es: "Inicio", en: "Home", pt: "Inicial" }[this.language];
  }

  get pageNotFoundPathLabel() {
    return {
      es: "Página no encontrada",
      en: "Page not found",
      pt: "Página não encontrada",
    }[this.language];
  }

  get forgotPasswordPathLabel() {
    return {
      es: "Olvidé mi contraseña",
      en: "Forgot Password",
      pt: "Esqueci minha senha",
    }[this.language];
  }

  get resetPasswordPathLabel() {
    return {
      es: "Restablecer contraseña",
      en: "Reset Password",
      pt: "Redefinir senha",
    }[this.language];
  }

  get forgotPasswordTitle() {
    return {
      es: "Olvidé mi contraseña",
      en: "Forgot Password",
      pt: "Esqueci minha senha",
    }[this.language];
  }

  get forgotPasswordInstructions() {
    return {
      es: "Ingresá tu email y te vamos a enviar un link para restablecer tu contraseña.",
      en: "Enter your email and we'll send you a link to reset your password.",
      pt: "Digite seu e-mail e enviaremos um link para redefinir sua senha.",
    }[this.language];
  }

  get forgotPasswordEmailSent() {
    return {
      es: "Si el email existe en nuestro sistema, vas a recibir un link para restablecer tu contraseña.",
      en: "If the email exists in our system, you will receive a link to reset your password.",
      pt: "Se o e-mail existir em nosso sistema, você receberá um link para redefinir sua senha.",
    }[this.language];
  }

  get forgotPasswordLinkLabel() {
    return {
      es: "¿Olvidaste tu contraseña?",
      en: "Forgot your password?",
      pt: "Esqueceu sua senha?",
    }[this.language];
  }

  get sendResetLinkLabel() {
    return { es: "Enviar link", en: "Send link", pt: "Enviar link" }[
      this.language
    ];
  }

  get backToLoginLabel() {
    return {
      es: "Volver al login",
      en: "Back to login",
      pt: "Voltar ao login",
    }[this.language];
  }

  get resetPasswordTitle() {
    return {
      es: "Restablecer contraseña",
      en: "Reset Password",
      pt: "Redefinir senha",
    }[this.language];
  }

  get resetPasswordLabel() {
    return { es: "Restablecer", en: "Reset", pt: "Redefinir" }[this.language];
  }

  get passwordChangedTitle() {
    return {
      es: "Contraseña cambiada",
      en: "Password Changed",
      pt: "Senha alterada",
    }[this.language];
  }

  get passwordChangedMessage() {
    return {
      es: "Tu contraseña fue cambiada exitosamente. Te llevamos al login.",
      en: "Your password has been changed successfully. You will be redirected to login.",
      pt: "Sua senha foi alterada com sucesso. Você será redirecionado para o login.",
    }[this.language];
  }

  get invalidOrExpiredToken() {
    return {
      es: "El link es inválido o ha expirado",
      en: "The link is invalid or has expired",
      pt: "O link é inválido ou expirou",
    }[this.language];
  }

  get requestNewLinkLabel() {
    return {
      es: "Solicitar nuevo link",
      en: "Request new link",
      pt: "Solicitar novo link",
    }[this.language];
  }

  get passwordMinLength() {
    return {
      es: "Al menos 8 caracteres",
      en: "At least 8 characters",
      pt: "Pelo menos 8 caracteres",
    }[this.language];
  }

  get passwordRequiresUppercase() {
    return {
      es: "Al menos una letra mayúscula",
      en: "At least one uppercase letter",
      pt: "Pelo menos uma letra maiúscula",
    }[this.language];
  }

  get passwordRequiresLowercase() {
    return {
      es: "Al menos una letra minúscula",
      en: "At least one lowercase letter",
      pt: "Pelo menos uma letra minúscula",
    }[this.language];
  }

  get passwordRequiresNumber() {
    return {
      es: "Al menos un número",
      en: "At least one number",
      pt: "Pelo menos um número",
    }[this.language];
  }

  get passwordRequiresSpecial() {
    return {
      es: "Al menos un caracter especial (!@#$%...)",
      en: "At least one special character (!@#$%...)",
      pt: "Pelo menos um caractere especial (!@#$%...)",
    }[this.language];
  }

  get passwordNotCommon() {
    return {
      es: "No es una contraseña común",
      en: "Not a common password",
      pt: "Não é uma senha comum",
    }[this.language];
  }

  get passwordsMatchLabel() {
    return {
      es: "Las contraseñas coinciden",
      en: "Passwords match",
      pt: "As senhas coincidem",
    }[this.language];
  }

  get passwordRequirementsNotMet() {
    return {
      es: "La contraseña no cumple los requisitos",
      en: "Password does not meet requirements",
      pt: "A senha não atende aos requisitos",
    }[this.language];
  }

  get passwordIsCommon() {
    return {
      es: "Esta contraseña es muy común y no es segura",
      en: "This password is too common and not secure",
      pt: "Esta senha é muito comum e não é segura",
    }[this.language];
  }

  get passwordLabel() {
    return { es: "Contraseña", en: "Password", pt: "Senha" }[this.language];
  }

  get appTitle() {
    return { es: "Jupiter", en: "Jupiter", pt: "Jupiter" }[this.language];
  }

  get serverError() {
    return {
      es: "No se pudo completar la operación",
      en: "Could not complete the operation",
      pt: "Não foi possível completar a operação",
    }[this.language];
  }

  get duplicateValueError() {
    return {
      es: "Este valor ya existe en la lista",
      en: "This value already exists in the list",
      pt: "Este valor já existe na lista",
    }[this.language];
  }

  get authorizationError() {
    return {
      es: "Error de autorización",
      en: "Authorization error",
      pt: "Erro de autorização",
    }[this.language];
  }

  get doNotHavePermission() {
    return {
      es: "Tu usuario no tiene permiso para realizar esta operación",
      en: "Do not have permission to perform this operation",
      pt: "Não tem permissão para realizar esta operação",
    }[this.language];
  }

  get UnsavedChangesWarning() {
    return {
      es: "Hay cambios sin guardar. ¿Salir igual?",
      en: "You have unsaved changes. Are you sure you want to leave?",
      pt: "Você tem alterações não salvas. Tem certeza de que deseja sair?",
    }[this.language];
  }

  get endOfListing() {
    return {
      es: "Final del listado",
      en: "End of listing",
      pt: "Fim da listagem",
    }[this.language];
  }

  get startOfListing() {
    return {
      es: "Inicio del listado",
      en: "Start of listing",
      pt: "Início da listagem",
    }[this.language];
  }

  get sortingHint() {
    return { es: "Ordenar", en: "Sort", pt: "Classificar" }[this.language];
  }

  get noColumnMessage() {
    return {
      es: "No hay columnas para mostrar",
      en: "There are no columns to display",
      pt: "Não há colunas para exibir",
    }[this.language];
  }

  get noDataMessage() {
    return {
      es: "No hay datos para mostrar",
      en: "There is no data to display",
      pt: "Não há dados para exibir",
    }[this.language];
  }

  get addCommand() {
    return { es: "Agregar", en: "Add", pt: "Adicionar" }[this.language];
  }

  get actionsLabel() {
    return { es: "Acciones", en: "Actions", pt: "Ações" }[this.language];
  }

  get groupedByLabel() {
    return { es: "Agrupado por ", en: "Grouped by ", pt: "Agrupado por " }[
      this.language
    ];
  }

  get restoreGroupingCommand() {
    return { es: "Reagrupar", en: "Regroup", pt: "Reagrupar" }[this.language];
  }

  get maximizeCommand() {
    return { es: "Maximizar", en: "Maximize", pt: "Maximizar" }[this.language];
  }

  get minimizeCommand() {
    return { es: "Minimizar", en: "Minimize", pt: "Minimizar" }[this.language];
  }

  get pipelineRetryingTitle() {
    return { es: "Reintentando", en: "Retrying", pt: "Tentando novamente" }[
      this.language
    ];
  }

  get pipelineRetryingMessage() {
    return {
      es: "Se produjo un inconveniente; estamos continuando el proceso",
      en: "A hiccup occurred; the process is continuing",
      pt: "Ocorreu um contratempo; o processo está continuando",
    }[this.language];
  }

  get errorBoundaryTitle() {
    return {
      es: "Ocurrió un error inesperado",
      en: "An unexpected error occurred",
      pt: "Ocorreu um erro inesperado",
    }[this.language];
  }

  get errorBoundaryMessage() {
    return {
      es: "No pudimos completar la operación. Por favor, contactá al administrador del sistema con el detalle de abajo.",
      en: "We couldn't complete the operation. Please contact the system administrator with the details below.",
      pt: "Não foi possível concluir a operação. Por favor, entre em contato com o administrador do sistema com os detalhes abaixo.",
    }[this.language];
  }

  get errorBoundaryDetails() {
    return {
      es: "Detalle técnico",
      en: "Technical details",
      pt: "Detalhe técnico",
    }[this.language];
  }

  get errorBoundaryReload() {
    return { es: "Recargar", en: "Reload", pt: "Recarregar" }[this.language];
  }

  get errorBoundaryHome() {
    return { es: "Ir al inicio", en: "Go to home", pt: "Ir para o início" }[
      this.language
    ];
  }

  get maintenanceTitle() {
    return {
      es: "Estamos trabajando en el sistema",
      en: "We're working on the system",
      pt: "Estamos trabalhando no sistema",
    }[this.language];
  }

  get maintenanceDefaultMessage() {
    return {
      es: "El servidor está en mantenimiento. La aplicación se reactivará automáticamente en cuanto termine.",
      en: "The server is under maintenance. The application will resume automatically as soon as it finishes.",
      pt: "O servidor está em manutenção. A aplicação será reativada automaticamente assim que terminar.",
    }[this.language];
  }

  get maintenanceRetrying() {
    return {
      es: "Verificando reconexión automáticamente…",
      en: "Checking for reconnection automatically…",
      pt: "Verificando reconexão automaticamente…",
    }[this.language];
  }

  get maintenanceWarnMessage() {
    return {
      es: "El sistema entrará en mantenimiento en unos instantes. Guardá los cambios que tengas abiertos.",
      en: "The system will enter maintenance shortly. Please save any open changes.",
      pt: "O sistema entrará em manutenção em instantes. Salve as alterações abertas.",
    }[this.language];
  }

  get fileNameLabel() {
    return { es: "Nombre", en: "Name", pt: "Nome" }[this.language];
  }

  get fileSizeLabel() {
    return { es: "Tamaño", en: "Size", pt: "Tamanho" }[this.language];
  }

  get fileTypeLabel() {
    return { es: "Tipo", en: "Type", pt: "Tipo" }[this.language];
  }

  get downloadCommand() {
    return { es: "Descargar", en: "Download", pt: "Baixar" }[this.language];
  }

  get searchCommand() {
    return { es: "Buscar", en: "Find", pt: "Procurar" }[this.language];
  }

  get newCommand() {
    return { es: "Nuevo", en: "New", pt: "Novo" }[this.language];
  }

  get duplicateAndEditCommand() {
    return {
      es: "Duplicar y editar",
      en: "Duplicate and Edit",
      pt: "Duplicar e Editar",
    }[this.language];
  }

  get duplicateCommand() {
    return { es: "Duplicar", en: "Duplicate", pt: "Duplicar" }[this.language];
  }

  get listCommand() {
    return { es: "Listar", en: "List", pt: "Listar" }[this.language];
  }

  get viewCommand() {
    return { es: "Ver", en: "View", pt: "Visualizar" }[this.language];
  }

  get editCommand() {
    return { es: "Editar", en: "Edit", pt: "Editar" }[this.language];
  }

  get deleteCommand() {
    return { es: "Borrar", en: "Delete", pt: "Excluir" }[this.language];
  }

  get restoreCommand() {
    return { es: "Restaurar", en: "Restore", pt: "Restaurar" }[this.language];
  }

  get resetPasswordCommand() {
    return {
      es: "Resetear contraseña",
      en: "Reset Password",
      pt: "Redefinir senha",
    }[this.language];
  }

  get selectAllCommand() {
    return {
      es: "Seleccionar todos",
      en: "Select all",
      pt: "Selecionar todos",
    }[this.language];
  }

  get deselectAllCommand() {
    return {
      es: "Deseleccionar todos",
      en: "Deselect all",
      pt: "Desmarcar todos",
    }[this.language];
  }

  get selectAllShort() {
    return { es: "Todos", en: "All", pt: "Todos" }[this.language];
  }

  get selectNoneShort() {
    return { es: "Ninguno", en: "None", pt: "Nenhum" }[this.language];
  }

  get exportCommand() {
    return {
      es: "Exportar a Excel",
      en: "Export to Excel",
      pt: "Exportar para Excel",
    }[this.language];
  }

  get columnsCommand() {
    return { es: "Columnas", en: "Columns", pt: "Colunas" }[this.language];
  }

  get filtersCommand() {
    return { es: "Filtros", en: "Filters", pt: "Filtros" }[this.language];
  }

  get noRecordsMessage() {
    return { es: "No hay registros", en: "No records", pt: "Sem registros" }[
      this.language
    ];
  }

  get commitCommand() {
    return { es: "Ok", en: "Ok", pt: "Ok" }[this.language];
  }

  get cancelCommand() {
    return { es: "Cancelar", en: "Cancel", pt: "Cancelar" }[this.language];
  }

  get optionalFieldHint() {
    return { es: "Opcional", en: "Optional", pt: "Opcional" }[this.language];
  }

  get closeCommand() {
    return { es: "Cerrar", en: "Close", pt: "Fechar" }[this.language];
  }

  get dashboardCustomizeCommand() {
    return { es: "Personalizar", en: "Customize", pt: "Personalizar" }[
      this.language
    ];
  }

  get dashboardCustomizeTitle() {
    return {
      es: "Personalizar inicio",
      en: "Customize home",
      pt: "Personalizar início",
    }[this.language];
  }

  get dashboardCustomizeHint() {
    return {
      es: "Gráficos del inicio",
      en: "Choose which charts to show on your home.",
      pt: "Escolha quais gráficos ver no seu início.",
    }[this.language];
  }

  get dashboardCustomizeEmpty() {
    return {
      es: "No hay gráficos disponibles para tu cuenta.",
      en: "No charts are available for your account.",
      pt: "Não há gráficos disponíveis para a sua conta.",
    }[this.language];
  }

  get validationErrorsTitle() {
    return {
      es: "Hay errores para corregir:",
      en: "Please correct the following errors:",
      pt: "Por favor corrija os seguintes erros:",
    }[this.language];
  }

  get homeTitle() {
    return { es: "Inicio", en: "Home", pt: "Início" }[this.language];
  }

  get homeLabel() {
    return { es: "Inicio", en: "Home", pt: "Início" }[this.language];
  }

  get pageNotFound() {
    return {
      es: "Página no encontrada",
      en: "Page Not Found",
      pt: "Página não encontrada",
    }[this.language];
  }

  get processingTitle() {
    return { es: "Procesando", en: "Processing", pt: "Em processamento" }[
      this.language
    ];
  }

  get processingDescription() {
    return {
      es: "...conectando con el servidor",
      en: "...connecting to the server",
      pt: "...conectando ao servidor",
    }[this.language];
  }

  get saveProfile() {
    return { es: "Guardar formato", en: "Save format", pt: "Salvar formato" }[
      this.language
    ];
  }

  get printForm() {
    return { es: "Imprimir", en: "Print", pt: "Imprimir" }[this.language];
  }

  get editForm() {
    return { es: "Editar", en: "Edit", pt: "Editar" }[this.language];
  }

  get closeForm() {
    return { es: "Cerrar", en: "Close", pt: "Fechar" }[this.language];
  }

  get saveNewEntity() {
    return { es: "Guardar nuevo", en: "Save new", pt: "Salvar novo" }[
      this.language
    ];
  }

  get perPage() {
    return { es: "Por página", en: "Per page", pt: "Por página" }[
      this.language
    ];
  }

  get cancelNewEntity() {
    return { es: "Descartar", en: "Discard", pt: "Descartar" }[this.language];
  }

  get saveUpdatedEntity() {
    return { es: "Actualizar", en: "Update", pt: "Atualizar" }[this.language];
  }

  get saveEntity() {
    return { es: "Guardar", en: "Save", pt: "Salvar" }[this.language];
  }

  get cancelUpdatedEntity() {
    return {
      es: "Descartar cambios",
      en: "Discard changes",
      pt: "Descartar mudanças",
    }[this.language];
  }

  get columnsChooserLabel() {
    return { es: "Columnas", en: "Columns", pt: "Colunas" }[this.language];
  }

  get differentPasswords() {
    return {
      es: "Las contraseñas son diferentes",
      en: "Passwords are different",
      pt: "As senhas são diferentes",
    }[this.language];
  }

  get confirmRestePasswordTitle() {
    return {
      es: "Reseteo de contraseña",
      en: "Password reset",
      pt: "Redefinição de senha",
    }[this.language];
  }

  get confirmRestePasswordDescription() {
    return {
      es: "¿Resetear la contraseña del usuario?",
      en: "Do you confirm the password reset for the user?",
      pt: "Você confirma a redefinição de senha para o usuário?",
    }[this.language];
  }

  get badEmailOrPasswordError() {
    return {
      es: "Error en email o contraseña",
      en: "Bad Email or Password",
      pt: "Email ou senha incorretos",
    }[this.language];
  }

  get mandatoryField() {
    return {
      es: "Campo obligatorio",
      en: "Mandatory field",
      pt: "Campo obrigatório",
    }[this.language];
  }

  get uploadingFileTitle() {
    return {
      es: "Subiendo archivo",
      en: "Uploading file",
      pt: "Enviando arquivo",
    }[this.language];
  }

  get uploadConnectionLost() {
    return {
      es: "el archivo no llegó al servidor (se cortó la conexión o el navegador no pudo leerlo)",
      en: "the file did not reach the server (the connection dropped or the browser could not read it)",
      pt: "o arquivo não chegou ao servidor (a conexão caiu ou o navegador não conseguiu lê-lo)",
    }[this.language];
  }

  get atLeastOneBadEmail() {
    return {
      es: "Al menos un email no es válido",
      en: "The syntax of at least one email is incorrect",
      pt: "A sintaxe de pelo menos um e-mail está incorreta",
    }[this.language];
  }

  get badSyntaxEmail() {
    return {
      es: "El email no es válido",
      en: "The syntax of the email is incorrect",
      pt: "A sintaxe do e-mail está incorreta",
    }[this.language];
  }

  get atLeastOneValue() {
    return {
      es: "Debe tener al menos un valor",
      en: "Must have at least one value",
      pt: "Deve ter pelo menos um valor",
    }[this.language];
  }

  get maxLengthValue() {
    return {
      es: "Excede la longitud máxima",
      en: "Exceeds maximum length",
      pt: "Excede o comprimento máximo",
    }[this.language];
  }

  get maxValue() {
    return {
      es: "Excede el valor máximo",
      en: "Exceeds maximum value",
      pt: "Excede o valor máximo",
    }[this.language];
  }

  get maxDateValue() {
    return {
      es: "Excede la fecha máxima",
      en: "Exceeds maximum date",
      pt: "Excede a data máxima",
    }[this.language];
  }

  get minLengthValue() {
    return {
      es: "Es menor que la longitud mínima",
      en: "It is less than the minimum length",
      pt: "É menor que o comprimento mínimo",
    }[this.language];
  }

  get minValue() {
    return {
      es: "Es menor que el valor mínimo",
      en: "It is less than the minimum value",
      pt: "É menor que o valor mínimo",
    }[this.language];
  }

  get minDateValue() {
    return {
      es: "Es menor que la fecha mínima",
      en: "It is less than the minimum date",
      pt: "É menor que a data mínima",
    }[this.language];
  }

  get unsavedChanges() {
    return {
      es: "Cambios sin guardar",
      en: "Unsaved changes",
      pt: "Alterações não salvas",
    }[this.language];
  }

  get unsavedChangesMessage() {
    return {
      es: "Hay cambios sin guardar. ¿Descartarlos y continuar?",
      en: "You have unsaved changes. Do you want to discard them and continue?",
      pt: "Você tem alterações não salvas. Deseja descartá-las e continuar?",
    }[this.language];
  }

  get discardChanges() {
    return {
      es: "Descartar cambios",
      en: "Discard changes",
      pt: "Descartar alterações",
    }[this.language];
  }

  get exitWithoutSaving() {
    return {
      es: "Hay cambios sin guardar. ¿Salir sin guardar?",
      en: "There are unsaved changes. Do you want to exit without saving?",
      pt: "Há alterações não salvas. Deseja sair sem salvar?",
    }[this.language];
  }

  get cannotProceedWithChanges() {
    return {
      es: "No se puede continuar con cambios pendientes",
      en: "Cannot proceed with pending changes",
      pt: "Não é possível continuar com alterações pendentes",
    }[this.language];
  }

  get saveChangesFirst() {
    return {
      es: "Primero hay que guardar los cambios",
      en: "You must save changes first",
      pt: "Você deve salvar as alterações primeiro",
    }[this.language];
  }

  get confirmCancelTitle() {
    return {
      es: "Cambios sin guardar",
      en: "Unsaved changes",
      pt: "Alterações não salvas",
    }[this.language];
  }

  get newVersionAvailable() {
    return {
      es: "Nueva versión disponible",
      en: "New version available",
      pt: "Nova versão disponível",
    }[this.language];
  }

  get updateNow() {
    return { es: "Actualizar ahora", en: "Update now", pt: "Atualizar agora" }[
      this.language
    ];
  }

  get updateLater() {
    return { es: "Más tarde", en: "Later", pt: "Mais tarde" }[this.language];
  }

  get sessionExpiredTitle() {
    return {
      es: "Sesión expirada",
      en: "Session expired",
      pt: "Sessão expirada",
    }[this.language];
  }

  get sessionExpiredMessage() {
    return {
      es: "Tu sesión expiró. Por favor, iniciá sesión nuevamente.",
      en: "Your session has expired. Please login again.",
      pt: "Sua sessão expirou. Por favor, faça login novamente.",
    }[this.language];
  }

  get jsonValidationError() {
    return { es: "JSON inválido", en: "Invalid JSON", pt: "JSON inválido" }[
      this.language
    ];
  }

  get unsavedChangesInFutureTitle() {
    return {
      es: "Cambios sin guardar",
      en: "Unsaved changes",
      pt: "Alterações não salvas",
    }[this.language];
  }

  get unsavedChangesInFutureMessage() {
    return {
      es: "Las páginas {pageNames} tienen cambios sin guardar. ¿Continuar?",
      en: "Pages {pageNames} have unsaved changes. Continue?",
      pt: "As páginas {pageNames} têm alterações não salvas. Continuar?",
    }[this.language];
  }

  get cancel() {
    return { es: "Cancelar", en: "Cancel", pt: "Cancelar" }[this.language];
  }

  get continue() {
    return { es: "Continuar", en: "Continue", pt: "Continuar" }[this.language];
  }

  get Back() {
    return { es: "Volver", en: "Back", pt: "Voltar" }[this.language];
  }

  get Close() {
    return { es: "Cerrar", en: "Close", pt: "Fechar" }[this.language];
  }

  get itemUpdatedMayHaveMoved() {
    return {
      es: "Item actualizado. Puede haber cambiado de posición.",
      en: "Item updated. It may have changed position.",
      pt: "Item atualizado. Pode ter mudado de posição.",
    }[this.language];
  }

  get sseConnected() {
    return {
      es: "Conectado al servidor",
      en: "Connected to server",
      pt: "Conectado ao servidor",
    }[this.language];
  }

  get sseDisconnected() {
    return {
      es: "Error de conexión. Intentando reconectar...",
      en: "Connection error. Retrying...",
      pt: "Erro de conexão. Tentando reconectar...",
    }[this.language];
  }

  get sseReconnecting() {
    return {
      es: "Reconectando al servidor...",
      en: "Reconnecting to server...",
      pt: "Reconectando ao servidor...",
    }[this.language];
  }

  get sseConnectionFailed() {
    return {
      es: "Error de conexión con el servidor",
      en: "Server connection error",
      pt: "Erro de conexão com o servidor",
    }[this.language];
  }

  get systemMaintenanceScheduled() {
    return {
      es: "Sistema entrará en mantenimiento en {seconds} segundos",
      en: "System will enter maintenance in {seconds} seconds",
      pt: "Sistema entrará em manutenção em {seconds} segundos",
    }[this.language];
  }

  get systemInMaintenance() {
    return {
      es: "Sistema en mantenimiento",
      en: "System under maintenance",
      pt: "Sistema em manutenção",
    }[this.language];
  }

  get systemOperational() {
    return {
      es: "Sistema operacional",
      en: "System operational",
      pt: "Sistema operacional",
    }[this.language];
  }

  get maintenanceMessage() {
    return {
      es: "Estamos actualizando el sistema. Volveremos pronto.",
      en: "We are updating the system. We'll be back soon.",
      pt: "Estamos atualizando o sistema. Voltaremos em breve.",
    }[this.language];
  }

  get pleaSaveYourWork() {
    return {
      es: "Por favor, guardá tu trabajo. El sistema entrará en mantenimiento en breve.",
      en: "Please save your work. The system will enter maintenance soon.",
      pt: "Por favor, salve seu trabalho. O sistema entrará em manutenção em breve.",
    }[this.language];
  }

  get estimatedTime() {
    return {
      es: "Tiempo estimado",
      en: "Estimated time",
      pt: "Tempo estimado",
    }[this.language];
  }

  get activeOperationsLabel() {
    return {
      es: "Operaciones Activas",
      en: "Active Operations",
      pt: "Operações Ativas",
    }[this.language];
  }

  get noActiveOperations() {
    return {
      es: "Sin operaciones activas",
      en: "No active operations",
      pt: "Sem operações ativas",
    }[this.language];
  }

  get cancelOperationLabel() {
    return {
      es: "Cancelar operación",
      en: "Cancel operation",
      pt: "Cancelar operação",
    }[this.language];
  }

  get settingsLabel() {
    return { es: "Configuración", en: "Settings", pt: "Configurações" }[
      this.language
    ];
  }

  get myProfileLabel() {
    return { es: "Mi Perfil", en: "My Profile", pt: "Meu Perfil" }[
      this.language
    ];
  }

  get profileSettingsLabel() {
    return { es: "Perfil", en: "Profile", pt: "Perfil" }[this.language];
  }

  get securitySettingsLabel() {
    return { es: "Seguridad", en: "Security", pt: "Segurança" }[this.language];
  }

  get preferencesSettingsLabel() {
    return { es: "Preferencias", en: "Preferences", pt: "Preferências" }[
      this.language
    ];
  }

  get languageLabel() {
    return { es: "Idioma", en: "Language", pt: "Idioma" }[this.language];
  }

  get themeLabel() {
    return { es: "Tema", en: "Theme", pt: "Tema" }[this.language];
  }

  get darkModeLabel() {
    return { es: "Modo oscuro", en: "Dark mode", pt: "Modo escuro" }[
      this.language
    ];
  }

  get lightModeLabel() {
    return { es: "Modo claro", en: "Light mode", pt: "Modo claro" }[
      this.language
    ];
  }

  get saveChangesLabel() {
    return {
      es: "Guardar cambios",
      en: "Save changes",
      pt: "Salvar alterações",
    }[this.language];
  }

  get savingLabel() {
    return { es: "Guardando...", en: "Saving...", pt: "Salvando..." }[
      this.language
    ];
  }

  get savingInProgress() {
    return { es: "Guardando…", en: "Saving…", pt: "Salvando…" }[this.language];
  }

  get savedButOutdated() {
    return {
      es: "Se guardó, pero no se pudieron cargar los datos actualizados. Recargá para seguir editando.",
      en: "Saved, but the updated data could not be loaded. Reload to continue editing.",
      pt: "Foi salvo, mas não foi possível carregar os dados atualizados. Recarregue para continuar editando.",
    }[this.language];
  }

  get reloadRecord() {
    return { es: "Recargar", en: "Reload", pt: "Recarregar" }[this.language];
  }

  get reloadWithoutSaving() {
    return {
      es: "Hay cambios sin guardar. ¿Recargar y descartarlos?",
      en: "There are unsaved changes. Do you want to reload and discard them?",
      pt: "Há alterações não salvas. Deseja recarregar e descartá-las?",
    }[this.language];
  }

  get settingsSavedSuccessfullyLabel() {
    return {
      es: "Configuraciones guardadas exitosamente",
      en: "Settings saved successfully",
      pt: "Configurações salvas com sucesso",
    }[this.language];
  }

  get bioLabel() {
    return { es: "Biografía", en: "Bio", pt: "Biografia" }[this.language];
  }

  get nameLabel() {
    return { es: "Nombre", en: "Name", pt: "Nome" }[this.language];
  }

  get emailLabel() {
    return { es: "Email", en: "Email", pt: "Email" }[this.language];
  }

  get currentPasswordLabel() {
    return {
      es: "Contraseña actual",
      en: "Current password",
      pt: "Senha atual",
    }[this.language];
  }

  get confirmPasswordLabel() {
    return {
      es: "Confirmar contraseña",
      en: "Confirm password",
      pt: "Confirmar senha",
    }[this.language];
  }

  get passwordRequirementsLabel() {
    return {
      es: "Debe tener al menos 8 caracteres",
      en: "Must be at least 8 characters",
      pt: "Deve ter pelo menos 8 caracteres",
    }[this.language];
  }

  get passwordsDoNotMatchLabel() {
    return {
      es: "Las contraseñas no coinciden",
      en: "Passwords do not match",
      pt: "As senhas não coincidem",
    }[this.language];
  }

  get changeAvatarLabel() {
    return { es: "Cambiar avatar", en: "Change avatar", pt: "Alterar avatar" }[
      this.language
    ];
  }

  get uploadImageLabel() {
    return { es: "Subir imagen", en: "Upload image", pt: "Enviar imagem" }[
      this.language
    ];
  }

  get zoomLabel() {
    return { es: "Zoom", en: "Zoom", pt: "Zoom" }[this.language];
  }

  get rotationLabel() {
    return { es: "Rotación", en: "Rotation", pt: "Rotação" }[this.language];
  }

  get clickToUploadLabel() {
    return {
      es: "Subir una imagen",
      en: "Click to upload an image",
      pt: "Clique para enviar uma imagem",
    }[this.language];
  }

  get saveAvatarLabel() {
    return { es: "Guardar avatar", en: "Save avatar", pt: "Salvar avatar" }[
      this.language
    ];
  }

  get chooseAnotherImageLabel() {
    return {
      es: "Elegir otra imagen",
      en: "Choose another image",
      pt: "Escolher outra imagem",
    }[this.language];
  }

  get downloadOverwriteTitle() {
    return {
      es: "Carpeta existente",
      en: "Existing folder",
      pt: "Pasta existente",
    }[this.language];
  }

  get downloadOverwriteConfirm() {
    return { es: "Sobrescribir", en: "Overwrite", pt: "Sobrescrever" }[
      this.language
    ];
  }

  get downloadOverwriteCancel() {
    return { es: "Cancelar", en: "Cancel", pt: "Cancelar" }[this.language];
  }

  get downloadProgressTitle() {
    return {
      es: "Descargando archivos",
      en: "Downloading files",
      pt: "Baixando arquivos",
    }[this.language];
  }

  get downloadProgressOf() {
    return { es: "de", en: "of", pt: "de" }[this.language];
  }

  get downloadProgressFile() {
    return { es: "Archivo", en: "File", pt: "Arquivo" }[this.language];
  }

  get downloadProgressCancel() {
    return { es: "Cancelar", en: "Cancel", pt: "Cancelar" }[this.language];
  }

  get downloadCancelled() {
    return {
      es: "Descarga cancelada",
      en: "Download cancelled",
      pt: "Download cancelado",
    }[this.language];
  }

  get emailVerified() {
    return { es: "Verificado", en: "Verified", pt: "Verificado" }[
      this.language
    ];
  }

  get emailDataValidated() {
    return {
      es: "Datos confirmados",
      en: "Data confirmed",
      pt: "Dados confirmados",
    }[this.language];
  }

  get emailPending() {
    return { es: "Pendiente", en: "Pending", pt: "Pendente" }[this.language];
  }

  get emailNotVerified() {
    return { es: "No verificado", en: "Not verified", pt: "Não verificado" }[
      this.language
    ];
  }

  get emailSendVerification() {
    return {
      es: "Enviar verificación",
      en: "Send verification",
      pt: "Enviar verificação",
    }[this.language];
  }

  get emailSendValidation() {
    return {
      es: "Enviar validación",
      en: "Send validation",
      pt: "Enviar validação",
    }[this.language];
  }

  get emailSendPendingChanges() {
    return {
      es: "Hay que guardar los cambios antes de enviar la validación",
      en: "Save your changes before sending the validation",
      pt: "Salve as alterações antes de enviar a validação",
    }[this.language];
  }

  get emailResendVerification() {
    return {
      es: "Reenviar verificación",
      en: "Resend verification",
      pt: "Reenviar verificação",
    }[this.language];
  }

  get emailResendValidation() {
    return {
      es: "Reenviar validación",
      en: "Resend validation",
      pt: "Reenviar validação",
    }[this.language];
  }

  get emailResendConfirmTitle() {
    return { es: "Reenviar", en: "Resend", pt: "Reenviar" }[this.language];
  }

  get emailResendConfirmAlreadyVerified() {
    return {
      es: "Este email ya fue verificado. ¿Enviar una nueva verificación?",
      en: "This email has already been verified. Do you want to send a new verification?",
      pt: "Este e-mail já foi verificado. Deseja enviar uma nova verificação?",
    }[this.language];
  }

  get emailResendConfirmAlreadyValidated() {
    return {
      es: "Los datos ya fueron confirmados. ¿Enviar una nueva validación?",
      en: "The data has already been confirmed. Send a new validation?",
      pt: "Os dados já foram confirmados. Enviar uma nova validação?",
    }[this.language];
  }

  get emailResendConfirmPending() {
    return {
      es: "Ya se envió una verificación pendiente. ¿Reenviarla?",
      en: "A verification is already pending. Do you want to resend?",
      pt: "Já existe uma verificação pendente. Deseja reenviar?",
    }[this.language];
  }

  get emailResendAvailableAt() {
    return {
      es: "Reenviar disponible en",
      en: "Resend available in",
      pt: "Reenviar disponível em",
    }[this.language];
  }

  get emailSentAt() {
    return { es: "enviado", en: "sent", pt: "enviado" }[this.language];
  }

  get emailVerifyOnly() {
    return {
      es: "Verificar solo el email",
      en: "Verify email only",
      pt: "Verificar só o e-mail",
    }[this.language];
  }

  get emailResendVerifyOnly() {
    return {
      es: "Reenviar la verificación del email",
      en: "Resend email verification",
      pt: "Reenviar a verificação do e-mail",
    }[this.language];
  }

  get emailValidateData() {
    return {
      es: "Validar email y datos",
      en: "Validate email and data",
      pt: "Validar e-mail e dados",
    }[this.language];
  }

  get emailResendValidateData() {
    return {
      es: "Reenviar la validación de email y datos",
      en: "Resend email and data validation",
      pt: "Reenviar a validação de e-mail e dados",
    }[this.language];
  }

  get emailDataPending() {
    return {
      es: "Validación pendiente",
      en: "Validation pending",
      pt: "Validação pendente",
    }[this.language];
  }

  get emailDataExpired() {
    return {
      es: "Validación vencida",
      en: "Validation expired",
      pt: "Validação expirada",
    }[this.language];
  }

  get emailDataNotValidated() {
    return {
      es: "Datos sin confirmar",
      en: "Data not confirmed",
      pt: "Dados não confirmados",
    }[this.language];
  }

  get emailDataValidatedOn() {
    return {
      es: "Datos confirmados el",
      en: "Data confirmed on",
      pt: "Dados confirmados em",
    }[this.language];
  }

  get emailDataLastValidation() {
    return {
      es: "Última validación:",
      en: "Last validation:",
      pt: "Última validação:",
    }[this.language];
  }

  get emailVerifyOnlyBlockedByData() {
    return {
      es: "Hay una validación de datos pendiente, que también verifica el email",
      en: "A data validation is pending; it also verifies the email",
      pt: "Há uma validação de dados pendente, que também verifica o e-mail",
    }[this.language];
  }

  get emailSendPendingChangesVerification() {
    return {
      es: "Hay que guardar los cambios antes de enviar la verificación",
      en: "Save your changes before sending the verification",
      pt: "Salve as alterações antes de enviar a verificação",
    }[this.language];
  }

  get emailResendConfirmVerifyOnly() {
    return {
      es: "Se va a enviar un correo para verificar solo el email. Los datos confirmados no cambian. ¿Enviarlo?",
      en: "An email will be sent to verify the email address only. Confirmed data won't change. Send it?",
      pt: "Será enviado um e-mail para verificar só o endereço. Os dados confirmados não mudam. Enviar?",
    }[this.language];
  }

  get emailResendConfirmDataPending() {
    return {
      es: "Ya hay una validación de datos pendiente. ¿Reenviarla?",
      en: "A data validation is already pending. Resend it?",
      pt: "Já existe uma validação de dados pendente. Reenviá-la?",
    }[this.language];
  }

  get emailResendConfirmValidateData() {
    return {
      es: "Se va a enviar un correo para validar el email y los datos. El email queda pendiente hasta que se confirmen los datos. ¿Enviarlo?",
      en: "An email will be sent to validate the email address and the data. The email stays pending until the data is confirmed. Send it?",
      pt: "Será enviado um e-mail para validar o endereço e os dados. O e-mail fica pendente até os dados serem confirmados. Enviar?",
    }[this.language];
  }

  get emailSendRateLimited() {
    return {
      es: "Ya se envió un correo a este email hace poco.",
      en: "An email was sent to this address a moment ago.",
      pt: "Um e-mail foi enviado para este endereço há pouco.",
    }[this.language];
  }

  get emailSendRetryIn() {
    return {
      es: "Se puede volver a enviar en",
      en: "It can be sent again in",
      pt: "Dá para enviar de novo em",
    }[this.language];
  }

  get emailSendDataValidationPending() {
    return {
      es: "No se envió: hay una validación de datos pendiente con este email, que también lo verifica.",
      en: "Not sent: there's a pending data validation for this email, and it also verifies it.",
      pt: "Não foi enviado: há uma validação de dados pendente com este e-mail, que também o verifica.",
    }[this.language];
  }

  get emailSendScopeNotSupported() {
    return {
      es: "No se envió: este email tiene una validación de datos, y solo se puede volver a enviar la de email y datos.",
      en: "Not sent: this email has a data validation, so only the email and data validation can be sent again.",
      pt: "Não foi enviado: este e-mail tem uma validação de dados, e só dá para enviar de novo a de e-mail e dados.",
    }[this.language];
  }

  get emailSendConflict() {
    return {
      es: "No se envió: se hizo otro envío a este email al mismo tiempo. El estado ya está actualizado.",
      en: "Not sent: another send to this email happened at the same time. The status is already updated.",
      pt: "Não foi enviado: outro envio para este e-mail aconteceu ao mesmo tempo. O status já está atualizado.",
    }[this.language];
  }

  get emailSendFailed() {
    return {
      es: "No se pudo enviar el correo",
      en: "Couldn't send the email",
      pt: "Não deu para enviar o e-mail",
    }[this.language];
  }

  get workflowExecutingPrefix() {
    return { es: "Ejecutando", en: "Executing", pt: "Executando" }[
      this.language
    ];
  }

  get workflowNewStatePrefix() {
    return { es: "Nuevo estado", en: "New state", pt: "Novo estado" }[
      this.language
    ];
  }

  get notificationsTitle() {
    return { es: "Notificaciones", en: "Notifications", pt: "Notificações" }[
      this.language
    ];
  }

  get notificationsUnread() {
    return {
      es: "notificaciones sin leer",
      en: "unread notifications",
      pt: "notificações não lidas",
    }[this.language];
  }

  get notification_externalValidationSubmitted_title() {
    return {
      es: "Validación de datos recibida",
      en: "Data validation received",
      pt: "Validação de dados recebida",
    }[this.language];
  }

  get notification_externalValidationSubmitted_body() {
    return {
      es: "{contactName} cargó sus datos.",
      en: "{contactName} submitted their data.",
      pt: "{contactName} enviou seus dados.",
    }[this.language];
  }

  get notification_externalValidationSubmitted_body_verified() {
    return {
      es: "{contactName} cargó y validó sus datos.",
      en: "{contactName} submitted and verified their data.",
      pt: "{contactName} enviou e validou seus dados.",
    }[this.language];
  }

  get markAllAsRead() {
    return {
      es: "Marcar todas como leídas",
      en: "Mark all as read",
      pt: "Marcar todas como lidas",
    }[this.language];
  }

  get noNotifications() {
    return {
      es: "No hay notificaciones",
      en: "No notifications",
      pt: "Sem notificações",
    }[this.language];
  }

  get pipelineErrorTitle() {
    return {
      es: "No se pudo completar el proceso",
      en: "The process could not be completed",
      pt: "Não foi possível concluir o processo",
    }[this.language];
  }

  get htmlEditorEditButton() {
    return { es: "Editar", en: "Edit", pt: "Editar" }[this.language];
  }

  get htmlEditorNoContent() {
    return { es: "Sin contenido", en: "No content", pt: "Sem conteúdo" }[
      this.language
    ];
  }

  get htmlEditorSaveButton() {
    return { es: "Guardar", en: "Save", pt: "Salvar" }[this.language];
  }

  get twoFaSetupTitle() {
    return {
      es: "Configurar autenticación en dos pasos",
      en: "Set up Two-Factor Authentication",
      pt: "Configurar autenticação de dois fatores",
    }[this.language];
  }

  get twoFaSetupInstructions() {
    return {
      es: "Escaneá este código QR con tu aplicación de autenticación (Google Authenticator, Authy, etc.)",
      en: "Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)",
      pt: "Escaneie este código QR com seu aplicativo de autenticação (Google Authenticator, Authy, etc.)",
    }[this.language];
  }

  get twoFaManualEntry() {
    return {
      es: "Clave de ingreso manual:",
      en: "Manual entry key:",
      pt: "Chave de entrada manual:",
    }[this.language];
  }

  get twoFaBackupCodesTitle() {
    return {
      es: "Códigos de respaldo",
      en: "Backup Codes",
      pt: "Códigos de backup",
    }[this.language];
  }

  get twoFaBackupCodesWarning() {
    return {
      es: "Guardá estos códigos en un lugar seguro. Cada código solo puede usarse una vez.",
      en: "Save these codes in a safe place. Each code can only be used once.",
      pt: "Salve estes códigos em um lugar seguro. Cada código só pode ser usado uma vez.",
    }[this.language];
  }

  get twoFaEnterCode() {
    return {
      es: "Código de 6 dígitos",
      en: "Enter 6-digit code",
      pt: "Digite o código de 6 dígitos",
    }[this.language];
  }

  get twoFaConfirmSetup() {
    return {
      es: "Confirmar configuración",
      en: "Confirm Setup",
      pt: "Confirmar configuração",
    }[this.language];
  }

  get twoFaVerifyTitle() {
    return {
      es: "Autenticación en dos pasos",
      en: "Two-Factor Authentication",
      pt: "Autenticação de dois fatores",
    }[this.language];
  }

  get twoFaEnterAppCode() {
    return {
      es: "Código de 6 dígitos de tu aplicación de autenticación",
      en: "Enter the 6-digit code from your authenticator app",
      pt: "Digite o código de 6 dígitos do seu aplicativo de autenticação",
    }[this.language];
  }

  get twoFaEnterBackupCode() {
    return {
      es: "Uno de tus códigos de respaldo",
      en: "Enter one of your backup codes",
      pt: "Digite um dos seus códigos de backup",
    }[this.language];
  }

  get twoFaVerifyButton() {
    return { es: "Verificar", en: "Verify", pt: "Verificar" }[this.language];
  }

  get twoFaUseBackupCode() {
    return {
      es: "Usar un código de respaldo",
      en: "Use a backup code",
      pt: "Usar um código de backup",
    }[this.language];
  }

  get twoFaUseAppCode() {
    return {
      es: "Usar aplicación de autenticación",
      en: "Use authenticator app",
      pt: "Usar aplicativo de autenticação",
    }[this.language];
  }

  get twoFaInvalidCode() {
    return {
      es: "Código incorrecto, volvé a intentar",
      en: "Incorrect code, please try again",
      pt: "Código incorreto, tente novamente",
    }[this.language];
  }

  get twoFaRememberDevice() {
    return {
      es: "Recordar este dispositivo por 10 días",
      en: "Remember this device for 10 days",
      pt: "Lembrar deste dispositivo por 10 dias",
    }[this.language];
  }

  get trustedDevicesTitle() {
    return {
      es: "Dispositivos recordados (2FA)",
      en: "Remembered devices (2FA)",
      pt: "Dispositivos lembrados (2FA)",
    }[this.language];
  }

  get trustedDevicesEmpty() {
    return {
      es: "No hay dispositivos recordados. Cada dispositivo pedirá el código 2FA.",
      en: "No remembered devices. Each device will ask for the 2FA code.",
      pt: "Nenhum dispositivo lembrado. Cada dispositivo pedirá o código 2FA.",
    }[this.language];
  }

  get trustedDeviceRevoke() {
    return { es: "Revocar", en: "Revoke", pt: "Revogar" }[this.language];
  }

  get trustedDevicesRevokeAll() {
    return { es: "Revocar todos", en: "Revoke all", pt: "Revogar todos" }[
      this.language
    ];
  }

  get trustedDeviceLastUsed() {
    return { es: "Último uso", en: "Last used", pt: "Último uso" }[
      this.language
    ];
  }

  get trustedDeviceExpires() {
    return { es: "Expira", en: "Expires", pt: "Expira" }[this.language];
  }

  get trustedDeviceUnknown() {
    return { es: "Dispositivo", en: "Device", pt: "Dispositivo" }[
      this.language
    ];
  }

  maskedField(mask: string) {
    return {
      es: `El campo no cumple con el formato "${mask}"`,
      en: `The field does not match the format "${mask}"`,
      pt: `O campo não corresponde ao formato "${mask}"`,
    }[this.language];
  }

  IsForAllTenantsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      en: { s: "Tenant usable", p: "Tenant usable" },
      es: { s: "Disponible para inquilinos", p: "Disponible para inquilinos" },
      pt: { s: "Disponível para inquilinos", p: "Disponível para inquilinos" },
    }[lang ? lang : this.language].s;
  }

  get IsForAllTenantsLabel() {
    return this.IsForAllTenantsLabelByLang();
  }

  // User

  get UserLabels() {
    return {
      en: { s: "User", p: "Users", g: undefined },
      es: { s: "Usuario", p: "Usuarios", g: "m" },
      pt: { s: "Usuário", p: "Usuários", g: "m" },
    }[this.language];
  }

  get UserSingular() {
    return this.UserLabels.s;
  }

  get UserPlural() {
    return this.UserLabels.p;
  }

  get UserGender() {
    return this.UserLabels.g;
  }

  get groupUserLabel() {
    return {
      en: { s: "User", p: "Users" },
      es: { s: "Usuario", p: "Usuarios", g: "m" },
      pt: { s: "Usuário", p: "Usuários", g: "m" },
    }[this.language].s;
  }

  get groupUserMainLabel() {
    return {
      es: { s: "Datos principales", p: "M" },
      en: { s: "Main" },
      pt: { s: "Dados principais", p: "M" },
    }[this.language].s;
  }

  get groupUserDetailsLabel() {
    return {
      es: { s: "Detalles", p: "M" },
      en: { s: "Details" },
      pt: { s: "Detalhes", p: "M" },
    }[this.language].s;
  }

  UserNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "M" },
      en: { s: "Name" },
      pt: { s: "Nome", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNameLabel() {
    return this.UserNameLabelByLang();
  }

  get UserNameDescription() {
    return {
      es: {
        s: "Nombre completo del usuario para identificación y visualización.",
      },
      en: { s: "User's full name for identification and display." },
      pt: { s: "Nome completo do usuário para identificação e exibição." },
    }[this.language].s;
  }

  UserEmailLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Email", p: "M" },
      en: { s: "Email" },
      pt: { s: "Email", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserEmailLabel() {
    return this.UserEmailLabelByLang();
  }

  get UserEmailDescription() {
    return {
      es: {
        s: "Dirección de correo electrónico del usuario, utilizada para autenticación y comunicaciones.",
      },
      en: {
        s: "User's email address, used for authentication and communications.",
      },
      pt: {
        s: "Endereço de e-mail do usuário, usado para autenticação e comunicações.",
      },
    }[this.language].s;
  }

  UserRoleLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Rol", p: "M" },
      en: { s: "Role" },
      pt: { s: "Função", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserRoleLabel() {
    return this.UserRoleLabelByLang();
  }

  get UserRoleDescription() {
    return {
      es: {
        s: "Rol asignado al usuario que determina sus permisos y acceso a funcionalidades.",
      },
      en: {
        s: "Role assigned to the user that determines their permissions and feature access.",
      },
      pt: {
        s: "Papel atribuído ao usuário que determina suas permissões e acesso a funcionalidades.",
      },
    }[this.language].s;
  }

  UserLanguageLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Idioma", p: "Idioma de la interfaz del usuario", g: "M" },
      en: { s: "Language", p: "User interface language" },
      pt: { s: "Idioma", p: "Idioma da interface do usuário", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserLanguageLabel() {
    return this.UserLanguageLabelByLang();
  }

  get UserLanguageDescription() {
    return {
      es: {
        s: "Idioma en el que se muestra la interfaz del sistema para este usuario.",
      },
      en: {
        s: "Language in which the system interface is displayed for this user.",
      },
      pt: {
        s: "Idioma no qual a interface do sistema é exibida para este usuário.",
      },
    }[this.language].s;
  }

  UserAvatarLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Avatar", p: "Foto de perfil del usuario", g: "M" },
      en: { s: "Avatar", p: "User profile picture" },
      pt: { s: "Avatar", p: "Foto de perfil do usuário", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserAvatarLabel() {
    return this.UserAvatarLabelByLang();
  }

  get UserAvatarDescription() {
    return {
      es: { s: "Imagen de perfil del usuario para identificación visual." },
      en: { s: "User's profile image for visual identification." },
      pt: { s: "Imagem de perfil do usuário para identificação visual." },
    }[this.language].s;
  }

  UserPasswordResetTokenLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "PasswordResetToken" },
      en: { s: "PasswordResetToken" },
      pt: { s: "PasswordResetToken" },
    }[lang ? lang : this.language].s;
  }

  get UserPasswordResetTokenLabel() {
    return this.UserPasswordResetTokenLabelByLang();
  }

  get UserPasswordResetTokenDescription() {
    return {
      es: {
        s: "Token temporal para restablecer la contraseña, válido por tiempo limitado.",
      },
      en: {
        s: "Temporary token for password reset, valid for a limited time.",
      },
      pt: {
        s: "Token temporário para redefinição de senha, válido por tempo limitado.",
      },
    }[this.language].s;
  }

  UserPasswordResetExpiresAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "PasswordResetExpiresAt" },
      en: { s: "PasswordResetExpiresAt" },
      pt: { s: "PasswordResetExpiresAt" },
    }[lang ? lang : this.language].s;
  }

  get UserPasswordResetExpiresAtLabel() {
    return this.UserPasswordResetExpiresAtLabelByLang();
  }

  get UserPasswordResetExpiresAtDescription() {
    return {
      es: {
        s: "Fecha y hora de expiración del token de restablecimiento de contraseña.",
      },
      en: { s: "Expiration date and time for the password reset token." },
      pt: { s: "Data e hora de expiração do token de redefinição de senha." },
    }[this.language].s;
  }

  UserTargetTenantLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Cliente", p: "Cliente", g: "M" },
      en: { s: "Tenant", p: "Tenant" },
      pt: { s: "Cliente", p: "Cliente", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserTargetTenantLabel() {
    return this.UserTargetTenantLabelByLang();
  }

  get UserTargetTenantDescription() {
    return {
      es: {
        s: "Tenant al que pertenece el usuario, define el alcance de sus datos y permisos.",
      },
      en: {
        s: "Tenant the user belongs to, defines the scope of their data and permissions.",
      },
      pt: {
        s: "Tenant ao qual o usuário pertence, define o escopo de seus dados e permissões.",
      },
    }[this.language].s;
  }

  UserTenantDisplayNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Cuenta", p: "Cuenta", g: "F" },
      en: { s: "Account", p: "Account" },
      pt: { s: "Conta", p: "Conta", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserTenantDisplayNameLabel() {
    return this.UserTenantDisplayNameLabelByLang();
  }

  get UserTenantDisplayNameDescription() {
    return {
      es: {
        s: "Nombre del tenant para visualización, calculado automáticamente por el servidor.",
      },
      en: {
        s: "Tenant name for display, automatically calculated by the server.",
      },
      pt: {
        s: "Nome do tenant para exibição, calculado automaticamente pelo servidor.",
      },
    }[this.language].s;
  }

  UserActiveLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Está activo?", p: "M" },
      en: { s: "Is active?" },
      pt: { s: "Está ativo?", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserActiveLabel() {
    return this.UserActiveLabelByLang();
  }

  get UserActiveDescription() {
    return {
      es: {
        s: "Indica si el usuario puede acceder al sistema. Usuarios inactivos no pueden iniciar sesión.",
      },
      en: {
        s: "Indicates whether the user can access the system. Inactive users cannot log in.",
      },
      pt: {
        s: "Indica se o usuário pode acessar o sistema. Usuários inativos não podem fazer login.",
      },
    }[this.language].s;
  }

  UserCellphoneLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Celular", p: "M" },
      en: { s: "Cellphone" },
      pt: { s: "Celular", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserCellphoneLabel() {
    return this.UserCellphoneLabelByLang();
  }

  get UserCellphoneDescription() {
    return {
      es: { s: "Número de celular del usuario." },
      en: { s: "User's cellphone number." },
      pt: { s: "Número de celular do usuário." },
    }[this.language].s;
  }

  UserTwoFaseAuthLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Requiere 2FA", p: "M" },
      en: { s: "Requires 2FA" },
      pt: { s: "Requer 2FA", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserTwoFaseAuthLabel() {
    return this.UserTwoFaseAuthLabelByLang();
  }

  get UserTwoFaseAuthDescription() {
    return {
      es: {
        s: "Indica si el usuario debe utilizar autenticación de dos factores para iniciar sesión.",
      },
      en: {
        s: "Indicates whether the user must use two-factor authentication to log in.",
      },
      pt: {
        s: "Indica se o usuário deve usar autenticação de dois fatores para fazer login.",
      },
    }[this.language].s;
  }

  UserTwoFaSecretLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "TwoFaSecret" },
      en: { s: "TwoFaSecret" },
      pt: { s: "TwoFaSecret" },
    }[lang ? lang : this.language].s;
  }

  get UserTwoFaSecretLabel() {
    return this.UserTwoFaSecretLabelByLang();
  }

  get UserTwoFaSecretDescription() {
    return {
      es: {
        s: "Secreto TOTP encriptado con AES-256-GCM para autenticación de dos factores.",
      },
      en: {
        s: "AES-256-GCM encrypted TOTP secret for two-factor authentication.",
      },
      pt: {
        s: "Segredo TOTP criptografado com AES-256-GCM para autenticação de dois fatores.",
      },
    }[this.language].s;
  }

  UserTwoFaBackupCodesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "TwoFaBackupCodes" },
      en: { s: "TwoFaBackupCodes" },
      pt: { s: "TwoFaBackupCodes" },
    }[lang ? lang : this.language].s;
  }

  get UserTwoFaBackupCodesLabel() {
    return this.UserTwoFaBackupCodesLabelByLang();
  }

  get UserTwoFaBackupCodesDescription() {
    return {
      es: {
        s: "Códigos de respaldo hasheados con SHA-256 para recuperación de acceso 2FA.",
      },
      en: { s: "SHA-256 hashed backup codes for 2FA access recovery." },
      pt: {
        s: "Códigos de backup hasheados com SHA-256 para recuperação de acesso 2FA.",
      },
    }[this.language].s;
  }

  UserTwoFaVerifiedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "TwoFaVerifiedAt" },
      en: { s: "TwoFaVerifiedAt" },
      pt: { s: "TwoFaVerifiedAt" },
    }[lang ? lang : this.language].s;
  }

  get UserTwoFaVerifiedAtLabel() {
    return this.UserTwoFaVerifiedAtLabelByLang();
  }

  get UserTwoFaVerifiedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que el usuario completó la configuración de 2FA.",
      },
      en: { s: "Date and time when the user completed 2FA setup." },
      pt: {
        s: "Data e hora em que o usuário completou a configuração de 2FA.",
      },
    }[this.language].s;
  }

  UserProfileLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Perfil", p: "M" },
      en: { s: "Profile" },
      pt: { s: "Perfil", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserProfileLabel() {
    return this.UserProfileLabelByLang();
  }

  get UserProfileDescription() {
    return {
      es: { s: "Datos adicionales del perfil del usuario en formato JSON." },
      en: { s: "Additional user profile data in JSON format." },
      pt: { s: "Dados adicionais do perfil do usuário em formato JSON." },
    }[this.language].s;
  }

  UserIdCardTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Documento", p: "M" },
      en: { s: "ID Type" },
      pt: { s: "Tipo de Documento", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserIdCardTypeLabel() {
    return this.UserIdCardTypeLabelByLang();
  }

  get UserIdCardTypeDescription() {
    return {
      es: { s: "Tipo de documento de identificación." },
      en: { s: "Type of identification document." },
      pt: { s: "Tipo de documento de identificação." },
    }[this.language].s;
  }

  UserIdCardLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "DNI", p: "Documento Nacional de Identidad", g: "M" },
      en: { s: "National ID", p: "National Identity Document" },
      pt: { s: "DNI", p: "Documento Nacional de Identidade", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserIdCardLabel() {
    return this.UserIdCardLabelByLang();
  }

  get UserIdCardDescription() {
    return {
      es: { s: "Número de documento nacional de identidad del usuario." },
      en: { s: "User's national identity document number." },
      pt: { s: "Número do documento nacional de identidade do usuário." },
    }[this.language].s;
  }

  UserGenderLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Género", p: "Géneros", g: "M" },
      en: { s: "Gender", p: "Genders" },
      pt: { s: "Gênero", p: "Gêneros", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserGenderLabel() {
    return this.UserGenderLabelByLang();
  }

  get UserGenderDescription() {
    return {
      es: { s: "Clasifica el género de un individuo." },
      en: { s: "Classifies the gender of an individual." },
      pt: { s: "Classifica o gênero de um indivíduo." },
    }[this.language].s;
  }

  // Role

  get RoleLabels() {
    return {
      en: { s: "Role", p: "Roles", g: undefined },
      es: { s: "Rol", p: "Roles", g: "m" },
      pt: { s: "Papel", p: "Funções", g: "f" },
    }[this.language];
  }

  get RoleSingular() {
    return this.RoleLabels.s;
  }

  get RolePlural() {
    return this.RoleLabels.p;
  }

  get RoleGender() {
    return this.RoleLabels.g;
  }

  get groupRoleLabel() {
    return {
      en: { s: "Role", p: "Roles" },
      es: { s: "Rol", p: "Roles", g: "m" },
      pt: { s: "Papel", p: "Funções", g: "f" },
    }[this.language].s;
  }

  RoleNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "M" },
      en: { s: "Name" },
      pt: { s: "Nome", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get RoleNameLabel() {
    return this.RoleNameLabelByLang();
  }

  get RoleNameDescription() {
    return {
      es: {
        s: "Nombre identificador del rol, traducible a múltiples idiomas.",
      },
      en: {
        s: "Identifier name of the role, translatable to multiple languages.",
      },
      pt: {
        s: "Nome identificador do papel, traduzível para múltiplos idiomas.",
      },
    }[this.language].s;
  }

  RolePermissionsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Permisos", p: "M" },
      en: { s: "Permissions" },
      pt: { s: "Permissões", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get RolePermissionsLabel() {
    return this.RolePermissionsLabelByLang();
  }

  get RolePermissionsDescription() {
    return {
      es: {
        s: "Conjunto de permisos que otorga este rol a los usuarios asignados.",
      },
      en: { s: "Set of permissions this role grants to assigned users." },
      pt: {
        s: "Conjunto de permissões que este papel concede aos usuários atribuídos.",
      },
    }[this.language].s;
  }

  RoleAudienceLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      en: { s: "Audience" },
      es: { s: "Audiencia" },
      pt: { s: "Público" },
    }[lang ? lang : this.language].s;
  }

  get RoleAudienceLabel() {
    return this.RoleAudienceLabelByLang();
  }

  RoleIsForAllTenantsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      en: { s: "Tenant usable", p: "Tenant usable" },
      es: { s: "Disponible para inquilinos", p: "Disponible para inquilinos" },
      pt: { s: "Disponível para inquilinos", p: "Disponível para inquilinos" },
    }[lang ? lang : this.language].s;
  }

  get RoleIsForAllTenantsLabel() {
    return this.RoleIsForAllTenantsLabelByLang();
  }

  // Permission

  get PermissionLabels() {
    return {
      en: { s: "Permission", p: "Permissions", g: undefined },
      es: { s: "Permiso", p: "Permisos", g: "m" },
      pt: { s: "Permisão", p: "Permissões", g: "m" },
    }[this.language];
  }

  get PermissionSingular() {
    return this.PermissionLabels.s;
  }

  get PermissionPlural() {
    return this.PermissionLabels.p;
  }

  get PermissionGender() {
    return this.PermissionLabels.g;
  }

  get groupPermissionLabel() {
    return {
      en: { s: "Permission", p: "Permissions" },
      es: { s: "Permiso", p: "Permisos", g: "m" },
      pt: { s: "Permisão", p: "Permissões", g: "m" },
    }[this.language].s;
  }

  PermissionNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "M" },
      en: { s: "Name" },
      pt: { s: "Nome", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get PermissionNameLabel() {
    return this.PermissionNameLabelByLang();
  }

  get PermissionNameDescription() {
    return {
      es: {
        s: "Nombre identificador del permiso, traducible a múltiples idiomas.",
      },
      en: {
        s: "Identifier name of the permission, translatable to multiple languages.",
      },
      pt: {
        s: "Nome identificador da permissão, traduzível para múltiplos idiomas.",
      },
    }[this.language].s;
  }

  // Tenant

  get TenantLabels() {
    return {
      es: { s: "Cliente Júpiter", p: "Clientes Júpiter", g: "M" },
      en: { s: "Tenant", p: "Tenants", g: undefined },
      pt: { s: "Cliente Júpiter", p: "Clientes Júpiter", g: "M" },
    }[this.language];
  }

  get TenantSingular() {
    return this.TenantLabels.s;
  }

  get TenantPlural() {
    return this.TenantLabels.p;
  }

  get TenantGender() {
    return this.TenantLabels.g;
  }

  get groupTenantLabel() {
    return {
      es: { s: "Cliente Júpiter", p: "Clientes Júpiter", g: "M" },
      en: { s: "Tenant", p: "Tenants" },
      pt: { s: "Cliente Júpiter", p: "Clientes Júpiter", g: "M" },
    }[this.language].s;
  }

  get groupTenantTenantLabel() {
    return {
      es: { s: "Datos Generales", p: "M" },
      en: { s: "General Data" },
      pt: { s: "Dados Gerais", p: "M" },
    }[this.language].s;
  }

  get groupTenantLlmLimitsLabel() {
    return {
      es: { s: "Límites de Tokens LLM", p: "M" },
      en: { s: "LLM Token Limits" },
      pt: { s: "Limites de Tokens LLM", p: "M" },
    }[this.language].s;
  }

  get groupTenantStorageLimitsLabel() {
    return {
      es: { s: "Límites de Almacenamiento", p: "M" },
      en: { s: "Storage Limits" },
      pt: { s: "Limites de Armazenamento", p: "M" },
    }[this.language].s;
  }

  TenantNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre completo del cliente", g: "M" },
      en: { s: "Name", p: "Full name of the tenant" },
      pt: { s: "Nome", p: "Nome completo do cliente", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantNameLabel() {
    return this.TenantNameLabelByLang();
  }

  get TenantNameDescription() {
    return {
      es: { s: "Nombre comercial o de fantasía del cliente." },
      en: { s: "Commercial or trade name of the tenant." },
      pt: { s: "Nome comercial ou fantasia do cliente." },
    }[this.language].s;
  }

  TenantTenantTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Tipo de Cliente",
        p: "Tipo de Cliente (e.g., Corporativo, Individual, etc.)",
        g: "M",
      },
      en: {
        s: "Tenant Type",
        p: "Type of tenant (e.g., Corporate, Individual, etc.)",
      },
      pt: {
        s: "Tipo de Cliente",
        p: "Tipo de Cliente (por exemplo, Corporativo, Individual, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantTenantTypeLabel() {
    return this.TenantTenantTypeLabelByLang();
  }

  get TenantTenantTypeDescription() {
    return {
      es: {
        s: "Clasificación del cliente según su naturaleza (Corporativo, Individual, etc.).",
      },
      en: {
        s: "Client classification based on their nature (Corporate, Individual, etc.).",
      },
      pt: {
        s: "Classificação do cliente de acordo com sua natureza (Corporativo, Individual, etc.).",
      },
    }[this.language].s;
  }

  TenantStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Estado",
        p: "Estado del Cliente (Activo, Inactivo, Suspendido, etc.)",
        g: "M",
      },
      en: {
        s: "Status",
        p: "Status of the tenant (Active, Inactive, Suspended, etc.)",
      },
      pt: {
        s: "Status",
        p: "Status do Cliente (Ativo, Inativo, Suspenso, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantStatusLabel() {
    return this.TenantStatusLabelByLang();
  }

  get TenantStatusDescription() {
    return {
      es: { s: "Estado operativo actual del cliente en la plataforma." },
      en: { s: "Current operational status of the client on the platform." },
      pt: { s: "Estado operacional atual do cliente na plataforma." },
    }[this.language].s;
  }

  TenantPlanLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Plan",
        p: "Condiciones especiales aplicables al Cliente",
        g: "M",
      },
      en: { s: "Plan", p: "Special conditions applicable to the Tenant" },
      pt: {
        s: "Plano",
        p: "Condições especiais aplicáveis ao Cliente",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantPlanLabel() {
    return this.TenantPlanLabelByLang();
  }

  get TenantPlanDescription() {
    return {
      es: { s: "Plan de suscripción o condiciones especiales del cliente." },
      en: { s: "Client's subscription plan or special conditions." },
      pt: { s: "Plano de assinatura ou condições especiais do cliente." },
    }[this.language].s;
  }

  TenantLogoLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Logo", p: "Logo del Cliente", g: "M" },
      en: { s: "Logo", p: "Tenant logo" },
      pt: { s: "Logo", p: "Logo do Cliente", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantLogoLabel() {
    return this.TenantLogoLabelByLang();
  }

  get TenantLogoDescription() {
    return {
      es: { s: "Imagen del logo del cliente para identificación visual." },
      en: { s: "Client's logo image for visual identification." },
      pt: { s: "Imagem do logo do cliente para identificação visual." },
    }[this.language].s;
  }

  TenantPublicUrlLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "URL Pública", p: "Sitio web público del cliente", g: "F" },
      en: { s: "Public URL", p: "Tenant's public website" },
      pt: { s: "URL Pública", p: "Site público do cliente", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantPublicUrlLabel() {
    return this.TenantPublicUrlLabelByLang();
  }

  get TenantPublicUrlDescription() {
    return {
      es: {
        s: "URL del sitio web público del cliente, utilizada en emails de verificación y landing pages.",
      },
      en: {
        s: "Tenant's public website URL, used in verification emails and landing pages.",
      },
      pt: {
        s: "URL do site público do cliente, usada em e-mails de verificação e landing pages.",
      },
    }[this.language].s;
  }

  TenantIdERPLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "ID ERP",
        p: "Identificador del Cliente en el sistema ERP",
        g: "M",
      },
      en: { s: "ERP ID", p: "Tenant's identifier in the ERP system" },
      pt: { s: "ID ERP", p: "Identificador do Cliente no sistema ERP", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantIdERPLabel() {
    return this.TenantIdERPLabelByLang();
  }

  get TenantIdERPDescription() {
    return {
      es: {
        s: "Identificador del cliente en el sistema ERP externo para sincronización.",
      },
      en: {
        s: "Client's identifier in the external ERP system for synchronization.",
      },
      pt: {
        s: "Identificador do cliente no sistema ERP externo para sincronização.",
      },
    }[this.language].s;
  }

  TenantIdCRMLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "ID CRM",
        p: "Identificador del Cliente en el sistema CRM",
        g: "M",
      },
      en: { s: "CRM ID", p: "Tenant's identifier in the CRM system" },
      pt: { s: "ID CRM", p: "Identificador do Cliente no sistema CRM", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantIdCRMLabel() {
    return this.TenantIdCRMLabelByLang();
  }

  get TenantIdCRMDescription() {
    return {
      es: {
        s: "Identificador del cliente en el sistema CRM externo para sincronización.",
      },
      en: {
        s: "Client's identifier in the external CRM system for synchronization.",
      },
      pt: {
        s: "Identificador do cliente no sistema CRM externo para sincronização.",
      },
    }[this.language].s;
  }

  TenantMonthlyTokenLimitLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Límite Mensual de Tokens",
        p: "Cantidad máxima de tokens permitidos por mes",
        g: "M",
      },
      en: { s: "Monthly Token Limit", p: "Maximum tokens allowed per month" },
      pt: {
        s: "Limite Mensal de Tokens",
        p: "Quantidade máxima de tokens permitidos por mês",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantMonthlyTokenLimitLabel() {
    return this.TenantMonthlyTokenLimitLabelByLang();
  }

  get TenantMonthlyTokenLimitDescription() {
    return {
      es: {
        s: "Límite máximo de tokens que el tenant puede consumir por mes. Null significa sin límite.",
      },
      en: {
        s: "Maximum tokens the tenant can consume per month. Null means unlimited.",
      },
      pt: {
        s: "Limite máximo de tokens que o tenant pode consumir por mês. Null significa ilimitado.",
      },
    }[this.language].s;
  }

  TenantCurrentMonthTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tokens del Mes Actual",
        p: "Tokens consumidos en el mes actual",
        g: "M",
      },
      en: { s: "Current Month Tokens", p: "Tokens consumed in current month" },
      pt: {
        s: "Tokens do Mês Atual",
        p: "Tokens consumidos no mês atual",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantCurrentMonthTokensLabel() {
    return this.TenantCurrentMonthTokensLabelByLang();
  }

  get TenantCurrentMonthTokensDescription() {
    return {
      es: {
        s: "Cantidad de tokens consumidos en el período actual. Se resetea mensualmente.",
      },
      en: { s: "Tokens consumed in current period. Resets monthly." },
      pt: {
        s: "Quantidade de tokens consumidos no período atual. Reseta mensalmente.",
      },
    }[this.language].s;
  }

  TenantTokenLimitResetDayLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Día de Reset",
        p: "Día del mes para resetear el contador",
        g: "M",
      },
      en: { s: "Reset Day", p: "Day of month to reset counter" },
      pt: {
        s: "Dia de Reset",
        p: "Dia do mês para resetar o contador",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenLimitResetDayLabel() {
    return this.TenantTokenLimitResetDayLabelByLang();
  }

  get TenantTokenLimitResetDayDescription() {
    return {
      es: {
        s: "Día del mes (1-28) en que se resetea el contador de tokens. Por defecto el día 1.",
      },
      en: {
        s: "Day of month (1-28) when token counter resets. Defaults to day 1.",
      },
      pt: {
        s: "Dia do mês (1-28) em que o contador de tokens é resetado. Padrão é dia 1.",
      },
    }[this.language].s;
  }

  TenantLastTokenResetDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Último Reset",
        p: "Fecha del último reset del contador",
        g: "M",
      },
      en: { s: "Last Reset", p: "Date of last counter reset" },
      pt: { s: "Último Reset", p: "Data do último reset do contador", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantLastTokenResetDateLabel() {
    return this.TenantLastTokenResetDateLabelByLang();
  }

  get TenantLastTokenResetDateDescription() {
    return {
      es: {
        s: "Fecha y hora del último reset automático del contador de tokens.",
      },
      en: { s: "Date and time of last automatic token counter reset." },
      pt: {
        s: "Data e hora do último reset automático do contador de tokens.",
      },
    }[this.language].s;
  }

  TenantRagLlmProviderLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Proveedor LLM para RAG",
        p: "Proveedor de LLM específico para búsqueda en documentos",
        g: "M",
      },
      en: {
        s: "RAG LLM Provider",
        p: "LLM provider specific for document search",
      },
      pt: {
        s: "Provedor LLM para RAG",
        p: "Provedor de LLM específico para busca em documentos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantRagLlmProviderLabel() {
    return this.TenantRagLlmProviderLabelByLang();
  }

  get TenantRagLlmProviderDescription() {
    return {
      es: {
        s: "Proveedor LLM opcional para operaciones RAG (búsqueda en documentos). Si está vacío, usa el LLM de chat configurado.",
      },
      en: {
        s: "Optional LLM provider for RAG operations (document search). If empty, uses the configured chat LLM.",
      },
      pt: {
        s: "Provedor LLM opcional para operações RAG (busca em documentos). Se vazio, usa o LLM de chat configurado.",
      },
    }[this.language].s;
  }

  TenantRagLlmModelLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Modelo LLM para RAG",
        p: "Modelo específico para búsqueda en documentos",
        g: "M",
      },
      en: { s: "RAG LLM Model", p: "Model specific for document search" },
      pt: {
        s: "Modelo LLM para RAG",
        p: "Modelo específico para busca em documentos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantRagLlmModelLabel() {
    return this.TenantRagLlmModelLabelByLang();
  }

  get TenantRagLlmModelDescription() {
    return {
      es: {
        s: "Modelo LLM opcional para RAG (ej: gpt-4o-mini, claude-3-haiku). Si está vacío, usa el modelo de chat configurado.",
      },
      en: {
        s: "Optional LLM model for RAG (e.g., gpt-4o-mini, claude-3-haiku). If empty, uses the configured chat model.",
      },
      pt: {
        s: "Modelo LLM opcional para RAG (ex: gpt-4o-mini, claude-3-haiku). Se vazio, usa o modelo de chat configurado.",
      },
    }[this.language].s;
  }

  TenantCurrentStorageBytesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Bytes Almacenados",
        p: "Total de bytes de documentos almacenados",
        g: "M",
      },
      en: { s: "Stored Bytes", p: "Total bytes of stored documents" },
      pt: {
        s: "Bytes Armazenados",
        p: "Total de bytes de documentos armazenados",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantCurrentStorageBytesLabel() {
    return this.TenantCurrentStorageBytesLabelByLang();
  }

  get TenantCurrentStorageBytesDescription() {
    return {
      es: {
        s: "Total de bytes de documentos almacenados. Se actualiza automáticamente al subir/eliminar documentos.",
      },
      en: {
        s: "Total bytes of stored documents. Updated automatically when uploading/deleting documents.",
      },
      pt: {
        s: "Total de bytes de documentos armazenados. Atualizado automaticamente ao enviar/excluir documentos.",
      },
    }[this.language].s;
  }

  TenantStorageByteLimitLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Límite de Almacenamiento",
        p: "Límite máximo de bytes permitidos",
        g: "M",
      },
      en: { s: "Storage Limit", p: "Maximum allowed bytes" },
      pt: {
        s: "Limite de Armazenamento",
        p: "Limite máximo de bytes permitidos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageByteLimitLabel() {
    return this.TenantStorageByteLimitLabelByLang();
  }

  get TenantStorageByteLimitDescription() {
    return {
      es: {
        s: "Límite de almacenamiento en bytes. Null significa sin límite.",
      },
      en: { s: "Storage limit in bytes. Null means unlimited." },
      pt: { s: "Limite de armazenamento em bytes. Null significa ilimitado." },
    }[this.language].s;
  }

  TenantESignatureProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor de Firma Electronica" },
      en: { s: "E-Signature Provider" },
      pt: { s: "Provedor de Assinatura Eletronica" },
    }[lang ? lang : this.language].s;
  }

  get TenantESignatureProviderLabel() {
    return this.TenantESignatureProviderLabelByLang();
  }

  // TenantProfile

  get TenantProfileLabels() {
    return {
      es: { s: "Datos del Cliente", p: "Datos del Cliente", g: "M" },
      en: { s: "Client Data", p: "Client Data", g: undefined },
      pt: { s: "Dados do Cliente", p: "Dados do Cliente", g: "M" },
    }[this.language];
  }

  get TenantProfileSingular() {
    return this.TenantProfileLabels.s;
  }

  get TenantProfilePlural() {
    return this.TenantProfileLabels.p;
  }

  get TenantProfileGender() {
    return this.TenantProfileLabels.g;
  }

  get groupTenantProfileLabel() {
    return {
      es: { s: "Datos del Cliente", p: "Datos del Cliente", g: "M" },
      en: { s: "Client Data", p: "Client Data" },
      pt: { s: "Dados do Cliente", p: "Dados do Cliente", g: "M" },
    }[this.language].s;
  }

  get groupTenantProfileBusinessLabel() {
    return {
      es: { s: "Datos de la empresa", p: "M" },
      en: { s: "Business data" },
      pt: { s: "Dados da empresa", p: "M" },
    }[this.language].s;
  }

  get groupTenantProfileAddressLabel() {
    return {
      es: { s: "Domicilio Legal", p: "M" },
      en: { s: "Legal Address" },
      pt: { s: "Endereço Legal", p: "M" },
    }[this.language].s;
  }

  get groupTenantProfileBillingLabel() {
    return {
      es: { s: "Datos de facturación", p: "M" },
      en: { s: "Billing data" },
      pt: { s: "Dados de faturamento", p: "M" },
    }[this.language].s;
  }

  get groupTenantProfilePrinterLabel() {
    return {
      es: { s: "Impresor", p: "M" },
      en: { s: "Printer" },
      pt: { s: "Impresor", p: "M" },
    }[this.language].s;
  }

  get groupTenantProfileIntegrationsSadaicLabel() {
    return {
      es: { s: "Integración SADAIC", p: "M" },
      en: { s: "SADAIC Integration" },
      pt: { s: "Integração SADAIC", p: "M" },
    }[this.language].s;
  }

  get groupTenantProfileIntegrationsSadaicDescription() {
    return {
      es: {
        s: "Credenciales del servidor FTP de SADAIC al que se suben los contratos firmados y el ejemplar de cada edición. Se usan por el bot de subida SADAIC post-firma.",
      },
      en: {
        s: "SADAIC FTP server credentials used to upload signed contracts and the exemplar of each edition. Consumed by the post-signature SADAIC upload bot.",
      },
      pt: {
        s: "Credenciais do servidor FTP da SADAIC usadas para enviar os contratos assinados e o exemplar de cada edição. Consumidas pelo bot de envio SADAIC pós-assinatura.",
      },
    }[this.language].s;
  }

  TenantProfilePersonTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Persona",
        p: "Tipo de persona (Jurídica o Física)",
        g: "M",
      },
      en: { s: "Person Type", p: "Type of person (Legal or Natural)" },
      pt: {
        s: "Tipo de Pessoa",
        p: "Tipo de pessoa (Jurídica ou Física)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePersonTypeLabel() {
    return this.TenantProfilePersonTypeLabelByLang();
  }

  get TenantProfilePersonTypeDescription() {
    return {
      es: {
        s: "Indica si la editorial es una entidad legal (empresa) o una persona física.",
      },
      en: {
        s: "Indicates whether the publisher is a legal entity (company) or a natural person.",
      },
      pt: {
        s: "Indica se a editora é uma pessoa jurídica (empresa) ou uma pessoa física.",
      },
    }[this.language].s;
  }

  TenantProfileLegalNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Razón Social",
        p: "Razón social o nombre legal de la empresa",
        g: "F",
      },
      en: { s: "Legal Name", p: "Legal name of the company" },
      pt: { s: "Razão Social", p: "Nome empresarial ou razão social", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileLegalNameLabel() {
    return this.TenantProfileLegalNameLabelByLang();
  }

  get TenantProfileLegalNameDescription() {
    return {
      es: { s: "Razón social o nombre legal registrado de la empresa." },
      en: { s: "Registered legal name of the company." },
      pt: { s: "Razão social ou nome legal registrado da empresa." },
    }[this.language].s;
  }

  TenantProfileOwnPublisherLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Editorial del Cliente",
        p: "Editorial propia del tenant que participa en todas sus obras",
        g: "F",
      },
      en: {
        s: "Tenant Publisher",
        p: "Tenant's own publisher participating in all its works",
      },
      pt: {
        s: "Editora do Cliente",
        p: "Editora própria do tenant que participa de todas as suas obras",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileOwnPublisherLabel() {
    return this.TenantProfileOwnPublisherLabelByLang();
  }

  get TenantProfileOwnPublisherDescription() {
    return {
      es: {
        s: "Editorial propia del cliente. Debe ser una editorial creada por este mismo cliente. Participa obligatoriamente en toda obra.",
      },
      en: {
        s: "The client's own publisher. Must be a publisher created by this same client. Mandatory participant in every work.",
      },
      pt: {
        s: "Editora própria do cliente. Deve ser uma editora criada por este mesmo cliente. Participa obrigatoriamente de toda obra.",
      },
    }[this.language].s;
  }

  TenantProfileBusinessIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "CUIT",
        p: "Identificador único del cliente según el país (DNI, CUIT, etc.)",
        g: "M",
      },
      en: {
        s: "Business ID",
        p: "Unique tenant identifier according to country (DNI, CUIT, etc.)",
      },
      pt: {
        s: "ID de Negócio",
        p: "Identificador único do cliente de acordo com o país (DNI, CUIT, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileBusinessIdLabel() {
    return this.TenantProfileBusinessIdLabelByLang();
  }

  get TenantProfileBusinessIdDescription() {
    return {
      es: {
        s: "Identificador tributario o de negocios único del cliente (CUIT, DNI, etc.).",
      },
      en: {
        s: "Client's unique tax or business identifier (CUIT, DNI, etc.).",
      },
      pt: {
        s: "Identificador tributário ou de negócios único do cliente (CNPJ, CPF, etc.).",
      },
    }[this.language].s;
  }

  TenantProfileLegalEmailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Correo Electrónico", p: "M" },
      en: { s: "Email" },
      pt: { s: "Email", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileLegalEmailLabel() {
    return this.TenantProfileLegalEmailLabelByLang();
  }

  get TenantProfileLegalEmailDescription() {
    return {
      es: { s: "Correo electrónico oficial para comunicaciones legales." },
      en: { s: "Official email for legal communications." },
      pt: { s: "E-mail oficial para comunicações legais." },
    }[this.language].s;
  }

  TenantProfileStreetAndNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Calle y Número", p: "Calle y número del domicilio", g: "F" },
      en: { s: "Street and Number", p: "Street and house number" },
      pt: { s: "Rua e Número", p: "Rua e número do endereço", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileStreetAndNumberLabel() {
    return this.TenantProfileStreetAndNumberLabelByLang();
  }

  get TenantProfileStreetAndNumberDescription() {
    return {
      es: { s: "Dirección física principal de la editorial." },
      en: { s: "Main physical address of the publisher." },
      pt: { s: "Endereço físico principal da editora." },
    }[this.language].s;
  }

  TenantProfileFloorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Piso", p: "Número de piso", g: "M" },
      en: { s: "Floor", p: "Floor number" },
      pt: { s: "Andar", p: "Número do andar" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileFloorLabel() {
    return this.TenantProfileFloorLabelByLang();
  }

  get TenantProfileFloorDescription() {
    return {
      es: { s: "Número de piso en el edificio de la editorial, si aplica." },
      en: { s: "Floor number in the publisher's building, if applicable." },
      pt: { s: "Número do andar no edifício da editora, se aplicável." },
    }[this.language].s;
  }

  TenantProfileSuiteLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Departamento",
        p: "Número o letra de departamento/oficina",
        g: "M",
      },
      en: { s: "Suite/Office", p: "Suite or office number/letter" },
      pt: {
        s: "Apartamento/Sala",
        p: "Número ou letra do apartamento/escritório",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileSuiteLabel() {
    return this.TenantProfileSuiteLabelByLang();
  }

  get TenantProfileSuiteDescription() {
    return {
      es: {
        s: "Identificador de la oficina o departamento específico de la editorial.",
      },
      en: { s: "Specific office or suite identifier for the publisher." },
      pt: { s: "Identificador do escritório ou sala específica da editora." },
    }[this.language].s;
  }

  TenantProfilePostalCodeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Código Postal", p: "Código postal del domicilio", g: "M" },
      en: { s: "Postal Code", p: "Postal code of the address" },
      pt: { s: "Código Postal", p: "Código postal do endereço", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePostalCodeLabel() {
    return this.TenantProfilePostalCodeLabelByLang();
  }

  get TenantProfilePostalCodeDescription() {
    return {
      es: { s: "Código postal de la dirección de la editorial." },
      en: { s: "Postal code for the publisher's address." },
      pt: { s: "Código postal do endereço da editora." },
    }[this.language].s;
  }

  TenantProfileCountryLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "País", p: "País donde se encuentra el domicilio", g: "M" },
      en: { s: "Country", p: "Country where address is located" },
      pt: { s: "País", p: "País onde o endereço está localizado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileCountryLabel() {
    return this.TenantProfileCountryLabelByLang();
  }

  get TenantProfileCountryDescription() {
    return {
      es: { s: "País donde la editorial tiene su sede principal." },
      en: { s: "Country where the publisher is primarily based." },
      pt: { s: "País onde a editora está sediada principalmente." },
    }[this.language].s;
  }

  TenantProfileProvinceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Provincia", p: "Provincia o estado", g: "F" },
      en: { s: "State/Province", p: "State or province" },
      pt: { s: "Estado/Província", p: "Estado ou província", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileProvinceLabel() {
    return this.TenantProfileProvinceLabelByLang();
  }

  get TenantProfileProvinceDescription() {
    return {
      es: { s: "Estado o provincia de la editorial." },
      en: { s: "State or province of the publisher." },
      pt: { s: "Estado ou província da editora." },
    }[this.language].s;
  }

  TenantProfileCityLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Localidad/Barrio", p: "Ciudad o localidad", g: "F" },
      en: { s: "City", p: "City or town" },
      pt: { s: "Cidade", p: "Cidade ou localidade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileCityLabel() {
    return this.TenantProfileCityLabelByLang();
  }

  get TenantProfileCityDescription() {
    return {
      es: { s: "Ciudad o localidad donde se ubica la editorial." },
      en: { s: "City or town where the publisher is located." },
      pt: { s: "Cidade ou localidade onde a editora está localizada." },
    }[this.language].s;
  }

  TenantProfileVatStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Condición frente al IVA",
        p: "Condición del cliente frente al Impuesto al Valor Agregado",
        g: "F",
      },
      en: { s: "VAT Status", p: "Value Added Tax status of the tenant" },
      pt: {
        s: "Situação Tributária",
        p: "Situação do cliente perante impostos (IVA)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileVatStatusLabel() {
    return this.TenantProfileVatStatusLabelByLang();
  }

  get TenantProfileVatStatusDescription() {
    return {
      es: { s: "Categoría tributaria del cliente respecto al IVA." },
      en: { s: "Client's tax category regarding VAT." },
      pt: { s: "Categoria tributária do cliente em relação ao IVA." },
    }[this.language].s;
  }

  TenantProfileTaxIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "CUIT", p: "Clave Única de Identificación Tributaria", g: "M" },
      en: { s: "Tax ID", p: "Tax Identification Number" },
      pt: {
        s: "CNPJ/CPF",
        p: "Cadastro Nacional da Pessoa Jurídica ou Física",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileTaxIdLabel() {
    return this.TenantProfileTaxIdLabelByLang();
  }

  get TenantProfileTaxIdDescription() {
    return {
      es: { s: "Número de identificación tributaria para facturación." },
      en: { s: "Tax identification number for billing." },
      pt: { s: "Número de identificação tributária para faturamento." },
    }[this.language].s;
  }

  TenantProfileGrossReceiptsStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Situación IIBB", p: "Situación en Ingresos Brutos", g: "F" },
      en: {
        s: "Gross Receipts Status",
        p: "Status regarding Gross Receipts Tax",
      },
      pt: {
        s: "Situação Estadual",
        p: "Situação da Inscrição Estadual",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileGrossReceiptsStatusLabel() {
    return this.TenantProfileGrossReceiptsStatusLabelByLang();
  }

  get TenantProfileGrossReceiptsStatusDescription() {
    return {
      es: {
        s: "Situación del cliente respecto al impuesto sobre Ingresos Brutos.",
      },
      en: { s: "Client's status regarding Gross Receipts Tax." },
      pt: { s: "Situação do cliente em relação ao imposto estadual." },
    }[this.language].s;
  }

  TenantProfileGrossReceiptsIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Ingresos Brutos", p: "Número de Ingresos Brutos", g: "M" },
      en: {
        s: "Gross Receipts ID",
        p: "Gross Receipts Tax Identification Number",
      },
      pt: {
        s: "Inscrição Estadual",
        p: "Número de Inscrição Estadual",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileGrossReceiptsIdLabel() {
    return this.TenantProfileGrossReceiptsIdLabelByLang();
  }

  get TenantProfileGrossReceiptsIdDescription() {
    return {
      es: { s: "Número de inscripción en Ingresos Brutos." },
      en: { s: "Gross Receipts Tax registration number." },
      pt: { s: "Número de inscrição estadual." },
    }[this.language].s;
  }

  TenantProfileBillingEmailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Correo Electrónico de Facturación", p: "M" },
      en: { s: "Billing Email" },
      pt: { s: "Email de faturamento", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileBillingEmailLabel() {
    return this.TenantProfileBillingEmailLabelByLang();
  }

  get TenantProfileBillingEmailDescription() {
    return {
      es: {
        s: "Correo electrónico para recibir facturas y documentos de facturación.",
      },
      en: { s: "Email address for receiving invoices and billing documents." },
      pt: { s: "E-mail para receber faturas e documentos de faturamento." },
    }[this.language].s;
  }

  TenantProfileBillingCurrencyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Moneda de Facturación",
        p: "Moneda usada para la facturación",
        g: "F",
      },
      en: { s: "Billing Currency", p: "Currency used for billing" },
      pt: {
        s: "Moeda de Faturamento",
        p: "Moeda usada para faturamento",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileBillingCurrencyLabel() {
    return this.TenantProfileBillingCurrencyLabelByLang();
  }

  get TenantProfileBillingCurrencyDescription() {
    return {
      es: { s: "Moneda preferida para la emisión de facturas." },
      en: { s: "Preferred currency for invoice issuance." },
      pt: { s: "Moeda preferida para emissão de faturas." },
    }[this.language].s;
  }

  TenantProfilePrinterNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nombre del Impresor",
        p: "Nombre del impresor para registros DNDA",
        g: "M",
      },
      en: { s: "Printer Name", p: "Printer name for DNDA records" },
      pt: {
        s: "Nome do Impressor",
        p: "Nome do impressor para registros DNDA",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterNameLabel() {
    return this.TenantProfilePrinterNameLabelByLang();
  }

  get TenantProfilePrinterNameDescription() {
    return {
      es: { s: "Nombre del impresor autorizado para registros ante DNDA." },
      en: { s: "Name of the authorized printer for DNDA records." },
      pt: { s: "Nome do impressor autorizado para registros junto ao DNDA." },
    }[this.language].s;
  }

  TenantProfilePrinterStreetAndNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Calle y Número", p: "Calle y número del domicilio", g: "F" },
      en: { s: "Street and Number", p: "Street and house number" },
      pt: { s: "Rua e Número", p: "Rua e número do endereço", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterStreetAndNumberLabel() {
    return this.TenantProfilePrinterStreetAndNumberLabelByLang();
  }

  get TenantProfilePrinterStreetAndNumberDescription() {
    return {
      es: { s: "Dirección física principal de la editorial." },
      en: { s: "Main physical address of the publisher." },
      pt: { s: "Endereço físico principal da editora." },
    }[this.language].s;
  }

  TenantProfilePrinterFloorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Piso", p: "Número de piso", g: "M" },
      en: { s: "Floor", p: "Floor number" },
      pt: { s: "Andar", p: "Número do andar" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterFloorLabel() {
    return this.TenantProfilePrinterFloorLabelByLang();
  }

  get TenantProfilePrinterFloorDescription() {
    return {
      es: { s: "Número de piso en el edificio de la editorial, si aplica." },
      en: { s: "Floor number in the publisher's building, if applicable." },
      pt: { s: "Número do andar no edifício da editora, se aplicável." },
    }[this.language].s;
  }

  TenantProfilePrinterSuiteLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Departamento",
        p: "Número o letra de departamento/oficina",
        g: "M",
      },
      en: { s: "Suite/Office", p: "Suite or office number/letter" },
      pt: {
        s: "Apartamento/Sala",
        p: "Número ou letra do apartamento/escritório",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterSuiteLabel() {
    return this.TenantProfilePrinterSuiteLabelByLang();
  }

  get TenantProfilePrinterSuiteDescription() {
    return {
      es: {
        s: "Identificador de la oficina o departamento específico de la editorial.",
      },
      en: { s: "Specific office or suite identifier for the publisher." },
      pt: { s: "Identificador do escritório ou sala específica da editora." },
    }[this.language].s;
  }

  TenantProfilePrinterPostalCodeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Código Postal", p: "Código postal del domicilio", g: "M" },
      en: { s: "Postal Code", p: "Postal code of the address" },
      pt: { s: "Código Postal", p: "Código postal do endereço", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterPostalCodeLabel() {
    return this.TenantProfilePrinterPostalCodeLabelByLang();
  }

  get TenantProfilePrinterPostalCodeDescription() {
    return {
      es: { s: "Código postal de la dirección de la editorial." },
      en: { s: "Postal code for the publisher's address." },
      pt: { s: "Código postal do endereço da editora." },
    }[this.language].s;
  }

  TenantProfilePrinterCountryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "País", p: "País donde se encuentra el domicilio", g: "M" },
      en: { s: "Country", p: "Country where address is located" },
      pt: { s: "País", p: "País onde o endereço está localizado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterCountryLabel() {
    return this.TenantProfilePrinterCountryLabelByLang();
  }

  get TenantProfilePrinterCountryDescription() {
    return {
      es: { s: "País donde la editorial tiene su sede principal." },
      en: { s: "Country where the publisher is primarily based." },
      pt: { s: "País onde a editora está sediada principalmente." },
    }[this.language].s;
  }

  TenantProfilePrinterProvinceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Provincia", p: "Provincia o estado", g: "F" },
      en: { s: "State/Province", p: "State or province" },
      pt: { s: "Estado/Província", p: "Estado ou província", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterProvinceLabel() {
    return this.TenantProfilePrinterProvinceLabelByLang();
  }

  get TenantProfilePrinterProvinceDescription() {
    return {
      es: { s: "Estado o provincia de la editorial." },
      en: { s: "State or province of the publisher." },
      pt: { s: "Estado ou província da editora." },
    }[this.language].s;
  }

  TenantProfilePrinterCityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Localidad/Barrio", p: "Ciudad o localidad", g: "F" },
      en: { s: "City", p: "City or town" },
      pt: { s: "Cidade", p: "Cidade ou localidade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfilePrinterCityLabel() {
    return this.TenantProfilePrinterCityLabelByLang();
  }

  get TenantProfilePrinterCityDescription() {
    return {
      es: { s: "Ciudad o localidad donde se ubica la editorial." },
      en: { s: "City or town where the publisher is located." },
      pt: { s: "Cidade ou localidade onde a editora está localizada." },
    }[this.language].s;
  }

  TenantProfileSadaicFtpUrlLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "URL del Servidor FTP",
        p: "URL del servidor FTP de SADAIC del cliente",
        g: "F",
      },
      en: { s: "FTP Server URL", p: "Tenant's SADAIC FTP server URL" },
      pt: {
        s: "URL do Servidor FTP",
        p: "URL do servidor FTP da SADAIC do cliente",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileSadaicFtpUrlLabel() {
    return this.TenantProfileSadaicFtpUrlLabelByLang();
  }

  get TenantProfileSadaicFtpUrlDescription() {
    return {
      es: {
        s: "URL del servidor FTP de SADAIC asignado al cliente para subir contratos y ejemplares.",
      },
      en: {
        s: "SADAIC FTP server URL assigned to the tenant for uploading contracts and exemplars.",
      },
      pt: {
        s: "URL do servidor FTP da SADAIC atribuído ao cliente para enviar contratos e exemplares.",
      },
    }[this.language].s;
  }

  TenantProfileSadaicFtpUserLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Usuario FTP", p: "Usuario del FTP de SADAIC", g: "M" },
      en: { s: "FTP User", p: "SADAIC FTP user" },
      pt: { s: "Usuário FTP", p: "Usuário do FTP da SADAIC", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileSadaicFtpUserLabel() {
    return this.TenantProfileSadaicFtpUserLabelByLang();
  }

  get TenantProfileSadaicFtpUserDescription() {
    return {
      es: { s: "Usuario de la cuenta FTP de SADAIC del cliente." },
      en: { s: "Tenant's SADAIC FTP account username." },
      pt: { s: "Usuário da conta FTP da SADAIC do cliente." },
    }[this.language].s;
  }

  TenantProfileSadaicFtpPasswordLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Contraseña FTP", p: "Contraseña del FTP de SADAIC", g: "F" },
      en: { s: "FTP Password", p: "SADAIC FTP password" },
      pt: { s: "Senha FTP", p: "Senha do FTP da SADAIC", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileSadaicFtpPasswordLabel() {
    return this.TenantProfileSadaicFtpPasswordLabelByLang();
  }

  get TenantProfileSadaicFtpPasswordDescription() {
    return {
      es: {
        s: "Contraseña de la cuenta FTP de SADAIC del cliente. Se almacena cifrada y no se devuelve en queries.",
      },
      en: {
        s: "Tenant's SADAIC FTP account password. Stored encrypted; never returned in queries.",
      },
      pt: {
        s: "Senha da conta FTP da SADAIC do cliente. Armazenada criptografada; nunca retornada em queries.",
      },
    }[this.language].s;
  }

  TenantProfileSadaicEmailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Email de SADAIC",
        p: "Email de aviso a SADAIC tras la subida FTP",
        g: "M",
      },
      en: {
        s: "SADAIC Email",
        p: "SADAIC notification email after FTP upload",
      },
      pt: {
        s: "Email da SADAIC",
        p: "Email de aviso à SADAIC após o envio FTP",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantProfileSadaicEmailLabel() {
    return this.TenantProfileSadaicEmailLabelByLang();
  }

  get TenantProfileSadaicEmailDescription() {
    return {
      es: {
        s: "Dirección de email a la que se notifica a SADAIC luego de subir la entrega de una edición al FTP. El email incluye el nombre de la edición, la carpeta destino y el árbol de archivos subidos.",
      },
      en: {
        s: "Email address used to notify SADAIC after uploading an edition delivery to the FTP. The email includes the edition name, destination folder and the tree of uploaded files.",
      },
      pt: {
        s: "Endereço de email usado para notificar a SADAIC após enviar a entrega de uma edição ao FTP. O email inclui o nome da edição, a pasta de destino e a árvore de arquivos enviados.",
      },
    }[this.language].s;
  }

  // VatStatus

  get VatStatusLabels() {
    return {
      es: {
        s: "Situación frente al IVA",
        p: "Situaciones frente al IVA",
        g: "F",
      },
      en: { s: "VAT Status", p: "VAT Statuses", g: undefined },
      pt: { s: "Situação Tributária", p: "Situações Tributárias", g: "F" },
    }[this.language];
  }

  get VatStatusSingular() {
    return this.VatStatusLabels.s;
  }

  get VatStatusPlural() {
    return this.VatStatusLabels.p;
  }

  get VatStatusGender() {
    return this.VatStatusLabels.g;
  }

  get groupVatStatusLabel() {
    return {
      es: {
        s: "Situación frente al IVA",
        p: "Situaciones frente al IVA",
        g: "F",
      },
      en: { s: "VAT Status", p: "VAT Statuses" },
      pt: { s: "Situação Tributária", p: "Situações Tributárias", g: "F" },
    }[this.language].s;
  }

  VatStatusNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código", p: "Código de la condición de IVA", g: "M" },
      en: { s: "Code", p: "VAT status code" },
      pt: { s: "Código", p: "Código da situação tributária", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get VatStatusNameLabel() {
    return this.VatStatusNameLabelByLang();
  }

  get VatStatusNameDescription() {
    return {
      es: { s: "Código identificador de la condición frente al IVA." },
      en: { s: "Identifier code for the VAT status." },
      pt: { s: "Código identificador da situação tributária." },
    }[this.language].s;
  }

  VatStatusDescriptionLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Descripción", p: "Descripción de la condición de IVA", g: "F" },
      en: { s: "Description", p: "Description of the VAT status" },
      pt: { s: "Descrição", p: "Descrição da situação tributária", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get VatStatusDescriptionLabel() {
    return this.VatStatusDescriptionLabelByLang();
  }

  get VatStatusDescriptionDescription() {
    return {
      es: { s: "Descripción detallada de la condición tributaria." },
      en: { s: "Detailed description of the tax status." },
      pt: { s: "Descrição detalhada da situação tributária." },
    }[this.language].s;
  }

  // GrossReceiptsStatus

  get GrossReceiptsStatusLabels() {
    return {
      es: { s: "Situación IIBB", p: "Situaciones en Ingresos Brutos", g: "F" },
      en: {
        s: "Gross Receipts Status",
        p: "Gross Receipts Statuses",
        g: undefined,
      },
      pt: {
        s: "Situação Estadual",
        p: "Situações de Inscrição Estadual",
        g: "F",
      },
    }[this.language];
  }

  get GrossReceiptsStatusSingular() {
    return this.GrossReceiptsStatusLabels.s;
  }

  get GrossReceiptsStatusPlural() {
    return this.GrossReceiptsStatusLabels.p;
  }

  get GrossReceiptsStatusGender() {
    return this.GrossReceiptsStatusLabels.g;
  }

  get groupGrossReceiptsStatusLabel() {
    return {
      es: { s: "Situación IIBB", p: "Situaciones en Ingresos Brutos", g: "F" },
      en: { s: "Gross Receipts Status", p: "Gross Receipts Statuses" },
      pt: {
        s: "Situação Estadual",
        p: "Situações de Inscrição Estadual",
        g: "F",
      },
    }[this.language].s;
  }

  GrossReceiptsStatusNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Código", p: "Código de situación en IIBB", g: "M" },
      en: { s: "Code", p: "Gross receipts status code" },
      pt: { s: "Código", p: "Código da situação estadual", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get GrossReceiptsStatusNameLabel() {
    return this.GrossReceiptsStatusNameLabelByLang();
  }

  get GrossReceiptsStatusNameDescription() {
    return {
      es: { s: "Código identificador de la situación en Ingresos Brutos." },
      en: { s: "Identifier code for the Gross Receipts status." },
      pt: { s: "Código identificador da situação estadual." },
    }[this.language].s;
  }

  GrossReceiptsStatusDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Descripción",
        p: "Descripción de la situación en IIBB",
        g: "F",
      },
      en: { s: "Description", p: "Description of the gross receipts status" },
      pt: { s: "Descrição", p: "Descrição da situação estadual", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get GrossReceiptsStatusDescriptionLabel() {
    return this.GrossReceiptsStatusDescriptionLabelByLang();
  }

  get GrossReceiptsStatusDescriptionDescription() {
    return {
      es: { s: "Descripción detallada de la situación en Ingresos Brutos." },
      en: { s: "Detailed description of the Gross Receipts status." },
      pt: { s: "Descrição detalhada da situação estadual." },
    }[this.language].s;
  }

  // TenantType

  get TenantTypeLabels() {
    return {
      es: { s: "Tipo de Cliente", p: "Tipos de Cliente", g: "M" },
      en: { s: "Tenant Type", p: "Tenant Types", g: undefined },
      pt: { s: "Tipo de Cliente", p: "Tipos de Cliente", g: "M" },
    }[this.language];
  }

  get TenantTypeSingular() {
    return this.TenantTypeLabels.s;
  }

  get TenantTypePlural() {
    return this.TenantTypeLabels.p;
  }

  get TenantTypeGender() {
    return this.TenantTypeLabels.g;
  }

  get groupTenantTypeLabel() {
    return {
      es: { s: "Tipo de Cliente", p: "Tipos de Cliente", g: "M" },
      en: { s: "Tenant Type", p: "Tenant Types" },
      pt: { s: "Tipo de Cliente", p: "Tipos de Cliente", g: "M" },
    }[this.language].s;
  }

  TenantTypeNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código", p: "Código único del tipo de cliente", g: "M" },
      en: { s: "Code", p: "Unique code for the tenant type" },
      pt: { s: "Código", p: "Código único do tipo de cliente", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTypeNameLabel() {
    return this.TenantTypeNameLabelByLang();
  }

  get TenantTypeNameDescription() {
    return {
      es: { s: "Código identificador del tipo de cliente." },
      en: { s: "Identifier code for the tenant type." },
      pt: { s: "Código identificador do tipo de cliente." },
    }[this.language].s;
  }

  TenantTypeDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Descripción", p: "Descripción del tipo de cliente", g: "F" },
      en: { s: "Description", p: "Description of the tenant type" },
      pt: { s: "Descrição", p: "Descrição do tipo de cliente", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantTypeDescriptionLabel() {
    return this.TenantTypeDescriptionLabelByLang();
  }

  get TenantTypeDescriptionDescription() {
    return {
      es: { s: "Descripción detallada del tipo de cliente." },
      en: { s: "Detailed description of the tenant type." },
      pt: { s: "Descrição detalhada do tipo de cliente." },
    }[this.language].s;
  }

  // TenantStatus

  get TenantStatusLabels() {
    return {
      es: { s: "Estado de Cliente", p: "Estados de Cliente", g: "M" },
      en: { s: "Tenant Status", p: "Tenant Statuses", g: undefined },
      pt: { s: "Status do Cliente", p: "Status dos Clientes", g: "M" },
    }[this.language];
  }

  get TenantStatusSingular() {
    return this.TenantStatusLabels.s;
  }

  get TenantStatusPlural() {
    return this.TenantStatusLabels.p;
  }

  get TenantStatusGender() {
    return this.TenantStatusLabels.g;
  }

  get groupTenantStatusLabel() {
    return {
      es: { s: "Estado de Cliente", p: "Estados de Cliente", g: "M" },
      en: { s: "Tenant Status", p: "Tenant Statuses" },
      pt: { s: "Status do Cliente", p: "Status dos Clientes", g: "M" },
    }[this.language].s;
  }

  TenantStatusNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código", p: "Código único del estado del cliente", g: "M" },
      en: { s: "Code", p: "Unique code for the tenant status" },
      pt: { s: "Código", p: "Código único do status do cliente", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantStatusNameLabel() {
    return this.TenantStatusNameLabelByLang();
  }

  get TenantStatusNameDescription() {
    return {
      es: { s: "Código identificador del estado del cliente." },
      en: { s: "Identifier code for the tenant status." },
      pt: { s: "Código identificador do status do cliente." },
    }[this.language].s;
  }

  TenantStatusDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Descripción", p: "Descripción del estado del cliente", g: "F" },
      en: { s: "Description", p: "Description of the tenant status" },
      pt: { s: "Descrição", p: "Descrição do status do cliente", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantStatusDescriptionLabel() {
    return this.TenantStatusDescriptionLabelByLang();
  }

  get TenantStatusDescriptionDescription() {
    return {
      es: { s: "Descripción detallada del estado del cliente." },
      en: { s: "Detailed description of the tenant status." },
      pt: { s: "Descrição detalhada do status do cliente." },
    }[this.language].s;
  }

  TenantStatusBlocksAccessLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Bloquea acceso",
        p: "Si este estado impide el acceso de los usuarios del cliente",
        g: "M",
      },
      en: {
        s: "Blocks access",
        p: "Whether this status prevents the client's users from accessing",
      },
      pt: {
        s: "Bloqueia acesso",
        p: "Se este status impede o acesso dos usuários do cliente",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantStatusBlocksAccessLabel() {
    return this.TenantStatusBlocksAccessLabelByLang();
  }

  get TenantStatusBlocksAccessDescription() {
    return {
      es: {
        s: "Si está activo, los usuarios de un cliente con este estado no pueden iniciar sesión ni operar (el cliente queda desactivado).",
      },
      en: {
        s: "If enabled, users of a client with this status cannot log in or operate (the client is deactivated).",
      },
      pt: {
        s: "Se ativado, os usuários de um cliente com este status não podem fazer login nem operar (o cliente fica desativado).",
      },
    }[this.language].s;
  }

  // Currency

  get CurrencyLabels() {
    return {
      es: { s: "Moneda", p: "Monedas", g: "F" },
      en: { s: "Currency", p: "Currencies", g: undefined },
      pt: { s: "Moeda", p: "Moedas", g: "F" },
    }[this.language];
  }

  get CurrencySingular() {
    return this.CurrencyLabels.s;
  }

  get CurrencyPlural() {
    return this.CurrencyLabels.p;
  }

  get CurrencyGender() {
    return this.CurrencyLabels.g;
  }

  get groupCurrencyLabel() {
    return {
      es: { s: "Moneda", p: "Monedas", g: "F" },
      en: { s: "Currency", p: "Currencies" },
      pt: { s: "Moeda", p: "Moedas", g: "F" },
    }[this.language].s;
  }

  CurrencyCodeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Código",
        p: "Código corto de la moneda (por ejemplo, USD, EUR)",
        g: "M",
      },
      en: { s: "Code", p: "Short code of the currency (e.g., USD, EUR)" },
      pt: {
        s: "Código",
        p: "Código curto da moeda (por exemplo, USD, EUR)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get CurrencyCodeLabel() {
    return this.CurrencyCodeLabelByLang();
  }

  get CurrencyCodeDescription() {
    return {
      es: { s: "Código ISO 4217 de la moneda (USD, EUR, ARS, etc.)." },
      en: { s: "ISO 4217 currency code (USD, EUR, ARS, etc.)." },
      pt: { s: "Código ISO 4217 da moeda (USD, EUR, ARS, etc.)." },
    }[this.language].s;
  }

  CurrencyNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Nombre",
        p: "Nombre completo de la moneda (por ejemplo, Dólar estadounidense)",
        g: "M",
      },
      en: {
        s: "Name",
        p: "Full name of the currency (e.g., United States Dollar)",
      },
      pt: {
        s: "Nome",
        p: "Nome completo da moeda (por exemplo, Dólar americano)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get CurrencyNameLabel() {
    return this.CurrencyNameLabelByLang();
  }

  get CurrencyNameDescription() {
    return {
      es: { s: "Nombre completo de la moneda en el idioma local." },
      en: { s: "Full name of the currency in the local language." },
      pt: { s: "Nome completo da moeda no idioma local." },
    }[this.language].s;
  }

  // TenantPlan

  get TenantPlanLabels() {
    return {
      es: { s: "Plan del Cliente", p: "Planes del Cliente", g: "M" },
      en: { s: "Tenant Plan", p: "Tenant Plans", g: undefined },
      pt: { s: "Plano do Cliente", p: "Planos do Cliente", g: "M" },
    }[this.language];
  }

  get TenantPlanSingular() {
    return this.TenantPlanLabels.s;
  }

  get TenantPlanPlural() {
    return this.TenantPlanLabels.p;
  }

  get TenantPlanGender() {
    return this.TenantPlanLabels.g;
  }

  get groupTenantPlanLabel() {
    return {
      es: { s: "Plan del Cliente", p: "Planes del Cliente", g: "M" },
      en: { s: "Tenant Plan", p: "Tenant Plans" },
      pt: { s: "Plano do Cliente", p: "Planos do Cliente", g: "M" },
    }[this.language].s;
  }

  TenantPlanNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Nombre",
        p: "Nombre de la condición (por ejemplo, 'Requiere orden de compra')",
        g: "M",
      },
      en: { s: "Name", p: "Condition name (e.g., 'Requires purchase order')" },
      pt: {
        s: "Nome",
        p: "Nome da condição (ex., 'Requer ordem de compra')",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantPlanNameLabel() {
    return this.TenantPlanNameLabelByLang();
  }

  get TenantPlanNameDescription() {
    return {
      es: { s: "Nombre identificador del plan de suscripción." },
      en: { s: "Identifier name of the subscription plan." },
      pt: { s: "Nome identificador do plano de assinatura." },
    }[this.language].s;
  }

  TenantPlanDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Descripción",
        p: "Descripción detallada de la condición",
        g: "F",
      },
      en: { s: "Description", p: "Detailed description of the condition" },
      pt: { s: "Descrição", p: "Descrição detalhada da condição", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantPlanDescriptionLabel() {
    return this.TenantPlanDescriptionLabelByLang();
  }

  get TenantPlanDescriptionDescription() {
    return {
      es: { s: "Descripción detallada del plan y sus condiciones." },
      en: { s: "Detailed description of the plan and its conditions." },
      pt: { s: "Descrição detalhada do plano e suas condições." },
    }[this.language].s;
  }

  TenantPlanLimitsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Características del plan",
        p: "Descripción detallada del plan",
        g: "F",
      },
      en: { s: "Plan features", p: "Detailed description of the plan" },
      pt: {
        s: "Características do plano",
        p: "Descrição detalhada do plano",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantPlanLimitsLabel() {
    return this.TenantPlanLimitsLabelByLang();
  }

  get TenantPlanLimitsDescription() {
    return {
      es: {
        s: "JSON con los límites y características del plan (usuarios, almacenamiento, etc.).",
      },
      en: { s: "JSON with plan limits and features (users, storage, etc.)." },
      pt: {
        s: "JSON com os limites e recursos do plano (usuários, armazenamento, etc.).",
      },
    }[this.language].s;
  }

  // Country

  get CountryLabels() {
    return {
      en: { s: "Country", p: "Countries", g: undefined },
      es: { s: "País", p: "Países", g: "m" },
      pt: { s: "País", p: "Países", g: "m" },
    }[this.language];
  }

  get CountrySingular() {
    return this.CountryLabels.s;
  }

  get CountryPlural() {
    return this.CountryLabels.p;
  }

  get CountryGender() {
    return this.CountryLabels.g;
  }

  get groupCountryLabel() {
    return {
      en: { s: "Country", p: "Countries" },
      es: { s: "País", p: "Países", g: "m" },
      pt: { s: "País", p: "Países", g: "m" },
    }[this.language].s;
  }

  CountryNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "M" },
      en: { s: "Name" },
      pt: { s: "Nome", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get CountryNameLabel() {
    return this.CountryNameLabelByLang();
  }

  get CountryNameDescription() {
    return {
      es: { s: "Nombre del país en el idioma local." },
      en: { s: "Country name in the local language." },
      pt: { s: "Nome do país no idioma local." },
    }[this.language].s;
  }

  CountryCodeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código", p: "M" },
      en: { s: "Code" },
      pt: { s: "Código", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get CountryCodeLabel() {
    return this.CountryCodeLabelByLang();
  }

  get CountryCodeDescription() {
    return {
      es: { s: "Código ISO 3166-1 alfa-2 del país (AR, US, BR, etc.)." },
      en: { s: "ISO 3166-1 alpha-2 country code (AR, US, BR, etc.)." },
      pt: { s: "Código ISO 3166-1 alfa-2 do país (AR, US, BR, etc.)." },
    }[this.language].s;
  }

  CountryCurrencyLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Moneda", p: "F" },
      en: { s: "Currency" },
      pt: { s: "Moeda", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get CountryCurrencyLabel() {
    return this.CountryCurrencyLabelByLang();
  }

  get CountryCurrencyDescription() {
    return {
      es: { s: "Código de la moneda predeterminada del país." },
      en: { s: "Default currency code for the country." },
      pt: { s: "Código da moeda padrão do país." },
    }[this.language].s;
  }

  CountryDemonymLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Gentilicio", p: "Gentilicio para la nacionalidad", g: "M" },
      en: { s: "Demonym", p: "Demonym for nationality" },
      pt: { s: "Gentílico", p: "Gentílico para a nacionalidade", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CountryDemonymLabel() {
    return this.CountryDemonymLabelByLang();
  }

  get CountryDemonymDescription() {
    return {
      es: {
        s: "Gentilicio en femenino, para concordar con «nacionalidad»: «de nacionalidad argentina».",
      },
      en: { s: "Demonym used for nationality: «of Argentine nationality»." },
      pt: {
        s: "Gentílico no feminino, para concordar com «nacionalidade»: «de nacionalidade argentina».",
      },
    }[this.language].s;
  }

  // ProvinceState

  get ProvinceStateLabels() {
    return {
      en: { s: "Province/State", p: "Provinces/States", g: undefined },
      es: { s: "Provincia/Estado", p: "Provincias/Estados", g: "f" },
      pt: { s: "Província/Estado", p: "Províncias/Estados", g: "f" },
    }[this.language];
  }

  get ProvinceStateSingular() {
    return this.ProvinceStateLabels.s;
  }

  get ProvinceStatePlural() {
    return this.ProvinceStateLabels.p;
  }

  get ProvinceStateGender() {
    return this.ProvinceStateLabels.g;
  }

  get groupProvinceStateLabel() {
    return {
      en: { s: "Province/State", p: "Provinces/States" },
      es: { s: "Provincia/Estado", p: "Provincias/Estados", g: "f" },
      pt: { s: "Província/Estado", p: "Províncias/Estados", g: "f" },
    }[this.language].s;
  }

  ProvinceStateNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "M" },
      en: { s: "Name" },
      pt: { s: "Nome", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get ProvinceStateNameLabel() {
    return this.ProvinceStateNameLabelByLang();
  }

  get ProvinceStateNameDescription() {
    return {
      es: { s: "Nombre de la provincia o estado en el idioma local." },
      en: { s: "Province or state name in the local language." },
      pt: { s: "Nome da província ou estado no idioma local." },
    }[this.language].s;
  }

  ProvinceStateCountryLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "País", p: "M" },
      en: { s: "Country" },
      pt: { s: "País", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get ProvinceStateCountryLabel() {
    return this.ProvinceStateCountryLabelByLang();
  }

  get ProvinceStateCountryDescription() {
    return {
      es: { s: "País al que pertenece la provincia o estado." },
      en: { s: "Country to which the province or state belongs." },
      pt: { s: "País ao qual a província ou estado pertence." },
    }[this.language].s;
  }

  // CreditTier

  get CreditTierLabels() {
    return {
      es: { s: "Nivel de Créditos", p: "Niveles de Créditos", g: "M" },
      en: { s: "Credit Tier", p: "Credit Tiers", g: undefined },
      pt: { s: "Nível de Créditos", p: "Níveis de Créditos", g: "M" },
    }[this.language];
  }

  get CreditTierSingular() {
    return this.CreditTierLabels.s;
  }

  get CreditTierPlural() {
    return this.CreditTierLabels.p;
  }

  get CreditTierGender() {
    return this.CreditTierLabels.g;
  }

  get groupCreditTierLabel() {
    return {
      es: { s: "Nivel de Créditos", p: "Niveles de Créditos", g: "M" },
      en: { s: "Credit Tier", p: "Credit Tiers" },
      pt: { s: "Nível de Créditos", p: "Níveis de Créditos", g: "M" },
    }[this.language].s;
  }

  CreditTierNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del nivel", g: "M" },
      en: { s: "Name", p: "Tier name" },
      pt: { s: "Nome", p: "Nome do nível" },
    }[lang ? lang : this.language].s;
  }

  get CreditTierNameLabel() {
    return this.CreditTierNameLabelByLang();
  }

  get CreditTierNameDescription() {
    return {
      es: {
        s: "Nombre comercial del nivel (ej. Inicial, Profesional, Editorial).",
      },
      en: {
        s: "Commercial name of the tier (e.g., Starter, Professional, Publisher).",
      },
      pt: {
        s: "Nome comercial do nível (ex. Inicial, Profissional, Editorial).",
      },
    }[this.language].s;
  }

  CreditTierMonthlyCreditsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Créditos Mensuales", p: "Créditos incluidos por mes", g: "M" },
      en: { s: "Monthly Credits", p: "Credits included per month" },
      pt: { s: "Créditos Mensais", p: "Créditos incluídos por mês", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditTierMonthlyCreditsLabel() {
    return this.CreditTierMonthlyCreditsLabelByLang();
  }

  get CreditTierMonthlyCreditsDescription() {
    return {
      es: {
        s: "Cantidad de créditos que incluye el nivel por mes. La acreditación mensual la registra el administrador del sistema como una Compra de Créditos.",
      },
      en: {
        s: "Number of credits the tier includes per month. The monthly accreditation is registered by the system administrator as a Credit Purchase.",
      },
      pt: {
        s: "Quantidade de créditos que o nível inclui por mês. O crédito mensal é registrado pelo administrador do sistema como uma Compra de Créditos.",
      },
    }[this.language].s;
  }

  CreditTierPricePerCreditLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Precio por Crédito", p: "Precio del crédito incluido", g: "M" },
      en: { s: "Price per Credit", p: "Price of the included credit" },
      pt: { s: "Preço por Crédito", p: "Preço do crédito incluído", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditTierPricePerCreditLabel() {
    return this.CreditTierPricePerCreditLabelByLang();
  }

  get CreditTierPricePerCreditDescription() {
    return {
      es: { s: "Precio unitario de los créditos incluidos en el nivel." },
      en: { s: "Unit price of the credits included in the tier." },
      pt: { s: "Preço unitário dos créditos incluídos no nível." },
    }[this.language].s;
  }

  CreditTierPricePerExtraCreditLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Precio por Crédito Extra",
        p: "Precio del crédito fuera del nivel",
        g: "M",
      },
      en: {
        s: "Price per Extra Credit",
        p: "Price of the credit beyond the tier",
      },
      pt: {
        s: "Preço por Crédito Extra",
        p: "Preço do crédito além do nível",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get CreditTierPricePerExtraCreditLabel() {
    return this.CreditTierPricePerExtraCreditLabelByLang();
  }

  get CreditTierPricePerExtraCreditDescription() {
    return {
      es: {
        s: "Precio unitario de los créditos comprados por encima de los incluidos en el nivel.",
      },
      en: {
        s: "Unit price of credits purchased beyond those included in the tier.",
      },
      pt: {
        s: "Preço unitário dos créditos comprados além dos incluídos no nível.",
      },
    }[this.language].s;
  }

  CreditTierDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Descripción", p: "Descripción del nivel", g: "F" },
      en: { s: "Description", p: "Tier description" },
      pt: { s: "Descrição", p: "Descrição do nível", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditTierDescriptionLabel() {
    return this.CreditTierDescriptionLabelByLang();
  }

  // CreditAccount

  get CreditAccountLabels() {
    return {
      es: { s: "Cuenta de Créditos", p: "Cuentas de Créditos", g: "F" },
      en: { s: "Credit Account", p: "Credit Accounts", g: undefined },
      pt: { s: "Conta de Créditos", p: "Contas de Créditos", g: "F" },
    }[this.language];
  }

  get CreditAccountSingular() {
    return this.CreditAccountLabels.s;
  }

  get CreditAccountPlural() {
    return this.CreditAccountLabels.p;
  }

  get CreditAccountGender() {
    return this.CreditAccountLabels.g;
  }

  get groupCreditAccountLabel() {
    return {
      es: { s: "Cuenta de Créditos", p: "Cuentas de Créditos", g: "F" },
      en: { s: "Credit Account", p: "Credit Accounts" },
      pt: { s: "Conta de Créditos", p: "Contas de Créditos", g: "F" },
    }[this.language].s;
  }

  CreditAccountBalanceLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Saldo", p: "Créditos disponibles", g: "M" },
      en: { s: "Balance", p: "Available credits" },
      pt: { s: "Saldo", p: "Créditos disponíveis", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditAccountBalanceLabel() {
    return this.CreditAccountBalanceLabelByLang();
  }

  get CreditAccountBalanceDescription() {
    return {
      es: {
        s: "Saldo actual de créditos. Un crédito equivale a una obra al generar la documentación de una edición. Lo mantiene el sistema.",
      },
      en: {
        s: "Current credit balance. One credit equals one work when generating an edition's documentation. Maintained by the system.",
      },
      pt: {
        s: "Saldo atual de créditos. Um crédito equivale a uma obra ao gerar a documentação de uma edição. Mantido pelo sistema.",
      },
    }[this.language].s;
  }

  CreditAccountTierLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nivel", p: "Nivel de créditos asignado", g: "M" },
      en: { s: "Tier", p: "Assigned credit tier" },
      pt: { s: "Nível", p: "Nível de créditos atribuído", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditAccountTierLabel() {
    return this.CreditAccountTierLabelByLang();
  }

  get CreditAccountTierDescription() {
    return {
      es: {
        s: "Nivel comercial asignado al cliente: define los créditos mensuales incluidos y los precios. Concepto interno: solo visible para la administración del sistema.",
      },
      en: {
        s: "Commercial tier assigned to the client: defines the included monthly credits and the prices. Internal concept: only visible to system administration.",
      },
      pt: {
        s: "Nível comercial atribuído ao cliente: define os créditos mensais incluídos e os preços. Conceito interno: só visível para a administração do sistema.",
      },
    }[this.language].s;
  }

  CreditAccountMovementsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Movimientos",
        p: "Historial de movimientos de la cuenta",
        g: "M",
      },
      en: { s: "Movements", p: "Account movement history" },
      pt: { s: "Movimentos", p: "Histórico de movimentos da conta", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditAccountMovementsLabel() {
    return this.CreditAccountMovementsLabelByLang();
  }

  get CreditAccountMovementsDescription() {
    return {
      es: {
        s: "Historial completo de la cuenta: compras, consumos, devoluciones y ajustes. Lo escribe el sistema.",
      },
      en: {
        s: "Complete account history: purchases, consumptions, refunds and adjustments. Written by the system.",
      },
      pt: {
        s: "Histórico completo da conta: compras, consumos, devoluções e ajustes. Escrito pelo sistema.",
      },
    }[this.language].s;
  }

  // CreditPurchase

  get CreditPurchaseLabels() {
    return {
      es: { s: "Compra de Créditos", p: "Compras de Créditos", g: "F" },
      en: { s: "Credit Purchase", p: "Credit Purchases", g: undefined },
      pt: { s: "Compra de Créditos", p: "Compras de Créditos", g: "F" },
    }[this.language];
  }

  get CreditPurchaseSingular() {
    return this.CreditPurchaseLabels.s;
  }

  get CreditPurchasePlural() {
    return this.CreditPurchaseLabels.p;
  }

  get CreditPurchaseGender() {
    return this.CreditPurchaseLabels.g;
  }

  get groupCreditPurchaseLabel() {
    return {
      es: { s: "Compra de Créditos", p: "Compras de Créditos", g: "F" },
      en: { s: "Credit Purchase", p: "Credit Purchases" },
      pt: { s: "Compra de Créditos", p: "Compras de Créditos", g: "F" },
    }[this.language].s;
  }

  CreditPurchaseTenantLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Cliente", p: "Cliente que compra los créditos", g: "M" },
      en: { s: "Tenant", p: "Client purchasing the credits" },
      pt: { s: "Cliente", p: "Cliente que compra os créditos", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditPurchaseTenantLabel() {
    return this.CreditPurchaseTenantLabelByLang();
  }

  get CreditPurchaseTenantDescription() {
    return {
      es: {
        s: "Cliente al que se acreditan los créditos de esta compra. Define la cuenta de destino. Obligatorio al crear (lo exige el server); el contrato de lectura es nullable para que los roles sin acceso a Clientes puedan leer la compra.",
      },
      en: {
        s: "Client whose account is credited by this purchase. Defines the target account. Required on create (enforced by the server); the read contract is nullable so roles without Tenant access can read the purchase.",
      },
      pt: {
        s: "Cliente cuja conta é creditada por esta compra. Define a conta de destino. Obrigatório ao criar (exigido pelo server); o contrato de leitura é nullable para que papéis sem acesso a Clientes possam ler a compra.",
      },
    }[this.language].s;
  }

  CreditPurchaseQuantityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Cantidad", p: "Créditos comprados", g: "F" },
      en: { s: "Quantity", p: "Credits purchased" },
      pt: { s: "Quantidade", p: "Créditos comprados", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditPurchaseQuantityLabel() {
    return this.CreditPurchaseQuantityLabelByLang();
  }

  get CreditPurchaseQuantityDescription() {
    return {
      es: { s: "Cantidad de créditos que acredita esta compra." },
      en: { s: "Number of credits this purchase accredits." },
      pt: { s: "Quantidade de créditos que esta compra credita." },
    }[this.language].s;
  }

  CreditPurchasePurchaseDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha de Compra",
        p: "Fecha de la compra o acreditación",
        g: "F",
      },
      en: { s: "Purchase Date", p: "Date of the purchase or accreditation" },
      pt: { s: "Data de Compra", p: "Data da compra ou crédito", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditPurchasePurchaseDateLabel() {
    return this.CreditPurchasePurchaseDateLabelByLang();
  }

  get CreditPurchasePurchaseDateDescription() {
    return {
      es: { s: "Fecha de la compra o de la acreditación mensual." },
      en: { s: "Date of the purchase or of the monthly accreditation." },
      pt: { s: "Data da compra ou do crédito mensal." },
    }[this.language].s;
  }

  CreditPurchaseExpirationDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Vencimiento", p: "Vencimiento de estos créditos", g: "M" },
      en: { s: "Expiration", p: "Expiration of these credits" },
      pt: { s: "Vencimento", p: "Vencimento destes créditos", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditPurchaseExpirationDateLabel() {
    return this.CreditPurchaseExpirationDateLabelByLang();
  }

  get CreditPurchaseExpirationDateDescription() {
    return {
      es: {
        s: "Vencimiento de los créditos de esta compra. Si se deja vacío al crear, el sistema asigna 3 meses desde la fecha de compra. Al vencer, los créditos no utilizados del lote se descuentan del saldo (el consumo se imputa siempre al lote que vence antes).",
      },
      en: {
        s: "Expiration of this purchase's credits. If left empty on create, the system assigns 3 months from the purchase date. Upon expiration, the lot's unused credits are deducted from the balance (consumption is always allocated to the earliest-expiring lot).",
      },
      pt: {
        s: "Vencimento dos créditos desta compra. Se ficar vazio ao criar, o sistema atribui 3 meses a partir da data da compra. Ao vencer, os créditos não utilizados do lote são descontados do saldo (o consumo é sempre imputado ao lote que vence antes).",
      },
    }[this.language].s;
  }

  CreditPurchaseUnitPriceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Precio Unitario", p: "Precio aplicado por crédito", g: "M" },
      en: { s: "Unit Price", p: "Price applied per credit" },
      pt: { s: "Preço Unitário", p: "Preço aplicado por crédito", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditPurchaseUnitPriceLabel() {
    return this.CreditPurchaseUnitPriceLabelByLang();
  }

  get CreditPurchaseUnitPriceDescription() {
    return {
      es: {
        s: "Precio por crédito aplicado en esta compra (normal o extra según el nivel del cliente).",
      },
      en: {
        s: "Price per credit applied in this purchase (regular or extra according to the client's tier).",
      },
      pt: {
        s: "Preço por crédito aplicado nesta compra (normal ou extra segundo o nível do cliente).",
      },
    }[this.language].s;
  }

  CreditPurchaseNotesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Notas", p: "Referencia de pago u observaciones", g: "F" },
      en: { s: "Notes", p: "Payment reference or remarks" },
      pt: { s: "Notas", p: "Referência de pagamento ou observações", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditPurchaseNotesLabel() {
    return this.CreditPurchaseNotesLabelByLang();
  }

  get CreditPurchaseNotesDescription() {
    return {
      es: { s: "Referencia del pago u observaciones de la compra." },
      en: { s: "Payment reference or purchase remarks." },
      pt: { s: "Referência do pagamento ou observações da compra." },
    }[this.language].s;
  }

  // CreditMovement

  get CreditMovementLabels() {
    return {
      es: { s: "Movimiento de Créditos", p: "Movimientos de Créditos", g: "M" },
      en: { s: "Credit Movement", p: "Credit Movements", g: undefined },
      pt: { s: "Movimento de Créditos", p: "Movimentos de Créditos", g: "M" },
    }[this.language];
  }

  get CreditMovementSingular() {
    return this.CreditMovementLabels.s;
  }

  get CreditMovementPlural() {
    return this.CreditMovementLabels.p;
  }

  get CreditMovementGender() {
    return this.CreditMovementLabels.g;
  }

  get groupCreditMovementLabel() {
    return {
      es: { s: "Movimiento de Créditos", p: "Movimientos de Créditos", g: "M" },
      en: { s: "Credit Movement", p: "Credit Movements" },
      pt: { s: "Movimento de Créditos", p: "Movimentos de Créditos", g: "M" },
    }[this.language].s;
  }

  CreditMovementTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo", p: "Tipo de movimiento", g: "M" },
      en: { s: "Type", p: "Movement type" },
      pt: { s: "Tipo", p: "Tipo de movimento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementTypeLabel() {
    return this.CreditMovementTypeLabelByLang();
  }

  CreditMovementDeltaLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Créditos", p: "Variación de créditos con signo", g: "M" },
      en: { s: "Credits", p: "Signed credit variation" },
      pt: { s: "Créditos", p: "Variação de créditos com sinal", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementDeltaLabel() {
    return this.CreditMovementDeltaLabelByLang();
  }

  get CreditMovementDeltaDescription() {
    return {
      es: {
        s: "Variación de saldo con signo: positiva en compras y devoluciones, negativa en consumos y en ajustes que descuentan.",
      },
      en: {
        s: "Signed balance variation: positive for purchases and refunds, negative for consumptions and deducting adjustments.",
      },
      pt: {
        s: "Variação de saldo com sinal: positiva em compras e devoluções, negativa em consumos e em ajustes que descontam.",
      },
    }[this.language].s;
  }

  CreditMovementBalanceAfterLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Saldo Resultante", p: "Saldo después del movimiento", g: "M" },
      en: { s: "Resulting Balance", p: "Balance after the movement" },
      pt: { s: "Saldo Resultante", p: "Saldo após o movimento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementBalanceAfterLabel() {
    return this.CreditMovementBalanceAfterLabelByLang();
  }

  CreditMovementMovementAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha", p: "Fecha y hora del movimiento", g: "F" },
      en: { s: "Date", p: "Date and time of the movement" },
      pt: { s: "Data", p: "Data e hora do movimento", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementMovementAtLabel() {
    return this.CreditMovementMovementAtLabelByLang();
  }

  CreditMovementDetailLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Detalle", p: "Descripción del movimiento", g: "M" },
      en: { s: "Detail", p: "Movement description" },
      pt: { s: "Detalhe", p: "Descrição do movimento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementDetailLabel() {
    return this.CreditMovementDetailLabelByLang();
  }

  CreditMovementEditionKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Id de Edición",
        p: "Identificador de la edición asociada",
        g: "M",
      },
      en: { s: "Edition Id", p: "Identifier of the associated edition" },
      pt: { s: "Id de Edição", p: "Identificador da edição associada", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementEditionKeyLabel() {
    return this.CreditMovementEditionKeyLabelByLang();
  }

  get CreditMovementEditionKeyDescription() {
    return {
      es: {
        s: "Identificador de la edición que consumió o devolvió los créditos. Se conserva como texto para que el historial sobreviva al borrado o congelamiento de la edición.",
      },
      en: {
        s: "Identifier of the edition that consumed or refunded the credits. Kept as text so the history survives the edition being deleted or frozen.",
      },
      pt: {
        s: "Identificador da edição que consumiu ou devolveu os créditos. Mantido como texto para que o histórico sobreviva à exclusão ou congelamento da edição.",
      },
    }[this.language].s;
  }

  CreditMovementEditionNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Edición", p: "Nombre de la edición asociada", g: "F" },
      en: { s: "Edition", p: "Name of the associated edition" },
      pt: { s: "Edição", p: "Nome da edição associada", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementEditionNameLabel() {
    return this.CreditMovementEditionNameLabelByLang();
  }

  CreditMovementPurchaseLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Compra", p: "Compra que originó el movimiento", g: "F" },
      en: { s: "Purchase", p: "Purchase that originated the movement" },
      pt: { s: "Compra", p: "Compra que originou o movimento", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get CreditMovementPurchaseLabel() {
    return this.CreditMovementPurchaseLabelByLang();
  }

  // WorkerEvent

  get WorkerEventLabels() {
    return {
      es: { s: "Evento de Worker", p: "Eventos de Worker", g: "M" },
      en: { s: "Worker Event", p: "Worker Events", g: undefined },
      pt: { s: "Evento de Worker", p: "Eventos de Worker", g: "M" },
    }[this.language];
  }

  get WorkerEventSingular() {
    return this.WorkerEventLabels.s;
  }

  get WorkerEventPlural() {
    return this.WorkerEventLabels.p;
  }

  get WorkerEventGender() {
    return this.WorkerEventLabels.g;
  }

  get groupWorkerEventLabel() {
    return {
      es: { s: "Evento de Worker", p: "Eventos de Worker", g: "M" },
      en: { s: "Worker Event", p: "Worker Events" },
      pt: { s: "Evento de Worker", p: "Eventos de Worker", g: "M" },
    }[this.language].s;
  }

  WorkerEventQueueNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Cola", p: "F" },
      en: { s: "Queue Name" },
      pt: { s: "Fila", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventQueueNameLabel() {
    return this.WorkerEventQueueNameLabelByLang();
  }

  get WorkerEventQueueNameDescription() {
    return {
      es: {
        s: "Nombre de la cola PGMQ (email_notifications, file_deletions, etc.)",
      },
      en: { s: "PGMQ queue name (email_notifications, file_deletions, etc.)" },
      pt: {
        s: "Nome da fila PGMQ (email_notifications, file_deletions, etc.)",
      },
    }[this.language].s;
  }

  WorkerEventJobTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Job", p: "M" },
      en: { s: "Job Type" },
      pt: { s: "Tipo de Job", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventJobTypeLabel() {
    return this.WorkerEventJobTypeLabelByLang();
  }

  get WorkerEventJobTypeDescription() {
    return {
      es: {
        s: "Tipo específico de job (welcome, passwordReset, file_delete, etc.)",
      },
      en: {
        s: "Specific job type (welcome, passwordReset, file_delete, etc.)",
      },
      pt: {
        s: "Tipo específico de job (welcome, passwordReset, file_delete, etc.)",
      },
    }[this.language].s;
  }

  WorkerEventStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "M" },
      en: { s: "Status" },
      pt: { s: "Status", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventStatusLabel() {
    return this.WorkerEventStatusLabelByLang();
  }

  get WorkerEventStatusDescription() {
    return {
      es: { s: "Estado actual del job" },
      en: { s: "Current job status" },
      pt: { s: "Status atual do job" },
    }[this.language].s;
  }

  WorkerEventEnqueuedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Encolado En", p: "M" },
      en: { s: "Enqueued At" },
      pt: { s: "Enfileirado Em", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventEnqueuedAtLabel() {
    return this.WorkerEventEnqueuedAtLabelByLang();
  }

  get WorkerEventEnqueuedAtDescription() {
    return {
      es: { s: "Momento en que se creó el job" },
      en: { s: "When the job was created" },
      pt: { s: "Quando o job foi criado" },
    }[this.language].s;
  }

  WorkerEventStartedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Iniciado En", p: "M" },
      en: { s: "Started At" },
      pt: { s: "Iniciado Em", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventStartedAtLabel() {
    return this.WorkerEventStartedAtLabelByLang();
  }

  get WorkerEventStartedAtDescription() {
    return {
      es: { s: "Momento en que el worker comenzó a procesar" },
      en: { s: "When worker started processing" },
      pt: { s: "Quando o worker começou a processar" },
    }[this.language].s;
  }

  WorkerEventCompletedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Completado En", p: "M" },
      en: { s: "Completed At" },
      pt: { s: "Completado Em", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventCompletedAtLabel() {
    return this.WorkerEventCompletedAtLabelByLang();
  }

  get WorkerEventCompletedAtDescription() {
    return {
      es: { s: "Momento en que terminó el procesamiento" },
      en: { s: "When processing completed" },
      pt: { s: "Quando o processamento foi concluído" },
    }[this.language].s;
  }

  WorkerEventPayloadLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Payload", p: "M" },
      en: { s: "Payload" },
      pt: { s: "Payload", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventPayloadLabel() {
    return this.WorkerEventPayloadLabelByLang();
  }

  get WorkerEventPayloadDescription() {
    return {
      es: { s: "Datos de entrada del job" },
      en: { s: "Job input data" },
      pt: { s: "Dados de entrada do job" },
    }[this.language].s;
  }

  WorkerEventResultLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Resultado", p: "M" },
      en: { s: "Result" },
      pt: { s: "Resultado", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventResultLabel() {
    return this.WorkerEventResultLabelByLang();
  }

  get WorkerEventResultDescription() {
    return {
      es: { s: "Resultado del job si completó exitosamente" },
      en: { s: "Job result if completed successfully" },
      pt: { s: "Resultado do job se concluído com sucesso" },
    }[this.language].s;
  }

  WorkerEventErrorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Error", p: "M" },
      en: { s: "Error" },
      pt: { s: "Erro", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventErrorLabel() {
    return this.WorkerEventErrorLabelByLang();
  }

  get WorkerEventErrorDescription() {
    return {
      es: { s: "Detalles del error si el job falló" },
      en: { s: "Error details if job failed" },
      pt: { s: "Detalhes do erro se o job falhou" },
    }[this.language].s;
  }

  WorkerEventRetryCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intentos", p: "M" },
      en: { s: "Retry Count" },
      pt: { s: "Tentativas", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventRetryCountLabel() {
    return this.WorkerEventRetryCountLabelByLang();
  }

  get WorkerEventRetryCountDescription() {
    return {
      es: { s: "Número de intentos de procesamiento" },
      en: { s: "Number of processing attempts" },
      pt: { s: "Número de tentativas de processamento" },
    }[this.language].s;
  }

  WorkerEventManualRetryCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Reintentos Manuales", p: "M" },
      en: { s: "Manual Retry Count" },
      pt: { s: "Reintentos Manuais", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventManualRetryCountLabel() {
    return this.WorkerEventManualRetryCountLabelByLang();
  }

  get WorkerEventManualRetryCountDescription() {
    return {
      es: { s: "Cantidad de reintentos manuales disparados por un operador" },
      en: { s: "Number of manual retries triggered by an operator" },
      pt: { s: "Número de reintentos manuais disparados por um operador" },
    }[this.language].s;
  }

  WorkerEventNextRetryAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Próximo Reintento", p: "M" },
      en: { s: "Next Retry At" },
      pt: { s: "Próxima Tentativa", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventNextRetryAtLabel() {
    return this.WorkerEventNextRetryAtLabelByLang();
  }

  get WorkerEventNextRetryAtDescription() {
    return {
      es: {
        s: "Momento estimado del próximo reintento automático (backoff). Null si no hay reintento pendiente.",
      },
      en: {
        s: "Estimated time of the next automatic retry (backoff). Null when no retry is pending.",
      },
      pt: {
        s: "Momento estimado da próxima tentativa automática (backoff). Null se não há tentativa pendente.",
      },
    }[this.language].s;
  }

  WorkerEventDurationLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Duración (ms)", p: "F" },
      en: { s: "Duration (ms)" },
      pt: { s: "Duração (ms)", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventDurationLabel() {
    return this.WorkerEventDurationLabelByLang();
  }

  get WorkerEventDurationDescription() {
    return {
      es: { s: "Tiempo de procesamiento en milisegundos" },
      en: { s: "Processing time in milliseconds" },
      pt: { s: "Tempo de processamento em milissegundos" },
    }[this.language].s;
  }

  WorkerEventCorrelationIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Correlación", p: "M" },
      en: { s: "Correlation ID" },
      pt: { s: "ID de Correlação", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventCorrelationIdLabel() {
    return this.WorkerEventCorrelationIdLabelByLang();
  }

  get WorkerEventCorrelationIdDescription() {
    return {
      es: { s: "Para agrupar jobs relacionados" },
      en: { s: "To group related jobs" },
      pt: { s: "Para agrupar jobs relacionados" },
    }[this.language].s;
  }

  WorkerEventTriggerUserIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Disparado Por", p: "M" },
      en: { s: "Triggered By" },
      pt: { s: "Disparado Por", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkerEventTriggerUserIdLabel() {
    return this.WorkerEventTriggerUserIdLabelByLang();
  }

  get WorkerEventTriggerUserIdDescription() {
    return {
      es: { s: "Usuario que triggeró este job" },
      en: { s: "User who triggered this job" },
      pt: { s: "Usuário que disparou este job" },
    }[this.language].s;
  }

  // SimulationSession

  get SimulationSessionLabels() {
    return {
      es: { s: "Sesión de Simulación", p: "Sesiones de Simulación", g: "F" },
      en: { s: "Simulation Session", p: "Simulation Sessions", g: undefined },
      pt: { s: "Sessão de Simulação", p: "Sessões de Simulação", g: "F" },
    }[this.language];
  }

  get SimulationSessionSingular() {
    return this.SimulationSessionLabels.s;
  }

  get SimulationSessionPlural() {
    return this.SimulationSessionLabels.p;
  }

  get SimulationSessionGender() {
    return this.SimulationSessionLabels.g;
  }

  get groupSimulationSessionLabel() {
    return {
      es: { s: "Sesión de Simulación", p: "Sesiones de Simulación", g: "F" },
      en: { s: "Simulation Session", p: "Simulation Sessions" },
      pt: { s: "Sessão de Simulação", p: "Sessões de Simulação", g: "F" },
    }[this.language].s;
  }

  SimulationSessionStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Estado", p: "M" },
      en: { s: "Status" },
      pt: { s: "Status", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionStatusLabel() {
    return this.SimulationSessionStatusLabelByLang();
  }

  get SimulationSessionStatusDescription() {
    return {
      es: { s: "Estado: running | waiting | completed | failed | aborted" },
      en: { s: "Status: running | waiting | completed | failed | aborted" },
      pt: { s: "Status: running | waiting | completed | failed | aborted" },
    }[this.language].s;
  }

  SimulationSessionModeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Modo", p: "M" },
      en: { s: "Mode" },
      pt: { s: "Modo", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionModeLabel() {
    return this.SimulationSessionModeLabelByLang();
  }

  get SimulationSessionModeDescription() {
    return {
      es: { s: "Modo de control: auto | manual" },
      en: { s: "Control mode: auto | manual" },
      pt: { s: "Modo de controle: auto | manual" },
    }[this.language].s;
  }

  SimulationSessionLabelLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Etiqueta", p: "F" },
      en: { s: "Label" },
      pt: { s: "Etiqueta", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionLabelLabel() {
    return this.SimulationSessionLabelLabelByLang();
  }

  get SimulationSessionLabelDescription() {
    return {
      es: { s: "Descripción de la sesión" },
      en: { s: "Session label" },
      pt: { s: "Descrição da sessão" },
    }[this.language].s;
  }

  SimulationSessionEntryPipelineLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Pipeline de Entrada", p: "M" },
      en: { s: "Entry Pipeline" },
      pt: { s: "Pipeline de Entrada", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionEntryPipelineLabel() {
    return this.SimulationSessionEntryPipelineLabelByLang();
  }

  get SimulationSessionEntryPipelineDescription() {
    return {
      es: { s: "Pipeline que inicia el circuito simulado" },
      en: { s: "Pipeline that starts the simulated circuit" },
      pt: { s: "Pipeline que inicia o circuito simulado" },
    }[this.language].s;
  }

  SimulationSessionScenarioLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Escenario", p: "M" },
      en: { s: "Scenario" },
      pt: { s: "Cenário", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionScenarioLabel() {
    return this.SimulationSessionScenarioLabelByLang();
  }

  get SimulationSessionScenarioDescription() {
    return {
      es: { s: "Config por gate: fail / auto-advance" },
      en: { s: "Per-gate config: fail / auto-advance" },
      pt: { s: "Config por gate: fail / auto-advance" },
    }[this.language].s;
  }

  SimulationSessionResolvedGatesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Gates Resueltos", p: "M" },
      en: { s: "Resolved Gates" },
      pt: { s: "Gates Resolvidos", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionResolvedGatesLabel() {
    return this.SimulationSessionResolvedGatesLabelByLang();
  }

  get SimulationSessionResolvedGatesDescription() {
    return {
      es: { s: "gate → acción resuelta (advance/fail/...)" },
      en: { s: "gate → resolved action (advance/fail/...)" },
      pt: { s: "gate → ação resolvida (advance/fail/...)" },
    }[this.language].s;
  }

  SimulationSessionCurrentGateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Gate Actual", p: "M" },
      en: { s: "Current Gate" },
      pt: { s: "Gate Atual", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionCurrentGateLabel() {
    return this.SimulationSessionCurrentGateLabelByLang();
  }

  get SimulationSessionCurrentGateDescription() {
    return {
      es: { s: "Gate en el que la sesión está esperando" },
      en: { s: "Gate the session is waiting on" },
      pt: { s: "Gate em que a sessão está esperando" },
    }[this.language].s;
  }

  SimulationSessionCorrelationIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Correlación", p: "M" },
      en: { s: "Correlation ID" },
      pt: { s: "ID de Correlação", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionCorrelationIdLabel() {
    return this.SimulationSessionCorrelationIdLabelByLang();
  }

  get SimulationSessionCorrelationIdDescription() {
    return {
      es: { s: "Agrupa los WorkerEvents/BotRuns del run" },
      en: { s: "Groups the run's WorkerEvents/BotRuns" },
      pt: { s: "Agrupa os WorkerEvents/BotRuns do run" },
    }[this.language].s;
  }

  SimulationSessionRelatedEntityTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Entidad", p: "M" },
      en: { s: "Related Entity Type" },
      pt: { s: "Tipo de Entidade", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionRelatedEntityTypeLabel() {
    return this.SimulationSessionRelatedEntityTypeLabelByLang();
  }

  get SimulationSessionRelatedEntityTypeDescription() {
    return {
      es: { s: "Entidad a la que se ancla la simulación (ej. SignedEdition)" },
      en: { s: "Entity the simulation is anchored to (e.g. SignedEdition)" },
      pt: {
        s: "Entidade à qual a simulação está ancorada (ex. SignedEdition)",
      },
    }[this.language].s;
  }

  SimulationSessionRelatedEntityIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Entidad", p: "M" },
      en: { s: "Related Entity ID" },
      pt: { s: "ID de Entidade", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionRelatedEntityIdLabel() {
    return this.SimulationSessionRelatedEntityIdLabelByLang();
  }

  get SimulationSessionRelatedEntityIdDescription() {
    return {
      es: {
        s: "ID de la entidad anclada (resuelve ctx.simulation por entidad)",
      },
      en: { s: "Anchored entity id (resolves ctx.simulation by entity)" },
      pt: {
        s: "ID da entidade ancorada (resolve ctx.simulation por entidade)",
      },
    }[this.language].s;
  }

  SimulationSessionStartedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Iniciado En", p: "M" },
      en: { s: "Started At" },
      pt: { s: "Iniciado Em", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionStartedAtLabel() {
    return this.SimulationSessionStartedAtLabelByLang();
  }

  get SimulationSessionStartedAtDescription() {
    return {
      es: { s: "Momento de inicio" },
      en: { s: "Start time" },
      pt: { s: "Momento de início" },
    }[this.language].s;
  }

  SimulationSessionCompletedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Completado En", p: "M" },
      en: { s: "Completed At" },
      pt: { s: "Completado Em", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionCompletedAtLabel() {
    return this.SimulationSessionCompletedAtLabelByLang();
  }

  get SimulationSessionCompletedAtDescription() {
    return {
      es: { s: "Momento de finalización" },
      en: { s: "Completion time" },
      pt: { s: "Momento de finalização" },
    }[this.language].s;
  }

  SimulationSessionTriggeredByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Disparado Por", p: "M" },
      en: { s: "Triggered By" },
      pt: { s: "Disparado Por", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SimulationSessionTriggeredByLabel() {
    return this.SimulationSessionTriggeredByLabelByLang();
  }

  get SimulationSessionTriggeredByDescription() {
    return {
      es: { s: "Usuario que disparó la simulación" },
      en: { s: "User who triggered the simulation" },
      pt: { s: "Usuário que disparou a simulação" },
    }[this.language].s;
  }

  // SnapshotOverride

  get SnapshotOverrideLabels() {
    return {
      es: { s: "Corrección de Trámite", p: "Correcciones de Trámite", g: "F" },
      en: { s: "Procedure Override", p: "Procedure Overrides", g: undefined },
      pt: { s: "Correção de Trâmite", p: "Correções de Trâmite", g: "F" },
    }[this.language];
  }

  get SnapshotOverrideSingular() {
    return this.SnapshotOverrideLabels.s;
  }

  get SnapshotOverridePlural() {
    return this.SnapshotOverrideLabels.p;
  }

  get SnapshotOverrideGender() {
    return this.SnapshotOverrideLabels.g;
  }

  get groupSnapshotOverrideLabel() {
    return {
      es: { s: "Corrección de Trámite", p: "Correcciones de Trámite", g: "F" },
      en: { s: "Procedure Override", p: "Procedure Overrides" },
      pt: { s: "Correção de Trâmite", p: "Correções de Trâmite", g: "F" },
    }[this.language].s;
  }

  SnapshotOverrideRelatedEntityTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Entidad", p: "M" },
      en: { s: "Entity Type" },
      pt: { s: "Tipo de Entidade", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SnapshotOverrideRelatedEntityTypeLabel() {
    return this.SnapshotOverrideRelatedEntityTypeLabelByLang();
  }

  get SnapshotOverrideRelatedEntityTypeDescription() {
    return {
      es: { s: "Tipo de la entidad snapshot corregida (ej. SignedEdition)." },
      en: { s: "Type of the corrected snapshot entity (e.g. SignedEdition)." },
      pt: { s: "Tipo da entidade snapshot corrigida (ex. SignedEdition)." },
    }[this.language].s;
  }

  SnapshotOverrideRelatedEntityIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Entidad", p: "M" },
      en: { s: "Entity ID" },
      pt: { s: "ID de Entidade", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SnapshotOverrideRelatedEntityIdLabel() {
    return this.SnapshotOverrideRelatedEntityIdLabelByLang();
  }

  get SnapshotOverrideRelatedEntityIdDescription() {
    return {
      es: { s: "ID de la entidad snapshot corregida." },
      en: { s: "ID of the corrected snapshot entity." },
      pt: { s: "ID da entidade snapshot corrigida." },
    }[this.language].s;
  }

  SnapshotOverridePathLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Campo (path)", p: "M" },
      en: { s: "Field (path)" },
      pt: { s: "Campo (path)", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SnapshotOverridePathLabel() {
    return this.SnapshotOverridePathLabelByLang();
  }

  get SnapshotOverridePathDescription() {
    return {
      es: {
        s: "Path sobre data_ del campo corregido/agregado (ej. works[2].authors[0].taxId). El set crea claves inexistentes y agrega elementos de array por índice.",
      },
      en: {
        s: "Path over data_ of the corrected/added field (e.g. works[2].authors[0].taxId). Set creates missing keys and appends array elements by index.",
      },
      pt: {
        s: "Path sobre data_ do campo corrigido/adicionado (ex. works[2].authors[0].taxId). O set cria chaves inexistentes e adiciona elementos de array por índice.",
      },
    }[this.language].s;
  }

  SnapshotOverrideValueLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Valor Corregido", p: "M" },
      en: { s: "Corrected Value" },
      pt: { s: "Valor Corrigido", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SnapshotOverrideValueLabel() {
    return this.SnapshotOverrideValueLabelByLang();
  }

  get SnapshotOverrideValueDescription() {
    return {
      es: {
        s: "Valor que reemplaza/agrega en el path (escalar u objeto completo).",
      },
      en: {
        s: "Value that replaces/adds at the path (scalar or full object).",
      },
      pt: {
        s: "Valor que substitui/adiciona no path (escalar ou objeto completo).",
      },
    }[this.language].s;
  }

  SnapshotOverrideNoteLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Motivo", p: "M" },
      en: { s: "Reason" },
      pt: { s: "Motivo", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SnapshotOverrideNoteLabel() {
    return this.SnapshotOverrideNoteLabelByLang();
  }

  get SnapshotOverrideNoteDescription() {
    return {
      es: {
        s: "Por qué se corrige (ej. 'CUIT rechazado por DNDA'). Obligatorio: es la auditoría humana de la corrección.",
      },
      en: {
        s: "Why it is corrected (e.g. 'CUIT rejected by DNDA'). Required: the human audit trail of the correction.",
      },
      pt: {
        s: "Por que é corrigido (ex. 'CUIT rejeitado pela DNDA'). Obrigatório: a trilha de auditoria humana da correção.",
      },
    }[this.language].s;
  }

  SnapshotOverrideActiveLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Activa", p: "F" },
      en: { s: "Active" },
      pt: { s: "Ativa", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get SnapshotOverrideActiveLabel() {
    return this.SnapshotOverrideActiveLabelByLang();
  }

  get SnapshotOverrideActiveDescription() {
    return {
      es: {
        s: "Si está activa se aplica al armar los trámites. Desactivar en lugar de borrar preserva el historial.",
      },
      en: {
        s: "When active it is applied when building procedures. Deactivating instead of deleting preserves history.",
      },
      pt: {
        s: "Quando ativa é aplicada ao montar os trâmites. Desativar em vez de excluir preserva o histórico.",
      },
    }[this.language].s;
  }

  // BotRun

  get BotRunLabels() {
    return {
      es: { s: "Ejecución de Bot", p: "Ejecuciones de Bot", g: "F" },
      en: { s: "Bot Run", p: "Bot Runs", g: undefined },
      pt: { s: "Execução de Bot", p: "Execuções de Bot", g: "F" },
    }[this.language];
  }

  get BotRunSingular() {
    return this.BotRunLabels.s;
  }

  get BotRunPlural() {
    return this.BotRunLabels.p;
  }

  get BotRunGender() {
    return this.BotRunLabels.g;
  }

  get groupBotRunLabel() {
    return {
      es: { s: "Ejecución de Bot", p: "Ejecuciones de Bot", g: "F" },
      en: { s: "Bot Run", p: "Bot Runs" },
      pt: { s: "Execução de Bot", p: "Execuções de Bot", g: "F" },
    }[this.language].s;
  }

  BotRunBotTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Tipo de Bot",
        p: "Identificador del tipo de bot ejecutado",
        g: "M",
      },
      en: { s: "Bot Type", p: "Identifier of the bot type executed" },
      pt: {
        s: "Tipo de Bot",
        p: "Identificador do tipo de bot executado",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunBotTypeLabel() {
    return this.BotRunBotTypeLabelByLang();
  }

  get BotRunBotTypeDescription() {
    return {
      es: {
        s: "Identificador del tipo de bot ejecutado (ej. dnda-registration, dnda-check).",
      },
      en: {
        s: "Identifier of the bot type executed (e.g., dnda-registration, dnda-check).",
      },
      pt: {
        s: "Identificador do tipo de bot executado (ex: dnda-registration, dnda-check).",
      },
    }[this.language].s;
  }

  BotRunRunNumberLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Número de Ejecución",
        p: "Número secuencial de la ejecución",
        g: "M",
      },
      en: { s: "Run Number", p: "Sequential run number" },
      pt: {
        s: "Número de Execução",
        p: "Número sequencial da execução",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunRunNumberLabel() {
    return this.BotRunRunNumberLabelByLang();
  }

  get BotRunRunNumberDescription() {
    return {
      es: { s: "Número secuencial único asignado a esta ejecución del bot." },
      en: { s: "Unique sequential number assigned to this bot execution." },
      pt: { s: "Número sequencial único atribuído a esta execução do bot." },
    }[this.language].s;
  }

  BotRunStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "Estado actual de la ejecución", g: "M" },
      en: { s: "Status", p: "Current execution status" },
      pt: { s: "Estado", p: "Estado atual da execução", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunStatusLabel() {
    return this.BotRunStatusLabelByLang();
  }

  get BotRunStatusDescription() {
    return {
      es: {
        s: "Estado actual de la ejecución: pending, running, completed, failed, cancelled.",
      },
      en: {
        s: "Current execution status: pending, running, completed, failed, cancelled.",
      },
      pt: {
        s: "Estado atual da execução: pending, running, completed, failed, cancelled.",
      },
    }[this.language].s;
  }

  BotRunCurrentPhaseLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Fase Actual", p: "Nombre de la fase en ejecución", g: "F" },
      en: { s: "Current Phase", p: "Name of the running phase" },
      pt: { s: "Fase Atual", p: "Nome da fase em execução", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get BotRunCurrentPhaseLabel() {
    return this.BotRunCurrentPhaseLabelByLang();
  }

  get BotRunCurrentPhaseDescription() {
    return {
      es: { s: "Nombre de la fase que se está ejecutando actualmente." },
      en: { s: "Name of the phase currently being executed." },
      pt: { s: "Nome da fase que está sendo executada atualmente." },
    }[this.language].s;
  }

  BotRunStartedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Inicio", p: "Fecha y hora de inicio de la ejecución", g: "M" },
      en: { s: "Started At", p: "Execution start date and time" },
      pt: { s: "Início", p: "Data e hora de início da execução", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunStartedAtLabel() {
    return this.BotRunStartedAtLabelByLang();
  }

  get BotRunStartedAtDescription() {
    return {
      es: { s: "Fecha y hora en que comenzó la ejecución del bot." },
      en: { s: "Date and time when bot execution started." },
      pt: { s: "Data e hora em que a execução do bot começou." },
    }[this.language].s;
  }

  BotRunCompletedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Fin",
        p: "Fecha y hora de finalización de la ejecución",
        g: "M",
      },
      en: { s: "Completed At", p: "Execution completion date and time" },
      pt: { s: "Fim", p: "Data e hora de conclusão da execução" },
    }[lang ? lang : this.language].s;
  }

  get BotRunCompletedAtLabel() {
    return this.BotRunCompletedAtLabelByLang();
  }

  get BotRunCompletedAtDescription() {
    return {
      es: { s: "Fecha y hora en que finalizó la ejecución del bot." },
      en: { s: "Date and time when bot execution finished." },
      pt: { s: "Data e hora em que a execução do bot terminou." },
    }[this.language].s;
  }

  BotRunDurationLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Duración", p: "Duración total en milisegundos", g: "F" },
      en: { s: "Duration", p: "Total duration in milliseconds" },
      pt: { s: "Duração", p: "Duração total em milissegundos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get BotRunDurationLabel() {
    return this.BotRunDurationLabelByLang();
  }

  get BotRunDurationDescription() {
    return {
      es: { s: "Duración total de la ejecución en milisegundos." },
      en: { s: "Total execution duration in milliseconds." },
      pt: { s: "Duração total da execução em milissegundos." },
    }[this.language].s;
  }

  BotRunParamsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Parámetros", p: "Parámetros de entrada del bot", g: "M" },
      en: { s: "Parameters", p: "Bot input parameters" },
      pt: { s: "Parâmetros", p: "Parâmetros de entrada do bot", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunParamsLabel() {
    return this.BotRunParamsLabelByLang();
  }

  get BotRunParamsDescription() {
    return {
      es: {
        s: "Parámetros de entrada proporcionados al bot para esta ejecución.",
      },
      en: { s: "Input parameters provided to the bot for this execution." },
      pt: { s: "Parâmetros de entrada fornecidos ao bot para esta execução." },
    }[this.language].s;
  }

  BotRunResultLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Resultado", p: "Resultado de la ejecución exitosa", g: "M" },
      en: { s: "Result", p: "Successful execution result" },
      pt: { s: "Resultado", p: "Resultado da execução bem-sucedida", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunResultLabel() {
    return this.BotRunResultLabelByLang();
  }

  get BotRunResultDescription() {
    return {
      es: { s: "Resultado devuelto por el bot si la ejecución fue exitosa." },
      en: { s: "Result returned by the bot if execution was successful." },
      pt: { s: "Resultado retornado pelo bot se a execução foi bem-sucedida." },
    }[this.language].s;
  }

  BotRunErrorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Error", p: "Detalle del error si la ejecución falló", g: "M" },
      en: { s: "Error", p: "Error detail if execution failed" },
      pt: { s: "Erro", p: "Detalhe do erro se a execução falhou", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunErrorLabel() {
    return this.BotRunErrorLabelByLang();
  }

  get BotRunErrorDescription() {
    return {
      es: {
        s: "Detalle del error si la ejecución falló, incluyendo mensaje y stack trace.",
      },
      en: {
        s: "Error detail if execution failed, including message and stack trace.",
      },
      pt: {
        s: "Detalhe do erro se a execução falhou, incluindo mensagem e stack trace.",
      },
    }[this.language].s;
  }

  BotRunCorrelationIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "ID de Correlación",
        p: "Identificador único para rastreo",
        g: "M",
      },
      en: { s: "Correlation ID", p: "Unique identifier for tracing" },
      pt: {
        s: "ID de Correlação",
        p: "Identificador único para rastreamento",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunCorrelationIdLabel() {
    return this.BotRunCorrelationIdLabelByLang();
  }

  get BotRunCorrelationIdDescription() {
    return {
      es: {
        s: "UUID único para rastrear esta ejecución a través de logs y sistemas.",
      },
      en: { s: "Unique UUID to trace this execution across logs and systems." },
      pt: {
        s: "UUID único para rastrear esta execução através de logs e sistemas.",
      },
    }[this.language].s;
  }

  BotRunTotalPhasesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Total de Fases", p: "Número total de fases del bot", g: "M" },
      en: { s: "Total Phases", p: "Total number of bot phases" },
      pt: { s: "Total de Fases", p: "Número total de fases do bot", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunTotalPhasesLabel() {
    return this.BotRunTotalPhasesLabelByLang();
  }

  get BotRunTotalPhasesDescription() {
    return {
      es: { s: "Número total de fases que componen esta ejecución del bot." },
      en: { s: "Total number of phases that make up this bot execution." },
      pt: { s: "Número total de fases que compõem esta execução do bot." },
    }[this.language].s;
  }

  BotRunCompletedPhasesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fases Completadas", p: "Número de fases completadas", g: "M" },
      en: { s: "Completed Phases", p: "Number of completed phases" },
      pt: { s: "Fases Concluídas", p: "Número de fases concluídas", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunCompletedPhasesLabel() {
    return this.BotRunCompletedPhasesLabelByLang();
  }

  get BotRunCompletedPhasesDescription() {
    return {
      es: { s: "Número de fases que han sido completadas exitosamente." },
      en: { s: "Number of phases that have been successfully completed." },
      pt: { s: "Número de fases que foram concluídas com sucesso." },
    }[this.language].s;
  }

  BotRunRequiresHumanInterventionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Requiere Intervención",
        p: "Indica si se necesita intervención humana",
        g: "F",
      },
      en: {
        s: "Requires Intervention",
        p: "Indicates if human intervention is needed",
      },
      pt: {
        s: "Requer Intervenção",
        p: "Indica se é necessária intervenção humana",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunRequiresHumanInterventionLabel() {
    return this.BotRunRequiresHumanInterventionLabelByLang();
  }

  get BotRunRequiresHumanInterventionDescription() {
    return {
      es: {
        s: "Indica si la ejecución está pausada esperando intervención humana.",
      },
      en: {
        s: "Indicates if execution is paused waiting for human intervention.",
      },
      pt: {
        s: "Indica se a execução está pausada aguardando intervenção humana.",
      },
    }[this.language].s;
  }

  BotRunInterventionReasonLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Razón de Intervención",
        p: "Motivo por el cual se requiere intervención",
        g: "F",
      },
      en: {
        s: "Intervention Reason",
        p: "Reason why intervention is required",
      },
      pt: {
        s: "Razão da Intervenção",
        p: "Motivo pelo qual é necessária intervenção",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunInterventionReasonLabel() {
    return this.BotRunInterventionReasonLabelByLang();
  }

  get BotRunInterventionReasonDescription() {
    return {
      es: {
        s: "Descripción del motivo por el cual se requiere intervención manual.",
      },
      en: { s: "Description of the reason manual intervention is required." },
      pt: {
        s: "Descrição do motivo pelo qual é necessária intervenção manual.",
      },
    }[this.language].s;
  }

  BotRunLogsPathLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Ruta de Logs",
        p: "Ruta al archivo de logs de la ejecución",
        g: "F",
      },
      en: { s: "Logs Path", p: "Path to execution log file" },
      pt: {
        s: "Caminho dos Logs",
        p: "Caminho para o arquivo de logs da execução",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunLogsPathLabel() {
    return this.BotRunLogsPathLabelByLang();
  }

  get BotRunLogsPathDescription() {
    return {
      es: {
        s: "Ruta al archivo de logs detallados de la ejecución en el servidor.",
      },
      en: { s: "Path to the detailed execution log file on the server." },
      pt: {
        s: "Caminho para o arquivo de logs detalhados da execução no servidor.",
      },
    }[this.language].s;
  }

  BotRunWorkerEventLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Evento de Worker",
        p: "Evento del worker que ejecutó el bot",
        g: "M",
      },
      en: { s: "Worker Event", p: "Worker event that executed the bot" },
      pt: {
        s: "Evento de Worker",
        p: "Evento do worker que executou o bot",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunWorkerEventLabel() {
    return this.BotRunWorkerEventLabelByLang();
  }

  get BotRunWorkerEventDescription() {
    return {
      es: {
        s: "Referencia al evento del worker que procesó esta ejecución del bot.",
      },
      en: {
        s: "Reference to the worker event that processed this bot execution.",
      },
      pt: {
        s: "Referência ao evento do worker que processou esta execução do bot.",
      },
    }[this.language].s;
  }

  BotRunTriggeredByLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Iniciado por", p: "Usuario que inició la ejecución", g: "M" },
      en: { s: "Triggered By", p: "User who started the execution" },
      pt: { s: "Iniciado por", p: "Usuário que iniciou a execução", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunTriggeredByLabel() {
    return this.BotRunTriggeredByLabelByLang();
  }

  get BotRunTriggeredByDescription() {
    return {
      es: { s: "Usuario que solicitó la ejecución del bot." },
      en: { s: "User who requested the bot execution." },
      pt: { s: "Usuário que solicitou a execução do bot." },
    }[this.language].s;
  }

  BotRunRelatedEntityTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Entidad Relacionada",
        p: "Tipo de la entidad relacionada con esta ejecución",
        g: "M",
      },
      en: {
        s: "Related Entity Type",
        p: "Type of entity related to this execution",
      },
      pt: {
        s: "Tipo de Entidade Relacionada",
        p: "Tipo da entidade relacionada com esta execução",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunRelatedEntityTypeLabel() {
    return this.BotRunRelatedEntityTypeLabelByLang();
  }

  get BotRunRelatedEntityTypeDescription() {
    return {
      es: {
        s: "Tipo de la entidad del dominio relacionada con esta ejecución (ej. Edition).",
      },
      en: {
        s: "Type of the domain entity related to this execution (e.g., Edition).",
      },
      pt: {
        s: "Tipo da entidade do domínio relacionada com esta execução (ex: Edition).",
      },
    }[this.language].s;
  }

  BotRunRelatedEntityIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "ID de Entidad Relacionada",
        p: "ID de la entidad relacionada con esta ejecución",
        g: "M",
      },
      en: {
        s: "Related Entity ID",
        p: "ID of entity related to this execution",
      },
      pt: {
        s: "ID de Entidade Relacionada",
        p: "ID da entidade relacionada com esta execução",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunRelatedEntityIdLabel() {
    return this.BotRunRelatedEntityIdLabelByLang();
  }

  get BotRunRelatedEntityIdDescription() {
    return {
      es: { s: "ID de la entidad del dominio relacionada con esta ejecución." },
      en: { s: "ID of the domain entity related to this execution." },
      pt: { s: "ID da entidade do domínio relacionada com esta execução." },
    }[this.language].s;
  }

  BotRunRelatedEntityLabelLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Entidad Relacionada",
        p: "Nombre legible de la entidad relacionada",
        g: "F",
      },
      en: {
        s: "Related Entity",
        p: "Human-readable name of the related entity",
      },
      pt: {
        s: "Entidade Relacionada",
        p: "Nome legível da entidade relacionada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunRelatedEntityLabelLabel() {
    return this.BotRunRelatedEntityLabelLabelByLang();
  }

  get BotRunRelatedEntityLabelDescription() {
    return {
      es: {
        s: "Nombre legible (displayId) de la entidad relacionada, snapshoteado al lanzar el bot. Permite identificar la ejecución en listados (ej. 'Album Los de Arriba').",
      },
      en: {
        s: "Human-readable name (displayId) of the related entity, snapshotted when the bot is launched. Identifies the run in lists.",
      },
      pt: {
        s: "Nome legível (displayId) da entidade relacionada, capturado ao lançar o bot. Identifica a execução em listagens.",
      },
    }[this.language].s;
  }

  BotRunCancellationRequestedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Cancelación Solicitada",
        p: "Fecha y hora de solicitud de cancelación",
        g: "F",
      },
      en: {
        s: "Cancellation Requested",
        p: "Cancellation request date and time",
      },
      pt: {
        s: "Cancelamento Solicitado",
        p: "Data e hora da solicitação de cancelamento",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunCancellationRequestedAtLabel() {
    return this.BotRunCancellationRequestedAtLabelByLang();
  }

  get BotRunCancellationRequestedAtDescription() {
    return {
      es: { s: "Fecha y hora en que se solicitó cancelar la ejecución." },
      en: { s: "Date and time when execution cancellation was requested." },
      pt: {
        s: "Data e hora em que o cancelamento da execução foi solicitado.",
      },
    }[this.language].s;
  }

  BotRunCancellationRequestedByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Cancelado por",
        p: "Usuario que solicitó la cancelación",
        g: "M",
      },
      en: { s: "Cancelled By", p: "User who requested cancellation" },
      pt: {
        s: "Cancelado por",
        p: "Usuário que solicitou o cancelamento",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotRunCancellationRequestedByLabel() {
    return this.BotRunCancellationRequestedByLabelByLang();
  }

  get BotRunCancellationRequestedByDescription() {
    return {
      es: { s: "Usuario que solicitó la cancelación de la ejecución." },
      en: { s: "User who requested execution cancellation." },
      pt: { s: "Usuário que solicitou o cancelamento da execução." },
    }[this.language].s;
  }

  BotRunCancellationReasonLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Motivo de Cancelación",
        p: "Razón por la cual se canceló",
        g: "M",
      },
      en: { s: "Cancellation Reason", p: "Reason for cancellation" },
      pt: { s: "Motivo do Cancelamento", p: "Razão do cancelamento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunCancellationReasonLabel() {
    return this.BotRunCancellationReasonLabelByLang();
  }

  get BotRunCancellationReasonDescription() {
    return {
      es: { s: "Motivo proporcionado para la cancelación de la ejecución." },
      en: { s: "Reason provided for execution cancellation." },
      pt: { s: "Motivo fornecido para o cancelamento da execução." },
    }[this.language].s;
  }

  BotRunPhasesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Fases", p: "Fases de ejecución del bot", g: "M" },
      en: { s: "Phases", p: "Bot execution phases" },
      pt: { s: "Fases", p: "Fases de execução do bot", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunPhasesLabel() {
    return this.BotRunPhasesLabelByLang();
  }

  get BotRunPhasesDescription() {
    return {
      es: { s: "Lista de fases de esta ejecución del bot." },
      en: { s: "List of phases for this bot execution." },
      pt: { s: "Lista de fases desta execução do bot." },
    }[this.language].s;
  }

  BotRunErrorsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Errores", p: "Errores ocurridos durante la ejecución", g: "M" },
      en: { s: "Errors", p: "Errors occurred during execution" },
      pt: { s: "Erros", p: "Erros ocorridos durante a execução", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotRunErrorsLabel() {
    return this.BotRunErrorsLabelByLang();
  }

  get BotRunErrorsDescription() {
    return {
      es: { s: "Lista de errores registrados durante la ejecución." },
      en: { s: "List of errors recorded during execution." },
      pt: { s: "Lista de erros registrados durante a execução." },
    }[this.language].s;
  }

  // BotPhase

  get BotPhaseLabels() {
    return {
      es: { s: "Fase de Bot", p: "Fases de Bot", g: "F" },
      en: { s: "Bot Phase", p: "Bot Phases", g: undefined },
      pt: { s: "Fase de Bot", p: "Fases de Bot", g: "F" },
    }[this.language];
  }

  get BotPhaseSingular() {
    return this.BotPhaseLabels.s;
  }

  get BotPhasePlural() {
    return this.BotPhaseLabels.p;
  }

  get BotPhaseGender() {
    return this.BotPhaseLabels.g;
  }

  get groupBotPhaseLabel() {
    return {
      es: { s: "Fase de Bot", p: "Fases de Bot", g: "F" },
      en: { s: "Bot Phase", p: "Bot Phases" },
      pt: { s: "Fase de Bot", p: "Fases de Bot", g: "F" },
    }[this.language].s;
  }

  BotPhasePhaseNumberLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Número de Fase", p: "Número de orden de la fase", g: "M" },
      en: { s: "Phase Number", p: "Phase order number" },
      pt: { s: "Número da Fase", p: "Número de ordem da fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhasePhaseNumberLabel() {
    return this.BotPhasePhaseNumberLabelByLang();
  }

  get BotPhasePhaseNumberDescription() {
    return {
      es: {
        s: "Número secuencial que indica el orden de la fase en la ejecución.",
      },
      en: { s: "Sequential number indicating the phase order in execution." },
      pt: { s: "Número sequencial que indica a ordem da fase na execução." },
    }[this.language].s;
  }

  BotPhasePhaseIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "ID de Fase", p: "Identificador único de la fase", g: "M" },
      en: { s: "Phase ID", p: "Unique phase identifier" },
      pt: { s: "ID da Fase", p: "Identificador único da fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhasePhaseIdLabel() {
    return this.BotPhasePhaseIdLabelByLang();
  }

  get BotPhasePhaseIdDescription() {
    return {
      es: {
        s: "Identificador único de la fase definido en el código del bot.",
      },
      en: { s: "Unique phase identifier defined in the bot code." },
      pt: { s: "Identificador único da fase definido no código do bot." },
    }[this.language].s;
  }

  BotPhasePhaseNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre de Fase", p: "Nombre descriptivo de la fase", g: "M" },
      en: { s: "Phase Name", p: "Descriptive phase name" },
      pt: { s: "Nome da Fase", p: "Nome descritivo da fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhasePhaseNameLabel() {
    return this.BotPhasePhaseNameLabelByLang();
  }

  get BotPhasePhaseNameDescription() {
    return {
      es: { s: "Nombre legible de la fase para visualización." },
      en: { s: "Human-readable phase name for display." },
      pt: { s: "Nome legível da fase para exibição." },
    }[this.language].s;
  }

  BotPhaseStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "Estado actual de la fase", g: "M" },
      en: { s: "Status", p: "Current phase status" },
      pt: { s: "Estado", p: "Estado atual da fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseStatusLabel() {
    return this.BotPhaseStatusLabelByLang();
  }

  get BotPhaseStatusDescription() {
    return {
      es: {
        s: "Estado actual de la fase: pending, running, completed, failed, skipped.",
      },
      en: {
        s: "Current phase status: pending, running, completed, failed, skipped.",
      },
      pt: {
        s: "Estado atual da fase: pending, running, completed, failed, skipped.",
      },
    }[this.language].s;
  }

  BotPhaseStepsTotalLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Total de Pasos",
        p: "Número total de pasos en la fase",
        g: "M",
      },
      en: { s: "Total Steps", p: "Total number of steps in the phase" },
      pt: { s: "Total de Passos", p: "Número total de passos na fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseStepsTotalLabel() {
    return this.BotPhaseStepsTotalLabelByLang();
  }

  get BotPhaseStepsTotalDescription() {
    return {
      es: { s: "Número total de pasos que componen esta fase." },
      en: { s: "Total number of steps that make up this phase." },
      pt: { s: "Número total de passos que compõem esta fase." },
    }[this.language].s;
  }

  BotPhaseStepsCompletedLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Pasos Completados", p: "Número de pasos completados", g: "M" },
      en: { s: "Completed Steps", p: "Number of completed steps" },
      pt: { s: "Passos Concluídos", p: "Número de passos concluídos", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseStepsCompletedLabel() {
    return this.BotPhaseStepsCompletedLabelByLang();
  }

  get BotPhaseStepsCompletedDescription() {
    return {
      es: { s: "Número de pasos que han sido completados exitosamente." },
      en: { s: "Number of steps that have been successfully completed." },
      pt: { s: "Número de passos que foram concluídos com sucesso." },
    }[this.language].s;
  }

  BotPhaseStartedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Inicio", p: "Fecha y hora de inicio de la fase", g: "M" },
      en: { s: "Started At", p: "Phase start date and time" },
      pt: { s: "Início", p: "Data e hora de início da fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseStartedAtLabel() {
    return this.BotPhaseStartedAtLabelByLang();
  }

  get BotPhaseStartedAtDescription() {
    return {
      es: { s: "Fecha y hora en que comenzó la ejecución de la fase." },
      en: { s: "Date and time when phase execution started." },
      pt: { s: "Data e hora em que a execução da fase começou." },
    }[this.language].s;
  }

  BotPhaseCompletedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Fin", p: "Fecha y hora de finalización de la fase", g: "M" },
      en: { s: "Completed At", p: "Phase completion date and time" },
      pt: { s: "Fim", p: "Data e hora de conclusão da fase" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseCompletedAtLabel() {
    return this.BotPhaseCompletedAtLabelByLang();
  }

  get BotPhaseCompletedAtDescription() {
    return {
      es: { s: "Fecha y hora en que finalizó la ejecución de la fase." },
      en: { s: "Date and time when phase execution finished." },
      pt: { s: "Data e hora em que a execução da fase terminou." },
    }[this.language].s;
  }

  BotPhaseDurationLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Duración", p: "Duración de la fase en milisegundos", g: "F" },
      en: { s: "Duration", p: "Phase duration in milliseconds" },
      pt: { s: "Duração", p: "Duração da fase em milissegundos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseDurationLabel() {
    return this.BotPhaseDurationLabelByLang();
  }

  get BotPhaseDurationDescription() {
    return {
      es: { s: "Duración total de la fase en milisegundos." },
      en: { s: "Total phase duration in milliseconds." },
      pt: { s: "Duração total da fase em milissegundos." },
    }[this.language].s;
  }

  BotPhaseErrorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Error", p: "Detalle del error si la fase falló", g: "M" },
      en: { s: "Error", p: "Error detail if phase failed" },
      pt: { s: "Erro", p: "Detalhe do erro se a fase falhou", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseErrorLabel() {
    return this.BotPhaseErrorLabelByLang();
  }

  get BotPhaseErrorDescription() {
    return {
      es: { s: "Detalle del error si la fase falló, con mensaje y contexto." },
      en: { s: "Error detail if phase failed, with message and context." },
      pt: { s: "Detalhe do erro se a fase falhou, com mensagem e contexto." },
    }[this.language].s;
  }

  BotPhaseStepsLogPathLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Ruta de Log de Pasos",
        p: "Ruta al archivo de log de pasos",
        g: "F",
      },
      en: { s: "Steps Log Path", p: "Path to steps log file" },
      pt: {
        s: "Caminho do Log de Passos",
        p: "Caminho para o arquivo de log de passos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseStepsLogPathLabel() {
    return this.BotPhaseStepsLogPathLabelByLang();
  }

  get BotPhaseStepsLogPathDescription() {
    return {
      es: { s: "Ruta al archivo de log con el detalle de cada paso." },
      en: { s: "Path to log file with detail of each step." },
      pt: { s: "Caminho para o arquivo de log com o detalhe de cada passo." },
    }[this.language].s;
  }

  BotPhaseScreenshotLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Captura de Pantalla",
        p: "Captura de pantalla de la fase",
        g: "F",
      },
      en: { s: "Screenshot", p: "Phase screenshot" },
      pt: { s: "Captura de Tela", p: "Captura de tela da fase", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseScreenshotLabel() {
    return this.BotPhaseScreenshotLabelByLang();
  }

  get BotPhaseScreenshotDescription() {
    return {
      es: { s: "Captura de pantalla tomada al finalizar o fallar la fase." },
      en: { s: "Screenshot taken when phase finished or failed." },
      pt: { s: "Captura de tela tirada quando a fase terminou ou falhou." },
    }[this.language].s;
  }

  BotPhaseStepsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Pasos", p: "Pasos de ejecución de la fase", g: "M" },
      en: { s: "Steps", p: "Phase execution steps" },
      pt: { s: "Passos", p: "Passos de execução da fase", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotPhaseStepsLabel() {
    return this.BotPhaseStepsLabelByLang();
  }

  get BotPhaseStepsDescription() {
    return {
      es: { s: "Lista de pasos individuales de esta fase." },
      en: { s: "List of individual steps in this phase." },
      pt: { s: "Lista de passos individuais desta fase." },
    }[this.language].s;
  }

  // BotStep

  get BotStepLabels() {
    return {
      es: { s: "Paso de Bot", p: "Pasos de Bot", g: "M" },
      en: { s: "Bot Step", p: "Bot Steps", g: undefined },
      pt: { s: "Passo de Bot", p: "Passos de Bot", g: "M" },
    }[this.language];
  }

  get BotStepSingular() {
    return this.BotStepLabels.s;
  }

  get BotStepPlural() {
    return this.BotStepLabels.p;
  }

  get BotStepGender() {
    return this.BotStepLabels.g;
  }

  get groupBotStepLabel() {
    return {
      es: { s: "Paso de Bot", p: "Pasos de Bot", g: "M" },
      en: { s: "Bot Step", p: "Bot Steps" },
      pt: { s: "Passo de Bot", p: "Passos de Bot", g: "M" },
    }[this.language].s;
  }

  BotStepStepNumberLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Número de Paso", p: "Número de orden del paso", g: "M" },
      en: { s: "Step Number", p: "Step order number" },
      pt: { s: "Número do Passo", p: "Número de ordem do passo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotStepStepNumberLabel() {
    return this.BotStepStepNumberLabelByLang();
  }

  get BotStepStepNumberDescription() {
    return {
      es: { s: "Número secuencial que indica el orden del paso en la fase." },
      en: { s: "Sequential number indicating the step order in the phase." },
      pt: { s: "Número sequencial que indica a ordem do passo na fase." },
    }[this.language].s;
  }

  BotStepStepIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "ID de Paso", p: "Identificador único del paso", g: "M" },
      en: { s: "Step ID", p: "Unique step identifier" },
      pt: { s: "ID do Passo", p: "Identificador único do passo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotStepStepIdLabel() {
    return this.BotStepStepIdLabelByLang();
  }

  get BotStepStepIdDescription() {
    return {
      es: { s: "Identificador único del paso definido en el código del bot." },
      en: { s: "Unique step identifier defined in the bot code." },
      pt: { s: "Identificador único do passo definido no código do bot." },
    }[this.language].s;
  }

  BotStepStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "Estado actual del paso", g: "M" },
      en: { s: "Status", p: "Current step status" },
      pt: { s: "Estado", p: "Estado atual do passo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotStepStatusLabel() {
    return this.BotStepStatusLabelByLang();
  }

  get BotStepStatusDescription() {
    return {
      es: { s: "Estado actual del paso: pending, running, completed, failed." },
      en: { s: "Current step status: pending, running, completed, failed." },
      pt: { s: "Estado atual do passo: pending, running, completed, failed." },
    }[this.language].s;
  }

  BotStepStartedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Inicio", p: "Fecha y hora de inicio del paso", g: "M" },
      en: { s: "Started At", p: "Step start date and time" },
      pt: { s: "Início", p: "Data e hora de início do passo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotStepStartedAtLabel() {
    return this.BotStepStartedAtLabelByLang();
  }

  get BotStepStartedAtDescription() {
    return {
      es: { s: "Fecha y hora en que comenzó la ejecución del paso." },
      en: { s: "Date and time when step execution started." },
      pt: { s: "Data e hora em que a execução do passo começou." },
    }[this.language].s;
  }

  BotStepCompletedAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Fin", p: "Fecha y hora de finalización del paso", g: "M" },
      en: { s: "Completed At", p: "Step completion date and time" },
      pt: { s: "Fim", p: "Data e hora de conclusão do passo" },
    }[lang ? lang : this.language].s;
  }

  get BotStepCompletedAtLabel() {
    return this.BotStepCompletedAtLabelByLang();
  }

  get BotStepCompletedAtDescription() {
    return {
      es: { s: "Fecha y hora en que finalizó la ejecución del paso." },
      en: { s: "Date and time when step execution finished." },
      pt: { s: "Data e hora em que a execução do passo terminou." },
    }[this.language].s;
  }

  BotStepDurationLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Duración", p: "Duración del paso en milisegundos", g: "F" },
      en: { s: "Duration", p: "Step duration in milliseconds" },
      pt: { s: "Duração", p: "Duração do passo em milissegundos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get BotStepDurationLabel() {
    return this.BotStepDurationLabelByLang();
  }

  get BotStepDurationDescription() {
    return {
      es: { s: "Duración total del paso en milisegundos." },
      en: { s: "Total step duration in milliseconds." },
      pt: { s: "Duração total do passo em milissegundos." },
    }[this.language].s;
  }

  BotStepErrorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Error", p: "Detalle del error si el paso falló", g: "M" },
      en: { s: "Error", p: "Error detail if step failed" },
      pt: { s: "Erro", p: "Detalhe do erro se o passo falhou", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotStepErrorLabel() {
    return this.BotStepErrorLabelByLang();
  }

  get BotStepErrorDescription() {
    return {
      es: { s: "Detalle del error si el paso falló, con mensaje y contexto." },
      en: { s: "Error detail if step failed, with message and context." },
      pt: { s: "Detalhe do erro se o passo falhou, com mensagem e contexto." },
    }[this.language].s;
  }

  BotStepDownloadedFileLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Archivo Descargado",
        p: "Archivo descargado en este paso",
        g: "M",
      },
      en: { s: "Downloaded File", p: "File downloaded in this step" },
      pt: { s: "Arquivo Baixado", p: "Arquivo baixado neste passo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotStepDownloadedFileLabel() {
    return this.BotStepDownloadedFileLabelByLang();
  }

  get BotStepDownloadedFileDescription() {
    return {
      es: {
        s: "Archivo que fue descargado durante la ejecución de este paso.",
      },
      en: { s: "File that was downloaded during this step execution." },
      pt: { s: "Arquivo que foi baixado durante a execução deste passo." },
    }[this.language].s;
  }

  // BotError

  get BotErrorLabels() {
    return {
      es: { s: "Error de Bot", p: "Errores de Bot", g: "M" },
      en: { s: "Bot Error", p: "Bot Errors", g: undefined },
      pt: { s: "Erro de Bot", p: "Erros de Bot", g: "M" },
    }[this.language];
  }

  get BotErrorSingular() {
    return this.BotErrorLabels.s;
  }

  get BotErrorPlural() {
    return this.BotErrorLabels.p;
  }

  get BotErrorGender() {
    return this.BotErrorLabels.g;
  }

  get groupBotErrorLabel() {
    return {
      es: { s: "Error de Bot", p: "Errores de Bot", g: "M" },
      en: { s: "Bot Error", p: "Bot Errors" },
      pt: { s: "Erro de Bot", p: "Erros de Bot", g: "M" },
    }[this.language].s;
  }

  BotErrorErrorTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Error", p: "Clasificación del tipo de error", g: "M" },
      en: { s: "Error Type", p: "Error type classification" },
      pt: { s: "Tipo de Erro", p: "Classificação do tipo de erro", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotErrorErrorTypeLabel() {
    return this.BotErrorErrorTypeLabelByLang();
  }

  get BotErrorErrorTypeDescription() {
    return {
      es: {
        s: "Clasificación del tipo de error: timeout, navigation, element, validation, network, unknown.",
      },
      en: {
        s: "Error type classification: timeout, navigation, element, validation, network, unknown.",
      },
      pt: {
        s: "Classificação do tipo de erro: timeout, navigation, element, validation, network, unknown.",
      },
    }[this.language].s;
  }

  BotErrorSeverityLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Severidad", p: "Nivel de severidad del error", g: "F" },
      en: { s: "Severity", p: "Error severity level" },
      pt: { s: "Severidade", p: "Nível de severidade do erro", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get BotErrorSeverityLabel() {
    return this.BotErrorSeverityLabelByLang();
  }

  get BotErrorSeverityDescription() {
    return {
      es: {
        s: "Nivel de severidad del error: info, warning, error, critical.",
      },
      en: { s: "Error severity level: info, warning, error, critical." },
      pt: { s: "Nível de severidade do erro: info, warning, error, critical." },
    }[this.language].s;
  }

  BotErrorMessageLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Mensaje", p: "Mensaje descriptivo del error", g: "M" },
      en: { s: "Message", p: "Descriptive error message" },
      pt: { s: "Mensagem", p: "Mensagem descritiva do erro" },
    }[lang ? lang : this.language].s;
  }

  get BotErrorMessageLabel() {
    return this.BotErrorMessageLabelByLang();
  }

  get BotErrorMessageDescription() {
    return {
      es: { s: "Mensaje descriptivo del error legible para humanos." },
      en: { s: "Human-readable descriptive error message." },
      pt: { s: "Mensagem descritiva do erro legível para humanos." },
    }[this.language].s;
  }

  BotErrorStackTraceLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Stack Trace", p: "Traza de la pila de ejecución", g: "M" },
      en: { s: "Stack Trace", p: "Execution stack trace" },
      pt: { s: "Stack Trace", p: "Rastreamento da pilha de execução", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotErrorStackTraceLabel() {
    return this.BotErrorStackTraceLabelByLang();
  }

  get BotErrorStackTraceDescription() {
    return {
      es: { s: "Traza completa de la pila de ejecución para debugging." },
      en: { s: "Full execution stack trace for debugging." },
      pt: { s: "Rastreamento completo da pilha de execução para debugging." },
    }[this.language].s;
  }

  BotErrorContextLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Contexto", p: "Información contextual del error", g: "M" },
      en: { s: "Context", p: "Error contextual information" },
      pt: { s: "Contexto", p: "Informação contextual do erro", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotErrorContextLabel() {
    return this.BotErrorContextLabelByLang();
  }

  get BotErrorContextDescription() {
    return {
      es: {
        s: "Información contextual adicional al momento del error: URL, selector, fase, paso.",
      },
      en: {
        s: "Additional contextual information at the time of error: URL, selector, phase, step.",
      },
      pt: {
        s: "Informação contextual adicional no momento do erro: URL, seletor, fase, passo.",
      },
    }[this.language].s;
  }

  BotErrorRecoverableLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Recuperable", p: "Indica si el error es recuperable", g: "M" },
      en: { s: "Recoverable", p: "Indicates if the error is recoverable" },
      pt: { s: "Recuperável", p: "Indica se o erro é recuperável", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get BotErrorRecoverableLabel() {
    return this.BotErrorRecoverableLabelByLang();
  }

  get BotErrorRecoverableDescription() {
    return {
      es: {
        s: "Indica si el bot puede intentar recuperarse automáticamente del error.",
      },
      en: {
        s: "Indicates if the bot can attempt automatic recovery from the error.",
      },
      pt: { s: "Indica se o bot pode tentar recuperação automática do erro." },
    }[this.language].s;
  }

  BotErrorRequiresHumanInterventionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Requiere Intervención",
        p: "Indica si se necesita intervención humana",
        g: "F",
      },
      en: {
        s: "Requires Intervention",
        p: "Indicates if human intervention is needed",
      },
      pt: {
        s: "Requer Intervenção",
        p: "Indica se é necessária intervenção humana",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get BotErrorRequiresHumanInterventionLabel() {
    return this.BotErrorRequiresHumanInterventionLabelByLang();
  }

  get BotErrorRequiresHumanInterventionDescription() {
    return {
      es: {
        s: "Indica si el error requiere que un humano intervenga para resolverlo.",
      },
      en: {
        s: "Indicates if the error requires a human to intervene to resolve it.",
      },
      pt: {
        s: "Indica se o erro requer que um humano intervenha para resolvê-lo.",
      },
    }[this.language].s;
  }

  BotErrorScreenshotLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Captura de Pantalla",
        p: "Captura de pantalla del momento del error",
        g: "F",
      },
      en: { s: "Screenshot", p: "Screenshot at the moment of error" },
      pt: {
        s: "Captura de Tela",
        p: "Captura de tela do momento do erro",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get BotErrorScreenshotLabel() {
    return this.BotErrorScreenshotLabelByLang();
  }

  get BotErrorScreenshotDescription() {
    return {
      es: {
        s: "Captura de pantalla del navegador en el momento en que ocurrió el error.",
      },
      en: { s: "Browser screenshot at the moment the error occurred." },
      pt: {
        s: "Captura de tela do navegador no momento em que o erro ocorreu.",
      },
    }[this.language].s;
  }

  BotErrorDomSnapshotLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Snapshot del DOM",
        p: "Captura del estado del DOM al momento del error",
        g: "M",
      },
      en: { s: "DOM Snapshot", p: "DOM state capture at the moment of error" },
      pt: {
        s: "Snapshot do DOM",
        p: "Captura do estado do DOM no momento do erro",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get BotErrorDomSnapshotLabel() {
    return this.BotErrorDomSnapshotLabelByLang();
  }

  get BotErrorDomSnapshotDescription() {
    return {
      es: {
        s: "Archivo HTML con el estado completo del DOM para análisis post-mortem.",
      },
      en: {
        s: "HTML file with the complete DOM state for post-mortem analysis.",
      },
      pt: {
        s: "Arquivo HTML com o estado completo do DOM para análise post-mortem.",
      },
    }[this.language].s;
  }

  // SatelliteMachine

  get SatelliteMachineLabels() {
    return {
      es: { s: "Máquina Satélite", p: "Máquinas Satélite", g: "F" },
      en: { s: "Satellite Machine", p: "Satellite Machines", g: undefined },
      pt: { s: "Máquina Satélite", p: "Máquinas Satélite", g: "F" },
    }[this.language];
  }

  get SatelliteMachineSingular() {
    return this.SatelliteMachineLabels.s;
  }

  get SatelliteMachinePlural() {
    return this.SatelliteMachineLabels.p;
  }

  get SatelliteMachineGender() {
    return this.SatelliteMachineLabels.g;
  }

  get groupSatelliteMachineLabel() {
    return {
      es: { s: "Máquina Satélite", p: "Máquinas Satélite", g: "F" },
      en: { s: "Satellite Machine", p: "Satellite Machines" },
      pt: { s: "Máquina Satélite", p: "Máquinas Satélite", g: "F" },
    }[this.language].s;
  }

  get groupSatelliteMachineIdentityLabel() {
    return {
      es: { s: "Identidad", p: "F" },
      en: { s: "Identity" },
      pt: { s: "Identidade", p: "F" },
    }[this.language].s;
  }

  get groupSatelliteMachineCertificateLabel() {
    return {
      es: { s: "Certificado", p: "M" },
      en: { s: "Certificate" },
      pt: { s: "Certificado", p: "M" },
    }[this.language].s;
  }

  get groupSatelliteMachineAuthenticationLabel() {
    return {
      es: { s: "Autenticación", p: "F" },
      en: { s: "Authentication" },
      pt: { s: "Autenticação", p: "F" },
    }[this.language].s;
  }

  get groupSatelliteMachineAuthorizationLabel() {
    return {
      es: { s: "Autorización", p: "F" },
      en: { s: "Authorization" },
      pt: { s: "Autorização", p: "F" },
    }[this.language].s;
  }

  get groupSatelliteMachineCapabilitiesLabel() {
    return {
      es: { s: "Capacidades", p: "M" },
      en: { s: "Capabilities" },
      pt: { s: "Capacidades", p: "M" },
    }[this.language].s;
  }

  get groupSatelliteMachineNetworkLabel() {
    return {
      es: { s: "Red", p: "F" },
      en: { s: "Network" },
      pt: { s: "Rede", p: "F" },
    }[this.language].s;
  }

  get groupSatelliteMachineMonitoringLabel() {
    return {
      es: { s: "Monitoreo", p: "M" },
      en: { s: "Monitoring" },
      pt: { s: "Monitoramento", p: "M" },
    }[this.language].s;
  }

  get groupSatelliteMachineRelationsLabel() {
    return {
      es: { s: "Relaciones", p: "M" },
      en: { s: "Relations" },
      pt: { s: "Relações", p: "M" },
    }[this.language].s;
  }

  SatelliteMachineNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Nombre",
        p: "Nombre descriptivo de la máquina satélite",
        g: "M",
      },
      en: { s: "Name", p: "Descriptive name of the satellite machine" },
      pt: { s: "Nome", p: "Nome descritivo da máquina satélite", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineNameLabel() {
    return this.SatelliteMachineNameLabelByLang();
  }

  get SatelliteMachineNameDescription() {
    return {
      es: {
        s: "Nombre único y descriptivo para identificar la máquina satélite.",
      },
      en: {
        s: "Unique and descriptive name to identify the satellite machine.",
      },
      pt: { s: "Nome único e descritivo para identificar a máquina satélite." },
    }[this.language].s;
  }

  SatelliteMachineMachineCodeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Código de Máquina",
        p: "Identificador único generado para la máquina",
        g: "M",
      },
      en: {
        s: "Machine Code",
        p: "Unique identifier generated for the machine",
      },
      pt: {
        s: "Código de Máquina",
        p: "Identificador único gerado para a máquina",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineMachineCodeLabel() {
    return this.SatelliteMachineMachineCodeLabelByLang();
  }

  get SatelliteMachineMachineCodeDescription() {
    return {
      es: {
        s: "Código único generado automáticamente para identificar la máquina en el sistema.",
      },
      en: {
        s: "Unique code automatically generated to identify the machine in the system.",
      },
      pt: {
        s: "Código único gerado automaticamente para identificar a máquina no sistema.",
      },
    }[this.language].s;
  }

  SatelliteMachineSatelliteTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Satélite",
        p: "Tipo de satélite: proxy, bot, worker",
        g: "M",
      },
      en: { s: "Satellite Type", p: "Satellite type: proxy, bot, worker" },
      pt: {
        s: "Tipo de Satélite",
        p: "Tipo de satélite: proxy, bot, worker",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineSatelliteTypeLabel() {
    return this.SatelliteMachineSatelliteTypeLabelByLang();
  }

  get SatelliteMachineSatelliteTypeDescription() {
    return {
      es: {
        s: "Tipo principal del satélite: proxy (SOCKS5/HTTP), bot (automatización), worker (tareas genéricas).",
      },
      en: {
        s: "Main satellite type: proxy (SOCKS5/HTTP), bot (automation), worker (generic tasks).",
      },
      pt: {
        s: "Tipo principal do satélite: proxy (SOCKS5/HTTP), bot (automação), worker (tarefas genéricas).",
      },
    }[this.language].s;
  }

  SatelliteMachineRegionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Región",
        p: "Código de región para agrupamiento (ar, br, us, etc.)",
        g: "F",
      },
      en: { s: "Region", p: "Region code for grouping (ar, br, us, etc.)" },
      pt: {
        s: "Região",
        p: "Código de região para agrupamento (ar, br, us, etc.)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineRegionLabel() {
    return this.SatelliteMachineRegionLabelByLang();
  }

  get SatelliteMachineRegionDescription() {
    return {
      es: {
        s: "Código de región para agrupar satélites por ubicación geográfica.",
      },
      en: { s: "Region code to group satellites by geographic location." },
      pt: {
        s: "Código de região para agrupar satélites por localização geográfica.",
      },
    }[this.language].s;
  }

  SatelliteMachineCertificateFingerprintLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Huella del Certificado",
        p: "SHA256 del certificado X.509",
        g: "F",
      },
      en: {
        s: "Certificate Fingerprint",
        p: "SHA256 of the X.509 certificate",
      },
      pt: {
        s: "Impressão Digital do Certificado",
        p: "SHA256 do certificado X.509",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineCertificateFingerprintLabel() {
    return this.SatelliteMachineCertificateFingerprintLabelByLang();
  }

  get SatelliteMachineCertificateFingerprintDescription() {
    return {
      es: {
        s: "Huella digital SHA256 del certificado X.509 para autenticación mTLS.",
      },
      en: {
        s: "SHA256 fingerprint of the X.509 certificate for mTLS authentication.",
      },
      pt: {
        s: "Impressão digital SHA256 do certificado X.509 para autenticação mTLS.",
      },
    }[this.language].s;
  }

  SatelliteMachineCertificateExpiresAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Expiración del Certificado",
        p: "Fecha de expiración del certificado",
        g: "F",
      },
      en: { s: "Certificate Expires At", p: "Certificate expiration date" },
      pt: {
        s: "Expiração do Certificado",
        p: "Data de expiração do certificado",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineCertificateExpiresAtLabel() {
    return this.SatelliteMachineCertificateExpiresAtLabelByLang();
  }

  get SatelliteMachineCertificateExpiresAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que expira el certificado, requiriendo renovación.",
      },
      en: {
        s: "Date and time when the certificate expires, requiring renewal.",
      },
      pt: { s: "Data e hora em que o certificado expira, exigindo renovação." },
    }[this.language].s;
  }

  SatelliteMachineAuthTokenHashLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Hash del Token",
        p: "Hash SHA256 del token de autenticación",
        g: "M",
      },
      en: { s: "Token Hash", p: "SHA256 hash of the authentication token" },
      pt: {
        s: "Hash do Token",
        p: "Hash SHA256 do token de autenticação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAuthTokenHashLabel() {
    return this.SatelliteMachineAuthTokenHashLabelByLang();
  }

  get SatelliteMachineAuthTokenHashDescription() {
    return {
      es: {
        s: "Hash SHA256 del token de autenticación para verificación sin almacenar el token.",
      },
      en: {
        s: "SHA256 hash of the authentication token for verification without storing the token.",
      },
      pt: {
        s: "Hash SHA256 do token de autenticação para verificação sem armazenar o token.",
      },
    }[this.language].s;
  }

  SatelliteMachineAuthTokenLastFourLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Últimos 4 del Token",
        p: "Últimos 4 caracteres del token para identificación",
        g: "M",
      },
      en: {
        s: "Token Last Four",
        p: "Last 4 characters of token for identification",
      },
      pt: {
        s: "Últimos 4 do Token",
        p: "Últimos 4 caracteres do token para identificação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAuthTokenLastFourLabel() {
    return this.SatelliteMachineAuthTokenLastFourLabelByLang();
  }

  get SatelliteMachineAuthTokenLastFourDescription() {
    return {
      es: {
        s: "Últimos 4 caracteres del token para que el administrador pueda identificarlo.",
      },
      en: {
        s: "Last 4 characters of the token so the administrator can identify it.",
      },
      pt: {
        s: "Últimos 4 caracteres do token para que o administrador possa identificá-lo.",
      },
    }[this.language].s;
  }

  SatelliteMachineAuthTokenCreatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Token Creado",
        p: "Fecha de creación del token actual",
        g: "M",
      },
      en: { s: "Token Created At", p: "Creation date of the current token" },
      pt: { s: "Token Criado", p: "Data de criação do token atual", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAuthTokenCreatedAtLabel() {
    return this.SatelliteMachineAuthTokenCreatedAtLabelByLang();
  }

  get SatelliteMachineAuthTokenCreatedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que se creó el token de autenticación actual.",
      },
      en: {
        s: "Date and time when the current authentication token was created.",
      },
      pt: { s: "Data e hora em que o token de autenticação atual foi criado." },
    }[this.language].s;
  }

  SatelliteMachineAuthTokenRotatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Última Rotación del Token",
        p: "Fecha de la última rotación del token",
        g: "F",
      },
      en: { s: "Token Last Rotated At", p: "Date of the last token rotation" },
      pt: {
        s: "Última Rotação do Token",
        p: "Data da última rotação do token",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAuthTokenRotatedAtLabel() {
    return this.SatelliteMachineAuthTokenRotatedAtLabelByLang();
  }

  get SatelliteMachineAuthTokenRotatedAtDescription() {
    return {
      es: { s: "Fecha y hora de la última rotación del token por seguridad." },
      en: { s: "Date and time of the last token rotation for security." },
      pt: { s: "Data e hora da última rotação do token por segurança." },
    }[this.language].s;
  }

  SatelliteMachineStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Estado",
        p: "Estado actual de la máquina (pending_authorization, authorized, online, offline, suspended, revoked)",
        g: "M",
      },
      en: {
        s: "Status",
        p: "Current machine status (pending_authorization, authorized, online, offline, suspended, revoked)",
      },
      pt: {
        s: "Estado",
        p: "Estado atual da máquina (pending_authorization, authorized, online, offline, suspended, revoked)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineStatusLabel() {
    return this.SatelliteMachineStatusLabelByLang();
  }

  get SatelliteMachineStatusDescription() {
    return {
      es: {
        s: "Estado actual de la máquina en el ciclo de vida de autorización y conexión.",
      },
      en: {
        s: "Current machine status in the authorization and connection lifecycle.",
      },
      pt: {
        s: "Estado atual da máquina no ciclo de vida de autorização e conexão.",
      },
    }[this.language].s;
  }

  SatelliteMachineAuthorizedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha de Autorización",
        p: "Fecha y hora en que se autorizó la máquina",
        g: "F",
      },
      en: {
        s: "Authorized At",
        p: "Date and time when the machine was authorized",
      },
      pt: {
        s: "Data de Autorização",
        p: "Data e hora em que a máquina foi autorizada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAuthorizedAtLabel() {
    return this.SatelliteMachineAuthorizedAtLabelByLang();
  }

  get SatelliteMachineAuthorizedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que un administrador autorizó la máquina para operar.",
      },
      en: {
        s: "Date and time when an administrator authorized the machine to operate.",
      },
      pt: {
        s: "Data e hora em que um administrador autorizou a máquina a operar.",
      },
    }[this.language].s;
  }

  SatelliteMachineAuthorizedByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Autorizado por", p: "Usuario que autorizó la máquina", g: "M" },
      en: { s: "Authorized By", p: "User who authorized the machine" },
      pt: { s: "Autorizado por", p: "Usuário que autorizou a máquina", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAuthorizedByLabel() {
    return this.SatelliteMachineAuthorizedByLabelByLang();
  }

  get SatelliteMachineAuthorizedByDescription() {
    return {
      es: {
        s: "Usuario administrador que aprobó la autorización de la máquina.",
      },
      en: { s: "Administrator user who approved the machine authorization." },
      pt: { s: "Usuário administrador que aprovou a autorização da máquina." },
    }[this.language].s;
  }

  SatelliteMachineSuspendedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha de Suspensión",
        p: "Fecha y hora en que se suspendió la máquina",
        g: "F",
      },
      en: {
        s: "Suspended At",
        p: "Date and time when the machine was suspended",
      },
      pt: {
        s: "Data de Suspensão",
        p: "Data e hora em que a máquina foi suspensa",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineSuspendedAtLabel() {
    return this.SatelliteMachineSuspendedAtLabelByLang();
  }

  get SatelliteMachineSuspendedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que se suspendió la máquina por un administrador.",
      },
      en: {
        s: "Date and time when the machine was suspended by an administrator.",
      },
      pt: {
        s: "Data e hora em que a máquina foi suspensa por um administrador.",
      },
    }[this.language].s;
  }

  SatelliteMachineSuspendedByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Suspendido por",
        p: "Usuario que suspendió la máquina",
        g: "M",
      },
      en: { s: "Suspended By", p: "User who suspended the machine" },
      pt: { s: "Suspenso por", p: "Usuário que suspendeu a máquina", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineSuspendedByLabel() {
    return this.SatelliteMachineSuspendedByLabelByLang();
  }

  get SatelliteMachineSuspendedByDescription() {
    return {
      es: { s: "Usuario administrador que suspendió la máquina." },
      en: { s: "Administrator user who suspended the machine." },
      pt: { s: "Usuário administrador que suspendeu a máquina." },
    }[this.language].s;
  }

  SatelliteMachineSuspensionReasonLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Motivo de Suspensión",
        p: "Razón por la cual se suspendió la máquina",
        g: "M",
      },
      en: { s: "Suspension Reason", p: "Reason why the machine was suspended" },
      pt: {
        s: "Motivo da Suspensão",
        p: "Razão pela qual a máquina foi suspensa",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineSuspensionReasonLabel() {
    return this.SatelliteMachineSuspensionReasonLabelByLang();
  }

  get SatelliteMachineSuspensionReasonDescription() {
    return {
      es: { s: "Explicación del motivo por el cual se suspendió la máquina." },
      en: { s: "Explanation of the reason why the machine was suspended." },
      pt: { s: "Explicação do motivo pelo qual a máquina foi suspensa." },
    }[this.language].s;
  }

  SatelliteMachineEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Habilitado",
        p: "Si está habilitado para recibir tareas (independiente del status)",
        g: "M",
      },
      en: {
        s: "Enabled",
        p: "Whether it's enabled to receive tasks (independent of status)",
      },
      pt: {
        s: "Habilitado",
        p: "Se está habilitado para receber tarefas (independente do status)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineEnabledLabel() {
    return this.SatelliteMachineEnabledLabelByLang();
  }

  get SatelliteMachineEnabledDescription() {
    return {
      es: {
        s: "Control administrativo para habilitar o deshabilitar la recepción de tareas.",
      },
      en: { s: "Administrative control to enable or disable task reception." },
      pt: {
        s: "Controle administrativo para habilitar ou desabilitar a recepção de tarefas.",
      },
    }[this.language].s;
  }

  SatelliteMachineAllowedTenantIdsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Empresas Permitidas",
        p: "Ids de empresa que este satélite puede atender, separados por coma",
        g: "F",
      },
      en: {
        s: "Allowed Companies",
        p: "Company ids this satellite may serve, comma-separated",
      },
      pt: {
        s: "Empresas Permitidas",
        p: "Ids de empresa que este satélite pode atender, separados por vírgula",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineAllowedTenantIdsLabel() {
    return this.SatelliteMachineAllowedTenantIdsLabelByLang();
  }

  get SatelliteMachineAllowedTenantIdsDescription() {
    return {
      es: {
        s: "Ids de empresa (Tenant) separados por coma que este satélite puede atender. Vacío = no atiende ninguna (falla cerrado): la máquina es infraestructura compartida y solo recibe tareas de las empresas que se declaren acá.",
      },
      en: {
        s: "Comma-separated company (Tenant) ids this satellite may serve. Empty = serves none (fail-closed): the machine is shared infrastructure and only receives tasks for the companies declared here.",
      },
      pt: {
        s: "Ids de empresa (Tenant) separados por vírgula que este satélite pode atender. Vazio = não atende nenhuma (falha fechado): a máquina é infraestrutura compartilhada e só recebe tarefas das empresas declaradas aqui.",
      },
    }[this.language].s;
  }

  SatelliteMachineProxyEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Proxy Habilitado",
        p: "Si el satellite puede funcionar como proxy",
        g: "M",
      },
      en: {
        s: "Proxy Enabled",
        p: "Whether the satellite can work as a proxy",
      },
      pt: {
        s: "Proxy Habilitado",
        p: "Se o satélite pode funcionar como proxy",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineProxyEnabledLabel() {
    return this.SatelliteMachineProxyEnabledLabelByLang();
  }

  get SatelliteMachineProxyEnabledDescription() {
    return {
      es: {
        s: "Indica si el satélite puede funcionar como servidor proxy SOCKS5/HTTP.",
      },
      en: {
        s: "Indicates if the satellite can function as a SOCKS5/HTTP proxy server.",
      },
      pt: {
        s: "Indica se o satélite pode funcionar como servidor proxy SOCKS5/HTTP.",
      },
    }[this.language].s;
  }

  SatelliteMachineProxyPortLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Puerto Proxy",
        p: "Puerto en el que escucha el proxy (ej: 1080)",
        g: "M",
      },
      en: { s: "Proxy Port", p: "Port where the proxy listens (e.g., 1080)" },
      pt: {
        s: "Porta Proxy",
        p: "Porta onde o proxy escuta (ex: 1080)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineProxyPortLabel() {
    return this.SatelliteMachineProxyPortLabelByLang();
  }

  get SatelliteMachineProxyPortDescription() {
    return {
      es: {
        s: "Puerto TCP donde el servicio proxy escucha conexiones entrantes.",
      },
      en: {
        s: "TCP port where the proxy service listens for incoming connections.",
      },
      pt: { s: "Porta TCP onde o serviço proxy escuta conexões de entrada." },
    }[this.language].s;
  }

  SatelliteMachineProxyProtocolsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Protocolos Proxy",
        p: "Protocolos soportados separados por coma (socks5, http)",
        g: "M",
      },
      en: {
        s: "Proxy Protocols",
        p: "Supported protocols comma-separated (socks5, http)",
      },
      pt: {
        s: "Protocolos Proxy",
        p: "Protocolos suportados separados por vírgula (socks5, http)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineProxyProtocolsLabel() {
    return this.SatelliteMachineProxyProtocolsLabelByLang();
  }

  get SatelliteMachineProxyProtocolsDescription() {
    return {
      es: { s: "Lista de protocolos de proxy soportados, separados por coma." },
      en: { s: "List of supported proxy protocols, comma-separated." },
      pt: {
        s: "Lista de protocolos de proxy suportados, separados por vírgula.",
      },
    }[this.language].s;
  }

  SatelliteMachineBotEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Bot Habilitado",
        p: "Si el satellite puede ejecutar bots",
        g: "M",
      },
      en: { s: "Bot Enabled", p: "Whether the satellite can run bots" },
      pt: {
        s: "Bot Habilitado",
        p: "Se o satélite pode executar bots",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineBotEnabledLabel() {
    return this.SatelliteMachineBotEnabledLabelByLang();
  }

  get SatelliteMachineBotEnabledDescription() {
    return {
      es: {
        s: "Indica si el satélite puede ejecutar bots de automatización con Playwright.",
      },
      en: {
        s: "Indicates if the satellite can run automation bots with Playwright.",
      },
      pt: {
        s: "Indica se o satélite pode executar bots de automação com Playwright.",
      },
    }[this.language].s;
  }

  SatelliteMachineBotMaxConcurrentLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Máx. Bots Concurrentes",
        p: "Máximo de ejecuciones de bot simultáneas",
        g: "M",
      },
      en: {
        s: "Max Concurrent Bots",
        p: "Maximum simultaneous bot executions",
      },
      pt: {
        s: "Máx. Bots Concorrentes",
        p: "Máximo de execuções de bot simultâneas",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineBotMaxConcurrentLabel() {
    return this.SatelliteMachineBotMaxConcurrentLabelByLang();
  }

  get SatelliteMachineBotMaxConcurrentDescription() {
    return {
      es: {
        s: "Número máximo de bots que pueden ejecutarse simultáneamente en el satélite.",
      },
      en: {
        s: "Maximum number of bots that can run simultaneously on the satellite.",
      },
      pt: {
        s: "Número máximo de bots que podem ser executados simultaneamente no satélite.",
      },
    }[this.language].s;
  }

  SatelliteMachineWorkerEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Worker Habilitado",
        p: "Si el satellite puede procesar trabajos genéricos",
        g: "M",
      },
      en: {
        s: "Worker Enabled",
        p: "Whether the satellite can process generic jobs",
      },
      pt: {
        s: "Worker Habilitado",
        p: "Se o satélite pode processar trabalhos genéricos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineWorkerEnabledLabel() {
    return this.SatelliteMachineWorkerEnabledLabelByLang();
  }

  get SatelliteMachineWorkerEnabledDescription() {
    return {
      es: {
        s: "Indica si el satélite puede procesar trabajos genéricos de worker.",
      },
      en: { s: "Indicates if the satellite can process generic worker jobs." },
      pt: {
        s: "Indica se o satélite pode processar trabalhos genéricos de worker.",
      },
    }[this.language].s;
  }

  SatelliteMachinePublicIpLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "IP Pública", p: "Dirección IP pública de la máquina", g: "F" },
      en: { s: "Public IP", p: "Public IP address of the machine" },
      pt: { s: "IP Público", p: "Endereço IP público da máquina", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachinePublicIpLabel() {
    return this.SatelliteMachinePublicIpLabelByLang();
  }

  get SatelliteMachinePublicIpDescription() {
    return {
      es: { s: "Dirección IP pública desde la cual se conecta la máquina." },
      en: { s: "Public IP address from which the machine connects." },
      pt: { s: "Endereço IP público a partir do qual a máquina se conecta." },
    }[this.language].s;
  }

  SatelliteMachineLocationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Ubicación",
        p: "Ubicación física de la máquina (e.g., 'Buenos Aires, Argentina')",
        g: "F",
      },
      en: {
        s: "Location",
        p: "Physical location of the machine (e.g., 'Buenos Aires, Argentina')",
      },
      pt: {
        s: "Localização",
        p: "Localização física da máquina (ex., 'Buenos Aires, Argentina')",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineLocationLabel() {
    return this.SatelliteMachineLocationLabelByLang();
  }

  get SatelliteMachineLocationDescription() {
    return {
      es: { s: "Ubicación geográfica de la máquina en formato ciudad, país." },
      en: { s: "Geographic location of the machine in city, country format." },
      pt: { s: "Localização geográfica da máquina em formato cidade, país." },
    }[this.language].s;
  }

  SatelliteMachineCountryCodeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Código de País",
        p: "Código ISO 3166-1 alpha-2 del país (e.g., 'AR')",
        g: "M",
      },
      en: {
        s: "Country Code",
        p: "ISO 3166-1 alpha-2 country code (e.g., 'AR')",
      },
      pt: {
        s: "Código do País",
        p: "Código ISO 3166-1 alpha-2 do país (ex., 'AR')",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineCountryCodeLabel() {
    return this.SatelliteMachineCountryCodeLabelByLang();
  }

  get SatelliteMachineCountryCodeDescription() {
    return {
      es: {
        s: "Código de país ISO 3166-1 alpha-2 para geo-targeting de tareas.",
      },
      en: { s: "ISO 3166-1 alpha-2 country code for task geo-targeting." },
      pt: {
        s: "Código de país ISO 3166-1 alpha-2 para geo-targeting de tarefas.",
      },
    }[this.language].s;
  }

  SatelliteMachineLastHeartbeatAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Último Heartbeat",
        p: "Fecha y hora del último heartbeat recibido",
        g: "M",
      },
      en: {
        s: "Last Heartbeat At",
        p: "Date and time of the last received heartbeat",
      },
      pt: {
        s: "Último Heartbeat",
        p: "Data e hora do último heartbeat recebido",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineLastHeartbeatAtLabel() {
    return this.SatelliteMachineLastHeartbeatAtLabelByLang();
  }

  get SatelliteMachineLastHeartbeatAtDescription() {
    return {
      es: { s: "Fecha y hora del último heartbeat recibido del satélite." },
      en: {
        s: "Date and time of the last heartbeat received from the satellite.",
      },
      pt: { s: "Data e hora do último heartbeat recebido do satélite." },
    }[this.language].s;
  }

  SatelliteMachineLastHeartbeatMetricsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Métricas del Último Heartbeat",
        p: "CPU, memoria, tareas activas del último heartbeat",
        g: "F",
      },
      en: {
        s: "Last Heartbeat Metrics",
        p: "CPU, memory, active tasks from last heartbeat",
      },
      pt: {
        s: "Métricas do Último Heartbeat",
        p: "CPU, memória, tarefas ativas do último heartbeat",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineLastHeartbeatMetricsLabel() {
    return this.SatelliteMachineLastHeartbeatMetricsLabelByLang();
  }

  get SatelliteMachineLastHeartbeatMetricsDescription() {
    return {
      es: {
        s: "Métricas del sistema (CPU, memoria, tareas) del último heartbeat.",
      },
      en: { s: "System metrics (CPU, memory, tasks) from the last heartbeat." },
      pt: {
        s: "Métricas do sistema (CPU, memória, tarefas) do último heartbeat.",
      },
    }[this.language].s;
  }

  SatelliteMachineConnectedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Conectado desde",
        p: "Fecha y hora de la conexión actual",
        g: "M",
      },
      en: { s: "Connected At", p: "Date and time of the current connection" },
      pt: { s: "Conectado desde", p: "Data e hora da conexão atual", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineConnectedAtLabel() {
    return this.SatelliteMachineConnectedAtLabelByLang();
  }

  get SatelliteMachineConnectedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que se estableció la conexión WebSocket actual.",
      },
      en: {
        s: "Date and time when the current WebSocket connection was established.",
      },
      pt: {
        s: "Data e hora em que a conexão WebSocket atual foi estabelecida.",
      },
    }[this.language].s;
  }

  SatelliteMachineDisconnectedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Desconectado desde",
        p: "Fecha y hora de la última desconexión",
        g: "M",
      },
      en: {
        s: "Disconnected At",
        p: "Date and time of the last disconnection",
      },
      pt: {
        s: "Desconectado desde",
        p: "Data e hora da última desconexão",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineDisconnectedAtLabel() {
    return this.SatelliteMachineDisconnectedAtLabelByLang();
  }

  get SatelliteMachineDisconnectedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que el satélite se desconectó por última vez.",
      },
      en: { s: "Date and time when the satellite was last disconnected." },
      pt: {
        s: "Data e hora em que o satélite foi desconectado pela última vez.",
      },
    }[this.language].s;
  }

  SatelliteMachineTunnelConnectedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Túnel Conectado",
        p: "Fecha y hora de conexión del túnel reverso (null si no hay túnel)",
        g: "M",
      },
      en: {
        s: "Tunnel Connected At",
        p: "Reverse tunnel connection timestamp (null if no tunnel)",
      },
      pt: {
        s: "Túnel Conectado",
        p: "Data e hora de conexão do túnel reverso (null se não há túnel)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineTunnelConnectedAtLabel() {
    return this.SatelliteMachineTunnelConnectedAtLabelByLang();
  }

  get SatelliteMachineTunnelConnectedAtDescription() {
    return {
      es: {
        s: "Fecha y hora de establecimiento del túnel SSH reverso, null si no aplica.",
      },
      en: {
        s: "Date and time when reverse SSH tunnel was established, null if not applicable.",
      },
      pt: {
        s: "Data e hora de estabelecimento do túnel SSH reverso, null se não aplicável.",
      },
    }[this.language].s;
  }

  SatelliteMachineTasksLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tareas", p: "Tareas asignadas a esta máquina", g: "F" },
      en: { s: "Tasks", p: "Tasks assigned to this machine" },
      pt: { s: "Tarefas", p: "Tarefas atribuídas a esta máquina", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteMachineTasksLabel() {
    return this.SatelliteMachineTasksLabelByLang();
  }

  get SatelliteMachineTasksDescription() {
    return {
      es: {
        s: "Colección de tareas asignadas y procesadas por este satélite.",
      },
      en: {
        s: "Collection of tasks assigned and processed by this satellite.",
      },
      pt: {
        s: "Coleção de tarefas atribuídas e processadas por este satélite.",
      },
    }[this.language].s;
  }

  // SatelliteTask

  get SatelliteTaskLabels() {
    return {
      es: { s: "Tarea de Satélite", p: "Tareas de Satélite", g: "F" },
      en: { s: "Satellite Task", p: "Satellite Tasks", g: undefined },
      pt: { s: "Tarefa de Satélite", p: "Tarefas de Satélite", g: "F" },
    }[this.language];
  }

  get SatelliteTaskSingular() {
    return this.SatelliteTaskLabels.s;
  }

  get SatelliteTaskPlural() {
    return this.SatelliteTaskLabels.p;
  }

  get SatelliteTaskGender() {
    return this.SatelliteTaskLabels.g;
  }

  get groupSatelliteTaskLabel() {
    return {
      es: { s: "Tarea de Satélite", p: "Tareas de Satélite", g: "F" },
      en: { s: "Satellite Task", p: "Satellite Tasks" },
      pt: { s: "Tarefa de Satélite", p: "Tarefas de Satélite", g: "F" },
    }[this.language].s;
  }

  SatelliteTaskTaskTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Tarea",
        p: "Tipo de tarea (proxy_request, bot_execution, etc.)",
        g: "M",
      },
      en: {
        s: "Task Type",
        p: "Task type (proxy_request, bot_execution, etc.)",
      },
      pt: {
        s: "Tipo de Tarefa",
        p: "Tipo de tarefa (proxy_request, bot_execution, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskTaskTypeLabel() {
    return this.SatelliteTaskTaskTypeLabelByLang();
  }

  get SatelliteTaskTaskTypeDescription() {
    return {
      es: {
        s: "Clasificación del tipo de tarea para enrutamiento al handler correcto.",
      },
      en: { s: "Task type classification for routing to the correct handler." },
      pt: {
        s: "Classificação do tipo de tarefa para roteamento ao handler correto.",
      },
    }[this.language].s;
  }

  SatelliteTaskStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Estado",
        p: "Estado de la tarea (queued, sent, running, completed, failed, cancelled)",
        g: "M",
      },
      en: {
        s: "Status",
        p: "Task status (queued, sent, running, completed, failed, cancelled)",
      },
      pt: {
        s: "Estado",
        p: "Estado da tarefa (queued, sent, running, completed, failed, cancelled)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskStatusLabel() {
    return this.SatelliteTaskStatusLabelByLang();
  }

  get SatelliteTaskStatusDescription() {
    return {
      es: {
        s: "Estado actual de la tarea en el ciclo de vida de ejecución remota.",
      },
      en: { s: "Current task status in the remote execution lifecycle." },
      pt: { s: "Estado atual da tarefa no ciclo de vida de execução remota." },
    }[this.language].s;
  }

  SatelliteTaskPriorityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Prioridad",
        p: "Prioridad de la tarea (mayor número = mayor prioridad)",
        g: "F",
      },
      en: {
        s: "Priority",
        p: "Task priority (higher number = higher priority)",
      },
      pt: {
        s: "Prioridade",
        p: "Prioridade da tarefa (número maior = maior prioridade)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskPriorityLabel() {
    return this.SatelliteTaskPriorityLabelByLang();
  }

  get SatelliteTaskPriorityDescription() {
    return {
      es: {
        s: "Prioridad para ordenamiento en la cola, valores mayores se procesan primero.",
      },
      en: {
        s: "Priority for queue ordering, higher values are processed first.",
      },
      pt: {
        s: "Prioridade para ordenamento na fila, valores maiores são processados primeiro.",
      },
    }[this.language].s;
  }

  SatelliteTaskInputLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Entrada", p: "Datos de entrada para la tarea", g: "F" },
      en: { s: "Input", p: "Input data for the task" },
      pt: { s: "Entrada", p: "Dados de entrada para a tarefa", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskInputLabel() {
    return this.SatelliteTaskInputLabelByLang();
  }

  get SatelliteTaskInputDescription() {
    return {
      es: {
        s: "Datos de entrada serializados en JSON enviados al satélite para procesar.",
      },
      en: {
        s: "JSON-serialized input data sent to the satellite for processing.",
      },
      pt: {
        s: "Dados de entrada serializados em JSON enviados ao satélite para processamento.",
      },
    }[this.language].s;
  }

  SatelliteTaskOutputLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Salida", p: "Datos de salida de la tarea", g: "F" },
      en: { s: "Output", p: "Output data from the task" },
      pt: { s: "Saída", p: "Dados de saída da tarefa", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskOutputLabel() {
    return this.SatelliteTaskOutputLabelByLang();
  }

  get SatelliteTaskOutputDescription() {
    return {
      es: {
        s: "Resultado de la ejecución exitosa de la tarea, serializado en JSON.",
      },
      en: { s: "Result of successful task execution, JSON-serialized." },
      pt: {
        s: "Resultado da execução bem-sucedida da tarefa, serializado em JSON.",
      },
    }[this.language].s;
  }

  SatelliteTaskErrorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Error", p: "Detalle del error si la tarea falló", g: "M" },
      en: { s: "Error", p: "Error detail if the task failed" },
      pt: { s: "Erro", p: "Detalhe do erro se a tarefa falhou", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskErrorLabel() {
    return this.SatelliteTaskErrorLabelByLang();
  }

  get SatelliteTaskErrorDescription() {
    return {
      es: {
        s: "Detalle del error si la tarea falló, incluyendo mensaje y stack trace.",
      },
      en: {
        s: "Error detail if the task failed, including message and stack trace.",
      },
      pt: {
        s: "Detalhe do erro se a tarefa falhou, incluindo mensagem e stack trace.",
      },
    }[this.language].s;
  }

  SatelliteTaskCorrelationIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "ID de Correlación",
        p: "Identificador único para rastreo",
        g: "M",
      },
      en: { s: "Correlation ID", p: "Unique identifier for tracing" },
      pt: {
        s: "ID de Correlação",
        p: "Identificador único para rastreamento",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskCorrelationIdLabel() {
    return this.SatelliteTaskCorrelationIdLabelByLang();
  }

  get SatelliteTaskCorrelationIdDescription() {
    return {
      es: {
        s: "UUID único para rastrear la tarea a través de logs distribuidos.",
      },
      en: { s: "Unique UUID to trace the task across distributed logs." },
      pt: {
        s: "UUID único para rastrear a tarefa através de logs distribuídos.",
      },
    }[this.language].s;
  }

  SatelliteTaskTimeoutMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Timeout (ms)",
        p: "Tiempo máximo de ejecución en milisegundos",
        g: "M",
      },
      en: { s: "Timeout (ms)", p: "Maximum execution time in milliseconds" },
      pt: {
        s: "Timeout (ms)",
        p: "Tempo máximo de execução em milissegundos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskTimeoutMsLabel() {
    return this.SatelliteTaskTimeoutMsLabelByLang();
  }

  get SatelliteTaskTimeoutMsDescription() {
    return {
      es: {
        s: "Tiempo máximo en milisegundos antes de que la tarea se considere timeout.",
      },
      en: {
        s: "Maximum time in milliseconds before the task is considered timed out.",
      },
      pt: {
        s: "Tempo máximo em milissegundos antes que a tarefa seja considerada timeout.",
      },
    }[this.language].s;
  }

  SatelliteTaskRetryCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intentos", p: "Número de reintentos realizados", g: "M" },
      en: { s: "Retry Count", p: "Number of retries performed" },
      pt: { s: "Tentativas", p: "Número de tentativas realizadas", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskRetryCountLabel() {
    return this.SatelliteTaskRetryCountLabelByLang();
  }

  get SatelliteTaskRetryCountDescription() {
    return {
      es: { s: "Número de veces que se ha reintentado la tarea tras fallos." },
      en: { s: "Number of times the task has been retried after failures." },
      pt: {
        s: "Número de vezes que a tarefa foi tentada novamente após falhas.",
      },
    }[this.language].s;
  }

  SatelliteTaskMaxRetriesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Máximo de Reintentos",
        p: "Número máximo de reintentos permitidos",
        g: "M",
      },
      en: { s: "Max Retries", p: "Maximum number of allowed retries" },
      pt: {
        s: "Máximo de Tentativas",
        p: "Número máximo de tentativas permitidas",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskMaxRetriesLabel() {
    return this.SatelliteTaskMaxRetriesLabelByLang();
  }

  get SatelliteTaskMaxRetriesDescription() {
    return {
      es: {
        s: "Número máximo de reintentos antes de marcar la tarea como fallida definitivamente.",
      },
      en: {
        s: "Maximum number of retries before marking the task as permanently failed.",
      },
      pt: {
        s: "Número máximo de tentativas antes de marcar a tarefa como falha definitivamente.",
      },
    }[this.language].s;
  }

  SatelliteTaskQueuedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Encolada",
        p: "Fecha y hora en que se encoló la tarea",
        g: "F",
      },
      en: { s: "Queued At", p: "Date and time when the task was queued" },
      pt: {
        s: "Enfileirada",
        p: "Data e hora em que a tarefa foi enfileirada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskQueuedAtLabel() {
    return this.SatelliteTaskQueuedAtLabelByLang();
  }

  get SatelliteTaskQueuedAtDescription() {
    return {
      es: { s: "Fecha y hora en que la tarea fue creada y añadida a la cola." },
      en: {
        s: "Date and time when the task was created and added to the queue.",
      },
      pt: { s: "Data e hora em que a tarefa foi criada e adicionada à fila." },
    }[this.language].s;
  }

  SatelliteTaskSentAtLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Enviada",
        p: "Fecha y hora en que se envió al satélite",
        g: "F",
      },
      en: {
        s: "Sent At",
        p: "Date and time when it was sent to the satellite",
      },
      pt: {
        s: "Enviada",
        p: "Data e hora em que foi enviada ao satélite",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskSentAtLabel() {
    return this.SatelliteTaskSentAtLabelByLang();
  }

  get SatelliteTaskSentAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que la tarea fue enviada al satélite via WebSocket.",
      },
      en: {
        s: "Date and time when the task was sent to the satellite via WebSocket.",
      },
      pt: {
        s: "Data e hora em que a tarefa foi enviada ao satélite via WebSocket.",
      },
    }[this.language].s;
  }

  SatelliteTaskStartedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Iniciada",
        p: "Fecha y hora en que comenzó la ejecución",
        g: "F",
      },
      en: { s: "Started At", p: "Date and time when execution started" },
      pt: { s: "Iniciada", p: "Data e hora em que a execução começou", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskStartedAtLabel() {
    return this.SatelliteTaskStartedAtLabelByLang();
  }

  get SatelliteTaskStartedAtDescription() {
    return {
      es: { s: "Fecha y hora en que el satélite comenzó a ejecutar la tarea." },
      en: { s: "Date and time when the satellite started executing the task." },
      pt: { s: "Data e hora em que o satélite começou a executar a tarefa." },
    }[this.language].s;
  }

  SatelliteTaskCompletedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Completada",
        p: "Fecha y hora en que finalizó la ejecución",
        g: "F",
      },
      en: { s: "Completed At", p: "Date and time when execution finished" },
      pt: {
        s: "Completada",
        p: "Data e hora em que a execução terminou",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskCompletedAtLabel() {
    return this.SatelliteTaskCompletedAtLabelByLang();
  }

  get SatelliteTaskCompletedAtDescription() {
    return {
      es: { s: "Fecha y hora en que la tarea finalizó, exitosa o fallida." },
      en: {
        s: "Date and time when the task finished, successfully or failed.",
      },
      pt: { s: "Data e hora em que a tarefa terminou, com sucesso ou falha." },
    }[this.language].s;
  }

  SatelliteTaskDurationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Duración",
        p: "Duración de la ejecución en milisegundos",
        g: "F",
      },
      en: { s: "Duration", p: "Execution duration in milliseconds" },
      pt: { s: "Duração", p: "Duração da execução em milissegundos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskDurationLabel() {
    return this.SatelliteTaskDurationLabelByLang();
  }

  get SatelliteTaskDurationDescription() {
    return {
      es: {
        s: "Duración total de la ejecución en milisegundos (completedAt - startedAt).",
      },
      en: {
        s: "Total execution duration in milliseconds (completedAt - startedAt).",
      },
      pt: {
        s: "Duração total da execução em milissegundos (completedAt - startedAt).",
      },
    }[this.language].s;
  }

  SatelliteTaskBotRunLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Ejecución de Bot",
        p: "Ejecución de bot relacionada (si aplica)",
        g: "F",
      },
      en: { s: "Bot Run", p: "Related bot run (if applicable)" },
      pt: {
        s: "Execução de Bot",
        p: "Execução de bot relacionada (se aplicável)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteTaskBotRunLabel() {
    return this.SatelliteTaskBotRunLabelByLang();
  }

  get SatelliteTaskBotRunDescription() {
    return {
      es: { s: "Referencia al BotRun si esta tarea es una ejecución de bot." },
      en: { s: "Reference to the BotRun if this task is a bot execution." },
      pt: { s: "Referência ao BotRun se esta tarefa é uma execução de bot." },
    }[this.language].s;
  }

  // SatelliteHeartbeat

  get SatelliteHeartbeatLabels() {
    return {
      es: { s: "Heartbeat de Satélite", p: "Heartbeats de Satélite", g: "M" },
      en: { s: "Satellite Heartbeat", p: "Satellite Heartbeats", g: undefined },
      pt: { s: "Heartbeat de Satélite", p: "Heartbeats de Satélite", g: "M" },
    }[this.language];
  }

  get SatelliteHeartbeatSingular() {
    return this.SatelliteHeartbeatLabels.s;
  }

  get SatelliteHeartbeatPlural() {
    return this.SatelliteHeartbeatLabels.p;
  }

  get SatelliteHeartbeatGender() {
    return this.SatelliteHeartbeatLabels.g;
  }

  get groupSatelliteHeartbeatLabel() {
    return {
      es: { s: "Heartbeat de Satélite", p: "Heartbeats de Satélite", g: "M" },
      en: { s: "Satellite Heartbeat", p: "Satellite Heartbeats" },
      pt: { s: "Heartbeat de Satélite", p: "Heartbeats de Satélite", g: "M" },
    }[this.language].s;
  }

  SatelliteHeartbeatReceivedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Recibido",
        p: "Fecha y hora de recepción del heartbeat",
        g: "M",
      },
      en: {
        s: "Received At",
        p: "Date and time when the heartbeat was received",
      },
      pt: {
        s: "Recebido",
        p: "Data e hora de recebimento do heartbeat",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatReceivedAtLabel() {
    return this.SatelliteHeartbeatReceivedAtLabelByLang();
  }

  get SatelliteHeartbeatReceivedAtDescription() {
    return {
      es: { s: "Fecha y hora en que el servidor recibió este heartbeat." },
      en: { s: "Date and time when the server received this heartbeat." },
      pt: { s: "Data e hora em que o servidor recebeu este heartbeat." },
    }[this.language].s;
  }

  SatelliteHeartbeatPublicIpLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "IP Pública",
        p: "IP pública reportada en el heartbeat",
        g: "F",
      },
      en: { s: "Public IP", p: "Public IP reported in the heartbeat" },
      pt: { s: "IP Público", p: "IP público reportado no heartbeat", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatPublicIpLabel() {
    return this.SatelliteHeartbeatPublicIpLabelByLang();
  }

  get SatelliteHeartbeatPublicIpDescription() {
    return {
      es: {
        s: "Dirección IP pública reportada por el satélite en este heartbeat.",
      },
      en: {
        s: "Public IP address reported by the satellite in this heartbeat.",
      },
      pt: { s: "Endereço IP público reportado pelo satélite neste heartbeat." },
    }[this.language].s;
  }

  SatelliteHeartbeatLatencyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Latencia", p: "Latencia de red en milisegundos", g: "F" },
      en: { s: "Latency", p: "Network latency in milliseconds" },
      pt: { s: "Latência", p: "Latência de rede em milissegundos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatLatencyLabel() {
    return this.SatelliteHeartbeatLatencyLabelByLang();
  }

  get SatelliteHeartbeatLatencyDescription() {
    return {
      es: {
        s: "Latencia de red medida en milisegundos entre satélite y servidor.",
      },
      en: {
        s: "Network latency measured in milliseconds between satellite and server.",
      },
      pt: {
        s: "Latência de rede medida em milissegundos entre satélite e servidor.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatCpuUsageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Uso de CPU", p: "Porcentaje de uso de CPU (0-100)", g: "M" },
      en: { s: "CPU Usage", p: "CPU usage percentage (0-100)" },
      pt: { s: "Uso de CPU", p: "Porcentagem de uso de CPU (0-100)", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatCpuUsageLabel() {
    return this.SatelliteHeartbeatCpuUsageLabelByLang();
  }

  get SatelliteHeartbeatCpuUsageDescription() {
    return {
      es: {
        s: "Porcentaje de uso de CPU del sistema al momento del heartbeat.",
      },
      en: { s: "System CPU usage percentage at the time of the heartbeat." },
      pt: {
        s: "Porcentagem de uso de CPU do sistema no momento do heartbeat.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatMemoryUsageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Uso de Memoria",
        p: "Porcentaje de uso de memoria (0-100)",
        g: "M",
      },
      en: { s: "Memory Usage", p: "Memory usage percentage (0-100)" },
      pt: {
        s: "Uso de Memória",
        p: "Porcentagem de uso de memória (0-100)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatMemoryUsageLabel() {
    return this.SatelliteHeartbeatMemoryUsageLabelByLang();
  }

  get SatelliteHeartbeatMemoryUsageDescription() {
    return {
      es: {
        s: "Porcentaje de memoria RAM utilizada al momento del heartbeat.",
      },
      en: { s: "RAM memory usage percentage at the time of the heartbeat." },
      pt: {
        s: "Porcentagem de memória RAM utilizada no momento do heartbeat.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatDiskUsageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Uso de Disco",
        p: "Porcentaje de uso de disco (0-100)",
        g: "M",
      },
      en: { s: "Disk Usage", p: "Disk usage percentage (0-100)" },
      pt: {
        s: "Uso de Disco",
        p: "Porcentagem de uso de disco (0-100)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatDiskUsageLabel() {
    return this.SatelliteHeartbeatDiskUsageLabelByLang();
  }

  get SatelliteHeartbeatDiskUsageDescription() {
    return {
      es: {
        s: "Porcentaje de espacio en disco utilizado al momento del heartbeat.",
      },
      en: { s: "Disk space usage percentage at the time of the heartbeat." },
      pt: {
        s: "Porcentagem de espaço em disco utilizado no momento do heartbeat.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatActiveTasksCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tareas Activas",
        p: "Número de tareas activas al momento del heartbeat",
        g: "F",
      },
      en: {
        s: "Active Tasks Count",
        p: "Number of active tasks at heartbeat time",
      },
      pt: {
        s: "Tarefas Ativas",
        p: "Número de tarefas ativas no momento do heartbeat",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatActiveTasksCountLabel() {
    return this.SatelliteHeartbeatActiveTasksCountLabelByLang();
  }

  get SatelliteHeartbeatActiveTasksCountDescription() {
    return {
      es: {
        s: "Número de tareas que el satélite estaba procesando al enviar el heartbeat.",
      },
      en: {
        s: "Number of tasks the satellite was processing when sending the heartbeat.",
      },
      pt: {
        s: "Número de tarefas que o satélite estava processando ao enviar o heartbeat.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatProxyStatsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Estadísticas de Proxy",
        p: "Conexiones activas, bytes transferidos, etc.",
        g: "F",
      },
      en: {
        s: "Proxy Stats",
        p: "Active connections, bytes transferred, etc.",
      },
      pt: {
        s: "Estatísticas de Proxy",
        p: "Conexões ativas, bytes transferidos, etc.",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatProxyStatsLabel() {
    return this.SatelliteHeartbeatProxyStatsLabelByLang();
  }

  get SatelliteHeartbeatProxyStatsDescription() {
    return {
      es: {
        s: "Estadísticas del servicio proxy: conexiones activas, bytes transferidos.",
      },
      en: {
        s: "Proxy service statistics: active connections, bytes transferred.",
      },
      pt: {
        s: "Estatísticas do serviço proxy: conexões ativas, bytes transferidos.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatSystemInfoLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Info del Sistema",
        p: "Información adicional del sistema (versión, uptime, etc.)",
        g: "M",
      },
      en: {
        s: "System Info",
        p: "Additional system information (version, uptime, etc.)",
      },
      pt: {
        s: "Info do Sistema",
        p: "Informação adicional do sistema (versão, uptime, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatSystemInfoLabel() {
    return this.SatelliteHeartbeatSystemInfoLabelByLang();
  }

  get SatelliteHeartbeatSystemInfoDescription() {
    return {
      es: {
        s: "Información adicional: versión del agente, uptime, sistema operativo.",
      },
      en: { s: "Additional info: agent version, uptime, operating system." },
      pt: {
        s: "Informação adicional: versão do agente, uptime, sistema operacional.",
      },
    }[this.language].s;
  }

  SatelliteHeartbeatSatelliteMachineLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Máquina Satélite",
        p: "Máquina satélite que envió el heartbeat",
        g: "F",
      },
      en: {
        s: "Satellite Machine",
        p: "Satellite machine that sent the heartbeat",
      },
      pt: {
        s: "Máquina Satélite",
        p: "Máquina satélite que enviou o heartbeat",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SatelliteHeartbeatSatelliteMachineLabel() {
    return this.SatelliteHeartbeatSatelliteMachineLabelByLang();
  }

  get SatelliteHeartbeatSatelliteMachineDescription() {
    return {
      es: { s: "Referencia a la máquina satélite que envió este heartbeat." },
      en: { s: "Reference to the satellite machine that sent this heartbeat." },
      pt: { s: "Referência à máquina satélite que enviou este heartbeat." },
    }[this.language].s;
  }

  // DashboardWidget

  get DashboardWidgetLabels() {
    return {
      es: { s: "Widget", p: "Widgets", g: "M" },
      en: { s: "Widget", p: "Widgets", g: undefined },
      pt: { s: "Widget", p: "Widgets", g: "M" },
    }[this.language];
  }

  get DashboardWidgetSingular() {
    return this.DashboardWidgetLabels.s;
  }

  get DashboardWidgetPlural() {
    return this.DashboardWidgetLabels.p;
  }

  get DashboardWidgetGender() {
    return this.DashboardWidgetLabels.g;
  }

  get groupDashboardWidgetLabel() {
    return {
      es: { s: "Widget", p: "Widgets", g: "M" },
      en: { s: "Widget", p: "Widgets" },
      pt: { s: "Widget", p: "Widgets", g: "M" },
    }[this.language].s;
  }

  DashboardWidgetWidgetKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Clave", p: "Identificador único del widget", g: "F" },
      en: { s: "Key", p: "Unique widget identifier" },
      pt: { s: "Chave", p: "Identificador único do widget", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetWidgetKeyLabel() {
    return this.DashboardWidgetWidgetKeyLabelByLang();
  }

  get DashboardWidgetWidgetKeyDescription() {
    return {
      es: {
        s: "Identificador único del widget usado para referencias internas y configuración.",
      },
      en: {
        s: "Unique widget identifier used for internal references and configuration.",
      },
      pt: {
        s: "Identificador único do widget usado para referências internas e configuração.",
      },
    }[this.language].s;
  }

  DashboardWidgetTitleLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Título", p: "Nombre para mostrar", g: "M" },
      en: { s: "Title", p: "Display name" },
      pt: { s: "Título", p: "Nome para exibição", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetTitleLabel() {
    return this.DashboardWidgetTitleLabelByLang();
  }

  get DashboardWidgetTitleDescription() {
    return {
      es: { s: "Nombre del widget que se muestra en la interfaz de usuario." },
      en: { s: "Widget name displayed in the user interface." },
      pt: { s: "Nome do widget exibido na interface do usuário." },
    }[this.language].s;
  }

  DashboardWidgetDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Descripción", p: "Descripción del propósito", g: "F" },
      en: { s: "Description", p: "Purpose description" },
      pt: { s: "Descrição", p: "Descrição do propósito", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetDescriptionLabel() {
    return this.DashboardWidgetDescriptionLabelByLang();
  }

  get DashboardWidgetDescriptionDescription() {
    return {
      es: {
        s: "Descripción detallada del propósito y funcionalidad del widget.",
      },
      en: {
        s: "Detailed description of the widget's purpose and functionality.",
      },
      pt: { s: "Descrição detalhada do propósito e funcionalidade do widget." },
    }[this.language].s;
  }

  DashboardWidgetCategoryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Categoría",
        p: "Categoría: entities, bots, satellites, system",
        g: "F",
      },
      en: { s: "Category", p: "Category: entities, bots, satellites, system" },
      pt: {
        s: "Categoria",
        p: "Categoria: entities, bots, satellites, system",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetCategoryLabel() {
    return this.DashboardWidgetCategoryLabelByLang();
  }

  get DashboardWidgetCategoryDescription() {
    return {
      es: {
        s: "Categoría para agrupar widgets: entities, bots, satellites, system.",
      },
      en: {
        s: "Category for grouping widgets: entities, bots, satellites, system.",
      },
      pt: {
        s: "Categoria para agrupar widgets: entities, bots, satellites, system.",
      },
    }[this.language].s;
  }

  DashboardWidgetComponentLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Componente", p: "Nombre del componente React", g: "M" },
      en: { s: "Component", p: "React component name" },
      pt: { s: "Componente", p: "Nome do componente React" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetComponentLabel() {
    return this.DashboardWidgetComponentLabelByLang();
  }

  get DashboardWidgetComponentDescription() {
    return {
      es: { s: "Nombre del componente React que renderiza este widget." },
      en: { s: "Name of the React component that renders this widget." },
      pt: { s: "Nome do componente React que renderiza este widget." },
    }[this.language].s;
  }

  DashboardWidgetDataFetcherKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fetcher", p: "Clave del data fetcher", g: "M" },
      en: { s: "Fetcher", p: "Data fetcher key" },
      pt: { s: "Fetcher", p: "Chave do data fetcher", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetDataFetcherKeyLabel() {
    return this.DashboardWidgetDataFetcherKeyLabelByLang();
  }

  get DashboardWidgetDataFetcherKeyDescription() {
    return {
      es: {
        s: "Clave que identifica la función de obtención de datos para el widget.",
      },
      en: {
        s: "Key that identifies the data fetching function for the widget.",
      },
      pt: {
        s: "Chave que identifica a função de obtenção de dados para o widget.",
      },
    }[this.language].s;
  }

  DashboardWidgetDefaultConfigLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Config Default", p: "Configuración por defecto", g: "F" },
      en: { s: "Default Config", p: "Default configuration" },
      pt: { s: "Config Padrão", p: "Configuração padrão", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetDefaultConfigLabel() {
    return this.DashboardWidgetDefaultConfigLabelByLang();
  }

  get DashboardWidgetDefaultConfigDescription() {
    return {
      es: {
        s: "Configuración inicial del widget que puede ser sobrescrita por tenant o usuario.",
      },
      en: {
        s: "Initial widget configuration that can be overridden by tenant or user.",
      },
      pt: {
        s: "Configuração inicial do widget que pode ser sobrescrita por tenant ou usuário.",
      },
    }[this.language].s;
  }

  DashboardWidgetIconLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Ícono", p: "Nombre del ícono MUI", g: "M" },
      en: { s: "Icon", p: "MUI icon name" },
      pt: { s: "Ícone", p: "Nome do ícone MUI" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetIconLabel() {
    return this.DashboardWidgetIconLabelByLang();
  }

  get DashboardWidgetIconDescription() {
    return {
      es: {
        s: "Nombre del ícono de Material UI para mostrar junto al widget.",
      },
      en: { s: "Material UI icon name to display alongside the widget." },
      pt: { s: "Nome do ícone do Material UI para exibir junto ao widget." },
    }[this.language].s;
  }

  DashboardWidgetMinWidthLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Ancho Mín", p: "Ancho mínimo en grid (1-12)", g: "M" },
      en: { s: "Min Width", p: "Minimum width in grid (1-12)" },
      pt: { s: "Largura Mín", p: "Largura mínima no grid (1-12)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetMinWidthLabel() {
    return this.DashboardWidgetMinWidthLabelByLang();
  }

  get DashboardWidgetMinWidthDescription() {
    return {
      es: { s: "Ancho mínimo del widget en columnas del grid (1-12)." },
      en: { s: "Minimum widget width in grid columns (1-12)." },
      pt: { s: "Largura mínima do widget em colunas do grid (1-12)." },
    }[this.language].s;
  }

  DashboardWidgetMinHeightLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Alto Mín", p: "Alto mínimo en unidades de grid", g: "M" },
      en: { s: "Min Height", p: "Minimum height in grid units" },
      pt: { s: "Altura Mín", p: "Altura mínima em unidades de grid", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetMinHeightLabel() {
    return this.DashboardWidgetMinHeightLabelByLang();
  }

  get DashboardWidgetMinHeightDescription() {
    return {
      es: { s: "Alto mínimo del widget en unidades de grid." },
      en: { s: "Minimum widget height in grid units." },
      pt: { s: "Altura mínima do widget em unidades de grid." },
    }[this.language].s;
  }

  DashboardWidgetRefreshIntervalLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Intervalo",
        p: "Segundos entre actualizaciones (0=manual)",
        g: "M",
      },
      en: { s: "Interval", p: "Seconds between refreshes (0=manual)" },
      pt: {
        s: "Intervalo",
        p: "Segundos entre atualizações (0=manual)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetRefreshIntervalLabel() {
    return this.DashboardWidgetRefreshIntervalLabelByLang();
  }

  get DashboardWidgetRefreshIntervalDescription() {
    return {
      es: {
        s: "Intervalo en segundos para actualización automática de datos (0=manual).",
      },
      en: { s: "Interval in seconds for automatic data refresh (0=manual)." },
      pt: {
        s: "Intervalo em segundos para atualização automática de dados (0=manual).",
      },
    }[this.language].s;
  }

  DashboardWidgetRequiredPermissionsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Permisos", p: "Permisos necesarios para ver", g: "M" },
      en: { s: "Permissions", p: "Permissions needed to view" },
      pt: { s: "Permissões", p: "Permissões necessárias para ver", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetRequiredPermissionsLabel() {
    return this.DashboardWidgetRequiredPermissionsLabelByLang();
  }

  get DashboardWidgetRequiredPermissionsDescription() {
    return {
      es: {
        s: "Lista de permisos que el usuario debe tener para ver este widget.",
      },
      en: { s: "List of permissions the user must have to view this widget." },
      pt: {
        s: "Lista de permissões que o usuário deve ter para ver este widget.",
      },
    }[this.language].s;
  }

  DashboardWidgetEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Habilitado", p: "Disponible en el sistema", g: "M" },
      en: { s: "Enabled", p: "Available in the system" },
      pt: { s: "Habilitado", p: "Disponível no sistema", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardWidgetEnabledLabel() {
    return this.DashboardWidgetEnabledLabelByLang();
  }

  get DashboardWidgetEnabledDescription() {
    return {
      es: {
        s: "Indica si el widget está disponible globalmente en el sistema.",
      },
      en: {
        s: "Indicates whether the widget is globally available in the system.",
      },
      pt: { s: "Indica se o widget está disponível globalmente no sistema." },
    }[this.language].s;
  }

  // TenantWidgetAccess

  get TenantWidgetAccessLabels() {
    return {
      es: { s: "Acceso a Widget", p: "Accesos a Widgets", g: "M" },
      en: { s: "Widget Access", p: "Widget Accesses", g: undefined },
      pt: { s: "Acesso a Widget", p: "Acessos a Widgets", g: "M" },
    }[this.language];
  }

  get TenantWidgetAccessSingular() {
    return this.TenantWidgetAccessLabels.s;
  }

  get TenantWidgetAccessPlural() {
    return this.TenantWidgetAccessLabels.p;
  }

  get TenantWidgetAccessGender() {
    return this.TenantWidgetAccessLabels.g;
  }

  get groupTenantWidgetAccessLabel() {
    return {
      es: { s: "Acceso a Widget", p: "Accesos a Widgets", g: "M" },
      en: { s: "Widget Access", p: "Widget Accesses" },
      pt: { s: "Acesso a Widget", p: "Acessos a Widgets", g: "M" },
    }[this.language].s;
  }

  TenantWidgetAccessTenantLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Cliente", p: "Cliente al que se habilita el widget", g: "M" },
      en: { s: "Tenant", p: "Tenant the widget is enabled for" },
      pt: { s: "Cliente", p: "Cliente ao qual o widget é habilitado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantWidgetAccessTenantLabel() {
    return this.TenantWidgetAccessTenantLabelByLang();
  }

  get TenantWidgetAccessTenantDescription() {
    return {
      es: {
        s: "Cliente cuyos usuarios verán el widget en su dashboard. Define la cuenta de destino del acceso.",
      },
      en: {
        s: "Tenant whose users will see the widget on their dashboard. Defines the target account of the access.",
      },
      pt: {
        s: "Cliente cujos usuários verão o widget no seu dashboard. Define a conta de destino do acesso.",
      },
    }[this.language].s;
  }

  TenantWidgetAccessWidgetLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Widget", p: "Widget asignado", g: "M" },
      en: { s: "Widget", p: "Assigned widget" },
      pt: { s: "Widget", p: "Widget atribuído", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantWidgetAccessWidgetLabel() {
    return this.TenantWidgetAccessWidgetLabelByLang();
  }

  get TenantWidgetAccessWidgetDescription() {
    return {
      es: { s: "Widget del catálogo global al que se otorga acceso." },
      en: { s: "Widget from the global catalog to which access is granted." },
      pt: { s: "Widget do catálogo global ao qual o acesso é concedido." },
    }[this.language].s;
  }

  TenantWidgetAccessEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Habilitado", p: "Activo para este tenant", g: "M" },
      en: { s: "Enabled", p: "Active for this tenant" },
      pt: { s: "Habilitado", p: "Ativo para este tenant", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantWidgetAccessEnabledLabel() {
    return this.TenantWidgetAccessEnabledLabelByLang();
  }

  get TenantWidgetAccessEnabledDescription() {
    return {
      es: {
        s: "Indica si el widget está habilitado y disponible para usuarios del tenant.",
      },
      en: {
        s: "Indicates whether the widget is enabled and available for tenant users.",
      },
      pt: {
        s: "Indica se o widget está habilitado e disponível para usuários do tenant.",
      },
    }[this.language].s;
  }

  TenantWidgetAccessConfigOverrideLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Config Override",
        p: "Configuración específica del tenant",
        g: "F",
      },
      en: { s: "Config Override", p: "Tenant-specific configuration" },
      pt: {
        s: "Config Override",
        p: "Configuração específica do tenant",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantWidgetAccessConfigOverrideLabel() {
    return this.TenantWidgetAccessConfigOverrideLabelByLang();
  }

  get TenantWidgetAccessConfigOverrideDescription() {
    return {
      es: {
        s: "Configuración personalizada del widget que sobrescribe los valores por defecto para este tenant.",
      },
      en: {
        s: "Custom widget configuration that overrides default values for this tenant.",
      },
      pt: {
        s: "Configuração personalizada do widget que sobrescreve os valores padrão para este tenant.",
      },
    }[this.language].s;
  }

  // UserWidgetConfig

  get UserWidgetConfigLabels() {
    return {
      es: { s: "Widget de Usuario", p: "Widgets de Usuario", g: "M" },
      en: { s: "User Widget", p: "User Widgets", g: undefined },
      pt: { s: "Widget de Usuário", p: "Widgets de Usuário", g: "M" },
    }[this.language];
  }

  get UserWidgetConfigSingular() {
    return this.UserWidgetConfigLabels.s;
  }

  get UserWidgetConfigPlural() {
    return this.UserWidgetConfigLabels.p;
  }

  get UserWidgetConfigGender() {
    return this.UserWidgetConfigLabels.g;
  }

  get groupUserWidgetConfigLabel() {
    return {
      es: { s: "Widget de Usuario", p: "Widgets de Usuario", g: "M" },
      en: { s: "User Widget", p: "User Widgets" },
      pt: { s: "Widget de Usuário", p: "Widgets de Usuário", g: "M" },
    }[this.language].s;
  }

  UserWidgetConfigUserLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Usuario", p: "Usuario dueño", g: "M" },
      en: { s: "User", p: "Owner user" },
      pt: { s: "Usuário", p: "Usuário dono", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserWidgetConfigUserLabel() {
    return this.UserWidgetConfigUserLabelByLang();
  }

  get UserWidgetConfigUserDescription() {
    return {
      es: { s: "Usuario propietario de esta configuración de widget." },
      en: { s: "User who owns this widget configuration." },
      pt: { s: "Usuário proprietário desta configuração de widget." },
    }[this.language].s;
  }

  UserWidgetConfigWidgetLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Widget", p: "Widget configurado", g: "M" },
      en: { s: "Widget", p: "Configured widget" },
      pt: { s: "Widget", p: "Widget configurado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserWidgetConfigWidgetLabel() {
    return this.UserWidgetConfigWidgetLabelByLang();
  }

  get UserWidgetConfigWidgetDescription() {
    return {
      es: { s: "Widget del dashboard que el usuario ha configurado." },
      en: { s: "Dashboard widget that the user has configured." },
      pt: { s: "Widget do dashboard que o usuário configurou." },
    }[this.language].s;
  }

  UserWidgetConfigEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Habilitado", p: "Se muestra en el dashboard", g: "M" },
      en: { s: "Enabled", p: "Shown in dashboard" },
      pt: { s: "Habilitado", p: "Exibido no dashboard", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserWidgetConfigEnabledLabel() {
    return this.UserWidgetConfigEnabledLabelByLang();
  }

  get UserWidgetConfigEnabledDescription() {
    return {
      es: { s: "Indica si el widget se muestra en el dashboard del usuario." },
      en: {
        s: "Indicates whether the widget is shown in the user's dashboard.",
      },
      pt: { s: "Indica se o widget é exibido no dashboard do usuário." },
    }[this.language].s;
  }

  UserWidgetConfigPositionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Posición",
        p: "Posición en grid: {x, y, width, height}",
        g: "F",
      },
      en: { s: "Position", p: "Grid position: {x, y, width, height}" },
      pt: { s: "Posição", p: "Posição no grid: {x, y, width, height}", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserWidgetConfigPositionLabel() {
    return this.UserWidgetConfigPositionLabelByLang();
  }

  get UserWidgetConfigPositionDescription() {
    return {
      es: {
        s: "Posición y tamaño del widget en el grid: {x, y, width, height}.",
      },
      en: { s: "Widget position and size in the grid: {x, y, width, height}." },
      pt: { s: "Posição e tamanho do widget no grid: {x, y, width, height}." },
    }[this.language].s;
  }

  UserWidgetConfigConfigOverrideLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Config Override", p: "Configuración personalizada", g: "F" },
      en: { s: "Config Override", p: "Personalized configuration" },
      pt: { s: "Config Override", p: "Configuração personalizada", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserWidgetConfigConfigOverrideLabel() {
    return this.UserWidgetConfigConfigOverrideLabelByLang();
  }

  get UserWidgetConfigConfigOverrideDescription() {
    return {
      es: {
        s: "Configuración personalizada del usuario que sobrescribe valores de tenant y defaults.",
      },
      en: {
        s: "User's personalized configuration that overrides tenant and default values.",
      },
      pt: {
        s: "Configuração personalizada do usuário que sobrescreve valores de tenant e padrão.",
      },
    }[this.language].s;
  }

  UserWidgetConfigDisplayOrderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Orden",
        p: "Orden de visualización (alternativo a grid)",
        g: "M",
      },
      en: { s: "Order", p: "Display order (alternative to grid)" },
      pt: { s: "Ordem", p: "Ordem de exibição (alternativo ao grid)" },
    }[lang ? lang : this.language].s;
  }

  get UserWidgetConfigDisplayOrderLabel() {
    return this.UserWidgetConfigDisplayOrderLabelByLang();
  }

  get UserWidgetConfigDisplayOrderDescription() {
    return {
      es: {
        s: "Orden de visualización alternativo al posicionamiento por grid.",
      },
      en: { s: "Alternative display order to grid positioning." },
      pt: { s: "Ordem de exibição alternativa ao posicionamento por grid." },
    }[this.language].s;
  }

  // DashboardLayoutTemplate

  get DashboardLayoutTemplateLabels() {
    return {
      es: { s: "Template de Layout", p: "Templates de Layout", g: "M" },
      en: { s: "Layout Template", p: "Layout Templates", g: undefined },
      pt: { s: "Template de Layout", p: "Templates de Layout", g: "M" },
    }[this.language];
  }

  get DashboardLayoutTemplateSingular() {
    return this.DashboardLayoutTemplateLabels.s;
  }

  get DashboardLayoutTemplatePlural() {
    return this.DashboardLayoutTemplateLabels.p;
  }

  get DashboardLayoutTemplateGender() {
    return this.DashboardLayoutTemplateLabels.g;
  }

  get groupDashboardLayoutTemplateLabel() {
    return {
      es: { s: "Template de Layout", p: "Templates de Layout", g: "M" },
      en: { s: "Layout Template", p: "Layout Templates" },
      pt: { s: "Template de Layout", p: "Templates de Layout", g: "M" },
    }[this.language].s;
  }

  DashboardLayoutTemplateNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del template", g: "M" },
      en: { s: "Name", p: "Template name" },
      pt: { s: "Nome", p: "Nome do template", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardLayoutTemplateNameLabel() {
    return this.DashboardLayoutTemplateNameLabelByLang();
  }

  get DashboardLayoutTemplateNameDescription() {
    return {
      es: { s: "Nombre identificador del template de layout." },
      en: { s: "Identifier name of the layout template." },
      pt: { s: "Nome identificador do template de layout." },
    }[this.language].s;
  }

  DashboardLayoutTemplateForRoleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Para Rol",
        p: "Rol al que aplica (null = cualquier rol)",
        g: "M",
      },
      en: { s: "For Role", p: "Role it applies to (null = any role)" },
      pt: {
        s: "Para Papel",
        p: "Papel ao qual se aplica (null = qualquer papel)",
      },
    }[lang ? lang : this.language].s;
  }

  get DashboardLayoutTemplateForRoleLabel() {
    return this.DashboardLayoutTemplateForRoleLabelByLang();
  }

  get DashboardLayoutTemplateForRoleDescription() {
    return {
      es: {
        s: "Rol para el cual aplica este template (null = cualquier rol).",
      },
      en: { s: "Role for which this template applies (null = any role)." },
      pt: {
        s: "Papel para o qual este template se aplica (null = qualquer papel).",
      },
    }[this.language].s;
  }

  DashboardLayoutTemplateWidgetsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Widgets", p: "Lista: [{widgetKey, position, configOverride}]" },
      en: { s: "Widgets", p: "List: [{widgetKey, position, configOverride}]" },
      pt: { s: "Widgets", p: "Lista: [{widgetKey, position, configOverride}]" },
    }[lang ? lang : this.language].s;
  }

  get DashboardLayoutTemplateWidgetsLabel() {
    return this.DashboardLayoutTemplateWidgetsLabelByLang();
  }

  get DashboardLayoutTemplateWidgetsDescription() {
    return {
      es: {
        s: "Lista de widgets con su configuración inicial: [{widgetKey, position, configOverride}].",
      },
      en: {
        s: "List of widgets with their initial configuration: [{widgetKey, position, configOverride}].",
      },
      pt: {
        s: "Lista de widgets com sua configuração inicial: [{widgetKey, position, configOverride}].",
      },
    }[this.language].s;
  }

  DashboardLayoutTemplateIsDefaultLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Es Default", p: "Template por defecto para el rol", g: "M" },
      en: { s: "Is Default", p: "Default template for the role" },
      pt: { s: "É Padrão", p: "Template padrão para o papel", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardLayoutTemplateIsDefaultLabel() {
    return this.DashboardLayoutTemplateIsDefaultLabelByLang();
  }

  get DashboardLayoutTemplateIsDefaultDescription() {
    return {
      es: {
        s: "Indica si es el template por defecto para el rol especificado.",
      },
      en: {
        s: "Indicates whether this is the default template for the specified role.",
      },
      pt: {
        s: "Indica se este é o template padrão para o papel especificado.",
      },
    }[this.language].s;
  }

  DashboardLayoutTemplateEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Habilitado", p: "Template activo", g: "M" },
      en: { s: "Enabled", p: "Template active" },
      pt: { s: "Habilitado", p: "Template ativo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DashboardLayoutTemplateEnabledLabel() {
    return this.DashboardLayoutTemplateEnabledLabelByLang();
  }

  get DashboardLayoutTemplateEnabledDescription() {
    return {
      es: { s: "Indica si el template está activo y disponible para uso." },
      en: {
        s: "Indicates whether the template is active and available for use.",
      },
      pt: { s: "Indica se o template está ativo e disponível para uso." },
    }[this.language].s;
  }

  DashboardLayoutTemplateIsForAllTenantsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      en: { s: "Tenant usable", p: "Tenant usable" },
      es: { s: "Disponible para inquilinos", p: "Disponible para inquilinos" },
      pt: { s: "Disponível para inquilinos", p: "Disponível para inquilinos" },
    }[lang ? lang : this.language].s;
  }

  get DashboardLayoutTemplateIsForAllTenantsLabel() {
    return this.DashboardLayoutTemplateIsForAllTenantsLabelByLang();
  }

  // Report

  get ReportLabels() {
    return {
      es: { s: "Reporte", p: "Reportes", g: "M" },
      en: { s: "Report", p: "Reports", g: undefined },
      pt: { s: "Relatório", p: "Relatórios", g: "M" },
    }[this.language];
  }

  get ReportSingular() {
    return this.ReportLabels.s;
  }

  get ReportPlural() {
    return this.ReportLabels.p;
  }

  get ReportGender() {
    return this.ReportLabels.g;
  }

  get groupReportLabel() {
    return {
      es: { s: "Reporte", p: "Reportes", g: "M" },
      en: { s: "Report", p: "Reports" },
      pt: { s: "Relatório", p: "Relatórios", g: "M" },
    }[this.language].s;
  }

  ReportNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del reporte", g: "M" },
      en: { s: "Name", p: "Name of the report" },
      pt: { s: "Nome", p: "Nome do relatório", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ReportNameLabel() {
    return this.ReportNameLabelByLang();
  }

  get ReportNameDescription() {
    return {
      es: { s: "Nombre único identificatorio del reporte." },
      en: { s: "Unique identifying name for the report." },
      pt: { s: "Nome identificador exclusivo do relatório." },
    }[this.language].s;
  }

  ReportReportTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Reporte", p: "Tipos de Reporte", g: "M" },
      en: { s: "Report Type", p: "Report Types" },
      pt: { s: "Tipo de Relatório", p: "Tipos de Relatório", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ReportReportTypeLabel() {
    return this.ReportReportTypeLabelByLang();
  }

  get ReportReportTypeDescription() {
    return {
      es: { s: "Categoría o tipo al que pertenece el reporte." },
      en: { s: "Category or type to which the report belongs." },
      pt: { s: "Categoria ou tipo ao qual o relatório pertence." },
    }[this.language].s;
  }

  ReportTemplateLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Plantilla", p: "Plantilla del reporte", g: "F" },
      en: { s: "Template", p: "Report template" },
      pt: { s: "Modelo", p: "Modelo do relatório", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ReportTemplateLabel() {
    return this.ReportTemplateLabelByLang();
  }

  get ReportTemplateDescription() {
    return {
      es: {
        s: "Plantilla de formato (ej. HTML, Markdown, Handlebars) utilizada para renderizar los datos del reporte.",
      },
      en: {
        s: "Formatting template (e.g., HTML, Markdown, Handlebars) used to render the report data.",
      },
      pt: {
        s: "Modelo de formatação (ex: HTML, Markdown, Handlebars) usado para renderizar os dados do relatório.",
      },
    }[this.language].s;
  }

  ReportConstantsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Constantes", p: "Constantes del sistema", g: "M" },
      en: { s: "Constants", p: "System constants" },
      pt: { s: "Constantes", p: "Constantes do sistema", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ReportConstantsLabel() {
    return this.ReportConstantsLabelByLang();
  }

  get ReportConstantsDescription() {
    return {
      es: {
        s: "Valores constantes o parámetros fijos que pueden ser utilizados por la plantilla o consulta del reporte, en formato JSON.",
      },
      en: {
        s: "Constant values or fixed parameters that can be used by the report template or query, in JSON format.",
      },
      pt: {
        s: "Valores constantes ou parâmetros fixos que podem ser usados pelo modelo ou consulta do relatório, em formato JSON.",
      },
    }[this.language].s;
  }

  ReportQueryLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Consulta", p: "Consulta del reporte", g: "F" },
      en: { s: "Query", p: "Report query" },
      pt: { s: "Consulta", p: "Consulta do relatório", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get ReportQueryLabel() {
    return this.ReportQueryLabelByLang();
  }

  get ReportQueryDescription() {
    return {
      es: {
        s: "Consulta (ej. SQL, GraphQL, o un lenguaje de consulta específico del sistema) utilizada para obtener los datos del reporte.",
      },
      en: {
        s: "Query (e.g., SQL, GraphQL, or a system-specific query language) used to fetch the report data.",
      },
      pt: {
        s: "Consulta (ex: SQL, GraphQL ou uma linguagem de consulta específica do sistema) usada para buscar os dados do relatório.",
      },
    }[this.language].s;
  }

  ReportDescriptionLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Descripción", p: "Descripción detallada del reporte", g: "F" },
      en: { s: "Description", p: "Detailed report description" },
      pt: { s: "Descrição", p: "Descrição detalhada do relatório", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get ReportDescriptionLabel() {
    return this.ReportDescriptionLabelByLang();
  }

  get ReportDescriptionDescription() {
    return {
      es: {
        s: "Descripción del propósito, contenido y uso esperado del reporte.",
      },
      en: {
        s: "Description of the purpose, content, and expected use of the report.",
      },
      pt: {
        s: "Descrição do propósito, conteúdo e uso esperado do relatório.",
      },
    }[this.language].s;
  }

  // ReportType

  get ReportTypeLabels() {
    return {
      es: { s: "Tipo de Reporte", p: "Tipos de Reporte", g: "M" },
      en: { s: "Report Type", p: "Report Types", g: undefined },
      pt: { s: "Tipo de Relatório", p: "Tipos de Relatório", g: "M" },
    }[this.language];
  }

  get ReportTypeSingular() {
    return this.ReportTypeLabels.s;
  }

  get ReportTypePlural() {
    return this.ReportTypeLabels.p;
  }

  get ReportTypeGender() {
    return this.ReportTypeLabels.g;
  }

  get groupReportTypeLabel() {
    return {
      es: { s: "Tipo de Reporte", p: "Tipos de Reporte", g: "M" },
      en: { s: "Report Type", p: "Report Types" },
      pt: { s: "Tipo de Relatório", p: "Tipos de Relatório", g: "M" },
    }[this.language].s;
  }

  ReportTypeNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del tipo de reporte", g: "M" },
      en: { s: "Name", p: "Name of the report type" },
      pt: { s: "Nome", p: "Nome do tipo de relatório", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ReportTypeNameLabel() {
    return this.ReportTypeNameLabelByLang();
  }

  get ReportTypeNameDescription() {
    return {
      es: {
        s: "Nombre único para el tipo de reporte (ej. 'Regalías por Artista', 'Ventas Trimestrales').",
      },
      en: {
        s: "Unique name for the report type (e.g., 'Royalties by Artist', 'Quarterly Sales').",
      },
      pt: {
        s: "Nome exclusivo para o tipo de relatório (ex: 'Royalties por Artista', 'Vendas Trimestrais').",
      },
    }[this.language].s;
  }

  ReportTypeDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Descripción",
        p: "Descripción detallada del tipo de reporte",
        g: "F",
      },
      en: { s: "Description", p: "Detailed report type description" },
      pt: {
        s: "Descrição",
        p: "Descrição detalhada do tipo de relatório",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get ReportTypeDescriptionLabel() {
    return this.ReportTypeDescriptionLabelByLang();
  }

  get ReportTypeDescriptionDescription() {
    return {
      es: {
        s: "Descripción más amplia de lo que representa esta categoría de reportes.",
      },
      en: {
        s: "Broader description of what this category of reports represents.",
      },
      pt: {
        s: "Descrição mais ampla do que esta categoria de relatórios representa.",
      },
    }[this.language].s;
  }

  // UserNotification

  get UserNotificationLabels() {
    return {
      es: { s: "Notificación", p: "Notificaciones", g: "F" },
      en: { s: "Notification", p: "Notifications", g: undefined },
      pt: { s: "Notificação", p: "Notificações", g: "F" },
    }[this.language];
  }

  get UserNotificationSingular() {
    return this.UserNotificationLabels.s;
  }

  get UserNotificationPlural() {
    return this.UserNotificationLabels.p;
  }

  get UserNotificationGender() {
    return this.UserNotificationLabels.g;
  }

  get groupUserNotificationLabel() {
    return {
      es: { s: "Notificación", p: "Notificaciones", g: "F" },
      en: { s: "Notification", p: "Notifications" },
      pt: { s: "Notificação", p: "Notificações", g: "F" },
    }[this.language].s;
  }

  UserNotificationTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo", p: "Tipo de notificación", g: "M" },
      en: { s: "Type", p: "Notification type" },
      pt: { s: "Tipo", p: "Tipo de notificação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationTypeLabel() {
    return this.UserNotificationTypeLabelByLang();
  }

  UserNotificationPriorityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Prioridad", p: "Nivel de prioridad", g: "F" },
      en: { s: "Priority", p: "Priority level" },
      pt: { s: "Prioridade", p: "Nível de prioridade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationPriorityLabel() {
    return this.UserNotificationPriorityLabelByLang();
  }

  UserNotificationTitleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Título", p: "Título de la notificación", g: "M" },
      en: { s: "Title", p: "Notification title" },
      pt: { s: "Título", p: "Título da notificação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationTitleLabel() {
    return this.UserNotificationTitleLabelByLang();
  }

  get UserNotificationTitleDescription() {
    return {
      es: { s: "Título breve y descriptivo de la notificación." },
      en: { s: "Short and descriptive notification title." },
      pt: { s: "Título breve e descritivo da notificação." },
    }[this.language].s;
  }

  UserNotificationBodyLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Mensaje", p: "Cuerpo del mensaje", g: "M" },
      en: { s: "Message", p: "Message body" },
      pt: { s: "Mensagem", p: "Corpo da mensagem" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationBodyLabel() {
    return this.UserNotificationBodyLabelByLang();
  }

  get UserNotificationBodyDescription() {
    return {
      es: { s: "Contenido detallado de la notificación." },
      en: { s: "Detailed notification content." },
      pt: { s: "Conteúdo detalhado da notificação." },
    }[this.language].s;
  }

  UserNotificationActorUserLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Actor", p: "Usuario que generó la notificación", g: "M" },
      en: { s: "Actor", p: "User who generated the notification" },
      pt: { s: "Ator", p: "Usuário que gerou a notificação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationActorUserLabel() {
    return this.UserNotificationActorUserLabelByLang();
  }

  get UserNotificationActorUserDescription() {
    return {
      es: { s: "Usuario que originó la acción (null si es sistema)." },
      en: { s: "User who originated the action (null if system)." },
      pt: { s: "Usuário que originou a ação (null se for sistema)." },
    }[this.language].s;
  }

  UserNotificationObjectTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Objeto", p: "Tipo de entidad relacionada", g: "M" },
      en: { s: "Object Type", p: "Related entity type" },
      pt: { s: "Tipo de Objeto", p: "Tipo de entidade relacionada", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationObjectTypeLabel() {
    return this.UserNotificationObjectTypeLabelByLang();
  }

  get UserNotificationObjectTypeDescription() {
    return {
      es: {
        s: "Tipo de entidad relacionada: Issue, Document, Comment, Tenant, etc.",
      },
      en: { s: "Related entity type: Issue, Document, Comment, Tenant, etc." },
      pt: {
        s: "Tipo de entidade relacionada: Issue, Document, Comment, Tenant, etc.",
      },
    }[this.language].s;
  }

  UserNotificationObjectIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Objeto", p: "ID de la entidad relacionada", g: "M" },
      en: { s: "Object ID", p: "Related entity ID" },
      pt: { s: "ID de Objeto", p: "ID da entidade relacionada", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationObjectIdLabel() {
    return this.UserNotificationObjectIdLabelByLang();
  }

  get UserNotificationObjectIdDescription() {
    return {
      es: { s: "Identificador único de la entidad relacionada." },
      en: { s: "Unique identifier of the related entity." },
      pt: { s: "Identificador único da entidade relacionada." },
    }[this.language].s;
  }

  UserNotificationActionUrlLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "URL de Acción", p: "Enlace para navegar al contexto", g: "F" },
      en: { s: "Action URL", p: "Link to navigate to context" },
      pt: { s: "URL de Ação", p: "Link para navegar ao contexto", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationActionUrlLabel() {
    return this.UserNotificationActionUrlLabelByLang();
  }

  get UserNotificationActionUrlDescription() {
    return {
      es: {
        s: "URL relativa o absoluta para navegar al contexto de la notificación.",
      },
      en: {
        s: "Relative or absolute URL to navigate to the notification context.",
      },
      pt: {
        s: "URL relativa ou absoluta para navegar ao contexto da notificação.",
      },
    }[this.language].s;
  }

  UserNotificationReadAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Leída", p: "Fecha y hora de lectura", g: "F" },
      en: { s: "Read At", p: "Read date and time" },
      pt: { s: "Lida", p: "Data e hora de leitura", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationReadAtLabel() {
    return this.UserNotificationReadAtLabelByLang();
  }

  get UserNotificationReadAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que el usuario marcó la notificación como leída. Null = no leída.",
      },
      en: {
        s: "Date and time when user marked notification as read. Null = unread.",
      },
      pt: {
        s: "Data e hora em que o usuário marcou a notificação como lida. Null = não lida.",
      },
    }[this.language].s;
  }

  UserNotificationNotificationCreatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Creada", p: "Fecha y hora de creación", g: "F" },
      en: { s: "Created At", p: "Creation date and time" },
      pt: { s: "Criada", p: "Data e hora de criação", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationNotificationCreatedAtLabel() {
    return this.UserNotificationNotificationCreatedAtLabelByLang();
  }

  get UserNotificationNotificationCreatedAtDescription() {
    return {
      es: { s: "Fecha y hora de creación de la notificación." },
      en: { s: "Date and time when the notification was created." },
      pt: { s: "Data e hora de criação da notificação." },
    }[this.language].s;
  }

  UserNotificationArchivedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Archivada", p: "Fecha y hora de archivo", g: "F" },
      en: { s: "Archived At", p: "Archive date and time" },
      pt: { s: "Arquivada", p: "Data e hora de arquivo", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationArchivedAtLabel() {
    return this.UserNotificationArchivedAtLabelByLang();
  }

  get UserNotificationArchivedAtDescription() {
    return {
      es: { s: "Fecha y hora en que el usuario archivó la notificación." },
      en: { s: "Date and time when user archived the notification." },
      pt: { s: "Data e hora em que o usuário arquivou a notificação." },
    }[this.language].s;
  }

  UserNotificationMetadataLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Metadatos", p: "Datos adicionales en formato JSON", g: "M" },
      en: { s: "Metadata", p: "Additional data in JSON format" },
      pt: { s: "Metadados", p: "Dados adicionais em formato JSON", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationMetadataLabel() {
    return this.UserNotificationMetadataLabelByLang();
  }

  get UserNotificationMetadataDescription() {
    return {
      es: {
        s: "Información adicional flexible para diferentes tipos de notificación.",
      },
      en: {
        s: "Flexible additional information for different notification types.",
      },
      pt: {
        s: "Informação adicional flexível para diferentes tipos de notificação.",
      },
    }[this.language].s;
  }

  // UserNotificationPreference

  get UserNotificationPreferenceLabels() {
    return {
      es: {
        s: "Preferencia de Notificación",
        p: "Preferencias de Notificación",
        g: "F",
      },
      en: {
        s: "Notification Preference",
        p: "Notification Preferences",
        g: undefined,
      },
      pt: {
        s: "Preferência de Notificação",
        p: "Preferências de Notificação",
        g: "F",
      },
    }[this.language];
  }

  get UserNotificationPreferenceSingular() {
    return this.UserNotificationPreferenceLabels.s;
  }

  get UserNotificationPreferencePlural() {
    return this.UserNotificationPreferenceLabels.p;
  }

  get UserNotificationPreferenceGender() {
    return this.UserNotificationPreferenceLabels.g;
  }

  get groupUserNotificationPreferenceLabel() {
    return {
      es: {
        s: "Preferencia de Notificación",
        p: "Preferencias de Notificación",
        g: "F",
      },
      en: { s: "Notification Preference", p: "Notification Preferences" },
      pt: {
        s: "Preferência de Notificação",
        p: "Preferências de Notificação",
        g: "F",
      },
    }[this.language].s;
  }

  UserNotificationPreferenceNotificationTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Notificación",
        p: "Tipo de notificación a configurar",
        g: "M",
      },
      en: { s: "Notification Type", p: "Notification type to configure" },
      pt: {
        s: "Tipo de Notificação",
        p: "Tipo de notificação a configurar",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationPreferenceNotificationTypeLabel() {
    return this.UserNotificationPreferenceNotificationTypeLabelByLang();
  }

  UserNotificationPreferenceInAppEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "En App", p: "Mostrar notificaciones en la aplicación", g: "F" },
      en: { s: "In-App", p: "Show notifications in the application" },
      pt: { s: "No App", p: "Mostrar notificações no aplicativo", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationPreferenceInAppEnabledLabel() {
    return this.UserNotificationPreferenceInAppEnabledLabelByLang();
  }

  get UserNotificationPreferenceInAppEnabledDescription() {
    return {
      es: {
        s: "Si está habilitado, las notificaciones de este tipo se mostrarán en la campanita.",
      },
      en: {
        s: "If enabled, notifications of this type will be shown in the bell.",
      },
      pt: {
        s: "Se habilitado, as notificações deste tipo serão mostradas no sininho.",
      },
    }[this.language].s;
  }

  UserNotificationPreferenceEmailEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Email", p: "Enviar notificaciones por email", g: "M" },
      en: { s: "Email", p: "Send notifications by email" },
      pt: { s: "Email", p: "Enviar notificações por email", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationPreferenceEmailEnabledLabel() {
    return this.UserNotificationPreferenceEmailEnabledLabelByLang();
  }

  get UserNotificationPreferenceEmailEnabledDescription() {
    return {
      es: {
        s: "Si está habilitado, las notificaciones de este tipo se enviarán por correo electrónico.",
      },
      en: {
        s: "If enabled, notifications of this type will be sent by email.",
      },
      pt: {
        s: "Se habilitado, as notificações deste tipo serão enviadas por email.",
      },
    }[this.language].s;
  }

  UserNotificationPreferenceEmailFrequencyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Frecuencia de Email",
        p: "Con qué frecuencia enviar emails",
        g: "F",
      },
      en: { s: "Email Frequency", p: "How often to send emails" },
      pt: {
        s: "Frequência de Email",
        p: "Com que frequência enviar emails",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get UserNotificationPreferenceEmailFrequencyLabel() {
    return this.UserNotificationPreferenceEmailFrequencyLabelByLang();
  }

  // AlertRule

  get AlertRuleLabels() {
    return {
      es: { s: "Regla de Alerta", p: "Reglas de Alerta", g: "F" },
      en: { s: "Alert Rule", p: "Alert Rules", g: undefined },
      pt: { s: "Regra de Alerta", p: "Regras de Alerta", g: "F" },
    }[this.language];
  }

  get AlertRuleSingular() {
    return this.AlertRuleLabels.s;
  }

  get AlertRulePlural() {
    return this.AlertRuleLabels.p;
  }

  get AlertRuleGender() {
    return this.AlertRuleLabels.g;
  }

  get groupAlertRuleLabel() {
    return {
      es: { s: "Regla de Alerta", p: "Reglas de Alerta", g: "F" },
      en: { s: "Alert Rule", p: "Alert Rules" },
      pt: { s: "Regra de Alerta", p: "Regras de Alerta", g: "F" },
    }[this.language].s;
  }

  AlertRuleAlertTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Tipo de Alerta",
        p: "Identificador del evaluador registrado",
        g: "M",
      },
      en: { s: "Alert Type", p: "Registered evaluator id" },
      pt: {
        s: "Tipo de Alerta",
        p: "Identificador do avaliador registrado",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AlertRuleAlertTypeLabel() {
    return this.AlertRuleAlertTypeLabelByLang();
  }

  get AlertRuleAlertTypeDescription() {
    return {
      es: {
        s: "Id del tipo de alerta (uno de los registrados en el sistema; ver el selector del formulario).",
      },
      en: {
        s: "Alert type id (one of those registered in the system; see the form selector).",
      },
      pt: {
        s: "Id do tipo de alerta (um dos registrados no sistema; ver o seletor do formulário).",
      },
    }[this.language].s;
  }

  AlertRuleEnabledLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Habilitada", p: "Si la regla se evalúa", g: "F" },
      en: { s: "Enabled", p: "Whether the rule is evaluated" },
      pt: { s: "Habilitada", p: "Se a regra é avaliada", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AlertRuleEnabledLabel() {
    return this.AlertRuleEnabledLabelByLang();
  }

  AlertRuleParamsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Parámetros", p: "Parámetros según el tipo de alerta", g: "M" },
      en: { s: "Parameters", p: "Parameters per alert type" },
      pt: {
        s: "Parâmetros",
        p: "Parâmetros conforme o tipo de alerta",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AlertRuleParamsLabel() {
    return this.AlertRuleParamsLabelByLang();
  }

  get AlertRuleParamsDescription() {
    return {
      es: {
        s: "Parámetros del tipo, ej. days=7 o threshold=5. El esquema lo declara el tipo (ver el selector).",
      },
      en: {
        s: "Type parameters, e.g. days=7 or threshold=5. The schema is declared by the type (see selector).",
      },
      pt: {
        s: "Parâmetros do tipo, ex. days=7 ou threshold=5. O esquema é declarado pelo tipo (ver seletor).",
      },
    }[this.language].s;
  }

  AlertRuleLastEvaluatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Última Evaluación",
        p: "Cuándo el motor evaluó por última vez",
        g: "F",
      },
      en: { s: "Last Evaluated", p: "When the engine last evaluated" },
      pt: {
        s: "Última Avaliação",
        p: "Quando o motor avaliou pela última vez",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AlertRuleLastEvaluatedAtLabel() {
    return this.AlertRuleLastEvaluatedAtLabelByLang();
  }

  // AlertSubscription

  get AlertSubscriptionLabels() {
    return {
      es: { s: "Suscripción a Alerta", p: "Suscripciones a Alertas", g: "F" },
      en: { s: "Alert Subscription", p: "Alert Subscriptions", g: undefined },
      pt: { s: "Inscrição em Alerta", p: "Inscrições em Alertas", g: "F" },
    }[this.language];
  }

  get AlertSubscriptionSingular() {
    return this.AlertSubscriptionLabels.s;
  }

  get AlertSubscriptionPlural() {
    return this.AlertSubscriptionLabels.p;
  }

  get AlertSubscriptionGender() {
    return this.AlertSubscriptionLabels.g;
  }

  get groupAlertSubscriptionLabel() {
    return {
      es: { s: "Suscripción a Alerta", p: "Suscripciones a Alertas", g: "F" },
      en: { s: "Alert Subscription", p: "Alert Subscriptions" },
      pt: { s: "Inscrição em Alerta", p: "Inscrições em Alertas", g: "F" },
    }[this.language].s;
  }

  AlertSubscriptionAlertRuleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Regla de Alerta", p: "Regla a la que se suscribe", g: "F" },
      en: { s: "Alert Rule", p: "Rule subscribed to" },
      pt: { s: "Regra de Alerta", p: "Regra à qual se inscreve", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AlertSubscriptionAlertRuleLabel() {
    return this.AlertSubscriptionAlertRuleLabelByLang();
  }

  AlertSubscriptionEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Activa", p: "Si recibe esta alerta", g: "F" },
      en: { s: "Active", p: "Whether to receive this alert" },
      pt: { s: "Ativa", p: "Se recebe este alerta", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AlertSubscriptionEnabledLabel() {
    return this.AlertSubscriptionEnabledLabelByLang();
  }

  // EmailVerification

  get EmailVerificationLabels() {
    return {
      es: { s: "Verificación de Email", p: "Verificaciones de Email", g: "F" },
      en: { s: "Email Verification", p: "Email Verifications", g: undefined },
      pt: { s: "Verificação de Email", p: "Verificações de Email", g: "F" },
    }[this.language];
  }

  get EmailVerificationSingular() {
    return this.EmailVerificationLabels.s;
  }

  get EmailVerificationPlural() {
    return this.EmailVerificationLabels.p;
  }

  get EmailVerificationGender() {
    return this.EmailVerificationLabels.g;
  }

  get groupEmailVerificationLabel() {
    return {
      es: { s: "Verificación de Email", p: "Verificaciones de Email", g: "F" },
      en: { s: "Email Verification", p: "Email Verifications" },
      pt: { s: "Verificação de Email", p: "Verificações de Email", g: "F" },
    }[this.language].s;
  }

  EmailVerificationEmailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Email", p: "Correo a verificar" },
      en: { s: "Email", p: "Email to verify" },
      pt: { s: "Email", p: "Email a verificar" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationEmailLabel() {
    return this.EmailVerificationEmailLabelByLang();
  }

  get EmailVerificationEmailDescription() {
    return {
      es: { s: "Dirección de correo electrónico a verificar." },
      en: { s: "Email address to verify." },
      pt: { s: "Endereço de e-mail a verificar." },
    }[this.language].s;
  }

  EmailVerificationStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Estado", p: "Estado de verificación" },
      en: { s: "Status", p: "Verification status" },
      pt: { s: "Status", p: "Status da verificação" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationStatusLabel() {
    return this.EmailVerificationStatusLabelByLang();
  }

  get EmailVerificationStatusDescription() {
    return {
      es: { s: "Estado actual del proceso de verificación." },
      en: { s: "Current status of the verification process." },
      pt: { s: "Status atual do processo de verificação." },
    }[this.language].s;
  }

  EmailVerificationScopeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Alcance", p: "Qué pide el enlace vigente" },
      en: { s: "Scope", p: "What the current link asks for" },
      pt: { s: "Escopo", p: "O que o link vigente pede" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationScopeLabel() {
    return this.EmailVerificationScopeLabelByLang();
  }

  get EmailVerificationScopeDescription() {
    return {
      es: {
        s: "Qué pide el último envío: verificar solo el email o, además, validar los datos del registro origen. Vacío en los registros anteriores: se deduce de sourceEntity y sourceRecordId.",
      },
      en: {
        s: "What the last send asks for: verify only the email or also validate the data of the source record. Empty in older records: it is deduced from sourceEntity and sourceRecordId.",
      },
      pt: {
        s: "O que o último envio pede: verificar só o e-mail ou também validar os dados do registro de origem. Vazio nos registros anteriores: é deduzido de sourceEntity e sourceRecordId.",
      },
    }[this.language].s;
  }

  EmailVerificationExpiresAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Expira", p: "Fecha de expiración del enlace" },
      en: { s: "Expires", p: "Link expiration date" },
      pt: { s: "Expira", p: "Data de expiração do link" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationExpiresAtLabel() {
    return this.EmailVerificationExpiresAtLabelByLang();
  }

  get EmailVerificationExpiresAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que el enlace de verificación expira (72 horas desde el envío).",
      },
      en: {
        s: "Date and time when the verification link expires (72 hours from sending).",
      },
      pt: {
        s: "Data e hora em que o link de verificação expira (72 horas após o envio).",
      },
    }[this.language].s;
  }

  EmailVerificationSentAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Enviado", p: "Fecha de envío" },
      en: { s: "Sent", p: "Send date" },
      pt: { s: "Enviado", p: "Data de envio" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationSentAtLabel() {
    return this.EmailVerificationSentAtLabelByLang();
  }

  get EmailVerificationSentAtDescription() {
    return {
      es: { s: "Fecha y hora del primer envío del email de verificación." },
      en: { s: "Date and time of the first verification email send." },
      pt: { s: "Data e hora do primeiro envio do e-mail de verificação." },
    }[this.language].s;
  }

  EmailVerificationVerifiedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Verificado", p: "Fecha de verificación" },
      en: { s: "Verified", p: "Verification date" },
      pt: { s: "Verificado", p: "Data de verificação" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationVerifiedAtLabel() {
    return this.EmailVerificationVerifiedAtLabelByLang();
  }

  get EmailVerificationVerifiedAtDescription() {
    return {
      es: { s: "Fecha y hora en que el contacto confirmó su email." },
      en: { s: "Date and time when the contact confirmed their email." },
      pt: { s: "Data e hora em que o contato confirmou seu e-mail." },
    }[this.language].s;
  }

  EmailVerificationSentCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Envíos", p: "Cantidad de envíos" },
      en: { s: "Send count", p: "Number of sends" },
      pt: { s: "Envios", p: "Quantidade de envios" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationSentCountLabel() {
    return this.EmailVerificationSentCountLabelByLang();
  }

  get EmailVerificationSentCountDescription() {
    return {
      es: {
        s: "Cantidad de veces que se envió el email de verificación. Máximo 3 por período de 24 horas.",
      },
      en: {
        s: "Number of times the verification email was sent. Maximum 3 per 24-hour period.",
      },
      pt: {
        s: "Quantidade de vezes que o e-mail de verificação foi enviado. Máximo 3 por período de 24 horas.",
      },
    }[this.language].s;
  }

  EmailVerificationLastSentAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Último envío", p: "Fecha del último envío" },
      en: { s: "Last sent", p: "Last send date" },
      pt: { s: "Último envio", p: "Data do último envio" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationLastSentAtLabel() {
    return this.EmailVerificationLastSentAtLabelByLang();
  }

  get EmailVerificationLastSentAtDescription() {
    return {
      es: { s: "Fecha y hora del último reenvío del email de verificación." },
      en: { s: "Date and time of the last verification email resend." },
      pt: { s: "Data e hora do último reenvio do e-mail de verificação." },
    }[this.language].s;
  }

  EmailVerificationRequestedByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Solicitado por", p: "Usuario que solicitó la verificación" },
      en: { s: "Requested by", p: "User who requested verification" },
      pt: { s: "Solicitado por", p: "Usuário que solicitou a verificação" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationRequestedByLabel() {
    return this.EmailVerificationRequestedByLabelByLang();
  }

  get EmailVerificationRequestedByDescription() {
    return {
      es: { s: "Usuario operador que inició el proceso de verificación." },
      en: { s: "Operator user who initiated the verification process." },
      pt: { s: "Usuário operador que iniciou o processo de verificação." },
    }[this.language].s;
  }

  EmailVerificationSourceEntityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Entidad origen",
        p: "Entidad desde la cual se solicitó la verificación",
      },
      en: {
        s: "Source entity",
        p: "Entity from which verification was requested",
      },
      pt: {
        s: "Entidade origem",
        p: "Entidade da qual a verificação foi solicitada",
      },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationSourceEntityLabel() {
    return this.EmailVerificationSourceEntityLabelByLang();
  }

  get EmailVerificationSourceEntityDescription() {
    return {
      es: {
        s: "Nombre de la entidad asociada a esta verificación (ej. AuthorComposer). Permite vincular la verificación con validación de datos externa.",
      },
      en: {
        s: "Name of the entity associated with this verification (e.g. AuthorComposer). Links verification with external data validation.",
      },
      pt: {
        s: "Nome da entidade associada a esta verificação (ex. AuthorComposer). Vincula a verificação com validação de dados externa.",
      },
    }[this.language].s;
  }

  EmailVerificationSourceRecordIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID del registro origen" },
      en: { s: "Source record ID" },
      pt: { s: "ID do registro origem" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationSourceRecordIdLabel() {
    return this.EmailVerificationSourceRecordIdLabelByLang();
  }

  get EmailVerificationSourceRecordIdDescription() {
    return {
      es: {
        s: "Identificador del registro específico de la entidad origen cuya información se solicita validar.",
      },
      en: {
        s: "Identifier of the specific source entity record whose information is being validated.",
      },
      pt: {
        s: "Identificador do registro específico da entidade origem cuja informação está sendo validada.",
      },
    }[this.language].s;
  }

  EmailVerificationValidationChangesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Cambios de validación",
        p: "Diff de datos modificados por el contacto",
      },
      en: {
        s: "Validation changes",
        p: "Diff of data modified by the contact",
      },
      pt: {
        s: "Alterações de validação",
        p: "Diff de dados modificados pelo contato",
      },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationValidationChangesLabel() {
    return this.EmailVerificationValidationChangesLabelByLang();
  }

  get EmailVerificationValidationChangesDescription() {
    return {
      es: {
        s: "Registro JSON con los cambios realizados por el contacto externo: [{field, oldValue, newValue}]. Vacío si solo verificó sin modificar.",
      },
      en: {
        s: "JSON record of changes made by the external contact: [{field, oldValue, newValue}]. Empty if verified without modifying.",
      },
      pt: {
        s: "Registro JSON com as alterações feitas pelo contato externo: [{field, oldValue, newValue}]. Vazio se verificou sem modificar.",
      },
    }[this.language].s;
  }

  EmailVerificationValidationSubmittedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de validación" },
      en: { s: "Validation date" },
      pt: { s: "Data de validação" },
    }[lang ? lang : this.language].s;
  }

  get EmailVerificationValidationSubmittedAtLabel() {
    return this.EmailVerificationValidationSubmittedAtLabelByLang();
  }

  get EmailVerificationValidationSubmittedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que el contacto externo envió el formulario de validación de datos.",
      },
      en: {
        s: "Date and time when the external contact submitted the data validation form.",
      },
      pt: {
        s: "Data e hora em que o contato externo enviou o formulário de validação de dados.",
      },
    }[this.language].s;
  }

  // ShowcaseWorkflowState

  get ShowcaseWorkflowStateLabels() {
    return {
      es: {
        s: "Estado del Ciclo de la Muestra",
        p: "Estados del Ciclo de la Muestra",
        g: "M",
      },
      en: {
        s: "Showcase Lifecycle State",
        p: "Showcase Lifecycle States",
        g: undefined,
      },
      pt: {
        s: "Estado do Ciclo da Amostra",
        p: "Estados do Ciclo da Amostra",
        g: "M",
      },
    }[this.language];
  }

  get ShowcaseWorkflowStateSingular() {
    return this.ShowcaseWorkflowStateLabels.s;
  }

  get ShowcaseWorkflowStatePlural() {
    return this.ShowcaseWorkflowStateLabels.p;
  }

  get ShowcaseWorkflowStateGender() {
    return this.ShowcaseWorkflowStateLabels.g;
  }

  get groupShowcaseWorkflowStateLabel() {
    return {
      es: {
        s: "Estado del Ciclo de la Muestra",
        p: "Estados del Ciclo de la Muestra",
        g: "M",
      },
      en: { s: "Showcase Lifecycle State", p: "Showcase Lifecycle States" },
      pt: {
        s: "Estado do Ciclo da Amostra",
        p: "Estados do Ciclo da Amostra",
        g: "M",
      },
    }[this.language].s;
  }

  ShowcaseWorkflowStateNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nombre del Estado",
        p: "Nombre del estado en el ciclo",
        g: "M",
      },
      en: { s: "State Name", p: "Name of the state in the lifecycle" },
      pt: { s: "Nome do Estado", p: "Nome do estado no ciclo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseWorkflowStateNameLabel() {
    return this.ShowcaseWorkflowStateNameLabelByLang();
  }

  // ShowcaseCategory

  get ShowcaseCategoryLabels() {
    return {
      es: { s: "Categoría de Muestra", p: "Categorías de Muestra", g: "F" },
      en: { s: "Showcase Category", p: "Showcase Categories", g: undefined },
      pt: { s: "Categoria de Amostra", p: "Categorias de Amostra", g: "F" },
    }[this.language];
  }

  get ShowcaseCategorySingular() {
    return this.ShowcaseCategoryLabels.s;
  }

  get ShowcaseCategoryPlural() {
    return this.ShowcaseCategoryLabels.p;
  }

  get ShowcaseCategoryGender() {
    return this.ShowcaseCategoryLabels.g;
  }

  get groupShowcaseCategoryLabel() {
    return {
      es: { s: "Categoría de Muestra", p: "Categorías de Muestra", g: "F" },
      en: { s: "Showcase Category", p: "Showcase Categories" },
      pt: { s: "Categoria de Amostra", p: "Categorias de Amostra", g: "F" },
    }[this.language].s;
  }

  ShowcaseCategoryCodeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código", p: "Identificador corto", g: "M" },
      en: { s: "Code", p: "Short identifier" },
      pt: { s: "Código", p: "Identificador curto", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseCategoryCodeLabel() {
    return this.ShowcaseCategoryCodeLabelByLang();
  }

  ShowcaseCategoryNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre visible", g: "M" },
      en: { s: "Name", p: "Display name" },
      pt: { s: "Nome", p: "Nome visível", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseCategoryNameLabel() {
    return this.ShowcaseCategoryNameLabelByLang();
  }

  // ShowcaseLine

  get ShowcaseLineLabels() {
    return {
      es: { s: "Renglón", p: "Renglones", g: "M" },
      en: { s: "Line", p: "Lines", g: undefined },
      pt: { s: "Linha", p: "Linhas", g: "M" },
    }[this.language];
  }

  get ShowcaseLineSingular() {
    return this.ShowcaseLineLabels.s;
  }

  get ShowcaseLinePlural() {
    return this.ShowcaseLineLabels.p;
  }

  get ShowcaseLineGender() {
    return this.ShowcaseLineLabels.g;
  }

  get groupShowcaseLineLabel() {
    return {
      es: { s: "Renglón", p: "Renglones", g: "M" },
      en: { s: "Line", p: "Lines" },
      pt: { s: "Linha", p: "Linhas", g: "M" },
    }[this.language].s;
  }

  ShowcaseLineConceptLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Concepto", p: "Descripción del renglón", g: "M" },
      en: { s: "Concept", p: "Line description" },
      pt: { s: "Conceito", p: "Descrição da linha", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseLineConceptLabel() {
    return this.ShowcaseLineConceptLabelByLang();
  }

  ShowcaseLineQuantityLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Cantidad", p: "Unidades", g: "F" },
      en: { s: "Quantity", p: "Units" },
      pt: { s: "Quantidade", p: "Unidades", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseLineQuantityLabel() {
    return this.ShowcaseLineQuantityLabelByLang();
  }

  ShowcaseLineUnitPriceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Precio Unitario", p: "Importe por unidad", g: "M" },
      en: { s: "Unit Price", p: "Amount per unit" },
      pt: { s: "Preço Unitário", p: "Valor por unidade", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseLineUnitPriceLabel() {
    return this.ShowcaseLineUnitPriceLabelByLang();
  }

  ShowcaseLineNoteLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nota", p: "Texto libre del renglón", g: "F" },
      en: { s: "Note", p: "Free text for the line" },
      pt: { s: "Nota", p: "Texto livre da linha", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get ShowcaseLineNoteLabel() {
    return this.ShowcaseLineNoteLabelByLang();
  }

  // DesignShowcase

  get DesignShowcaseLabels() {
    return {
      es: { s: "Muestra de Diseño", p: "Muestras de Diseño", g: "F" },
      en: { s: "Design Showcase", p: "Design Showcases", g: undefined },
      pt: { s: "Amostra de Design", p: "Amostras de Design", g: "F" },
    }[this.language];
  }

  get DesignShowcaseSingular() {
    return this.DesignShowcaseLabels.s;
  }

  get DesignShowcasePlural() {
    return this.DesignShowcaseLabels.p;
  }

  get DesignShowcaseGender() {
    return this.DesignShowcaseLabels.g;
  }

  get groupDesignShowcaseLabel() {
    return {
      es: { s: "Muestra de Diseño", p: "Muestras de Diseño", g: "F" },
      en: { s: "Design Showcase", p: "Design Showcases" },
      pt: { s: "Amostra de Design", p: "Amostras de Design", g: "F" },
    }[this.language].s;
  }

  get groupDesignShowcaseIdentificationLabel() {
    return {
      es: { s: "Identificación", p: "F" },
      en: { s: "Identification" },
      pt: { s: "Identificação", p: "F" },
    }[this.language].s;
  }

  get groupDesignShowcaseClassificationLabel() {
    return {
      es: { s: "Clasificación", p: "F" },
      en: { s: "Classification" },
      pt: { s: "Classificação", p: "F" },
    }[this.language].s;
  }

  get groupDesignShowcaseNumbersLabel() {
    return {
      es: { s: "Números", p: "M" },
      en: { s: "Numbers" },
      pt: { s: "Números", p: "M" },
    }[this.language].s;
  }

  get groupDesignShowcaseDatesLabel() {
    return {
      es: { s: "Fechas", p: "F" },
      en: { s: "Dates" },
      pt: { s: "Datas", p: "F" },
    }[this.language].s;
  }

  get groupDesignShowcaseFlagsLabel() {
    return {
      es: { s: "Marcas", p: "F" },
      en: { s: "Flags" },
      pt: { s: "Marcas", p: "F" },
    }[this.language].s;
  }

  get groupDesignShowcaseContactLabel() {
    return {
      es: { s: "Contacto", p: "M" },
      en: { s: "Contact" },
      pt: { s: "Contato", p: "M" },
    }[this.language].s;
  }

  get groupDesignShowcaseDocumentsLabel() {
    return {
      es: { s: "Documentos", p: "M" },
      en: { s: "Documents" },
      pt: { s: "Documentos", p: "M" },
    }[this.language].s;
  }

  get groupDesignShowcaseRichContentLabel() {
    return {
      es: { s: "Contenido", p: "M" },
      en: { s: "Content" },
      pt: { s: "Conteúdo", p: "M" },
    }[this.language].s;
  }

  get groupDesignShowcaseAdvancedLabel() {
    return {
      es: { s: "Avanzado", p: "M" },
      en: { s: "Advanced" },
      pt: { s: "Avançado", p: "M" },
    }[this.language].s;
  }

  DesignShowcaseCodeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código", p: "Identificador único", g: "M" },
      en: { s: "Code", p: "Unique identifier" },
      pt: { s: "Código", p: "Identificador único", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseCodeLabel() {
    return this.DesignShowcaseCodeLabelByLang();
  }

  get DesignShowcaseCodeDescription() {
    return {
      es: {
        s: "Identificador único de la muestra. Campo de texto simple, requerido y único.",
      },
      en: {
        s: "Unique identifier of the showcase. Plain text field, required and unique.",
      },
      pt: {
        s: "Identificador único da amostra. Campo de texto simples, obrigatório e único.",
      },
    }[this.language].s;
  }

  DesignShowcaseNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre visible", g: "M" },
      en: { s: "Name", p: "Display name" },
      pt: { s: "Nome", p: "Nome visível", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseNameLabel() {
    return this.DesignShowcaseNameLabelByLang();
  }

  DesignShowcaseSummaryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Resumen", p: "Texto de varias líneas", g: "M" },
      en: { s: "Summary", p: "Multi-line text" },
      pt: { s: "Resumo", p: "Texto de várias linhas", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseSummaryLabel() {
    return this.DesignShowcaseSummaryLabelByLang();
  }

  get DesignShowcaseSummaryDescription() {
    return {
      es: {
        s: "Texto largo. Es donde se ve si el interlineado del área de texto acompaña al del resto.",
      },
      en: {
        s: "Long text. This is where you see whether the textarea line height matches the rest.",
      },
      pt: {
        s: "Texto longo. É onde se vê se o entrelinhamento da área de texto acompanha o resto.",
      },
    }[this.language].s;
  }

  DesignShowcaseStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "Estado de la muestra", g: "M" },
      en: { s: "Status", p: "Showcase status" },
      pt: { s: "Estado", p: "Estado da amostra", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseStatusLabel() {
    return this.DesignShowcaseStatusLabelByLang();
  }

  DesignShowcaseCategoryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Categoría", p: "Categoría principal", g: "F" },
      en: { s: "Category", p: "Main category" },
      pt: { s: "Categoria", p: "Categoria principal", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseCategoryLabel() {
    return this.DesignShowcaseCategoryLabelByLang();
  }

  get DesignShowcaseCategoryDescription() {
    return {
      es: {
        s: "Referencia simple. Muestra el selector con alta rápida y acción de ver.",
      },
      en: {
        s: "Simple reference. Shows the picker with quick create and view action.",
      },
      pt: {
        s: "Referência simples. Mostra o seletor com criação rápida e ação de ver.",
      },
    }[this.language].s;
  }

  DesignShowcaseRelatedCategoriesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Categorías Relacionadas", p: "Selección múltiple", g: "F" },
      en: { s: "Related Categories", p: "Multiple selection" },
      pt: { s: "Categorias Relacionadas", p: "Seleção múltipla", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseRelatedCategoriesLabel() {
    return this.DesignShowcaseRelatedCategoriesLabelByLang();
  }

  DesignShowcaseAppliedStatusesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Estados Aplicados", p: "Enum múltiple, en casilleros", g: "M" },
      en: { s: "Applied Statuses", p: "Multiple enum, as checkboxes" },
      pt: { s: "Estados Aplicados", p: "Enum múltiplo, em caixas", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseAppliedStatusesLabel() {
    return this.DesignShowcaseAppliedStatusesLabelByLang();
  }

  DesignShowcaseLifecycleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Ciclo", p: "Estado del ciclo de vida", g: "M" },
      en: { s: "Lifecycle", p: "Lifecycle state" },
      pt: { s: "Ciclo", p: "Estado do ciclo de vida", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseLifecycleLabel() {
    return this.DesignShowcaseLifecycleLabelByLang();
  }

  get DesignShowcaseLifecycleDescription() {
    return {
      es: {
        s: "Lo que dispara el gráfico de estados y los botones de transición. Es otra superficie que el desplegable de arriba: un enum es un valor, un workflow es un camino.",
      },
      en: {
        s: "What drives the state graph and the transition buttons. A different surface from the dropdown above: an enum is a value, a workflow is a path.",
      },
      pt: {
        s: "O que dispara o gráfico de estados e os botões de transição. É outra superfície que a lista acima: um enum é um valor, um workflow é um caminho.",
      },
    }[this.language].s;
  }

  get DesignShowcaseLifecycleTransitionLabels(): Record<string, string> {
    return {
      submit: {
        es: { s: "Enviar a Revisión", p: "M" },
        en: { s: "Submit for Review" },
        pt: { s: "Enviar para Revisão", p: "M" },
      }[this.language].s,
      approve: {
        es: { s: "Aprobar", p: "F" },
        en: { s: "Approve" },
        pt: { s: "Aprovar", p: "F" },
      }[this.language].s,
      reject: {
        es: { s: "Rechazar", p: "M" },
        en: { s: "Reject" },
        pt: { s: "Rejeitar", p: "M" },
      }[this.language].s,
      fail: {
        es: { s: "Marcar con Error", p: "M" },
        en: { s: "Mark as Failed" },
        pt: { s: "Marcar com Erro", p: "M" },
      }[this.language].s,
      retry: {
        es: { s: "Reintentar", p: "M" },
        en: { s: "Retry" },
        pt: { s: "Tentar Novamente", p: "M" },
      }[this.language].s,
      close: {
        es: { s: "Cerrar", p: "M" },
        en: { s: "Close" },
        pt: { s: "Fechar", p: "M" },
      }[this.language].s,
      reopen: {
        es: { s: "Reabrir", p: "M" },
        en: { s: "Reopen" },
        pt: { s: "Reabrir", p: "M" },
      }[this.language].s,
    };
  }

  get DesignShowcaseLifecycleStateLabels(): Record<string, string> {
    return {
      pending: {
        es: { s: "Pendiente", p: "F" },
        en: { s: "Pending" },
        pt: { s: "Pendente", p: "F" },
      }[this.language].s,
      inReview: {
        es: { s: "En Revisión", p: "F" },
        en: { s: "In Review" },
        pt: { s: "Em Revisão", p: "F" },
      }[this.language].s,
      approved: {
        es: { s: "Aprobada", p: "F" },
        en: { s: "Approved" },
        pt: { s: "Aprovada", p: "F" },
      }[this.language].s,
      rejected: {
        es: { s: "Rechazada", p: "F" },
        en: { s: "Rejected" },
        pt: { s: "Rejeitada", p: "F" },
      }[this.language].s,
      failed: {
        es: { s: "Con Error", p: "M" },
        en: { s: "Failed" },
        pt: { s: "Com Erro", p: "M" },
      }[this.language].s,
      closed: {
        es: { s: "Cerrada", p: "F" },
        en: { s: "Closed" },
        pt: { s: "Fechada", p: "F" },
      }[this.language].s,
    };
  }

  DesignShowcaseQuantityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Cantidad", p: "Entero con tope", g: "F" },
      en: { s: "Quantity", p: "Integer with bounds" },
      pt: { s: "Quantidade", p: "Inteiro com limite", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseQuantityLabel() {
    return this.DesignShowcaseQuantityLabelByLang();
  }

  DesignShowcaseDurationInYearsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Duración", p: "Entero como desplegable con unidad", g: "F" },
      en: { s: "Duration", p: "Integer as a dropdown with unit" },
      pt: {
        s: "Duração",
        p: "Inteiro como lista suspensa com unidade",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseDurationInYearsLabel() {
    return this.DesignShowcaseDurationInYearsLabelByLang();
  }

  get DesignShowcaseDurationInYearsNullText() {
    return {
      es: { s: "Sin límite", p: "No se define una duración" },
      en: { s: "No limit", p: "No duration is defined" },
      pt: { s: "Sem limite", p: "Não se define uma duração" },
    }[this.language].s;
  }

  get DesignShowcaseDurationInYearsDescription() {
    return {
      es: {
        s: "El dato sigue siendo entero; lo que cambia es cómo se pide. Con su texto para el vacío.",
      },
      en: {
        s: "The data is still an integer; what changes is how it is asked for. With its empty-value text.",
      },
      pt: {
        s: "O dado continua sendo inteiro; o que muda é como se pede. Com seu texto para o vazio.",
      },
    }[this.language].s;
  }

  DesignShowcaseAmountLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Importe", p: "Decimal con formato", g: "M" },
      en: { s: "Amount", p: "Formatted decimal" },
      pt: { s: "Valor", p: "Decimal com formato", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseAmountLabel() {
    return this.DesignShowcaseAmountLabelByLang();
  }

  get DesignShowcaseAmountDescription() {
    return {
      es: {
        s: "Es donde se ve si la celda numérica de la grilla queda alineada a la derecha.",
      },
      en: {
        s: "This is where you see whether the numeric grid cell stays right-aligned.",
      },
      pt: {
        s: "É onde se vê se a célula numérica da grade fica alinhada à direita.",
      },
    }[this.language].s;
  }

  DesignShowcaseRatioLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Proporción", p: "Decimal sin formato", g: "F" },
      en: { s: "Ratio", p: "Unformatted decimal" },
      pt: { s: "Proporção", p: "Decimal sem formato", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseRatioLabel() {
    return this.DesignShowcaseRatioLabelByLang();
  }

  DesignShowcaseStartDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Inicio", p: "Solo fecha", g: "F" },
      en: { s: "Start Date", p: "Date only" },
      pt: { s: "Data de Início", p: "Somente data", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseStartDateLabel() {
    return this.DesignShowcaseStartDateLabelByLang();
  }

  DesignShowcaseExpiresAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Vence", p: "Fecha y hora", g: "M" },
      en: { s: "Expires", p: "Date and time" },
      pt: { s: "Vence", p: "Data e hora", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseExpiresAtLabel() {
    return this.DesignShowcaseExpiresAtLabelByLang();
  }

  DesignShowcaseRegisteredAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Registrado", p: "Se fija al crear y no se edita", g: "M" },
      en: { s: "Registered", p: "Set on create, not editable" },
      pt: { s: "Registrado", p: "Definido ao criar, não editável", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseRegisteredAtLabel() {
    return this.DesignShowcaseRegisteredAtLabelByLang();
  }

  get DesignShowcaseRegisteredAtDescription() {
    return {
      es: {
        s: "Existe para ver cómo se rinde un campo que está en el alta y no en la edición.",
      },
      en: {
        s: "Exists to see how a field that is in create but not in update renders.",
      },
      pt: {
        s: "Existe para ver como se renderiza um campo que está na criação e não na edição.",
      },
    }[this.language].s;
  }

  DesignShowcaseActiveLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Activa", p: "Interruptor", g: "F" },
      en: { s: "Active", p: "Switch" },
      pt: { s: "Ativa", p: "Interruptor", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseActiveLabel() {
    return this.DesignShowcaseActiveLabelByLang();
  }

  DesignShowcaseHighlightedLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Destacada",
        p: "Segundo interruptor, para ver la separación",
        g: "F",
      },
      en: { s: "Highlighted", p: "Second switch, to see the spacing" },
      pt: {
        s: "Destacada",
        p: "Segundo interruptor, para ver o espaçamento",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseHighlightedLabel() {
    return this.DesignShowcaseHighlightedLabelByLang();
  }

  DesignShowcasePhoneLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Teléfono", p: "Control propio", g: "M" },
      en: { s: "Phone", p: "Dedicated control" },
      pt: { s: "Telefone", p: "Controle próprio", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcasePhoneLabel() {
    return this.DesignShowcasePhoneLabelByLang();
  }

  DesignShowcaseTaxIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Identificación Fiscal", p: "Campo con máscara", g: "F" },
      en: { s: "Tax ID", p: "Masked field" },
      pt: { s: "Identificação Fiscal", p: "Campo com máscara", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseTaxIdLabel() {
    return this.DesignShowcaseTaxIdLabelByLang();
  }

  DesignShowcaseAttachmentLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Adjunto", p: "Archivo acotado a PDF", g: "M" },
      en: { s: "Attachment", p: "File restricted to PDF" },
      pt: { s: "Anexo", p: "Arquivo restrito a PDF", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseAttachmentLabel() {
    return this.DesignShowcaseAttachmentLabelByLang();
  }

  DesignShowcaseAnyFileLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Archivo", p: "Sin restricción de tipo", g: "M" },
      en: { s: "File", p: "No type restriction" },
      pt: { s: "Arquivo", p: "Sem restrição de tipo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseAnyFileLabel() {
    return this.DesignShowcaseAnyFileLabelByLang();
  }

  DesignShowcaseAvatarLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Imagen de Perfil", p: "Archivo con control de avatar", g: "F" },
      en: { s: "Profile Image", p: "File with avatar control" },
      pt: {
        s: "Imagem de Perfil",
        p: "Arquivo com controle de avatar",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseAvatarLabel() {
    return this.DesignShowcaseAvatarLabelByLang();
  }

  DesignShowcaseKeywordsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Etiquetas", p: "Lista de textos", g: "F" },
      en: { s: "Tags", p: "List of strings" },
      pt: { s: "Etiquetas", p: "Lista de textos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseKeywordsLabel() {
    return this.DesignShowcaseKeywordsLabelByLang();
  }

  get DesignShowcaseKeywordsDescription() {
    return {
      es: {
        s: "Es el único campo que hace aparecer el control de lista de textos, en alta y en lectura.",
      },
      en: {
        s: "It is the only field that brings up the string-list control, in create and in read.",
      },
      pt: {
        s: "É o único campo que faz aparecer o controle de lista de textos, na criação e na leitura.",
      },
    }[this.language].s;
  }

  DesignShowcaseBodyLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Cuerpo", p: "Editor de texto rico", g: "M" },
      en: { s: "Body", p: "Rich text editor" },
      pt: { s: "Corpo", p: "Editor de texto rico", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseBodyLabel() {
    return this.DesignShowcaseBodyLabelByLang();
  }

  get DesignShowcaseBodyDescription() {
    return {
      es: {
        s: "El editor de texto rico es el control más alto del sistema y el que más tokens propios usa.",
      },
      en: {
        s: "The rich text editor is the tallest control in the system and the one that uses the most tokens of its own.",
      },
      pt: {
        s: "O editor de texto rico é o controle mais alto do sistema e o que mais usa tokens próprios.",
      },
    }[this.language].s;
  }

  DesignShowcaseMetadataLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Metadatos", p: "Editor de JSON", g: "M" },
      en: { s: "Metadata", p: "JSON editor" },
      pt: { s: "Metadados", p: "Editor de JSON", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseMetadataLabel() {
    return this.DesignShowcaseMetadataLabelByLang();
  }

  DesignShowcaseInternalNoteLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nota Interna", p: "Solo la ve SysAdmin", g: "F" },
      en: { s: "Internal Note", p: "Visible to SysAdmin only" },
      pt: { s: "Nota Interna", p: "Visível apenas para SysAdmin", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseInternalNoteLabel() {
    return this.DesignShowcaseInternalNoteLabelByLang();
  }

  get DesignShowcaseInternalNoteDescription() {
    return {
      es: {
        s: "Existe para ver que un campo restringido no deja un hueco en el formulario de quien no lo ve.",
      },
      en: {
        s: "Exists to check that a restricted field does not leave a gap in the form of whoever cannot see it.",
      },
      pt: {
        s: "Existe para ver que um campo restrito não deixa um buraco no formulário de quem não o vê.",
      },
    }[this.language].s;
  }

  DesignShowcaseApiKeyLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Clave", p: "Dato sensible", g: "F" },
      en: { s: "Key", p: "Sensitive data" },
      pt: { s: "Chave", p: "Dado sensível", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseApiKeyLabel() {
    return this.DesignShowcaseApiKeyLabelByLang();
  }

  get DesignShowcaseApiKeyDescription() {
    return {
      es: {
        s: "Es el único campo que muestra las superficies de dato sensible en un formulario real.",
      },
      en: {
        s: "It is the only field that shows the sensitive-data surfaces in a real form.",
      },
      pt: {
        s: "É o único campo que mostra as superfícies de dado sensível em um formulário real.",
      },
    }[this.language].s;
  }

  DesignShowcaseServerLabelLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Etiqueta del Servidor", p: "La completa el servidor", g: "F" },
      en: { s: "Server Label", p: "Filled in by the server" },
      pt: { s: "Etiqueta do Servidor", p: "Preenchida pelo servidor", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseServerLabelLabel() {
    return this.DesignShowcaseServerLabelLabelByLang();
  }

  DesignShowcaseLinesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Renglones", p: "Grilla editable anidada", g: "M" },
      en: { s: "Lines", p: "Nested editable grid" },
      pt: { s: "Linhas", p: "Grade editável aninhada", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get DesignShowcaseLinesLabel() {
    return this.DesignShowcaseLinesLabelByLang();
  }

  get DesignShowcaseLinesDescription() {
    return {
      es: {
        s: "La grilla dentro del formulario: encabezado, rayado por fila y celdas numéricas.",
      },
      en: {
        s: "The grid inside the form: header, row striping and numeric cells.",
      },
      pt: {
        s: "A grade dentro do formulário: cabeçalho, listras por linha e células numéricas.",
      },
    }[this.language].s;
  }

  // Theme

  get ThemeLabels() {
    return {
      es: { s: "Tema", p: "Temas", g: "M" },
      en: { s: "Theme", p: "Themes", g: undefined },
      pt: { s: "Tema", p: "Temas", g: "M" },
    }[this.language];
  }

  get ThemeSingular() {
    return this.ThemeLabels.s;
  }

  get ThemePlural() {
    return this.ThemeLabels.p;
  }

  get ThemeGender() {
    return this.ThemeLabels.g;
  }

  get groupThemeLabel() {
    return {
      es: { s: "Tema", p: "Temas", g: "M" },
      en: { s: "Theme", p: "Themes" },
      pt: { s: "Tema", p: "Temas", g: "M" },
    }[this.language].s;
  }

  ThemeNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del tema", g: "M" },
      en: { s: "Name", p: "Theme name" },
      pt: { s: "Nome", p: "Nome do tema", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ThemeNameLabel() {
    return this.ThemeNameLabelByLang();
  }

  ThemeActiveLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Activo", p: "Si es el tema que el runtime sirve hoy", g: "M" },
      en: {
        s: "Active",
        p: "Whether this is the theme the runtime serves today",
      },
      pt: { s: "Ativo", p: "Se é o tema que o runtime serve hoje", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ThemeActiveLabel() {
    return this.ThemeActiveLabelByLang();
  }

  ThemeAuthoringLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Autoría",
        p: "Fuente del tema, en el vocabulario del constructor",
        g: "F",
      },
      en: { s: "Authoring", p: "Theme source, in the builder's vocabulary" },
      pt: {
        s: "Autoria",
        p: "Fonte do tema, no vocabulário do construtor",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get ThemeAuthoringLabel() {
    return this.ThemeAuthoringLabelByLang();
  }

  ThemeResolvedLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Resuelto",
        p: "Tema ya resuelto a valores finales, lo que consume el runtime",
        g: "M",
      },
      en: {
        s: "Resolved",
        p: "Theme already resolved to final values, what the runtime consumes",
      },
      pt: {
        s: "Resolvido",
        p: "Tema já resolvido a valores finais, o que o runtime consome",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get ThemeResolvedLabel() {
    return this.ThemeResolvedLabelByLang();
  }

  // AuthorComposer

  get AuthorComposerLabels() {
    return {
      es: { s: "Autor/Compositor", p: "Autores/Compositores", g: "M" },
      en: { s: "Author/Composer", p: "Authors/Composers", g: undefined },
      pt: { s: "Autor/Compositor", p: "Autores/Compositores", g: "M" },
    }[this.language];
  }

  get AuthorComposerSingular() {
    return this.AuthorComposerLabels.s;
  }

  get AuthorComposerPlural() {
    return this.AuthorComposerLabels.p;
  }

  get AuthorComposerGender() {
    return this.AuthorComposerLabels.g;
  }

  get groupAuthorComposerLabel() {
    return {
      es: { s: "Autor/Compositor", p: "Autores/Compositores", g: "M" },
      en: { s: "Author/Composer", p: "Authors/Composers" },
      pt: { s: "Autor/Compositor", p: "Autores/Compositores", g: "M" },
    }[this.language].s;
  }

  get groupAuthorComposerPersonalInformationLabel() {
    return {
      es: { s: "Información Personal", p: "F" },
      en: { s: "Personal Information" },
      pt: { s: "Informação Pessoal", p: "F" },
    }[this.language].s;
  }

  get groupAuthorComposerAddressLabel() {
    return {
      es: { s: "Domicilio", p: "M" },
      en: { s: "Address" },
      pt: { s: "Endereço", p: "M" },
    }[this.language].s;
  }

  get groupAuthorComposerProfessionalInformationLabel() {
    return {
      es: { s: "Información Profesional", p: "F" },
      en: { s: "Professional Information" },
      pt: { s: "Informação Profissional", p: "F" },
    }[this.language].s;
  }

  get groupAuthorComposerSystemDatesLabel() {
    return {
      es: { s: "Fechas de sistema", p: "F" },
      en: { s: "System dates" },
      pt: { s: "Datas de sistema", p: "F" },
    }[this.language].s;
  }

  AuthorComposerFirstNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre/s", p: "Nombre/s del autor/compositor", g: "M" },
      en: { s: "First Name(s)", p: "Author/composer's first name(s)" },
      pt: { s: "Nome(s)", p: "Nome(s) do autor/compositor", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerFirstNameLabel() {
    return this.AuthorComposerFirstNameLabelByLang();
  }

  get AuthorComposerFirstNameDescription() {
    return {
      es: { s: "Nombre de pila de el/la autor/a o compositor/a." },
      en: { s: "First name of the author or composer." },
      pt: { s: "Nome próprio do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerLastNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Apellido/s", p: "Apellido/s del autor/compositor", g: "M" },
      en: { s: "Last Name(s)", p: "Author/composer's last name(s)" },
      pt: { s: "Sobrenome(s)", p: "Sobrenome(s) do autor/compositor", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerLastNameLabel() {
    return this.AuthorComposerLastNameLabelByLang();
  }

  get AuthorComposerLastNameDescription() {
    return {
      es: { s: "Apellido/s de el/la autor/a o compositor/a." },
      en: { s: "Last name(s) of the author or composer." },
      pt: { s: "Sobrenome(s) do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerGenderLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Género", p: "Géneros", g: "M" },
      en: { s: "Gender", p: "Genders" },
      pt: { s: "Gênero", p: "Gêneros", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerGenderLabel() {
    return this.AuthorComposerGenderLabelByLang();
  }

  get AuthorComposerGenderDescription() {
    return {
      es: { s: "Género de el/la autor/a o compositor/a." },
      en: { s: "Gender of the author or composer." },
      pt: { s: "Gênero do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerTaxIdTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Documento", p: "M" },
      en: { s: "ID Type" },
      pt: { s: "Tipo de Documento", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerTaxIdTypeLabel() {
    return this.AuthorComposerTaxIdTypeLabelByLang();
  }

  get AuthorComposerTaxIdTypeDescription() {
    return {
      es: { s: "Tipo de documento de identificación." },
      en: { s: "Type of identification document." },
      pt: { s: "Tipo de documento de identificação." },
    }[this.language].s;
  }

  AuthorComposerTaxIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "CUIT/CUIL/ID", p: "Número de identificación fiscal", g: "M" },
      en: { s: "Tax ID", p: "Fiscal identification number" },
      pt: { s: "ID Fiscal", p: "Número de identificação fiscal", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerTaxIdLabel() {
    return this.AuthorComposerTaxIdLabelByLang();
  }

  get AuthorComposerTaxIdDescription() {
    return {
      es: { s: "Número de documento de el/la autor/a o compositor/a." },
      en: { s: "ID number of the author or composer." },
      pt: { s: "Número de documento do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerCuitCertificateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Constancia de CUIL",
        p: "Constancia de CUIL (ANSES) del autor/compositor",
        g: "F",
      },
      en: {
        s: "CUIL Certificate",
        p: "Author/composer's CUIL certificate (ANSES)",
      },
      pt: {
        s: "Constância de CUIL",
        p: "Constância de CUIL (ANSES) do autor/compositor",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCuitCertificateLabel() {
    return this.AuthorComposerCuitCertificateLabelByLang();
  }

  get AuthorComposerCuitCertificateDescription() {
    return {
      es: {
        s: "Constancia de CUIL (ANSES), descargada automáticamente con los datos personales.",
      },
      en: {
        s: "CUIL certificate (ANSES), downloaded automatically from the personal data.",
      },
      pt: {
        s: "Constância de CUIL (ANSES), baixada automaticamente com os dados pessoais.",
      },
    }[this.language].s;
  }

  AuthorComposerCuitCertificateIssuedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Constancia descargada el",
        p: "Fecha y hora de descarga de la constancia",
        g: "F",
      },
      en: {
        s: "Certificate Downloaded At",
        p: "Date and time the certificate was downloaded",
      },
      pt: {
        s: "Constância baixada em",
        p: "Data e hora do download da constância",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCuitCertificateIssuedAtLabel() {
    return this.AuthorComposerCuitCertificateIssuedAtLabelByLang();
  }

  get AuthorComposerCuitCertificateIssuedAtDescription() {
    return {
      es: { s: "Fecha y hora en que se descargó la constancia de CUIL." },
      en: { s: "Date and time the CUIL certificate was downloaded." },
      pt: { s: "Data e hora em que a constância de CUIL foi baixada." },
    }[this.language].s;
  }

  AuthorComposerCuitCertificateErrorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Error de descarga de constancia",
        p: "Motivo del último intento fallido",
        g: "M",
      },
      en: {
        s: "Certificate Download Error",
        p: "Reason for the last failed attempt",
      },
      pt: {
        s: "Erro de download da constância",
        p: "Motivo da última tentativa com falha",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCuitCertificateErrorLabel() {
    return this.AuthorComposerCuitCertificateErrorLabelByLang();
  }

  get AuthorComposerCuitCertificateErrorDescription() {
    return {
      es: {
        s: "Motivo por el cual no se pudo descargar la constancia con los datos actuales.",
      },
      en: {
        s: "Reason the certificate could not be downloaded with the current data.",
      },
      pt: {
        s: "Motivo pelo qual não foi possível baixar a constância com os dados atuais.",
      },
    }[this.language].s;
  }

  AuthorComposerCuitCertificateErrorTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de error de constancia",
        p: "Clasificación del último fallo de ANSES",
        g: "M",
      },
      en: {
        s: "Certificate Error Type",
        p: "Classification of the last ANSES failure",
      },
      pt: {
        s: "Tipo de erro de constância",
        p: "Classificação da última falha da ANSES",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCuitCertificateErrorTypeLabel() {
    return this.AuthorComposerCuitCertificateErrorTypeLabelByLang();
  }

  get AuthorComposerCuitCertificateErrorTypeDescription() {
    return {
      es: {
        s: "invalidData = ANSES rechazó los datos (revisar); technical = fallo técnico, reintentando.",
      },
      en: {
        s: "invalidData = ANSES rejected the data (review); technical = technical failure, retrying.",
      },
      pt: {
        s: "invalidData = ANSES rejeitou os dados (revisar); technical = falha técnica, tentando novamente.",
      },
    }[this.language].s;
  }

  AuthorComposerDniDeliveredToSadaicAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "DNI enviado a SADAIC el",
        p: "Fecha y hora en que la foto del documento llegó a SADAIC",
        g: "F",
      },
      en: {
        s: "ID Sent to SADAIC At",
        p: "Date and time the ID photo reached SADAIC",
      },
      pt: {
        s: "Documento enviado à SADAIC em",
        p: "Data e hora em que a foto do documento chegou à SADAIC",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerDniDeliveredToSadaicAtLabel() {
    return this.AuthorComposerDniDeliveredToSadaicAtLabelByLang();
  }

  get AuthorComposerDniDeliveredToSadaicAtDescription() {
    return {
      es: {
        s: "Vacío = SADAIC todavía no tiene la foto del documento de este autor. Lo completa el sistema al confirmarse la entrega; la foto no se guarda acá (Ley 25.326).",
      },
      en: {
        s: "Empty = SADAIC does not have this author's ID photo yet. Filled by the system when delivery is confirmed; the photo itself is not stored here.",
      },
      pt: {
        s: "Vazio = a SADAIC ainda não tem a foto do documento deste autor. Preenchido pelo sistema ao confirmar-se a entrega; a foto não é guardada aqui.",
      },
    }[this.language].s;
  }

  AuthorComposerDniValidatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Datos validados con DNI el",
        p: "Fecha y hora de la última validación de datos con el DNI",
        g: "F",
      },
      en: {
        s: "Data Validated with ID At",
        p: "Date and time of the last data validation with the ID",
      },
      pt: {
        s: "Dados validados com o documento em",
        p: "Data e hora da última validação de dados com o documento",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerDniValidatedAtLabel() {
    return this.AuthorComposerDniValidatedAtLabelByLang();
  }

  get AuthorComposerDniValidatedAtDescription() {
    return {
      es: {
        s: "Vacío = ningún dato de este autor está validado con su DNI. Lo completa el sistema al leer el documento; se vacía si cambian todos los datos validados.",
      },
      en: {
        s: "Empty = none of this author's data is validated with the ID. Filled by the system when it reads the document; cleared if every validated value changes.",
      },
      pt: {
        s: "Vazio = nenhum dado deste autor está validado com o documento. Preenchido pelo sistema ao ler o documento; esvaziado se todos os dados validados mudarem.",
      },
    }[this.language].s;
  }

  AuthorComposerDniValidatedFieldsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Datos validados con DNI",
        p: "Datos que coinciden con el DNI del autor",
        g: "M",
      },
      en: { s: "Data Validated with ID", p: "Data that match the author's ID" },
      pt: {
        s: "Dados validados com o documento",
        p: "Dados que coincidem com o documento do autor",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerDniValidatedFieldsLabel() {
    return this.AuthorComposerDniValidatedFieldsLabelByLang();
  }

  get AuthorComposerDniValidatedFieldsDescription() {
    return {
      es: {
        s: "Por cada dato que coincide con el documento, el valor verificado. Lo escribe solo el sistema; un dato que cambia sale de acá en el mismo guardado.",
      },
      en: {
        s: "For each value that matches the document, the verified value. Written only by the system; a value that changes leaves this list in the same save.",
      },
      pt: {
        s: "Para cada dado que coincide com o documento, o valor verificado. Escrito só pelo sistema; um dado que muda sai daqui no mesmo salvamento.",
      },
    }[this.language].s;
  }

  AuthorComposerEmailLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Email", p: "Email de contacto", g: "M" },
      en: { s: "Email", p: "Contact email address" },
      pt: { s: "E-mail", p: "Endereço de email de contato" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerEmailLabel() {
    return this.AuthorComposerEmailLabelByLang();
  }

  get AuthorComposerEmailDescription() {
    return {
      es: { s: "Email oficial de el/la autor/a o compositor/a." },
      en: { s: "Official email of the author or composer." },
      pt: { s: "E-mail oficial do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerPhoneLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Teléfono", p: "Número de teléfono de contacto", g: "M" },
      en: { s: "Phone", p: "Contact phone number" },
      pt: { s: "Telefone", p: "Número de telefone de contato" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerPhoneLabel() {
    return this.AuthorComposerPhoneLabelByLang();
  }

  get AuthorComposerPhoneDescription() {
    return {
      es: { s: "Número de teléfono de el/la autor/a o compositor/a." },
      en: { s: "Phone number of the author or composer." },
      pt: { s: "Número de telefone do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerNationalityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nacionalidad", p: "País de nacionalidad", g: "F" },
      en: { s: "Nationality", p: "Country of nationality" },
      pt: { s: "Nacionalidade", p: "País de nacionalidade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerNationalityLabel() {
    return this.AuthorComposerNationalityLabelByLang();
  }

  get AuthorComposerNationalityDescription() {
    return {
      es: { s: "Nacionalidad de el/la autor/a o compositor/a." },
      en: { s: "Nationality of the author or composer." },
      pt: { s: "Nacionalidade do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerDateOfBirthLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de nacimiento", p: "F" },
      en: { s: "Date of birth" },
      pt: { s: "Data de nascimento", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerDateOfBirthLabel() {
    return this.AuthorComposerDateOfBirthLabelByLang();
  }

  get AuthorComposerDateOfBirthDescription() {
    return {
      es: { s: "Fecha de nacimiento de el/la autor/a o compositor/a." },
      en: { s: "Date of birth of the author or composer." },
      pt: { s: "Data de nascimento do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerStreetAndNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Calle y Número", p: "Calle y número del domicilio", g: "F" },
      en: { s: "Street and Number", p: "Street and house number" },
      pt: { s: "Rua e Número", p: "Rua e número do endereço", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerStreetAndNumberLabel() {
    return this.AuthorComposerStreetAndNumberLabelByLang();
  }

  get AuthorComposerStreetAndNumberDescription() {
    return {
      es: {
        s: "Nombre de la calle y número del domicilio de el/la autor/a o compositor/a.",
      },
      en: { s: "Street name and number of the author or composer's address." },
      pt: { s: "Nome da rua e número do endereço do autor ou compositor." },
    }[this.language].s;
  }

  AuthorComposerFloorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Piso", p: "Número de piso", g: "M" },
      en: { s: "Floor", p: "Floor number" },
      pt: { s: "Andar", p: "Número do andar" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerFloorLabel() {
    return this.AuthorComposerFloorLabelByLang();
  }

  get AuthorComposerFloorDescription() {
    return {
      es: { s: "Número de piso o planta en el edificio, si aplica." },
      en: { s: "Floor number in the building, if applicable." },
      pt: { s: "Número do andar no edifício, se aplicável." },
    }[this.language].s;
  }

  AuthorComposerSuiteLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Departamento",
        p: "Número o letra de departamento/oficina",
        g: "M",
      },
      en: { s: "Suite/Office", p: "Suite or office number/letter" },
      pt: {
        s: "Apartamento/Sala",
        p: "Número ou letra do apartamento/escritório",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerSuiteLabel() {
    return this.AuthorComposerSuiteLabelByLang();
  }

  get AuthorComposerSuiteDescription() {
    return {
      es: { s: "Letra o número identificador de departamento, si aplica." },
      en: { s: "Apartment letter or number, if applicable." },
      pt: { s: "Letra ou número do apartamento, se aplicável." },
    }[this.language].s;
  }

  AuthorComposerPostalCodeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Código Postal", p: "Código postal del domicilio", g: "M" },
      en: { s: "Postal Code", p: "Postal code of the address" },
      pt: { s: "Código Postal", p: "Código postal do endereço", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerPostalCodeLabel() {
    return this.AuthorComposerPostalCodeLabelByLang();
  }

  get AuthorComposerPostalCodeDescription() {
    return {
      es: { s: "Código postal o ZIP del domicilio." },
      en: { s: "Postal or ZIP code of the address." },
      pt: { s: "Código postal ou CEP do endereço." },
    }[this.language].s;
  }

  AuthorComposerCountryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "País", p: "País donde se encuentra el domicilio", g: "M" },
      en: { s: "Country", p: "Country where address is located" },
      pt: { s: "País", p: "País onde o endereço está localizado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCountryLabel() {
    return this.AuthorComposerCountryLabelByLang();
  }

  get AuthorComposerCountryDescription() {
    return {
      es: { s: "País en el que se encuentra el domicilio." },
      en: { s: "Country where the address is located." },
      pt: { s: "País onde o endereço está localizado." },
    }[this.language].s;
  }

  AuthorComposerProvinceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Provincia", p: "Provincia o estado", g: "F" },
      en: { s: "State/Province", p: "State or province" },
      pt: { s: "Estado/Província", p: "Estado ou província", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerProvinceLabel() {
    return this.AuthorComposerProvinceLabelByLang();
  }

  get AuthorComposerProvinceDescription() {
    return {
      es: { s: "Provincia en la que se encuentra el domicilio." },
      en: { s: "Province where the address is located." },
      pt: { s: "Província onde o endereço está localizado." },
    }[this.language].s;
  }

  AuthorComposerCityLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Localidad/Barrio", p: "Ciudad o localidad", g: "F" },
      en: { s: "City", p: "City or town" },
      pt: { s: "Cidade", p: "Cidade ou localidade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCityLabel() {
    return this.AuthorComposerCityLabelByLang();
  }

  get AuthorComposerCityDescription() {
    return {
      es: { s: "Localidad o Barrio en el que se encuentra el domicilio." },
      en: { s: "City or neighborhood where the address is located." },
      pt: { s: "Cidade ou bairro onde o endereço está localizado." },
    }[this.language].s;
  }

  AuthorComposerAkaLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "AKA", p: "También conocido como (nombre artístico)", g: "F" },
      en: { s: "AKA", p: "Also known as (artistic name)" },
      pt: { s: "AKA", p: "Também conhecido como (nome artístico)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerAkaLabel() {
    return this.AuthorComposerAkaLabelByLang();
  }

  get AuthorComposerAkaDescription() {
    return {
      es: {
        s: "Seudónimo o nombre artístico bajo el cual el/la autor/a o compositor/a, si existe.",
      },
      en: {
        s: "Pseudonym or stage name under which the author or composer is known, if any.",
      },
      pt: {
        s: "Pseudônimo ou nome artístico sob o qual o autor ou compositor é conhecido, se houver.",
      },
    }[this.language].s;
  }

  AuthorComposerIpiLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "IPI", p: "Número internacional otorgado por CISAC", g: "M" },
      en: { s: "IPI", p: "International number assigned by CISAC" },
      pt: { s: "IPI", p: "Número internacional atribuído pela CISAC", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerIpiLabel() {
    return this.AuthorComposerIpiLabelByLang();
  }

  get AuthorComposerIpiDescription() {
    return {
      es: {
        s: "IPI (Interested Party Information, anteriormente CAE) es un identificador internacional único de 9 a 11 dígitos que distingue a compositores, autores y editores musicales. Si el/la autor/a o compositor/a ya tiene su IPI, ingresarlo. Si no, dejarlo en blanco.",
      },
      en: {
        s: "IPI (Interested Party Information, formerly CAE) is a unique international identifier of 9 to 11 digits that distinguishes composers, authors, and music publishers. If the author or composer already has their IPI, enter it. If not, leave it blank.",
      },
      pt: {
        s: "IPI (Interested Party Information, anteriormente CAE) é um identificador internacional único de 9 a 11 dígitos que distingue compositores, autores e editores musicais. Se o autor ou compositor já possui seu IPI, insira-o. Caso contrário, deixe em branco.",
      },
    }[this.language].s;
  }

  AuthorComposerManagementSocietiesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Sociedades de Gestión",
        p: "Sociedades de gestión colectiva a las que pertenece",
        g: "M",
      },
      en: {
        s: "Collective Management Societies",
        p: "Collective management societies to which they belong",
      },
      pt: {
        s: "Sociedades de Gestão",
        p: "Sociedades de gestão coletiva às quais pertencem",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerManagementSocietiesLabel() {
    return this.AuthorComposerManagementSocietiesLabelByLang();
  }

  get AuthorComposerManagementSocietiesDescription() {
    return {
      es: {
        s: "Sociedad de Gestión Colectiva (de derechos de autor) que representa a el el/la autor/a o compositor/a, en el caso de que exista. Si el/la autor/a o compositor/a todavía no es representado por ninguna sociedad, dejar en blanco.",
      },
      en: {
        s: "Collective Management Organization (for copyright) representing the author or composer, if any. If the author or composer is not yet represented by any society, leave blank.",
      },
      pt: {
        s: "Sociedade de Gestão Coletiva (de direitos autorais) que representa o autor ou compositor, se houver. Se o autor ou compositor ainda não é representado por nenhuma sociedade, deixe em branco.",
      },
    }[this.language].s;
  }

  AuthorComposerPublishersLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Editoriales (Referencia)",
        p: "Porcentajes de referencia para obras nuevas",
        g: "M",
      },
      en: {
        s: "Publishers (Reference)",
        p: "Reference percentages for new works",
      },
      pt: {
        s: "Editoras (Referência)",
        p: "Porcentagens de referência para novas obras",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerPublishersLabel() {
    return this.AuthorComposerPublishersLabelByLang();
  }

  get AuthorComposerPublishersDescription() {
    return {
      es: {
        s: "Editorial/es Musical/es que representan a el/la autor/a o compositor/a. Importante: Este dato es un 'PRESET'. Son condiciones predeterminadas del acuerdo entre el autor y la/s editorial/es que deberan ser ratificados o modificados al momento de ingresar al autor dentro del reparto de una obra.",
      },
      en: {
        s: "Music publisher(s) representing the author or composer. Important: This data is a 'PRESET'. These are predetermined conditions of the agreement between the author and the publisher(s) that must be ratified or modified when entering the author into the distribution of a work.",
      },
      pt: {
        s: "Editora(s) musical(is) que representam o autor ou compositor. Importante: Este dado é um 'PRESET'. São condições predeterminadas do acordo entre o autor e a(s) editora(s) que deverão ser ratificadas ou modificadas ao inserir o autor na distribuição de uma obra.",
      },
    }[this.language].s;
  }

  AuthorComposerCreatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Creación", p: "F" },
      en: { s: "Creation Date" },
      pt: { s: "Data de Criação", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerCreatedAt_Label() {
    return this.AuthorComposerCreatedAt_LabelByLang();
  }

  get AuthorComposerCreatedAt_Description() {
    return {
      es: { s: "Fecha y hora del registro en el sistema de esta edición." },
      en: {
        s: "Date and time when this edtition was registered in the system.",
      },
      pt: { s: "Data e hora do registro no sistema desta edição." },
    }[this.language].s;
  }

  AuthorComposerLastUpdatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Última Actualización", p: "F" },
      en: { s: "Last Updated" },
      pt: { s: "Última Atualização", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorComposerLastUpdatedAt_Label() {
    return this.AuthorComposerLastUpdatedAt_LabelByLang();
  }

  get AuthorComposerLastUpdatedAt_Description() {
    return {
      es: { s: "Fecha y hora de la última modificación de esta edición." },
      en: { s: "Date and time of the last modification of this edition." },
      pt: { s: "Data e hora da última modificação desta edição." },
    }[this.language].s;
  }

  // CollectiveManagementSociety

  get CollectiveManagementSocietyLabels() {
    return {
      es: {
        s: "Sociedad de Gestión Colectiva",
        p: "Sociedades de Gestión Colectiva",
        g: "F",
      },
      en: {
        s: "Collective Management Society",
        p: "Collective Management Societies",
        g: undefined,
      },
      pt: {
        s: "Sociedade de Gestão Coletiva",
        p: "Sociedades de Gestão Coletiva",
        g: "F",
      },
    }[this.language];
  }

  get CollectiveManagementSocietySingular() {
    return this.CollectiveManagementSocietyLabels.s;
  }

  get CollectiveManagementSocietyPlural() {
    return this.CollectiveManagementSocietyLabels.p;
  }

  get CollectiveManagementSocietyGender() {
    return this.CollectiveManagementSocietyLabels.g;
  }

  get groupCollectiveManagementSocietyLabel() {
    return {
      es: {
        s: "Sociedad de Gestión Colectiva",
        p: "Sociedades de Gestión Colectiva",
        g: "F",
      },
      en: {
        s: "Collective Management Society",
        p: "Collective Management Societies",
      },
      pt: {
        s: "Sociedade de Gestão Coletiva",
        p: "Sociedades de Gestão Coletiva",
        g: "F",
      },
    }[this.language].s;
  }

  CollectiveManagementSocietyNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre completo de la sociedad", g: "M" },
      en: { s: "Name", p: "Full name of the society" },
      pt: { s: "Nome", p: "Nome completo da sociedade", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CollectiveManagementSocietyNameLabel() {
    return this.CollectiveManagementSocietyNameLabelByLang();
  }

  get CollectiveManagementSocietyNameDescription() {
    return {
      es: { s: "Nombre de la Sociedad de Gestión Coletiva." },
      en: { s: "Name of the Collective Management Organization." },
      pt: { s: "Nome da Sociedade de Gestão Coletiva." },
    }[this.language].s;
  }

  CollectiveManagementSocietyAcronymLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Siglas",
        p: "Abreviatura de la sociedad (ej. SADAIC, ASCAP)",
        g: "F",
      },
      en: { s: "Acronym", p: "Society abbreviation (e.g. SADAIC, ASCAP)" },
      pt: {
        s: "Sigla",
        p: "Abreviatura da sociedade (ex. SADAIC, ASCAP)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get CollectiveManagementSocietyAcronymLabel() {
    return this.CollectiveManagementSocietyAcronymLabelByLang();
  }

  get CollectiveManagementSocietyAcronymDescription() {
    return {
      es: {
        s: "Sigla o acrónimo comúnmente utilizado para referirse a la Sociedad de Gestión Coletiva..",
      },
      en: {
        s: "Acronym commonly used to refer to the Collective Management Organization.",
      },
      pt: {
        s: "Sigla comumente utilizada para se referir à Sociedade de Gestão Coletiva.",
      },
    }[this.language].s;
  }

  CollectiveManagementSocietyCountryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "País", p: "País donde opera la sociedad", g: "M" },
      en: { s: "Country", p: "Country where the society operates" },
      pt: { s: "País", p: "País onde a sociedade opera", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get CollectiveManagementSocietyCountryLabel() {
    return this.CollectiveManagementSocietyCountryLabelByLang();
  }

  get CollectiveManagementSocietyCountryDescription() {
    return {
      es: { s: "País en el cual opera la Sociedad de Gestión Coletiva." },
      en: {
        s: "Country in which the Collective Management Organization operates.",
      },
      pt: { s: "País no qual a Sociedade de Gestão Coletiva opera." },
    }[this.language].s;
  }

  // AuthorCollectingSociety

  get AuthorCollectingSocietyLabels() {
    return {
      es: { s: "Relación SGC-Autor", p: "Relaciones SGC-Autor", g: "F" },
      en: {
        s: "CMS-Author Relationship",
        p: "CMS-Author Relationships",
        g: undefined,
      },
      pt: { s: "Relação SGC-Autor", p: "Relações SGC-Autor", g: "F" },
    }[this.language];
  }

  get AuthorCollectingSocietySingular() {
    return this.AuthorCollectingSocietyLabels.s;
  }

  get AuthorCollectingSocietyPlural() {
    return this.AuthorCollectingSocietyLabels.p;
  }

  get AuthorCollectingSocietyGender() {
    return this.AuthorCollectingSocietyLabels.g;
  }

  get groupAuthorCollectingSocietyLabel() {
    return {
      es: { s: "Relación SGC-Autor", p: "Relaciones SGC-Autor", g: "F" },
      en: { s: "CMS-Author Relationship", p: "CMS-Author Relationships" },
      pt: { s: "Relação SGC-Autor", p: "Relações SGC-Autor", g: "F" },
    }[this.language].s;
  }

  AuthorCollectingSocietyCollectingSocietyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Sociedad de Gestión",
        p: "Sociedad de gestión colectiva",
        g: "F",
      },
      en: { s: "Management Society", p: "Collective management society" },
      pt: {
        s: "Sociedade de Gestão",
        p: "Sociedade de gestão coletiva",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorCollectingSocietyCollectingSocietyLabel() {
    return this.AuthorCollectingSocietyCollectingSocietyLabelByLang();
  }

  get AuthorCollectingSocietyCollectingSocietyDescription() {
    return {
      es: {
        s: "Sociedad de Gestión Colectiva (de derechos de autor) que representa a el el/la autor/a o compositor/a.",
      },
      en: {
        s: "Collective Management Organization (for copyright) representing the author or composer.",
      },
      pt: {
        s: "Sociedade de Gestão Coletiva (de direitos autorais) que representa o autor ou compositor.",
      },
    }[this.language].s;
  }

  AuthorCollectingSocietyMemberNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Número de Socio", p: "Número de socio en la SGC", g: "M" },
      en: { s: "Member Number", p: "Member number in the CMS" },
      pt: { s: "Número de Membro", p: "Número de membro na SGC", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AuthorCollectingSocietyMemberNumberLabel() {
    return this.AuthorCollectingSocietyMemberNumberLabelByLang();
  }

  get AuthorCollectingSocietyMemberNumberDescription() {
    return {
      es: {
        s: "Número de representado o socio de el/la autor/a compositor/a asignado por la Sociedad de Gestión Colectiva.",
      },
      en: {
        s: "Member or affiliate number of the author/composer assigned by the Collective Management Organization.",
      },
      pt: {
        s: "Número de representado ou sócio do autor/compositor atribuído pela Sociedade de Gestão Coletiva.",
      },
    }[this.language].s;
  }

  AuthorCollectingSocietyTerritoriesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Territorios",
        p: "Territorio/s donde la SGC representa al autor",
        g: "M",
      },
      en: {
        s: "Territories",
        p: "Territory/ies where the CMS represents the author",
      },
      pt: {
        s: "Territórios",
        p: "Território/s onde a SGC representa o autor",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorCollectingSocietyTerritoriesLabel() {
    return this.AuthorCollectingSocietyTerritoriesLabelByLang();
  }

  get AuthorCollectingSocietyTerritoriesDescription() {
    return {
      es: {
        s: "Territorio/s en los que la Sociedad de Gestión Colectiva gestiona los derechos de el/la autor/a compositor/a.",
      },
      en: {
        s: "Territory(ies) in which the Collective Management Organization manages the rights of the author/composer.",
      },
      pt: {
        s: "Território(s) nos quais a Sociedade de Gestão Coletiva administra os direitos do autor/compositor.",
      },
    }[this.language].s;
  }

  // Publisher

  get PublisherLabels() {
    return {
      es: { s: "Editorial", p: "Editoriales", g: "F" },
      en: { s: "Publisher", p: "Publishers", g: undefined },
      pt: { s: "Editora", p: "Editoras", g: "F" },
    }[this.language];
  }

  get PublisherSingular() {
    return this.PublisherLabels.s;
  }

  get PublisherPlural() {
    return this.PublisherLabels.p;
  }

  get PublisherGender() {
    return this.PublisherLabels.g;
  }

  get groupPublisherLabel() {
    return {
      es: { s: "Editorial", p: "Editoriales", g: "F" },
      en: { s: "Publisher", p: "Publishers" },
      pt: { s: "Editora", p: "Editoras", g: "F" },
    }[this.language].s;
  }

  get groupPublisherBasicInformationLabel() {
    return {
      es: { s: "Información Básica", p: "F" },
      en: { s: "Basic Information" },
      pt: { s: "Informação Básica", p: "F" },
    }[this.language].s;
  }

  get groupPublisherAddressLabel() {
    return {
      es: { s: "Domicilio", p: "M" },
      en: { s: "Address" },
      pt: { s: "Endereço", p: "M" },
    }[this.language].s;
  }

  get groupPublisherLegalInformationLabel() {
    return {
      es: { s: "Información Legal", p: "F" },
      en: { s: "Legal Information" },
      pt: { s: "Informações Legais", p: "M" },
    }[this.language].s;
  }

  get groupPublisherSystemDatesLabel() {
    return {
      es: { s: "Fechas de sistema", p: "F" },
      en: { s: "System dates" },
      pt: { s: "Datas de sistema", p: "F" },
    }[this.language].s;
  }

  PublisherPersonTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Tipo de Persona",
        p: "Tipo de persona (Jurídica o Física)",
        g: "M",
      },
      en: { s: "Person Type", p: "Type of person (Legal or Natural)" },
      pt: {
        s: "Tipo de Pessoa",
        p: "Tipo de pessoa (Jurídica ou Física)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherPersonTypeLabel() {
    return this.PublisherPersonTypeLabelByLang();
  }

  get PublisherPersonTypeDescription() {
    return {
      es: {
        s: "Indica si la editorial es una entidad legal (empresa) o una persona física.",
      },
      en: {
        s: "Indicates whether the publisher is a legal entity (company) or a natural person.",
      },
      pt: {
        s: "Indica se a editora é uma pessoa jurídica (empresa) ou uma pessoa física.",
      },
    }[this.language].s;
  }

  PublisherNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Razón Social",
        p: "Razón Social de la editorial musical",
        g: "M",
      },
      en: { s: "Legal Name", p: "Legal name of the music publisher" },
      pt: { s: "Nome Legal", p: "Razão social da editora musical", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherNameLabel() {
    return this.PublisherNameLabelByLang();
  }

  get PublisherNameDescription() {
    return {
      es: { s: "Razón Social de la editorial musical." },
      en: { s: "Legal name of the music publisher." },
      pt: { s: "Razão social da editora musical." },
    }[this.language].s;
  }

  PublisherMaritalStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Estado Civil",
        p: "Estado civil de la editorial cuando es persona física",
        g: "M",
      },
      en: {
        s: "Marital Status",
        p: "Marital status when the publisher is a natural person",
      },
      pt: {
        s: "Estado Civil",
        p: "Estado civil da editora quando é pessoa física",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherMaritalStatusLabel() {
    return this.PublisherMaritalStatusLabelByLang();
  }

  get PublisherMaritalStatusDescription() {
    return {
      es: {
        s: "Estado civil de la editorial. Solo aplica cuando el tipo de persona es física.",
      },
      en: {
        s: "Marital status of the publisher. Only applies when the person type is natural.",
      },
      pt: {
        s: "Estado civil da editora. Aplica-se apenas quando o tipo de pessoa é física.",
      },
    }[this.language].s;
  }

  PublisherNationalityLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nacionalidad", p: "País de nacionalidad", g: "F" },
      en: { s: "Nationality", p: "Country of nationality" },
      pt: { s: "Nacionalidade", p: "País de nacionalidade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get PublisherNationalityLabel() {
    return this.PublisherNationalityLabelByLang();
  }

  get PublisherNationalityDescription() {
    return {
      es: { s: "Nacionalidad de el/la autor/a o compositor/a." },
      en: { s: "Nationality of the author or composer." },
      pt: { s: "Nacionalidade do autor ou compositor." },
    }[this.language].s;
  }

  PublisherPseudonymLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Seudónimo de la Editorial",
        p: "SOLO COMPLETARLO SI LA EDITORIAL ACTÚA BAJO UN SEUDÓNIMO",
        g: "M",
      },
      en: {
        s: "Publisher Pseudonym",
        p: "ONLY FILL THIS IN IF THE PUBLISHER OPERATES UNDER A PSEUDONYM",
      },
      pt: {
        s: "Pseudônimo da Editora",
        p: "PREENCHA APENAS SE A EDITORA ATUAR SOB UM PSEUDÔNIMO",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherPseudonymLabel() {
    return this.PublisherPseudonymLabelByLang();
  }

  get PublisherPseudonymDescription() {
    return {
      es: {
        s: "Completar UNICAMENTE si la Editorial Musical actúa bajo un seudónimo.",
      },
      en: {
        s: "ONLY FILL THIS IN IF THE PUBLISHER OPERATES UNDER A PSEUDONYM.",
      },
      pt: { s: "PREENCHA APENAS SE A EDITORA ATUAR SOB UM PSEUDÔNIMO." },
    }[this.language].s;
  }

  PublisherTaxIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "CUIT/CUIL", p: "Número de identificación fiscal", g: "M" },
      en: { s: "Tax ID", p: "Tax identification number" },
      pt: { s: "CNPJ/CPF", p: "Número de identificação fiscal", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherTaxIdLabel() {
    return this.PublisherTaxIdLabelByLang();
  }

  get PublisherTaxIdDescription() {
    return {
      es: { s: "Identificador fiscal único de la editorial musical." },
      en: { s: "Unique tax identifier of the music publisher." },
      pt: { s: "Identificador fiscal único da editora musical." },
    }[this.language].s;
  }

  PublisherPhoneLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Teléfono", p: "Número de teléfono de contacto", g: "M" },
      en: { s: "Phone", p: "Contact phone number" },
      pt: { s: "Telefone", p: "Número de telefone de contato" },
    }[lang ? lang : this.language].s;
  }

  get PublisherPhoneLabel() {
    return this.PublisherPhoneLabelByLang();
  }

  get PublisherPhoneDescription() {
    return {
      es: {
        s: "Número de teléfono oficial de contacto de la editorial musical.",
      },
      en: { s: "Official contact phone number of the music publisher." },
      pt: { s: "Número de telefone oficial de contato da editora musical." },
    }[this.language].s;
  }

  PublisherEmailLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Email", p: "Correo electrónico de contacto", g: "M" },
      en: { s: "Email", p: "Contact email address" },
      pt: { s: "Email", p: "Endereço de email de contato", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherEmailLabel() {
    return this.PublisherEmailLabelByLang();
  }

  get PublisherEmailDescription() {
    return {
      es: {
        s: "Dirección de correo electrónico oficial de la editorial musical.",
      },
      en: { s: "Official email address of the music publisher." },
      pt: { s: "Endereço de e-mail oficial da editora musical." },
    }[this.language].s;
  }

  PublisherStreetAndNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Calle y Número", p: "Calle y número del domicilio", g: "F" },
      en: { s: "Street and Number", p: "Street and house number" },
      pt: { s: "Rua e Número", p: "Rua e número do endereço", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get PublisherStreetAndNumberLabel() {
    return this.PublisherStreetAndNumberLabelByLang();
  }

  get PublisherStreetAndNumberDescription() {
    return {
      es: { s: "Dirección física oficial de la editorial musical." },
      en: { s: "Official physical address of the music publisher." },
      pt: { s: "Endereço físico oficial da editora musical." },
    }[this.language].s;
  }

  PublisherFloorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Piso", p: "Número de piso", g: "M" },
      en: { s: "Floor", p: "Floor number" },
      pt: { s: "Andar", p: "Número do andar" },
    }[lang ? lang : this.language].s;
  }

  get PublisherFloorLabel() {
    return this.PublisherFloorLabelByLang();
  }

  get PublisherFloorDescription() {
    return {
      es: {
        s: "Número de piso de la dirección física oficial de la editorial musical, si aplica.",
      },
      en: {
        s: "Floor number of the official physical address of the music publisher, if applicable.",
      },
      pt: {
        s: "Número do andar do endereço físico oficial da editora musical, se aplicável.",
      },
    }[this.language].s;
  }

  PublisherSuiteLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Departamento",
        p: "Número o letra de departamento/oficina",
        g: "M",
      },
      en: { s: "Suite/Office", p: "Suite or office number/letter" },
      pt: {
        s: "Apartamento/Sala",
        p: "Número ou letra do apartamento/escritório",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherSuiteLabel() {
    return this.PublisherSuiteLabelByLang();
  }

  get PublisherSuiteDescription() {
    return {
      es: {
        s: "Identificador de departamento de la dirección física oficial de la editorial musical, si aplica.",
      },
      en: {
        s: "Apartment identifier of the official physical address of the music publisher, if applicable.",
      },
      pt: {
        s: "Identificador do apartamento do endereço físico oficial da editora musical, se aplicável.",
      },
    }[this.language].s;
  }

  PublisherPostalCodeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Código Postal", p: "Código postal del domicilio", g: "M" },
      en: { s: "Postal Code", p: "Postal code of the address" },
      pt: { s: "Código Postal", p: "Código postal do endereço", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherPostalCodeLabel() {
    return this.PublisherPostalCodeLabelByLang();
  }

  get PublisherPostalCodeDescription() {
    return {
      es: {
        s: "Código postal de la dirección física oficial de la editorial musical.",
      },
      en: {
        s: "Postal code of the official physical address of the music publisher.",
      },
      pt: { s: "Código postal do endereço físico oficial da editora musical." },
    }[this.language].s;
  }

  PublisherCountryLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "País", p: "País donde se encuentra el domicilio", g: "M" },
      en: { s: "Country", p: "Country where address is located" },
      pt: { s: "País", p: "País onde o endereço está localizado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherCountryLabel() {
    return this.PublisherCountryLabelByLang();
  }

  get PublisherCountryDescription() {
    return {
      es: {
        s: "País donde se encuentra la dirección física oficial de la editorial musical.",
      },
      en: {
        s: "Country where the official physical address of the music publisher is located.",
      },
      pt: {
        s: "País onde o endereço físico oficial da editora musical está localizado.",
      },
    }[this.language].s;
  }

  PublisherProvinceLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Provincia", p: "Provincia o estado", g: "F" },
      en: { s: "State/Province", p: "State or province" },
      pt: { s: "Estado/Província", p: "Estado ou província", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherProvinceLabel() {
    return this.PublisherProvinceLabelByLang();
  }

  get PublisherProvinceDescription() {
    return {
      es: {
        s: "Provincia donde se encuentra la dirección física oficial de la editorial musical.",
      },
      en: {
        s: "Province where the official physical address of the music publisher is located.",
      },
      pt: {
        s: "Província onde o endereço físico oficial da editora musical está localizado.",
      },
    }[this.language].s;
  }

  PublisherCityLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Localidad/Barrio", p: "Ciudad o localidad", g: "F" },
      en: { s: "City", p: "City or town" },
      pt: { s: "Cidade", p: "Cidade ou localidade", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get PublisherCityLabel() {
    return this.PublisherCityLabelByLang();
  }

  get PublisherCityDescription() {
    return {
      es: {
        s: "Localidad o barrio donde se encuentra la dirección física oficial de la editorial musical.",
      },
      en: {
        s: "City or neighborhood where the official physical address of the music publisher is located.",
      },
      pt: {
        s: "Cidade ou bairro onde o endereço físico oficial da editora musical está localizado.",
      },
    }[this.language].s;
  }

  PublisherCaeIpiLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "IPI", p: "Código IPI de la editorial", g: "M" },
      en: { s: "IPI", p: "Publisher's IPI code" },
      pt: { s: "IPI", p: "Código IPI da editora", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherCaeIpiLabel() {
    return this.PublisherCaeIpiLabelByLang();
  }

  get PublisherCaeIpiDescription() {
    return {
      es: {
        s: "IPI (Interested Party Information, anteriormente CAE) es un identificador internacional único de 9 a 11 dígitos que distingue a compositores, autores y editores musicales.",
      },
      en: {
        s: "IPI (Interested Party Information, formerly CAE) is a unique international identifier of 9 to 11 digits that distinguishes composers, authors, and music publishers.",
      },
      pt: {
        s: "IPI (Interested Party Information, anteriormente CAE) é um identificador internacional único de 9 a 11 dígitos que distingue compositores, autores e editores musicais.",
      },
    }[this.language].s;
  }

  PublisherRepresentativesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Representantes",
        p: "Personas que representan a la editorial",
        g: "M",
      },
      en: { s: "Representatives", p: "People who represent the publisher" },
      pt: {
        s: "Representantes",
        p: "Pessoas que representam a editora",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherRepresentativesLabel() {
    return this.PublisherRepresentativesLabelByLang();
  }

  get PublisherRepresentativesDescription() {
    return {
      es: {
        s: "Lista de representantes legales o apoderados de la editorial musical.",
      },
      en: {
        s: "List of legal representatives or attorneys of the music publisher.",
      },
      pt: {
        s: "Lista de representantes legais ou procuradores da editora musical.",
      },
    }[this.language].s;
  }

  PublisherManagementSocietiesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Sociedades de Gestión",
        p: "Sociedad/es de gestión colectiva del país de la editorial",
        g: "M",
      },
      en: {
        s: "Management Societies",
        p: "Collective management society/ies of the publisher's country",
      },
      pt: {
        s: "Sociedades de Gestão",
        p: "Sociedade/s de gestão coletiva do país da editora",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherManagementSocietiesLabel() {
    return this.PublisherManagementSocietiesLabelByLang();
  }

  get PublisherManagementSocietiesDescription() {
    return {
      es: {
        s: "Sociedad de Gestión Colectiva a la que la editorial musical está afiliada.",
      },
      en: {
        s: "Collective Management Organization to which the music publisher is affiliated.",
      },
      pt: {
        s: "Sociedade de Gestão Coletiva à qual a editora musical está afiliada.",
      },
    }[this.language].s;
  }

  PublisherCreatedAt_LabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Fecha de Creación", p: "F" },
      en: { s: "Creation Date" },
      pt: { s: "Data de Criação", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get PublisherCreatedAt_Label() {
    return this.PublisherCreatedAt_LabelByLang();
  }

  get PublisherCreatedAt_Description() {
    return {
      es: { s: "Fecha y hora del registro en el sistema de esta edición." },
      en: {
        s: "Date and time when this edtition was registered in the system.",
      },
      pt: { s: "Data e hora do registro no sistema desta edição." },
    }[this.language].s;
  }

  PublisherLastUpdatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Última Actualización", p: "F" },
      en: { s: "Last Updated" },
      pt: { s: "Última Atualização", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get PublisherLastUpdatedAt_Label() {
    return this.PublisherLastUpdatedAt_LabelByLang();
  }

  get PublisherLastUpdatedAt_Description() {
    return {
      es: { s: "Fecha y hora de la última modificación de esta edición." },
      en: { s: "Date and time of the last modification of this edition." },
      pt: { s: "Data e hora da última modificação desta edição." },
    }[this.language].s;
  }

  // AuthorPublisher

  get AuthorPublisherLabels() {
    return {
      es: {
        s: "Participación Editorial (Referencia)",
        p: "Participaciones Editoriales (Referencia)",
        g: "F",
      },
      en: {
        s: "Publisher Share (Reference)",
        p: "Publisher Shares (Reference)",
        g: undefined,
      },
      pt: {
        s: "Participação Editorial (Referência)",
        p: "Participações Editoriais (Referência)",
        g: "F",
      },
    }[this.language];
  }

  get AuthorPublisherSingular() {
    return this.AuthorPublisherLabels.s;
  }

  get AuthorPublisherPlural() {
    return this.AuthorPublisherLabels.p;
  }

  get AuthorPublisherGender() {
    return this.AuthorPublisherLabels.g;
  }

  get groupAuthorPublisherLabel() {
    return {
      es: {
        s: "Participación Editorial (Referencia)",
        p: "Participaciones Editoriales (Referencia)",
        g: "F",
      },
      en: {
        s: "Publisher Share (Reference)",
        p: "Publisher Shares (Reference)",
      },
      pt: {
        s: "Participação Editorial (Referência)",
        p: "Participações Editoriais (Referência)",
        g: "F",
      },
    }[this.language].s;
  }

  AuthorPublisherPublisherLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Editorial", p: "Editorial musical", g: "F" },
      en: { s: "Publisher", p: "Music publisher" },
      pt: { s: "Editora", p: "Editora musical", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorPublisherPublisherLabel() {
    return this.AuthorPublisherPublisherLabelByLang();
  }

  get AuthorPublisherPublisherDescription() {
    return {
      es: {
        s: "Referencia a la editorial musical involucrada en esta relación.",
      },
      en: {
        s: "Reference to the music publisher involved in this relationship.",
      },
      pt: { s: "Referência à editora musical envolvida nesta relação." },
    }[this.language].s;
  }

  AuthorPublisherPercentageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Porcentaje",
        p: "Porcentaje asignado al autor con esta editorial",
        g: "M",
      },
      en: {
        s: "Percentage",
        p: "Percentage assigned to the author with this publisher",
      },
      pt: {
        s: "Porcentagem",
        p: "Porcentagem atribuída ao autor com esta editora",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorPublisherPercentageLabel() {
    return this.AuthorPublisherPercentageLabelByLang();
  }

  get AuthorPublisherPercentageDescription() {
    return {
      es: {
        s: "Porcentaje de los derechos de la obra que corresponde al autor bajo el acuerdo con esta editorial.",
      },
      en: {
        s: "Percentage of the work's rights assigned to the author under the agreement with this publisher.",
      },
      pt: {
        s: "Porcentagem dos direitos da obra atribuída ao autor sob o acordo com esta editora.",
      },
    }[this.language].s;
  }

  AuthorPublisherDurationInYearsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Duración en años",
        p: "Cantidad de años para los contratos",
        g: "F",
      },
      en: { s: "Duration in Years", p: "Number of years for contracts" },
      pt: { s: "Duração em anos", p: "Número de anos para contratos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AuthorPublisherDurationInYearsLabel() {
    return this.AuthorPublisherDurationInYearsLabelByLang();
  }

  get AuthorPublisherDurationInYearsNullText() {
    return {
      es: {
        s: "Sin límite de duración",
        p: "Sin límite específico de duración de los contratos",
      },
      en: { s: "No limit", p: "No specific limit on contract duration" },
      pt: {
        s: "Sem limite",
        p: "Sem limite específico de duração dos contratos",
      },
    }[this.language].s;
  }

  get AuthorPublisherDurationInYearsDescription() {
    return {
      es: {
        s: "Duración estándar en años aplicada a los contratos con este autor/compositor.",
      },
      en: {
        s: "Standard duration in years applied to contracts with this author/composer.",
      },
      pt: {
        s: "Duração padrão em anos aplicada aos contratos com este autor/compositor.",
      },
    }[this.language].s;
  }

  AuthorPublisherAutoRenewalYearsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Renovación automática en años",
        p: "Cantidad de años para la renovación automática del contrato",
        g: "F",
      },
      en: {
        s: "Auto-renewal in Years",
        p: "Number of years for automatic contract renewal",
      },
      pt: {
        s: "Renovação automática em anos",
        p: "Número de anos para renovação automática do contrato",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AuthorPublisherAutoRenewalYearsLabel() {
    return this.AuthorPublisherAutoRenewalYearsLabelByLang();
  }

  get AuthorPublisherAutoRenewalYearsNullText() {
    return {
      es: { s: "NO", p: "Sin renovación automática" },
      en: { s: "NO", p: "No automatic renewal" },
      pt: { s: "NÃO", p: "Sem renovação automática" },
    }[this.language].s;
  }

  get AuthorPublisherAutoRenewalYearsDescription() {
    return {
      es: {
        s: "Si tiene un valor (entre 1 y 50), el contrato se renueva automáticamente cada esa cantidad de años. Si está vacío, no hay renovación automática.",
      },
      en: {
        s: "If set (between 1 and 50), the contract is automatically renewed every that number of years. If empty, there is no automatic renewal.",
      },
      pt: {
        s: "Se preenchido (entre 1 e 50), o contrato é renovado automaticamente a cada essa quantidade de anos. Se vazio, não há renovação automática.",
      },
    }[this.language].s;
  }

  // WorkAuthorPublisher

  get WorkAuthorPublisherLabels() {
    return {
      es: {
        s: "Participación Editorial (Obra)",
        p: "Participaciones Editoriales (Obra)",
        g: "F",
      },
      en: {
        s: "Publisher Share (Work)",
        p: "Publisher Shares (Work)",
        g: undefined,
      },
      pt: {
        s: "Participação Editorial (Obra)",
        p: "Participações Editoriais (Obra)",
        g: "F",
      },
    }[this.language];
  }

  get WorkAuthorPublisherSingular() {
    return this.WorkAuthorPublisherLabels.s;
  }

  get WorkAuthorPublisherPlural() {
    return this.WorkAuthorPublisherLabels.p;
  }

  get WorkAuthorPublisherGender() {
    return this.WorkAuthorPublisherLabels.g;
  }

  get groupWorkAuthorPublisherLabel() {
    return {
      es: {
        s: "Participación Editorial (Obra)",
        p: "Participaciones Editoriales (Obra)",
        g: "F",
      },
      en: { s: "Publisher Share (Work)", p: "Publisher Shares (Work)" },
      pt: {
        s: "Participação Editorial (Obra)",
        p: "Participações Editoriais (Obra)",
        g: "F",
      },
    }[this.language].s;
  }

  WorkAuthorPublisherPublisherLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Editorial", p: "Editorial musical", g: "F" },
      en: { s: "Publisher", p: "Music publisher" },
      pt: { s: "Editora", p: "Editora musical", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorPublisherPublisherLabel() {
    return this.WorkAuthorPublisherPublisherLabelByLang();
  }

  get WorkAuthorPublisherPublisherDescription() {
    return {
      es: {
        s: "Referencia a la editorial musical involucrada en esta relación.",
      },
      en: {
        s: "Reference to the music publisher involved in this relationship.",
      },
      pt: { s: "Referência à editora musical envolvida nesta relação." },
    }[this.language].s;
  }

  WorkAuthorPublisherPercentageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Porcentaje Cedido",
        p: "Porcentaje sobre la participación del autor",
        g: "M",
      },
      en: { s: "Assigned Percentage", p: "Percentage of author's share" },
      pt: {
        s: "Porcentagem Cedida",
        p: "Porcentagem sobre a participação do autor",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorPublisherPercentageLabel() {
    return this.WorkAuthorPublisherPercentageLabelByLang();
  }

  get WorkAuthorPublisherPercentageDescription() {
    return {
      es: {
        s: "Porcentaje que el autor cede a la editorial, calculado sobre su participación total en la obra (letra + música). Al cargar la obra se inicializa con el valor por defecto definido en la relación AuthorPublisher (autor↔editorial); puede modificarse acá para reflejar el acuerdo específico de esta obra.",
      },
      en: {
        s: "Percentage the author assigns to the publisher, calculated on the author's total share in the work (lyrics + music). Initialized from the default set in the AuthorPublisher relation (author↔publisher) when the work is created; can be modified here to reflect the specific agreement for this work.",
      },
      pt: {
        s: "Porcentagem que o autor cede à editora, calculada sobre sua participação total na obra (letra + música). Inicializada a partir do valor padrão definido na relação AuthorPublisher (autor↔editora) ao criar a obra; pode ser modificada aqui para refletir o acordo específico desta obra.",
      },
    }[this.language].s;
  }

  WorkAuthorPublisherDurationInYearsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Duración en años", p: "Duración del contrato en años", g: "F" },
      en: { s: "Duration in Years", p: "Contract duration in years" },
      pt: { s: "Duração em anos", p: "Duração do contrato em anos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorPublisherDurationInYearsLabel() {
    return this.WorkAuthorPublisherDurationInYearsLabelByLang();
  }

  get WorkAuthorPublisherDurationInYearsNullText() {
    return {
      es: {
        s: "Sin límite de duración",
        p: "Sin límite específico de duración de los contratos",
      },
      en: { s: "No limit", p: "No specific limit on contract duration" },
      pt: {
        s: "Sem limite",
        p: "Sem limite específico de duração dos contratos",
      },
    }[this.language].s;
  }

  get WorkAuthorPublisherDurationInYearsDescription() {
    return {
      es: {
        s: "Período en años durante el cual la cesión a la editorial permanece vigente para esta obra específica. Al cargar la obra se inicializa con el valor por defecto definido en la relación AuthorPublisher; puede modificarse acá para esta obra.",
      },
      en: {
        s: "Period in years during which the publisher assignment remains in effect for this specific work. Initialized from the default set in the AuthorPublisher relation when the work is created; can be modified here for this work.",
      },
      pt: {
        s: "Período em anos durante o qual a cessão à editora permanece vigente para esta obra específica. Inicializada a partir do valor padrão definido na relação AuthorPublisher ao criar a obra; pode ser modificada aqui para esta obra.",
      },
    }[this.language].s;
  }

  WorkAuthorPublisherAutoRenewalYearsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Renovación automática en años",
        p: "Cantidad de años para la renovación automática del contrato",
        g: "F",
      },
      en: {
        s: "Auto-renewal in Years",
        p: "Number of years for automatic contract renewal",
      },
      pt: {
        s: "Renovação automática em anos",
        p: "Número de anos para renovação automática do contrato",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorPublisherAutoRenewalYearsLabel() {
    return this.WorkAuthorPublisherAutoRenewalYearsLabelByLang();
  }

  get WorkAuthorPublisherAutoRenewalYearsNullText() {
    return {
      es: { s: "NO", p: "Sin renovación automática" },
      en: { s: "NO", p: "No automatic renewal" },
      pt: { s: "NÃO", p: "Sem renovação automática" },
    }[this.language].s;
  }

  get WorkAuthorPublisherAutoRenewalYearsDescription() {
    return {
      es: {
        s: "Si tiene un valor (entre 1 y 50), el contrato se renueva automáticamente cada esa cantidad de años. Si está vacío, no hay renovación automática. Al cargar la obra se inicializa con el valor por defecto definido en la relación AuthorPublisher; puede modificarse acá para esta obra.",
      },
      en: {
        s: "If set (between 1 and 50), the contract is automatically renewed every that number of years. If empty, there is no automatic renewal. Initialized from the default set in the AuthorPublisher relation when the work is created; can be modified here for this work.",
      },
      pt: {
        s: "Se preenchido (entre 1 e 50), o contrato é renovado automaticamente a cada essa quantidade de anos. Se vazio, não há renovação automática. Inicializada a partir do valor padrão definido na relação AuthorPublisher ao criar a obra; pode ser modificada aqui para esta obra.",
      },
    }[this.language].s;
  }

  // PublisherCollectingSociety

  get PublisherCollectingSocietyLabels() {
    return {
      es: {
        s: "Relación Editorial-SGC",
        p: "Relaciones Editorial-SGC",
        g: "F",
      },
      en: {
        s: "Publisher-CMS Relationship",
        p: "Publisher-CMS Relationships",
        g: undefined,
      },
      pt: { s: "Relação Editora-SGC", p: "Relações Editora-SGC", g: "F" },
    }[this.language];
  }

  get PublisherCollectingSocietySingular() {
    return this.PublisherCollectingSocietyLabels.s;
  }

  get PublisherCollectingSocietyPlural() {
    return this.PublisherCollectingSocietyLabels.p;
  }

  get PublisherCollectingSocietyGender() {
    return this.PublisherCollectingSocietyLabels.g;
  }

  get groupPublisherCollectingSocietyLabel() {
    return {
      es: {
        s: "Relación Editorial-SGC",
        p: "Relaciones Editorial-SGC",
        g: "F",
      },
      en: { s: "Publisher-CMS Relationship", p: "Publisher-CMS Relationships" },
      pt: { s: "Relação Editora-SGC", p: "Relações Editora-SGC", g: "F" },
    }[this.language].s;
  }

  PublisherCollectingSocietyCollectingSocietyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Sociedad de Gestión",
        p: "Sociedad de gestión colectiva",
        g: "F",
      },
      en: { s: "Management Society", p: "Collective management society" },
      pt: {
        s: "Sociedade de Gestão",
        p: "Sociedade de gestão coletiva",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherCollectingSocietyCollectingSocietyLabel() {
    return this.PublisherCollectingSocietyCollectingSocietyLabelByLang();
  }

  get PublisherCollectingSocietyCollectingSocietyDescription() {
    return {
      es: {
        s: "Sociedad de Gestión Colectiva a la que la editorial musical está afiliada.",
      },
      en: {
        s: "Collective Management Organization to which the music publisher is affiliated.",
      },
      pt: {
        s: "Sociedade de Gestão Coletiva à qual a editora musical está afiliada.",
      },
    }[this.language].s;
  }

  PublisherCollectingSocietyCmsAccountNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Número de Cuenta SGC",
        p: "Número de cuenta en la sociedad de gestión",
        g: "M",
      },
      en: {
        s: "CMS Account Number",
        p: "Account number in the collective management society",
      },
      pt: {
        s: "Número de conta SGC",
        p: "Número da conta na sociedade de gestão coletiva",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherCollectingSocietyCmsAccountNumberLabel() {
    return this.PublisherCollectingSocietyCmsAccountNumberLabelByLang();
  }

  get PublisherCollectingSocietyCmsAccountNumberDescription() {
    return {
      es: {
        s: "Número de cuenta de la editorial musical en la sociedad de gestión colectiva.",
      },
      en: {
        s: "Account number of the music publisher in the collective management organization.",
      },
      pt: {
        s: "Número de conta da editora musical na sociedade de gestão coletiva.",
      },
    }[this.language].s;
  }

  // PublisherRepresentative

  get PublisherRepresentativeLabels() {
    return {
      es: {
        s: "Representante de Editorial",
        p: "Representantes de Editorial",
        g: "M",
      },
      en: {
        s: "Publisher Representative",
        p: "Publisher Representatives",
        g: undefined,
      },
      pt: {
        s: "Representante de Editora",
        p: "Representantes de Editora",
        g: "M",
      },
    }[this.language];
  }

  get PublisherRepresentativeSingular() {
    return this.PublisherRepresentativeLabels.s;
  }

  get PublisherRepresentativePlural() {
    return this.PublisherRepresentativeLabels.p;
  }

  get PublisherRepresentativeGender() {
    return this.PublisherRepresentativeLabels.g;
  }

  get groupPublisherRepresentativeLabel() {
    return {
      es: {
        s: "Representante de Editorial",
        p: "Representantes de Editorial",
        g: "M",
      },
      en: { s: "Publisher Representative", p: "Publisher Representatives" },
      pt: {
        s: "Representante de Editora",
        p: "Representantes de Editora",
        g: "M",
      },
    }[this.language].s;
  }

  PublisherRepresentativeNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre completo del representante", g: "M" },
      en: { s: "Name", p: "Full name of the representative" },
      pt: { s: "Nome", p: "Nome completo do representante", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherRepresentativeNameLabel() {
    return this.PublisherRepresentativeNameLabelByLang();
  }

  get PublisherRepresentativeNameDescription() {
    return {
      es: {
        s: "Nombre y apellido del representante oficial de la editorial musical.",
      },
      en: {
        s: "Full name of the official representative of the music publisher.",
      },
      pt: { s: "Nome completo do representante oficial da editora musical." },
    }[this.language].s;
  }

  PublisherRepresentativeRoleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Rol", p: "Función del representante en la editorial", g: "M" },
      en: { s: "Role", p: "Role of the representative in the publisher" },
      pt: { s: "Papel", p: "Função do representante na editora" },
    }[lang ? lang : this.language].s;
  }

  get PublisherRepresentativeRoleLabel() {
    return this.PublisherRepresentativeRoleLabelByLang();
  }

  get PublisherRepresentativeRoleDescription() {
    return {
      es: { s: "Rol del representante oficial de la editorial musical." },
      en: { s: "Role of the official representative of the music publisher." },
      pt: { s: "Função do representante oficial da editora musical." },
    }[this.language].s;
  }

  PublisherRepresentativeEmailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Email", p: "Correo electrónico de contacto", g: "M" },
      en: { s: "Email", p: "Contact email address" },
      pt: { s: "Email", p: "Endereço de email de contato", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PublisherRepresentativeEmailLabel() {
    return this.PublisherRepresentativeEmailLabelByLang();
  }

  get PublisherRepresentativeEmailDescription() {
    return {
      es: {
        s: "Email oficial del representante oficial de la editorial musical.",
      },
      en: {
        s: "Official email of the official representative of the music publisher.",
      },
      pt: { s: "E-mail oficial do representante oficial da editora musical." },
    }[this.language].s;
  }

  PublisherRepresentativePhoneLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Teléfono", p: "Número de teléfono de contacto", g: "M" },
      en: { s: "Phone", p: "Contact phone number" },
      pt: { s: "Telefone", p: "Número de telefone de contato" },
    }[lang ? lang : this.language].s;
  }

  get PublisherRepresentativePhoneLabel() {
    return this.PublisherRepresentativePhoneLabelByLang();
  }

  get PublisherRepresentativePhoneDescription() {
    return {
      es: {
        s: "Teléfono oficial del representante oficial de la editorial musical.",
      },
      en: {
        s: "Official phone of the official representative of the music publisher.",
      },
      pt: {
        s: "Telefone oficial do representante oficial da editora musical.",
      },
    }[this.language].s;
  }

  PublisherRepresentativeIsSignerLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Es Firmante",
        p: "Indica si este representante firma los contratos",
        g: "M",
      },
      en: {
        s: "Is Signer",
        p: "Indicates if this representative signs contracts",
      },
      pt: {
        s: "É Signatário",
        p: "Indica se este representante assina os contratos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PublisherRepresentativeIsSignerLabel() {
    return this.PublisherRepresentativeIsSignerLabelByLang();
  }

  get PublisherRepresentativeIsSignerDescription() {
    return {
      es: {
        s: "Cuando está activo, este representante será incluido como firmante en los contratos de la editorial.",
      },
      en: {
        s: "When active, this representative will be included as a signer in publisher contracts.",
      },
      pt: {
        s: "Quando ativo, este representante será incluído como signatário nos contratos da editora.",
      },
    }[this.language].s;
  }

  // EditionType

  get EditionTypeLabels() {
    return {
      es: { s: "Tipo de Edición", p: "Tipos de Edición", g: "M" },
      en: { s: "Edition Type", p: "Edition Types", g: undefined },
      pt: { s: "Tipo de Edição", p: "Tipos de Edição", g: "M" },
    }[this.language];
  }

  get EditionTypeSingular() {
    return this.EditionTypeLabels.s;
  }

  get EditionTypePlural() {
    return this.EditionTypeLabels.p;
  }

  get EditionTypeGender() {
    return this.EditionTypeLabels.g;
  }

  get groupEditionTypeLabel() {
    return {
      es: { s: "Tipo de Edición", p: "Tipos de Edición", g: "M" },
      en: { s: "Edition Type", p: "Edition Types" },
      pt: { s: "Tipo de Edição", p: "Tipos de Edição", g: "M" },
    }[this.language].s;
  }

  EditionTypeNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del tipo de edición", g: "M" },
      en: { s: "Name", p: "Name of the edition type" },
      pt: { s: "Nome", p: "Nome do tipo de edição", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionTypeNameLabel() {
    return this.EditionTypeNameLabelByLang();
  }

  get EditionTypeNameDescription() {
    return {
      es: {
        s: "Obra Suelta = Edición con una sola obra.  / Álbum = Edición con mas de una obra. / Obra Integral = Edición con 2 o más obras que guardan entre sí una unidad temática, argumental o estructural. Ejemplos clásicos: una ópera, una cantata, una suite.",
      },
      en: {
        s: "Single Work = Edition with a single work. / Album = Edition with more than one work. / Integral Work = Edition with 2 or more works that share a thematic, narrative, or structural unity. Classic examples: an opera, a cantata, a suite.",
      },
      pt: {
        s: "Obra Avulsa = Edição com uma única obra. / Álbum = Edição com mais de uma obra. / Obra Integral = Edição com 2 ou mais obras que compartilham uma unidade temática, narrativa ou estrutural. Exemplos clássicos: uma ópera, uma cantata, uma suíte.",
      },
    }[this.language].s;
  }

  // MusicalWork

  get MusicalWorkLabels() {
    return {
      es: { s: "Obra Musical", p: "Obras Musicales", g: "F" },
      en: { s: "Musical Work", p: "Musical Works", g: undefined },
      pt: { s: "Obra Musical", p: "Obras Musicais", g: "F" },
    }[this.language];
  }

  get MusicalWorkSingular() {
    return this.MusicalWorkLabels.s;
  }

  get MusicalWorkPlural() {
    return this.MusicalWorkLabels.p;
  }

  get MusicalWorkGender() {
    return this.MusicalWorkLabels.g;
  }

  get groupMusicalWorkLabel() {
    return {
      es: { s: "Obra Musical", p: "Obras Musicales", g: "F" },
      en: { s: "Musical Work", p: "Musical Works" },
      pt: { s: "Obra Musical", p: "Obras Musicais", g: "F" },
    }[this.language].s;
  }

  get groupMusicalWorkWorkInfoLabel() {
    return {
      es: { s: "Información de la Obra", p: "F" },
      en: { s: "Work Information" },
      pt: { s: "Informação da Obra", p: "F" },
    }[this.language].s;
  }

  get groupMusicalWorkWorkRegistrationLabel() {
    return {
      es: { s: "Registro y Gestión", p: "M" },
      en: { s: "Registration & Management" },
      pt: { s: "Registro e Gestão", p: "M" },
    }[this.language].s;
  }

  get groupMusicalWorkWorkContentLabel() {
    return {
      es: { s: "Contenido", p: "M" },
      en: { s: "Content" },
      pt: { s: "Conteúdo", p: "M" },
    }[this.language].s;
  }

  get groupMusicalWorkContractsLabel() {
    return {
      es: { s: "Contratos", p: "M" },
      en: { s: "Contracts" },
      pt: { s: "Contratos", p: "M" },
    }[this.language].s;
  }

  get groupMusicalWorkSystemDatesLabel() {
    return {
      es: { s: "Fechas de sistema", p: "F" },
      en: { s: "System dates" },
      pt: { s: "Datas de sistema", p: "F" },
    }[this.language].s;
  }

  MusicalWorkTitleLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Título", p: "Título de la obra", g: "M" },
      en: { s: "Title", p: "Title of the work" },
      pt: { s: "Título", p: "Título da obra", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkTitleLabel() {
    return this.MusicalWorkTitleLabelByLang();
  }

  get MusicalWorkTitleDescription() {
    return {
      es: { s: "Título principal por el cual se conoce la obra musical." },
      en: { s: "Primary title by which the musical work is known." },
      pt: { s: "Título principal pelo qual a obra musical é conhecida." },
    }[this.language].s;
  }

  MusicalWorkMusicalGenreLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Género musical", p: "Género musical", g: "M" },
      en: { s: "Musical genre", p: "Musical genre" },
      pt: { s: "Gênero musical", p: "Gênero musical", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkMusicalGenreLabel() {
    return this.MusicalWorkMusicalGenreLabelByLang();
  }

  get MusicalWorkMusicalGenreDescription() {
    return {
      es: { s: "Género Musical de la obra musical." },
      en: { s: "Musical genre of the musical work." },
      pt: { s: "Gênero musical da obra musical." },
    }[this.language].s;
  }

  MusicalWorkAlternativeTitlesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Títulos Alternativos",
        p: "Otros títulos con los que se conoce la obra",
        g: "M",
      },
      en: {
        s: "Alternative Titles",
        p: "Other titles by which the work is known",
      },
      pt: {
        s: "Títulos Alternativos",
        p: "Outros títulos pelos quais a obra é conhecida",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkAlternativeTitlesLabel() {
    return this.MusicalWorkAlternativeTitlesLabelByLang();
  }

  get MusicalWorkAlternativeTitlesDescription() {
    return {
      es: {
        s: "Tambien conocidos como 'Subtítulos': Otros títulos o versiones del título de la obra.",
      },
      en: {
        s: "Also known as 'Subtitles': Other titles or versions of the work's title.",
      },
      pt: {
        s: "Também conhecidos como 'Subtítulos': Outros títulos ou versões do título da obra.",
      },
    }[this.language].s;
  }

  MusicalWorkAuthorsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Autores/Compositores",
        p: "Compositores y letristas con sus porcentajes",
        g: "M",
      },
      en: {
        s: "Authors/Composers",
        p: "Composers and lyricists with their percentages",
      },
      pt: {
        s: "Autores/Compositores",
        p: "Compositores e letristas com suas porcentagens",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkAuthorsLabel() {
    return this.MusicalWorkAuthorsLabelByLang();
  }

  get MusicalWorkAuthorsDescription() {
    return {
      es: { s: "Autores/Compositores de la obra musical." },
      en: { s: "Authors/Composers of the musical work." },
      pt: { s: "Autores/Compositores da obra musical." },
    }[this.language].s;
  }

  MusicalWorkNotesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Notas", p: "Notas adicionales", g: "F" },
      en: { s: "Notes", p: "Additional notes" },
      pt: { s: "Notas", p: "Notas adicionais", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkNotesLabel() {
    return this.MusicalWorkNotesLabelByLang();
  }

  get MusicalWorkNotesDescription() {
    return {
      es: {
        s: "Comentarios o información adicional de la obra. Esta información no se incluirá en ningún tipo de documentación generada.",
      },
      en: {
        s: "Additional comments or information about the work. This information will not be included in any generated documentation.",
      },
      pt: {
        s: "Comentários ou informações adicionais da obra. Esta informação não será incluída em nenhum tipo de documentação gerada.",
      },
    }[this.language].s;
  }

  MusicalWorkPerformersLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intérpretes", p: "Artistas que interpretan la obra", g: "M" },
      en: { s: "Performers", p: "Artists who perform the work" },
      pt: { s: "Intérpretes", p: "Artistas que interpretam a obra", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkPerformersLabel() {
    return this.MusicalWorkPerformersLabelByLang();
  }

  get MusicalWorkPerformersDescription() {
    return {
      es: { s: "Artistas o grupos musicales que han interpretado esta obra." },
      en: { s: "Artists or musical groups that have performed this work." },
      pt: { s: "Artistas ou grupos musicais que interpretaram esta obra." },
    }[this.language].s;
  }

  MusicalWorkCmsIdLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "ID de SGC",
        p: "Identificador en la sociedad de gestión colectiva",
        g: "M",
      },
      en: { s: "CMS ID", p: "Identifier in the collective management society" },
      pt: {
        s: "ID de SGC",
        p: "Identificador na sociedade de gestão coletiva",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkCmsIdLabel() {
    return this.MusicalWorkCmsIdLabelByLang();
  }

  get MusicalWorkCmsIdDescription() {
    return {
      es: {
        s: "Número o código de identificación asignado a la obra por la Sociedad De Gestión Colectiva que la haya registrado",
      },
      en: {
        s: "Number or identification code assigned to the work by the Collective Management Organization that registered it",
      },
      pt: {
        s: "Número ou código de identificação atribuído à obra pela Sociedade De Gestão Coletiva que a tenha registrado",
      },
    }[this.language].s;
  }

  MusicalWorkTerritoriesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Territorio",
        p: "Territorios donde se aplican los derechos",
        g: "M",
      },
      en: { s: "Territorie", p: "Territories where rights apply" },
      pt: {
        s: "Território",
        p: "Territórios onde os direitos se aplicam",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkTerritoriesLabel() {
    return this.MusicalWorkTerritoriesLabelByLang();
  }

  get MusicalWorkTerritoriesDescription() {
    return {
      es: {
        s: "Territorios geográficos en los que los derechos de esta obra son administrados por la/s editorial/es musical/es de los autores de la obra.",
      },
      en: {
        s: "Geographic territories in which the rights to this work are managed by the music publisher(s) of the work's authors.",
      },
      pt: {
        s: "Territórios geográficos nos quais os direitos desta obra são administrados pela(s) editora(s) musical(is) dos autores da obra.",
      },
    }[this.language].s;
  }

  MusicalWorkIswcLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "ISWC", p: "International Standard Musical Work Code", g: "M" },
      en: { s: "ISWC", p: "International Standard Musical Work Code" },
      pt: { s: "ISWC", p: "International Standard Musical Work Code", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkIswcLabel() {
    return this.MusicalWorkIswcLabelByLang();
  }

  get MusicalWorkIswcDescription() {
    return {
      es: {
        s: "El ISWC (International Standard Musical Work Code) es un número de referencia único, permanente y reconocido mundialmente para identificar obras musicales (composiciones), no grabaciones. Es una T seguida de 10 dígitos, el último de control (ej. T-034.524.680-1); ayuda a rastrear el uso y facilita el cobro de derechos de autor.",
      },
      en: {
        s: "The ISWC (International Standard Musical Work Code) is a unique, permanent, and globally recognized reference number to identify musical works (compositions), not recordings. It is a T followed by 10 digits, the last one a check digit (e.g., T-034.524.680-1); it helps track usage and facilitates copyright collection.",
      },
      pt: {
        s: "O ISWC (International Standard Musical Work Code) é um número de referência único, permanente e mundialmente reconhecido para identificar obras musicais (composições), não gravações. É um T seguido de 10 dígitos, o último de controle (ex. T-034.524.680-1); ajuda a rastrear o uso e facilita a arrecadação de direitos autorais.",
      },
    }[this.language].s;
  }

  MusicalWorkWorkNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID para CWR", p: "ID Obra", g: "M" },
      en: { s: "ID for CWR", p: "Work ID" },
      pt: { s: "ID para CWR", p: "ID Obra", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkWorkNumberLabel() {
    return this.MusicalWorkWorkNumberLabelByLang();
  }

  get MusicalWorkWorkNumberDescription() {
    return {
      es: {
        s: "En un archivo CWR, el Work Number (específicamente llamado Submitter Work Number) es el código de identificación único que el editor musical le asigna a una canción dentro de su propio catálogo.",
      },
      en: {
        s: "In a CWR file, the Work Number (specifically called Submitter Work Number) is the unique identification code that the music publisher assigns to a song within their own catalog.",
      },
      pt: {
        s: "Em um arquivo CWR, o Work Number (especificamente chamado Submitter Work Number) é o código de identificação único que a editora musical atribui a uma música dentro de seu próprio catálogo.",
      },
    }[this.language].s;
  }

  MusicalWorkScoreLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Partitura", p: "Partitura musical de la obra", g: "F" },
      en: { s: "Score", p: "Musical score of the work" },
      pt: { s: "Partitura", p: "Partitura musical da obra", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkScoreLabel() {
    return this.MusicalWorkScoreLabelByLang();
  }

  get MusicalWorkScoreDescription() {
    return {
      es: {
        s: "Archivo que contiene la partitura de la obra musical. En el caso que la obra contenga letra, la partitura deberá contener la letra.",
      },
      en: {
        s: "File containing the score of the musical work. If the work contains lyrics, the score must include the lyrics.",
      },
      pt: {
        s: "Arquivo contendo a partitura da obra musical. No caso de a obra conter letra, a partitura deverá incluir a letra.",
      },
    }[this.language].s;
  }

  MusicalWorkPhonogramsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fonogramas", p: "Grabaciones de esta obra", g: "M" },
      en: { s: "Phonograms", p: "Recordings of this work" },
      pt: { s: "Fonogramas", p: "Gravações desta obra", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkPhonogramsLabel() {
    return this.MusicalWorkPhonogramsLabelByLang();
  }

  get MusicalWorkPhonogramsDescription() {
    return {
      es: {
        s: "Conjunto de fonogramas (grabaciones) de esta obra musical. Cada fonograma tiene su propio ISRC, intérpretes y archivo de audio.",
      },
      en: {
        s: "Set of phonograms (recordings) of this musical work. Each phonogram has its own ISRC, performers, and audio file.",
      },
      pt: {
        s: "Conjunto de fonogramas (gravações) desta obra musical. Cada fonograma tem seu próprio ISRC, intérpretes e arquivo de áudio.",
      },
    }[this.language].s;
  }

  MusicalWorkOriginalLyricsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Letra Original", p: "Texto original de la obra", g: "F" },
      en: { s: "Original Lyrics", p: "Original text of the work" },
      pt: { s: "Letra Original", p: "Texto original da obra", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkOriginalLyricsLabel() {
    return this.MusicalWorkOriginalLyricsLabelByLang();
  }

  get MusicalWorkOriginalLyricsDescription() {
    return {
      es: { s: "La letra completa de la canción en su idioma original." },
      en: { s: "The full lyrics of the song in their original language." },
      pt: { s: "A letra completa da música em seu idioma original." },
    }[this.language].s;
  }

  MusicalWorkContractObservationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Observación de contrato",
        p: "Texto de observación en HTML",
        g: "F",
      },
      en: { s: "Contract observation", p: "Observation text in HTML" },
      pt: {
        s: "Observação de contrato",
        p: "Texto de observação em HTML",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkContractObservationLabel() {
    return this.MusicalWorkContractObservationLabelByLang();
  }

  get MusicalWorkContractObservationDescription() {
    return {
      es: {
        s: "El contenido de la observación se insertará en todos los contratos en los que se incluya la obra.",
      },
      en: {
        s: "The content of the observation will be inserted into every contract that includes the work.",
      },
      pt: {
        s: "O conteúdo da observação será inserido em todos os contratos que incluam a obra.",
      },
    }[this.language].s;
  }

  MusicalWorkCreatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Creación", p: "F" },
      en: { s: "Creation Date" },
      pt: { s: "Data de Criação", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkCreatedAt_Label() {
    return this.MusicalWorkCreatedAt_LabelByLang();
  }

  get MusicalWorkCreatedAt_Description() {
    return {
      es: {
        s: "Fecha y hora del registro en el sistema de esta obra musical.",
      },
      en: {
        s: "Date and time when this musical work was registered in the system.",
      },
      pt: { s: "Data e hora do registro no sistema desta obra musical." },
    }[this.language].s;
  }

  MusicalWorkLastUpdatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Última Actualización", p: "F" },
      en: { s: "Last Updated" },
      pt: { s: "Última Atualização", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkLastUpdatedAt_Label() {
    return this.MusicalWorkLastUpdatedAt_LabelByLang();
  }

  get MusicalWorkLastUpdatedAt_Description() {
    return {
      es: { s: "Fecha y hora de la última modificación de esta obra musical." },
      en: { s: "Date and time of the last modification of this musical work." },
      pt: { s: "Data e hora da última modificação desta obra musical." },
    }[this.language].s;
  }

  // Phonogram

  get PhonogramLabels() {
    return {
      es: { s: "Fonograma", p: "Fonogramas", g: "M" },
      en: { s: "Phonogram", p: "Phonograms", g: undefined },
      pt: { s: "Fonograma", p: "Fonogramas", g: "M" },
    }[this.language];
  }

  get PhonogramSingular() {
    return this.PhonogramLabels.s;
  }

  get PhonogramPlural() {
    return this.PhonogramLabels.p;
  }

  get PhonogramGender() {
    return this.PhonogramLabels.g;
  }

  get groupPhonogramLabel() {
    return {
      es: { s: "Fonograma", p: "Fonogramas", g: "M" },
      en: { s: "Phonogram", p: "Phonograms" },
      pt: { s: "Fonograma", p: "Fonogramas", g: "M" },
    }[this.language].s;
  }

  get groupPhonogramPhonogramInfoLabel() {
    return {
      es: { s: "Información del Fonograma", p: "F" },
      en: { s: "Phonogram Information" },
      pt: { s: "Informação do Fonograma", p: "F" },
    }[this.language].s;
  }

  PhonogramTrackNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre del Track", p: "M" },
      en: { s: "Track Name" },
      pt: { s: "Nome da Faixa", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get PhonogramTrackNameLabel() {
    return this.PhonogramTrackNameLabelByLang();
  }

  get PhonogramTrackNameDescription() {
    return {
      es: {
        s: "Nombre del track tal como aparece publicado. Puede incluir indicación de versión (ej. 'Mi Canción - Live', 'Mi Canción - Radio Edit').",
      },
      en: {
        s: "Track name as published. May include version indication (e.g., 'My Song - Live', 'My Song - Radio Edit').",
      },
      pt: {
        s: "Nome da faixa como publicada. Pode incluir indicação de versão (ex. 'Minha Música - Ao Vivo', 'Minha Música - Radio Edit').",
      },
    }[this.language].s;
  }

  PhonogramPerformersLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Intérpretes",
        p: "Artistas que participan en esta grabación",
        g: "M",
      },
      en: { s: "Performers", p: "Artists participating in this recording" },
      pt: {
        s: "Intérpretes",
        p: "Artistas que participam desta gravação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PhonogramPerformersLabel() {
    return this.PhonogramPerformersLabelByLang();
  }

  get PhonogramPerformersDescription() {
    return {
      es: {
        s: "Artistas o grupos musicales que interpretan esta grabación específica.",
      },
      en: {
        s: "Artists or musical groups performing this specific recording.",
      },
      pt: {
        s: "Artistas ou grupos musicais que interpretam esta gravação específica.",
      },
    }[this.language].s;
  }

  PhonogramAlbumNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Nombre del Álbum",
        p: "Álbum al que pertenece el fonograma",
        g: "M",
      },
      en: { s: "Album Name", p: "Album the phonogram belongs to" },
      pt: {
        s: "Nome do Álbum",
        p: "Álbum ao qual o fonograma pertence",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PhonogramAlbumNameLabel() {
    return this.PhonogramAlbumNameLabelByLang();
  }

  get PhonogramAlbumNameDescription() {
    return {
      es: {
        s: "Nombre del álbum o lanzamiento al que pertenece este fonograma, si aplica.",
      },
      en: {
        s: "Name of the album or release this phonogram belongs to, if applicable.",
      },
      pt: {
        s: "Nome do álbum ou lançamento ao qual este fonograma pertence, se aplicável.",
      },
    }[this.language].s;
  }

  PhonogramPublicationYearLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Año de Publicación", p: "M" },
      en: { s: "Publication Year" },
      pt: { s: "Ano de Publicação", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get PhonogramPublicationYearLabel() {
    return this.PhonogramPublicationYearLabelByLang();
  }

  get PhonogramPublicationYearDescription() {
    return {
      es: { s: "Año en que fue publicado el fonograma." },
      en: { s: "Year the phonogram was published." },
      pt: { s: "Ano em que o fonograma foi publicado." },
    }[this.language].s;
  }

  PhonogramIsrcLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "ISRC", p: "International Standard Recording Code", g: "M" },
      en: { s: "ISRC", p: "International Standard Recording Code" },
      pt: { s: "ISRC", p: "International Standard Recording Code", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PhonogramIsrcLabel() {
    return this.PhonogramIsrcLabelByLang();
  }

  get PhonogramIsrcDescription() {
    return {
      es: {
        s: "Código ISRC (International Standard Recording Code) — identificador único internacional para esta grabación sonora.",
      },
      en: {
        s: "ISRC (International Standard Recording Code) — unique international identifier for this sound recording.",
      },
      pt: {
        s: "Código ISRC (International Standard Recording Code) — identificador único internacional para esta gravação sonora.",
      },
    }[this.language].s;
  }

  PhonogramOriginalProducerLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Productor Originario",
        p: "Nombre del productor fonográfico originario",
        g: "M",
      },
      en: {
        s: "Original Producer",
        p: "Name of the original phonographic producer",
      },
      pt: {
        s: "Produtor Originário",
        p: "Nome do produtor fonográfico originário",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PhonogramOriginalProducerLabel() {
    return this.PhonogramOriginalProducerLabelByLang();
  }

  get PhonogramOriginalProducerDescription() {
    return {
      es: {
        s: "Nombre o razón social del productor fonográfico originario de esta grabación.",
      },
      en: {
        s: "Name or business name of the original phonographic producer of this recording.",
      },
      pt: {
        s: "Nome ou razão social do produtor fonográfico originário desta gravação.",
      },
    }[this.language].s;
  }

  PhonogramAudioFileLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Archivo de Audio",
        p: "Grabación de audio del fonograma",
        g: "M",
      },
      en: { s: "Audio File", p: "Audio recording of the phonogram" },
      pt: {
        s: "Arquivo de Áudio",
        p: "Gravação de áudio do fonograma",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PhonogramAudioFileLabel() {
    return this.PhonogramAudioFileLabelByLang();
  }

  get PhonogramAudioFileDescription() {
    return {
      es: { s: "Archivo de audio que contiene esta grabación (mp3 o m4a)." },
      en: { s: "Audio file containing this recording (mp3 or m4a)." },
      pt: { s: "Arquivo de áudio contendo esta gravação (mp3 ou m4a)." },
    }[this.language].s;
  }

  PhonogramMainAudioLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Audio de Registro SADAIC",
        p: "Audio elegido para la entrega a SADAIC",
        g: "M",
      },
      en: {
        s: "Audio for SADAIC registration",
        p: "Audio chosen for the SADAIC delivery",
      },
      pt: {
        s: "Áudio de Registração SADAIC",
        p: "Áudio escolhido para a entrega à SADAIC",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PhonogramMainAudioLabel() {
    return this.PhonogramMainAudioLabelByLang();
  }

  get PhonogramMainAudioDescription() {
    return {
      es: {
        s: "Indica que este fonograma es el audio principal de la obra: es el que va en la entrega a SADAIC de las ediciones con audios. Si la obra tiene 2 o más fonogramas, debe haber exactamente uno marcado antes de generar la documentación. Puede cambiarse entre ediciones; cada edición congela la elección vigente al firmarse.",
      },
      en: {
        s: "Marks this phonogram as the main audio of the work: it is the one included in the SADAIC delivery for editions with audio. If the work has 2 or more phonograms, exactly one must be marked before generating the documentation. It can change between editions; each edition freezes the choice in force when signed.",
      },
      pt: {
        s: "Indica que este fonograma é o áudio principal da obra: é o que vai na entrega à SADAIC nas edições com áudios. Se a obra tem 2 ou mais fonogramas, deve haver exatamente um marcado antes de gerar a documentação. Pode mudar entre edições; cada edição congela a escolha vigente ao ser assinada.",
      },
    }[this.language].s;
  }

  PhonogramLinksLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Links", p: "Links" },
      en: { s: "Platform Link", p: "Platform Links" },
      pt: { s: "Link de Plataforma", p: "Links de Plataformas", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PhonogramLinksLabel() {
    return this.PhonogramLinksLabelByLang();
  }

  get PhonogramLinksDescription() {
    return {
      es: {
        s: "Links a las plataformas de distribución donde el fonograma de la obra está disponible, ya sea para streaming o venta.",
      },
      en: {
        s: "Links to distribution platforms where the phonogram of the work is available, whether for streaming or sale.",
      },
      pt: {
        s: "Links para plataformas de distribuição onde o fonograma da obra está disponível, seja para streaming ou venda.",
      },
    }[this.language].s;
  }

  PhonogramNotesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Notas", p: "Notas adicionales", g: "F" },
      en: { s: "Notes", p: "Additional notes" },
      pt: { s: "Notas", p: "Notas adicionais", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get PhonogramNotesLabel() {
    return this.PhonogramNotesLabelByLang();
  }

  get PhonogramNotesDescription() {
    return {
      es: { s: "Comentarios o información adicional sobre este fonograma." },
      en: { s: "Additional comments or information about this phonogram." },
      pt: { s: "Comentários ou informações adicionais sobre este fonograma." },
    }[this.language].s;
  }

  // Territory

  get TerritoryLabels() {
    return {
      es: { s: "Territorio", p: "Territorios", g: "M" },
      en: { s: "Territory", p: "Territories", g: undefined },
      pt: { s: "Território", p: "Territórios", g: "M" },
    }[this.language];
  }

  get TerritorySingular() {
    return this.TerritoryLabels.s;
  }

  get TerritoryPlural() {
    return this.TerritoryLabels.p;
  }

  get TerritoryGender() {
    return this.TerritoryLabels.g;
  }

  get groupTerritoryLabel() {
    return {
      es: { s: "Territorio", p: "Territorios", g: "M" },
      en: { s: "Territory", p: "Territories" },
      pt: { s: "Território", p: "Territórios", g: "M" },
    }[this.language].s;
  }

  TerritoryNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del territorio", g: "M" },
      en: { s: "Name", p: "Name of the territory" },
      pt: { s: "Nome", p: "Nome do território", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TerritoryNameLabel() {
    return this.TerritoryNameLabelByLang();
  }

  get TerritoryNameDescription() {
    return {
      es: {
        s: "Nombre del territorio (ej. 'Mundo', 'Latinoamérica', 'USA & Canadá').",
      },
      en: {
        s: "Name of the territory (e.g., 'World', 'Latin America', 'USA & Canada').",
      },
      pt: {
        s: "Nome do território (ex: 'Mundo', 'América Latina', 'EUA e Canadá').",
      },
    }[this.language].s;
  }

  // MusicalWorkLink

  get MusicalWorkLinkLabels() {
    return {
      es: { s: "Link de fonograma", p: "Links de fonogramas", g: "M" },
      en: { s: "Phonogram Link", p: "Phonogram Links", g: undefined },
      pt: { s: "Link de fonograma", p: "Links de fonogramas", g: "M" },
    }[this.language];
  }

  get MusicalWorkLinkSingular() {
    return this.MusicalWorkLinkLabels.s;
  }

  get MusicalWorkLinkPlural() {
    return this.MusicalWorkLinkLabels.p;
  }

  get MusicalWorkLinkGender() {
    return this.MusicalWorkLinkLabels.g;
  }

  get groupMusicalWorkLinkLabel() {
    return {
      es: { s: "Link de fonograma", p: "Links de fonogramas", g: "M" },
      en: { s: "Phonogram Link", p: "Phonogram Links" },
      pt: { s: "Link de fonograma", p: "Links de fonogramas", g: "M" },
    }[this.language].s;
  }

  MusicalWorkLinkUrlLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "URL", p: "Link", g: "F" },
      en: { s: "URL", p: "Link" },
      pt: { s: "URL", p: "Link", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkLinkUrlLabel() {
    return this.MusicalWorkLinkUrlLabelByLang();
  }

  get MusicalWorkLinkUrlDescription() {
    return {
      es: { s: "URL completa del link en la plataforma especificada." },
      en: { s: "Complete URL of the link on the specified platform." },
      pt: { s: "URL completa do link na plataforma especificada." },
    }[this.language].s;
  }

  MusicalWorkLinkPlatformLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Plataforma", p: "Plataforma del link", g: "F" },
      en: { s: "Platform", p: "Link's platform" },
      pt: { s: "Plataforma", p: "Plataforma do link", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkLinkPlatformLabel() {
    return this.MusicalWorkLinkPlatformLabelByLang();
  }

  get MusicalWorkLinkPlatformDescription() {
    return {
      es: {
        s: "Plataforma en la que está publicado el fonograma de la obra (Ejemplo: Spotify, Apple Music, Tidal, etc.)",
      },
      en: {
        s: "Platform on which the phonogram of the work is published (Example: Spotify, Apple Music, Tidal, etc.)",
      },
      pt: {
        s: "Plataforma na qual o fonograma da obra está publicado (Exemplo: Spotify, Apple Music, Tidal, etc.)",
      },
    }[this.language].s;
  }

  MusicalWorkLinkTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Versión", p: "Versión del link", g: "F" },
      en: { s: "Version", p: "Link's version" },
      pt: { s: "Versão", p: "Versão do link", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get MusicalWorkLinkTypeLabel() {
    return this.MusicalWorkLinkTypeLabelByLang();
  }

  get MusicalWorkLinkTypeDescription() {
    return {
      es: {
        s: "Versión del lanzamiento en la que el fonograma de la obra está publicado (Single, EP, Álbum)",
      },
      en: {
        s: "Release version in which the phonogram of the work is published (Single, EP, Album)",
      },
      pt: {
        s: "Versão do lançamento na qual o fonograma da obra está publicado (Single, EP, Álbum)",
      },
    }[this.language].s;
  }

  // Platform

  get PlatformLabels() {
    return {
      es: { s: "Plataforma", p: "Plataformas", g: "F" },
      en: { s: "Platform", p: "Platforms", g: undefined },
      pt: { s: "Plataforma", p: "Plataformas", g: "F" },
    }[this.language];
  }

  get PlatformSingular() {
    return this.PlatformLabels.s;
  }

  get PlatformPlural() {
    return this.PlatformLabels.p;
  }

  get PlatformGender() {
    return this.PlatformLabels.g;
  }

  get groupPlatformLabel() {
    return {
      es: { s: "Plataforma", p: "Plataformas", g: "F" },
      en: { s: "Platform", p: "Platforms" },
      pt: { s: "Plataforma", p: "Plataformas", g: "F" },
    }[this.language].s;
  }

  PlatformNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre de la plataforma", g: "M" },
      en: { s: "Name", p: "Name of the platform" },
      pt: { s: "Nome", p: "Nome da plataforma", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PlatformNameLabel() {
    return this.PlatformNameLabelByLang();
  }

  get PlatformNameDescription() {
    return {
      es: { s: "El nombre único de la plataforma." },
      en: { s: "The unique name of the platform." },
      pt: { s: "O nome único da plataforma." },
    }[this.language].s;
  }

  // PlatformLinkType

  get PlatformLinkTypeLabels() {
    return {
      es: { s: "Versión", p: "Versiones", g: "F" },
      en: { s: "Version", p: "Versions", g: undefined },
      pt: { s: "Versão", p: "Versões", g: "F" },
    }[this.language];
  }

  get PlatformLinkTypeSingular() {
    return this.PlatformLinkTypeLabels.s;
  }

  get PlatformLinkTypePlural() {
    return this.PlatformLinkTypeLabels.p;
  }

  get PlatformLinkTypeGender() {
    return this.PlatformLinkTypeLabels.g;
  }

  get groupPlatformLinkTypeLabel() {
    return {
      es: { s: "Versión", p: "Versiones", g: "F" },
      en: { s: "Version", p: "Versions" },
      pt: { s: "Versão", p: "Versões", g: "F" },
    }[this.language].s;
  }

  PlatformLinkTypeNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Nombre",
        p: "Nombre de la versión (Ej: Álbum, Single, Venta)",
        g: "M",
      },
      en: { s: "Name", p: "Name of the version (e.g., Album, Single, Sale)" },
      pt: { s: "Nome", p: "Nome da versão (Ex: Álbum, Single, Venda)", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PlatformLinkTypeNameLabel() {
    return this.PlatformLinkTypeNameLabelByLang();
  }

  get PlatformLinkTypeNameDescription() {
    return {
      es: {
        s: "El nombre de la versión del lanzamiento a la que apunta el link.",
      },
      en: { s: "The name of the release version the link points to." },
      pt: { s: "O nome da versão do lançamento para a qual o link aponta." },
    }[this.language].s;
  }

  // WorkAuthor

  get WorkAuthorLabels() {
    return {
      es: { s: "Relación Obra-Autor", p: "Relaciones Obra-Autor", g: "F" },
      en: {
        s: "Work-Author Relationship",
        p: "Work-Author Relationships",
        g: undefined,
      },
      pt: { s: "Relação Obra-Autor", p: "Relações Obra-Autor", g: "F" },
    }[this.language];
  }

  get WorkAuthorSingular() {
    return this.WorkAuthorLabels.s;
  }

  get WorkAuthorPlural() {
    return this.WorkAuthorLabels.p;
  }

  get WorkAuthorGender() {
    return this.WorkAuthorLabels.g;
  }

  get groupWorkAuthorLabel() {
    return {
      es: { s: "Relación Obra-Autor", p: "Relaciones Obra-Autor", g: "F" },
      en: { s: "Work-Author Relationship", p: "Work-Author Relationships" },
      pt: { s: "Relação Obra-Autor", p: "Relações Obra-Autor", g: "F" },
    }[this.language].s;
  }

  WorkAuthorAuthorLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Autor", p: "Autor o compositor", g: "M" },
      en: { s: "Author", p: "Author or composer" },
      pt: { s: "Autor", p: "Autor ou compositor", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorAuthorLabel() {
    return this.WorkAuthorAuthorLabelByLang();
  }

  get WorkAuthorAuthorDescription() {
    return {
      es: {
        s: "Nombre de el/la autor/a y/o compositor/a involucrado en esta obra.",
      },
      en: { s: "Name of the author and/or composer involved in this work." },
      pt: { s: "Nome do autor e/ou compositor envolvido nesta obra." },
    }[this.language].s;
  }

  WorkAuthorLyricsPercentageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "% Letra",
        p: "Porcentaje de participación en la letra",
        g: "F",
      },
      en: { s: "% Lyrics", p: "Percentage of participation in the lyrics" },
      pt: { s: "% Letra", p: "Porcentagem de participação na letra", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorLyricsPercentageLabel() {
    return this.WorkAuthorLyricsPercentageLabelByLang();
  }

  get WorkAuthorLyricsPercentageDescription() {
    return {
      es: { s: "Porcentaje de titularidad sobre la obra del autor literario" },
      en: {
        s: "Percentage of ownership of the work held by the literary author",
      },
      pt: { s: "Porcentagem de titularidade sobre a obra do autor literário" },
    }[this.language].s;
  }

  WorkAuthorMusicPercentageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "% Música",
        p: "Porcentaje de participación en la música",
        g: "F",
      },
      en: { s: "% Music", p: "Percentage of participation in the music" },
      pt: { s: "% Música", p: "Porcentagem de participação na música", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorMusicPercentageLabel() {
    return this.WorkAuthorMusicPercentageLabelByLang();
  }

  get WorkAuthorMusicPercentageDescription() {
    return {
      es: {
        s: "Porcentaje de titularidad sobre la obra del compositor musical",
      },
      en: {
        s: "Percentage of ownership of the work held by the musical composer",
      },
      pt: {
        s: "Porcentagem de titularidade sobre a obra do compositor musical",
      },
    }[this.language].s;
  }

  WorkAuthorPublishersLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Participaciones Editoriales",
        p: "Editoriales con participación en esta obra",
        g: "M",
      },
      en: { s: "Publisher Shares", p: "Publishers with shares in this work" },
      pt: {
        s: "Participações Editoriais",
        p: "Editoras com participação nesta obra",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get WorkAuthorPublishersLabel() {
    return this.WorkAuthorPublishersLabelByLang();
  }

  get WorkAuthorPublishersDescription() {
    return {
      es: {
        s: "Porcentaje de la titularidad que el/la autor/a y/o compositor/a cede a la/s editorial/es musical/es seleccionada/s y, en el caso de estar definido, plazo de tiempo en años en el que dicho acuerdo tiene válidez.",
      },
      en: {
        s: "Percentage of ownership that the author and/or composer assigns to the selected music publisher(s) and, if defined, the time period in years during which said agreement is valid.",
      },
      pt: {
        s: "Porcentagem de titularidade que o autor e/ou compositor cede à(s) editora(s) musical(is) selecionada(s) e, se definido, o prazo em anos durante o qual o acordo é válido.",
      },
    }[this.language].s;
  }

  // Edition

  get EditionLabels() {
    return {
      es: {
        s: "Edición en Preparación",
        p: "Ediciones en Preparación",
        g: "F",
      },
      en: {
        s: "Edition in Preparation",
        p: "Editions in Preparation",
        g: undefined,
      },
      pt: { s: "Edição em Preparação", p: "Edições em Preparação", g: "F" },
    }[this.language];
  }

  get EditionSingular() {
    return this.EditionLabels.s;
  }

  get EditionPlural() {
    return this.EditionLabels.p;
  }

  get EditionGender() {
    return this.EditionLabels.g;
  }

  get groupEditionLabel() {
    return {
      es: {
        s: "Edición en Preparación",
        p: "Ediciones en Preparación",
        g: "F",
      },
      en: { s: "Edition in Preparation", p: "Editions in Preparation" },
      pt: { s: "Edição em Preparação", p: "Edições em Preparação", g: "F" },
    }[this.language].s;
  }

  get groupEditionEditionDataLabel() {
    return {
      es: { s: "Datos de la Edición", p: "M" },
      en: { s: "Edition Data" },
      pt: { s: "Dados da Edição", p: "M" },
    }[this.language].s;
  }

  get groupEditionContractsLabel() {
    return {
      es: { s: "Contratos", p: "M" },
      en: { s: "Contracts" },
      pt: { s: "Contratos", p: "M" },
    }[this.language].s;
  }

  get groupEditionEditionContractsLabel() {
    return {
      es: { s: "Documentación a firmar", p: "M" },
      en: { s: "Documentation to sign" },
      pt: { s: "Documentação a assinar", p: "M" },
    }[this.language].s;
  }

  get groupEditionEditionDocumentationLabel() {
    return {
      es: { s: "Documentación Adicional", p: "F" },
      en: { s: "Additional Documentation" },
      pt: { s: "Documentação Adicional", p: "F" },
    }[this.language].s;
  }

  get groupEditionEditionUserAttachmentsLabel() {
    return {
      es: { s: "Documentación Adjunta", p: "F" },
      en: { s: "Attached Documentation" },
      pt: { s: "Documentação Anexa", p: "F" },
    }[this.language].s;
  }

  get groupEditionSystemDatesLabel() {
    return {
      es: { s: "Fechas de sistema", p: "F" },
      en: { s: "System dates" },
      pt: { s: "Datas de sistema", p: "F" },
    }[this.language].s;
  }

  EditionDocumentationStateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Estado de Documentación",
        p: "Estado actual en el ciclo de vida de la documentación",
        g: "M",
      },
      en: {
        s: "Documentation State",
        p: "Current state in the documentation lifecycle",
      },
      pt: {
        s: "Estado da Documentação",
        p: "Estado atual no ciclo de vida da documentação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationStateLabel() {
    return this.EditionDocumentationStateLabelByLang();
  }

  get EditionDocumentationStateDescription() {
    return {
      es: {
        s: "Estado actual del proceso de gestión de la documentación y proceso de registro en DNDA para esta edición.",
      },
      en: {
        s: "Current status of the documentation management process and DNDA registration process for this edition.",
      },
      pt: {
        s: "Estado atual do processo de gestão de documentação e processo de registro na DNDA para esta edição.",
      },
    }[this.language].s;
  }

  get EditionDocumentationStateTransitionLabels(): Record<string, string> {
    return {
      generateDocumentation: {
        es: { s: "Generar Documentación", p: "F" },
        en: { s: "Generate Documentation" },
        pt: { s: "Gerar Documentação", p: "F" },
      }[this.language].s,
      deleteAndRestartFromSignature: {
        es: { s: "Borrar Documentación", p: "F" },
        en: { s: "Delete Documentation" },
        pt: { s: "Excluir Documentação", p: "F" },
      }[this.language].s,
      completeGeneration: {
        es: { s: "CompleteGeneration" },
        en: { s: "CompleteGeneration" },
        pt: { s: "CompleteGeneration" },
      }[this.language].s,
      failGeneration: {
        es: { s: "FailGeneration" },
        en: { s: "FailGeneration" },
        pt: { s: "FailGeneration" },
      }[this.language].s,
      cancelGeneration: {
        es: { s: "Cancelar Generación", p: "F" },
        en: { s: "Cancel Generation" },
        pt: { s: "Cancelar Geração", p: "F" },
      }[this.language].s,
      deleteAndRestartFromGenerated: {
        es: { s: "Borrar Documentación", p: "F" },
        en: { s: "Delete Documentation" },
        pt: { s: "Excluir Documentação", p: "F" },
      }[this.language].s,
      deleteAndRestartFromFailed: {
        es: { s: "Borrar Documentación", p: "F" },
        en: { s: "Delete Documentation" },
        pt: { s: "Excluir Documentação", p: "F" },
      }[this.language].s,
      retryGenerateDocumentation: {
        es: { s: "Reintentar Generación de Documentación", p: "F" },
        en: { s: "Retry Documentation Generation" },
        pt: { s: "Tentar Novamente Geração de Documentação", p: "F" },
      }[this.language].s,
      sendForSignature: {
        es: { s: "Enviar a Firmar" },
        en: { s: "Send for Signature" },
        pt: { s: "Enviar para Assinatura", p: "F" },
      }[this.language].s,
      completeSending: {
        es: { s: "CompleteSending" },
        en: { s: "CompleteSending" },
        pt: { s: "CompleteSending" },
      }[this.language].s,
      failSending: {
        es: { s: "FailSending" },
        en: { s: "FailSending" },
        pt: { s: "FailSending" },
      }[this.language].s,
      cancelSending: {
        es: { s: "Cancelar Envío de Firmas", p: "M" },
        en: { s: "Cancel Signature Sending" },
        pt: { s: "Cancelar Envio de Assinaturas", p: "M" },
      }[this.language].s,
      retrySendForSignature: {
        es: { s: "Reintentar Envío de Firmas", p: "M" },
        en: { s: "Retry Signature Sending" },
        pt: { s: "Tentar Novamente Envio de Assinaturas", p: "M" },
      }[this.language].s,
      backToDocsFromSignatureFailed: {
        es: { s: "Volver a Documentación Generada", p: "F" },
        en: { s: "Return to Generated Documentation" },
        pt: { s: "Voltar à Documentação Gerada", p: "F" },
      }[this.language].s,
      falloFirmas: {
        es: { s: "FalloFirmas" },
        en: { s: "FalloFirmas" },
        pt: { s: "FalloFirmas" },
      }[this.language].s,
      borrarDocumentacionFromFallo: {
        es: { s: "Borrar Documentación", p: "F" },
        en: { s: "Delete Documentation" },
        pt: { s: "Excluir Documentação", p: "F" },
      }[this.language].s,
      iniciarCierreFirmas: {
        es: { s: "IniciarCierreFirmas" },
        en: { s: "IniciarCierreFirmas" },
        pt: { s: "IniciarCierreFirmas" },
      }[this.language].s,
      completarCierreFirmas: {
        es: { s: "CompletarCierreFirmas" },
        en: { s: "CompletarCierreFirmas" },
        pt: { s: "CompletarCierreFirmas" },
      }[this.language].s,
      fallarCierreFirmas: {
        es: { s: "FallarCierreFirmas" },
        en: { s: "FallarCierreFirmas" },
        pt: { s: "FallarCierreFirmas" },
      }[this.language].s,
      reintentarCierreFirmas: {
        es: { s: "ReintentarCierreFirmas" },
        en: { s: "ReintentarCierreFirmas" },
        pt: { s: "ReintentarCierreFirmas" },
      }[this.language].s,
      falloFirmasDjct: {
        es: { s: "FalloFirmasDjct" },
        en: { s: "FalloFirmasDjct" },
        pt: { s: "FalloFirmasDjct" },
      }[this.language].s,
      borrarDocumentacionDesdeEsperaDjct: {
        es: { s: "Borrar Documentación", p: "F" },
        en: { s: "Delete Documentation" },
        pt: { s: "Excluir Documentação", p: "F" },
      }[this.language].s,
      borrarDocumentacionDesdeFalloEjemplar: {
        es: { s: "Borrar Documentación", p: "F" },
        en: { s: "Delete Documentation" },
        pt: { s: "Excluir Documentação", p: "F" },
      }[this.language].s,
    };
  }

  get EditionDocumentationStateStateLabels(): Record<string, string> {
    return {
      noDocumentation: {
        es: { s: "Sin Documentación", p: "F" },
        en: { s: "No Documentation" },
        pt: { s: "Sem Documentação", p: "F" },
      }[this.language].s,
      generatingDocs: {
        es: { s: "Generando Documentación", p: "M" },
        en: { s: "Generating Documentation" },
        pt: { s: "Gerando Documentação", p: "M" },
      }[this.language].s,
      generationFailed: {
        es: { s: "Error en Generación", p: "M" },
        en: { s: "Generation Failed" },
        pt: { s: "Falha na Geração", p: "F" },
      }[this.language].s,
      docsGenerated: {
        es: { s: "Documentación Generada", p: "F" },
        en: { s: "Documentation Generated" },
        pt: { s: "Documentação Gerada", p: "F" },
      }[this.language].s,
      sendingForSignature: {
        es: { s: "Enviando para Firmas", p: "M" },
        en: { s: "Sending for Signatures" },
        pt: { s: "Enviando para Assinaturas", p: "M" },
      }[this.language].s,
      signatureSendingFailed: {
        es: { s: "Error en Envío de Firmas", p: "M" },
        en: { s: "Signature Sending Failed" },
        pt: { s: "Falha no Envio de Assinaturas", p: "M" },
      }[this.language].s,
      sentForSignature: {
        es: { s: "Enviado para Firmas", p: "M" },
        en: { s: "Sent for Signatures" },
        pt: { s: "Enviado para Assinaturas", p: "M" },
      }[this.language].s,
      generandoEjemplarEdicion: {
        es: { s: "Generando Ejemplar de Edición", p: "M" },
        en: { s: "Generating Edition Exemplar" },
        pt: { s: "Gerando Exemplar da Edição", p: "M" },
      }[this.language].s,
      esperandoFirmaDjct: {
        es: { s: "Esperando Firma de DJCT", p: "F" },
        en: { s: "Awaiting DJCT Signature" },
        pt: { s: "Aguardando Assinatura da DJCT", p: "F" },
      }[this.language].s,
      falloGeneracionEjemplar: {
        es: { s: "Error Generando Ejemplar", p: "M" },
        en: { s: "Exemplar Generation Failed" },
        pt: { s: "Falha na Geração do Exemplar", p: "F" },
      }[this.language].s,
      falloDeFirmas: {
        es: { s: "Fallo de Firmas", p: "M" },
        en: { s: "Signature Failure" },
        pt: { s: "Falha de Assinaturas", p: "M" },
      }[this.language].s,
    };
  }

  EditionDocumentationLastOperationalErrorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Último Error Operacional de Documentación",
        p: "Mensaje del último error del proceso de documentación/firma (debug)",
        g: "M",
      },
      en: {
        s: "Documentation Last Operational Error",
        p: "Last documentation/signature process error message (debug)",
      },
      pt: {
        s: "Último Erro Operacional de Documentação",
        p: "Mensagem do último erro do processo de documentação/assinatura (debug)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationLastOperationalErrorLabel() {
    return this.EditionDocumentationLastOperationalErrorLabelByLang();
  }

  EditionPublicationDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha de Publicación",
        p: "Fecha en que se considera publicada la edición",
        g: "F",
      },
      en: {
        s: "Publication Date",
        p: "Date when the edition is considered published",
      },
      pt: {
        s: "Data de Publicação",
        p: "Data em que a edição é considerada publicada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionPublicationDateLabel() {
    return this.EditionPublicationDateLabelByLang();
  }

  get EditionPublicationDateDescription() {
    return {
      es: {
        s: "Se fija automáticamente el día en que se completa la firma del último contrato. Es la fecha que imprimen el ejemplar de edición y la DJCT.",
      },
      en: {
        s: "Set automatically on the day the last contract signature is completed. It is the date printed on the edition exemplar and the DJCT.",
      },
      pt: {
        s: "Definida automaticamente no dia em que a assinatura do último contrato é concluída. É a data impressa no exemplar da edição e na DJCT.",
      },
    }[this.language].s;
  }

  EditionEjemplarPdfLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Ejemplar de Edición",
        p: "PDF del ejemplar generado al cierre de contratos",
        g: "M",
      },
      en: {
        s: "Edition Exemplar",
        p: "Exemplar PDF generated at contract completion",
      },
      pt: {
        s: "Exemplar de Edição",
        p: "PDF do exemplar gerado no encerramento dos contratos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionEjemplarPdfLabel() {
    return this.EditionEjemplarPdfLabelByLang();
  }

  get EditionEjemplarPdfDescription() {
    return {
      es: {
        s: "Documento único de la edición (portada + partituras firmadas). Se genera al completarse la firma de todos los contratos, junto con la DJCT y con su misma fecha, para que pueda revisarse antes del cierre.",
      },
      en: {
        s: "Single document of the edition (cover page + signed scores). Generated when all contract signatures are completed, together with the DJCT and sharing its date, so it can be reviewed before closing.",
      },
      pt: {
        s: "Documento único da edição (capa + partituras assinadas). Gerado quando todas as assinaturas de contratos são concluídas, junto com a DJCT e com a mesma data, para que possa ser revisado antes do encerramento.",
      },
    }[this.language].s;
  }

  EditionNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre de edición", p: "Nombre de la edición", g: "M" },
      en: { s: "Edition name", p: "Edition name" },
      pt: { s: "Nome da edição", p: "Nome da edição", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionNameLabel() {
    return this.EditionNameLabelByLang();
  }

  get EditionNameDescription() {
    return {
      es: { s: "Nombre de la edición." },
      en: { s: "Name of the edition." },
      pt: { s: "Nome da edição." },
    }[this.language].s;
  }

  EditionEditionTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Edición", p: "Tipos de Edición", g: "M" },
      en: { s: "Edition Type", p: "Edition Types" },
      pt: { s: "Tipo de Edição", p: "Tipos de Edição", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionEditionTypeLabel() {
    return this.EditionEditionTypeLabelByLang();
  }

  get EditionEditionTypeDescription() {
    return {
      es: {
        s: "Obra Suelta = Edición con una sola obra.  / Álbum = Edición con mas de una obra. / Obra Integral = Edición con 2 o más obras que guardan entre sí una unidad temática, argumental o estructural. Ejemplos clásicos: una ópera, una cantata, una suite.",
      },
      en: {
        s: "Single Work = Edition with a single work. / Album = Edition with more than one work. / Integral Work = Edition with 2 or more works that share a thematic, narrative, or structural unity. Classic examples: an opera, a cantata, a suite.",
      },
      pt: {
        s: "Obra Avulsa = Edição com uma única obra. / Álbum = Edição com mais de uma obra. / Obra Integral = Edição com 2 ou mais obras que compartilham uma unidade temática, narrativa ou estrutural. Exemplos clássicos: uma ópera, uma cantata, uma suíte.",
      },
    }[this.language].s;
  }

  EditionContentTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Contenido", p: "M" },
      en: { s: "Content Type" },
      pt: { s: "Tipo de Conteúdo", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionContentTypeLabel() {
    return this.EditionContentTypeLabelByLang();
  }

  get EditionContentTypeDescription() {
    return {
      es: {
        s: "Con partituras: cada obra debe tener su partitura cargada. Con audios: cada obra debe tener al menos un fonograma con archivo de audio y un link de plataforma. Se valida al generar la documentación y define el contenido del ejemplar de edición.",
      },
      en: {
        s: "With scores: each work must have its score uploaded. With audios: each work must have at least one phonogram with an audio file and a platform link. Validated when generating documentation; defines the exemplar content.",
      },
      pt: {
        s: "Com partituras: cada obra deve ter sua partitura carregada. Com áudios: cada obra deve ter ao menos um fonograma com arquivo de áudio e um link de plataforma. Validado ao gerar a documentação; define o conteúdo do exemplar.",
      },
    }[this.language].s;
  }

  EditionPublicationLinkLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Link de Publicación",
        p: "Link donde se publica la edición",
        g: "M",
      },
      en: { s: "Publication Link", p: "Link where the edition is published" },
      pt: {
        s: "Link de Publicação",
        p: "Link onde a edição é publicada",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionPublicationLinkLabel() {
    return this.EditionPublicationLinkLabelByLang();
  }

  get EditionPublicationLinkDescription() {
    return {
      es: {
        s: "Link donde se publica la edición (por ejemplo, el álbum en Spotify). Solo aplica a ediciones de solo audios",
      },
      en: {
        s: "Link where the edition is published (for example, the album on Spotify). Only applies to audio-only editions",
      },
      pt: {
        s: "Link onde a edição é publicada (por exemplo, o álbum no Spotify). Aplica-se apenas a edições somente de áudios",
      },
    }[this.language].s;
  }

  EditionEditionNumberLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nº Edición", p: "Nº Edición" },
      en: { s: "EditionNumber" },
      pt: { s: "EditionNumber" },
    }[lang ? lang : this.language].s;
  }

  get EditionEditionNumberLabel() {
    return this.EditionEditionNumberLabelByLang();
  }

  get EditionEditionNumberDescription() {
    return {
      es: { s: "Número secuencial único asignado a esta edición." },
      en: { s: "Unique sequential number assigned to this edition." },
      pt: { s: "Número sequencial exclusivo atribuído a esta edição." },
    }[this.language].s;
  }

  EditionPriceLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Precio de Ejemplar de Edición",
        p: "Precio de Ejemplar de Edición",
        g: "M",
      },
      en: { s: "Edition Copy Price", p: "Edition Copy Price" },
      pt: {
        s: "Preço do Exemplar da Edição",
        p: "Preço do Exemplar da Edição",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionPriceLabel() {
    return this.EditionPriceLabelByLang();
  }

  get EditionPriceDescription() {
    return {
      es: {
        s: "Precio de Ejemplar de Edición que figura en la portada del Ejemplar de Edición. En ediciones con audios, cargar el precio de descarga del álbum o tema en la plataforma (es el precio que declara la DJCT).",
      },
      en: {
        s: "Edition Copy price shown on the cover of the Edition Copy. For editions with audio, enter the download price of the album or track on the platform (it is the price declared in the DJCT).",
      },
      pt: {
        s: "Preço do Exemplar de Edição que aparece na capa do Exemplar de Edição. Em edições com áudios, informar o preço de download do álbum ou faixa na plataforma (é o preço declarado na DJCT).",
      },
    }[this.language].s;
  }

  EditionWorksLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Obras",
        p: "Obras musicales incluidas en esta edición",
        g: "F",
      },
      en: { s: "Works", p: "Musical works included in this edition" },
      pt: { s: "Obras", p: "Obras musicais incluídas nesta edição", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionWorksLabel() {
    return this.EditionWorksLabelByLang();
  }

  get EditionWorksDescription() {
    return {
      es: {
        s: "Conjunto de obras musicales que forman parte de esta edición específica.",
      },
      en: { s: "Set of musical works that are part of this specific edition." },
      pt: {
        s: "Conjunto de obras musicais que fazem parte desta edição específica.",
      },
    }[this.language].s;
  }

  EditionNotesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Notas",
        p: "Notas adicionales sobre la edición y las obras que incluye",
        g: "F",
      },
      en: {
        s: "Notes",
        p: "Additional notes about the edition and the works it includes",
      },
      pt: {
        s: "Notas",
        p: "Notas adicionais sobre a edição e as obras que inclui",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionNotesLabel() {
    return this.EditionNotesLabelByLang();
  }

  get EditionNotesDescription() {
    return {
      es: {
        s: "Comentarios o información adicional de la edición. Esta información no se incluirá en ningún tipo de documentación generada.",
      },
      en: {
        s: "Additional comments or information about the edition. This information will not be included in any generated documentation.",
      },
      pt: {
        s: "Comentários ou informações adicionais da edição. Esta informação não será incluída em nenhum tipo de documentação gerada.",
      },
    }[this.language].s;
  }

  EditionDndaExpedienteNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nro. Expediente DNDA",
        p: "Número de expediente asignado por la DNDA",
        g: "M",
      },
      en: { s: "DNDA Case Number", p: "Case number assigned by DNDA" },
      pt: {
        s: "Nro. Expediente DNDA",
        p: "Número de expediente atribuído pela DNDA",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDndaExpedienteNumberLabel() {
    return this.EditionDndaExpedienteNumberLabelByLang();
  }

  get EditionDndaExpedienteNumberDescription() {
    return {
      es: {
        s: "Número de expediente asignado por la Dirección Nacional de Derecho de Autor al confirmar el trámite (formato EX-YYYY-NNNNNNN- -APN-DNDA#MJ).",
      },
      en: {
        s: "Case number assigned by the National Copyright Office upon confirming the registration (format EX-YYYY-NNNNNNN- -APN-DNDA#MJ).",
      },
      pt: {
        s: "Número de expediente atribuído pela Direção Nacional de Direito de Autor ao confirmar o trâmite (formato EX-YYYY-NNNNNNN- -APN-DNDA#MJ).",
      },
    }[this.language].s;
  }

  EditionContractObservationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Observación de contrato",
        p: "Texto de observación en HTML",
        g: "F",
      },
      en: { s: "Contract observation", p: "Observation text in HTML" },
      pt: {
        s: "Observação de contrato",
        p: "Texto de observação em HTML",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionContractObservationLabel() {
    return this.EditionContractObservationLabelByLang();
  }

  get EditionContractObservationDescription() {
    return {
      es: {
        s: "El contenido de la observación se insertará en todos los contratos de esta edición.",
      },
      en: {
        s: "The content of the observation will be inserted into every contract of this edition.",
      },
      pt: {
        s: "O conteúdo da observação será inserido em todos os contratos desta edição.",
      },
    }[this.language].s;
  }

  EditionDocumentationLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Documentación a firmar",
        p: "Conjunto de documentación a firmar de la edición",
        g: "M",
      },
      en: {
        s: "Documentation to sign",
        p: "Set of documentation to sign for the edition",
      },
      pt: {
        s: "Documentação a assinar",
        p: "Conjunto de documentação a assinar da edição",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationLabel() {
    return this.EditionDocumentationLabelByLang();
  }

  get EditionDocumentationDescription() {
    return {
      es: {
        s: "Contratos y otra documentación a firmar que pertenece/n a esta edición.",
      },
      en: {
        s: "Contracts and other documentation to sign belonging to this edition.",
      },
      pt: {
        s: "Contratos e outra documentação a assinar que pertence(m) a esta edição.",
      },
    }[this.language].s;
  }

  EditionAssociatedDocumentsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Documentación Adicional",
        p: "Documentación adicional de declaración de la edición",
        g: "F",
      },
      en: {
        s: "Additional documentation",
        p: "Additional declaration documentation for the edition",
      },
      pt: {
        s: "Documentação adicional",
        p: "Documentação adicional de declaração da edição",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionAssociatedDocumentsLabel() {
    return this.EditionAssociatedDocumentsLabelByLang();
  }

  get EditionAssociatedDocumentsDescription() {
    return {
      es: {
        s: "Documentos adicionales de declaración y otros documentos relevantes vinculados a esta edición.",
      },
      en: {
        s: "Additional declaration documents and other relevant documents linked to this edition.",
      },
      pt: {
        s: "Documentos adicionais de declaração e outros documentos relevantes vinculados a esta edição.",
      },
    }[this.language].s;
  }

  EditionUserAttachmentsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Documentación Adjunta",
        p: "Archivos adjuntos cargados por el usuario",
        g: "F",
      },
      en: { s: "Attached documentation", p: "User-uploaded attached files" },
      pt: {
        s: "Documentação Anexa",
        p: "Arquivos anexos carregados pelo usuário",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionUserAttachmentsLabel() {
    return this.EditionUserAttachmentsLabelByLang();
  }

  get EditionUserAttachmentsDescription() {
    return {
      es: {
        s: "Archivos adjuntos cargados por el usuario (PDF, Office, imágenes, etc.) hasta 10 MB cada uno.",
      },
      en: {
        s: "User-uploaded attached files (PDF, Office, images, etc.) up to 10 MB each.",
      },
      pt: {
        s: "Arquivos anexos carregados pelo usuário (PDF, Office, imagens, etc.) até 10 MB cada um.",
      },
    }[this.language].s;
  }

  EditionCreatedAt_LabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Fecha de Creación", p: "F" },
      en: { s: "Creation Date" },
      pt: { s: "Data de Criação", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionCreatedAt_Label() {
    return this.EditionCreatedAt_LabelByLang();
  }

  get EditionCreatedAt_Description() {
    return {
      es: { s: "Fecha y hora del registro en el sistema de esta edición." },
      en: {
        s: "Date and time when this edtition was registered in the system.",
      },
      pt: { s: "Data e hora do registro no sistema desta edição." },
    }[this.language].s;
  }

  EditionLastUpdatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Última Actualización", p: "F" },
      en: { s: "Last Updated" },
      pt: { s: "Última Atualização", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionLastUpdatedAt_Label() {
    return this.EditionLastUpdatedAt_LabelByLang();
  }

  get EditionLastUpdatedAt_Description() {
    return {
      es: { s: "Fecha y hora de la última modificación de esta edición." },
      en: { s: "Date and time of the last modification of this edition." },
      pt: { s: "Data e hora da última modificação desta edição." },
    }[this.language].s;
  }

  // SignedEdition

  get SignedEditionLabels() {
    return {
      es: { s: "Edición Firmada", p: "Ediciones Firmadas", g: "F" },
      en: { s: "Signed Edition", p: "Signed Editions", g: undefined },
      pt: { s: "Edição Assinada", p: "Edições Assinadas", g: "F" },
    }[this.language];
  }

  get SignedEditionSingular() {
    return this.SignedEditionLabels.s;
  }

  get SignedEditionPlural() {
    return this.SignedEditionLabels.p;
  }

  get SignedEditionGender() {
    return this.SignedEditionLabels.g;
  }

  get groupSignedEditionLabel() {
    return {
      es: { s: "Edición Firmada", p: "Ediciones Firmadas", g: "F" },
      en: { s: "Signed Edition", p: "Signed Editions" },
      pt: { s: "Edição Assinada", p: "Edições Assinadas", g: "F" },
    }[this.language].s;
  }

  get groupSignedEditionSignedEditionLabel() {
    return {
      es: { s: "Edición Firmada", p: "M" },
      en: { s: "Signed Edition" },
      pt: { s: "Edição Assinada", p: "F" },
    }[this.language].s;
  }

  get groupSignedEditionDndaTrackingLabel() {
    return {
      es: { s: "Seguimiento DNDA", p: "M" },
      en: { s: "DNDA Tracking" },
      pt: { s: "Acompanhamento DNDA", p: "M" },
    }[this.language].s;
  }

  get groupSignedEditionDndaTrackingDescription() {
    return {
      es: {
        s: "Datos del ciclo de upload del ejemplar a DNDA (clave efímera de 72hs, comprobante de obra publicada) y métricas de reintentos de bots.",
      },
      en: {
        s: "DNDA exemplar upload cycle data (ephemeral 72h key, published-work certificate) and bot retry metrics.",
      },
      pt: {
        s: "Dados do ciclo de envio do exemplar à DNDA (chave efêmera de 72h, comprovante de obra publicada) e métricas de tentativas dos bots.",
      },
    }[this.language].s;
  }

  get groupSignedEditionSadaicTrackingLabel() {
    return {
      es: { s: "Seguimiento SADAIC", p: "M" },
      en: { s: "SADAIC Tracking" },
      pt: { s: "Acompanhamento SADAIC", p: "M" },
    }[this.language].s;
  }

  get groupSignedEditionSadaicTrackingDescription() {
    return {
      es: {
        s: "Datos del ciclo de subida FTP y polling de SADAIC (puede tardar meses).",
      },
      en: { s: "SADAIC FTP upload and polling cycle data (may take months)." },
      pt: {
        s: "Dados do ciclo de envio FTP e polling da SADAIC (pode levar meses).",
      },
    }[this.language].s;
  }

  get groupSignedEditionSystemDatesLabel() {
    return {
      es: { s: "Fechas de sistema", p: "F" },
      en: { s: "System dates" },
      pt: { s: "Datas de sistema", p: "F" },
    }[this.language].s;
  }

  SignedEditionDndaStateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Estado DNDA", p: "Estado del registro en la DNDA", g: "M" },
      en: { s: "DNDA State", p: "State of the DNDA registration" },
      pt: { s: "Estado DNDA", p: "Estado do registro na DNDA", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaStateLabel() {
    return this.SignedEditionDndaStateLabelByLang();
  }

  get SignedEditionDndaStateTransitionLabels(): Record<string, string> {
    return {
      iniciarGeneracionEjemplar: {
        es: { s: "IniciarGeneracionEjemplar" },
        en: { s: "IniciarGeneracionEjemplar" },
        pt: { s: "IniciarGeneracionEjemplar" },
      }[this.language].s,
      completarGeneracionEjemplar: {
        es: { s: "CompletarGeneracionEjemplar" },
        en: { s: "CompletarGeneracionEjemplar" },
        pt: { s: "CompletarGeneracionEjemplar" },
      }[this.language].s,
      iniciarRegistroDnda: {
        es: { s: "IniciarRegistroDnda" },
        en: { s: "IniciarRegistroDnda" },
        pt: { s: "IniciarRegistroDnda" },
      }[this.language].s,
      completarRegistroDnda: {
        es: { s: "CompletarRegistroDnda" },
        en: { s: "CompletarRegistroDnda" },
        pt: { s: "CompletarRegistroDnda" },
      }[this.language].s,
      recibirHabilitacionUpload: {
        es: { s: "RecibirHabilitacionUpload" },
        en: { s: "RecibirHabilitacionUpload" },
        pt: { s: "RecibirHabilitacionUpload" },
      }[this.language].s,
      iniciarUploadEjemplar: {
        es: { s: "IniciarUploadEjemplar" },
        en: { s: "IniciarUploadEjemplar" },
        pt: { s: "IniciarUploadEjemplar" },
      }[this.language].s,
      completarUploadEjemplar: {
        es: { s: "CompletarUploadEjemplar" },
        en: { s: "CompletarUploadEjemplar" },
        pt: { s: "CompletarUploadEjemplar" },
      }[this.language].s,
      descargarComprobanteDnda: {
        es: { s: "DescargarComprobanteDnda" },
        en: { s: "DescargarComprobanteDnda" },
        pt: { s: "DescargarComprobanteDnda" },
      }[this.language].s,
      fallarGeneracionEjemplar: {
        es: { s: "FallarGeneracionEjemplar" },
        en: { s: "FallarGeneracionEjemplar" },
        pt: { s: "FallarGeneracionEjemplar" },
      }[this.language].s,
      fallarRegistroDnda: {
        es: { s: "FallarRegistroDnda" },
        en: { s: "FallarRegistroDnda" },
        pt: { s: "FallarRegistroDnda" },
      }[this.language].s,
      fallarUploadEjemplar: {
        es: { s: "FallarUploadEjemplar" },
        en: { s: "FallarUploadEjemplar" },
        pt: { s: "FallarUploadEjemplar" },
      }[this.language].s,
      fallarInicioRegistroDnda: {
        es: { s: "FallarInicioRegistroDnda" },
        en: { s: "FallarInicioRegistroDnda" },
        pt: { s: "FallarInicioRegistroDnda" },
      }[this.language].s,
      fallarInicioUploadDesdeRegistrado: {
        es: { s: "FallarInicioUploadDesdeRegistrado" },
        en: { s: "FallarInicioUploadDesdeRegistrado" },
        pt: { s: "FallarInicioUploadDesdeRegistrado" },
      }[this.language].s,
      fallarInicioUploadDesdeHabilitado: {
        es: { s: "FallarInicioUploadDesdeHabilitado" },
        en: { s: "FallarInicioUploadDesdeHabilitado" },
        pt: { s: "FallarInicioUploadDesdeHabilitado" },
      }[this.language].s,
      fallarAvanceRegistro: {
        es: { s: "FallarAvanceRegistro" },
        en: { s: "FallarAvanceRegistro" },
        pt: { s: "FallarAvanceRegistro" },
      }[this.language].s,
      fallarAvanceUpload: {
        es: { s: "FallarAvanceUpload" },
        en: { s: "FallarAvanceUpload" },
        pt: { s: "FallarAvanceUpload" },
      }[this.language].s,
      expirarUploadKeyDuranteUpload: {
        es: { s: "ExpirarUploadKeyDuranteUpload" },
        en: { s: "ExpirarUploadKeyDuranteUpload" },
        pt: { s: "ExpirarUploadKeyDuranteUpload" },
      }[this.language].s,
      reintentarGeneracionEjemplar: {
        es: { s: "ReintentarGeneracionEjemplar" },
        en: { s: "ReintentarGeneracionEjemplar" },
        pt: { s: "ReintentarGeneracionEjemplar" },
      }[this.language].s,
      reintentarRegistroDnda: {
        es: { s: "ReintentarRegistroDnda" },
        en: { s: "ReintentarRegistroDnda" },
        pt: { s: "ReintentarRegistroDnda" },
      }[this.language].s,
      reintentarUploadEjemplar: {
        es: { s: "ReintentarUploadEjemplar" },
        en: { s: "ReintentarUploadEjemplar" },
        pt: { s: "ReintentarUploadEjemplar" },
      }[this.language].s,
      reintentarAvanceRegistro: {
        es: { s: "ReintentarAvanceRegistro" },
        en: { s: "ReintentarAvanceRegistro" },
        pt: { s: "ReintentarAvanceRegistro" },
      }[this.language].s,
      reintentarAvanceUpload: {
        es: { s: "ReintentarAvanceUpload" },
        en: { s: "ReintentarAvanceUpload" },
        pt: { s: "ReintentarAvanceUpload" },
      }[this.language].s,
    };
  }

  get SignedEditionDndaStateStateLabels(): Record<string, string> {
    return {
      contratosFirmados: {
        es: { s: "Documentación Firmada", p: "F" },
        en: { s: "Signed Documentation" },
        pt: { s: "Documentação Assinada", p: "F" },
      }[this.language].s,
      generandoEjemplar: {
        es: { s: "Generando Ejemplar", p: "M" },
        en: { s: "Generating Exemplar" },
        pt: { s: "Gerando Exemplar", p: "M" },
      }[this.language].s,
      ejemplarGenerado: {
        es: { s: "Ejemplar de Edición Generado", p: "M" },
        en: { s: "Edition Exemplar Generated" },
        pt: { s: "Exemplar de Edição Gerado", p: "M" },
      }[this.language].s,
      registrandoDnda: {
        es: { s: "Registrando en DNDA", p: "M" },
        en: { s: "Registering with DNDA" },
        pt: { s: "Registrando na DNDA", p: "M" },
      }[this.language].s,
      dndaRegistrado: {
        es: { s: "Registro DNDA (Carga de Formulario)", p: "M" },
        en: { s: "DNDA Registration (Form Submission)" },
        pt: { s: "Registro DNDA (Carga de Formulário)", p: "M" },
      }[this.language].s,
      ejemplarHabilitadoUpload: {
        es: { s: "Habilitado para entrega digital (DNDA)", p: "M" },
        en: { s: "Enabled for Digital Delivery (DNDA)" },
        pt: { s: "Habilitado para Entrega Digital (DNDA)", p: "M" },
      }[this.language].s,
      subiendoEjemplar: {
        es: { s: "Subiendo Ejemplar", p: "M" },
        en: { s: "Uploading Exemplar" },
        pt: { s: "Enviando Exemplar", p: "M" },
      }[this.language].s,
      ejemplarSubido: {
        es: { s: "Entrega digital (DNDA)", p: "M" },
        en: { s: "Digital Delivery (DNDA)" },
        pt: { s: "Entrega Digital (DNDA)", p: "M" },
      }[this.language].s,
      comprobanteRecibido: {
        es: { s: "Comprobante de Registro Recibido (DNDA)", p: "M" },
        en: { s: "Registration Certificate Received (DNDA)" },
        pt: { s: "Comprovante de Registro Recebido (DNDA)", p: "M" },
      }[this.language].s,
      ejemplarGeneracionFallida: {
        es: { s: "Error Generando Ejemplar", p: "M" },
        en: { s: "Exemplar Generation Failed" },
        pt: { s: "Falha na Geração do Exemplar", p: "F" },
      }[this.language].s,
      registroDndaFallido: {
        es: { s: "Error en Registro DNDA", p: "M" },
        en: { s: "DNDA Registration Failed" },
        pt: { s: "Falha no Registro DNDA", p: "M" },
      }[this.language].s,
      uploadFallido: {
        es: { s: "Error Subiendo Ejemplar", p: "M" },
        en: { s: "Exemplar Upload Failed" },
        pt: { s: "Falha ao Enviar Exemplar", p: "M" },
      }[this.language].s,
      registroAvanceFallido: {
        es: { s: "Registro OK, avance pendiente", p: "M" },
        en: { s: "Registered, advance pending" },
        pt: { s: "Registro OK, avanço pendente", p: "M" },
      }[this.language].s,
      uploadAvanceFallido: {
        es: { s: "Subida OK, avance pendiente", p: "M" },
        en: { s: "Uploaded, advance pending" },
        pt: { s: "Envio OK, avanço pendente", p: "M" },
      }[this.language].s,
    };
  }

  SignedEditionSadaicStateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Estado SADAIC", p: "Estado del registro en SADAIC", g: "M" },
      en: { s: "SADAIC State", p: "State of the SADAIC registration" },
      pt: { s: "Estado SADAIC", p: "Estado do registro na SADAIC", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSadaicStateLabel() {
    return this.SignedEditionSadaicStateLabelByLang();
  }

  get SignedEditionSadaicStateTransitionLabels(): Record<string, string> {
    return {
      iniciarSubidaSadaic: {
        es: { s: "IniciarSubidaSadaic" },
        en: { s: "IniciarSubidaSadaic" },
        pt: { s: "IniciarSubidaSadaic" },
      }[this.language].s,
      completarSubidaSadaic: {
        es: { s: "CompletarSubidaSadaic" },
        en: { s: "CompletarSubidaSadaic" },
        pt: { s: "CompletarSubidaSadaic" },
      }[this.language].s,
      fallarSubidaSadaic: {
        es: { s: "FallarSubidaSadaic" },
        en: { s: "FallarSubidaSadaic" },
        pt: { s: "FallarSubidaSadaic" },
      }[this.language].s,
      fallarInicioSadaic: {
        es: { s: "FallarInicioSadaic" },
        en: { s: "FallarInicioSadaic" },
        pt: { s: "FallarInicioSadaic" },
      }[this.language].s,
      reintentarSubidaSadaic: {
        es: { s: "ReintentarSubidaSadaic" },
        en: { s: "ReintentarSubidaSadaic" },
        pt: { s: "ReintentarSubidaSadaic" },
      }[this.language].s,
      confirmarPublicacionSadaic: {
        es: { s: "ConfirmarPublicacionSadaic" },
        en: { s: "ConfirmarPublicacionSadaic" },
        pt: { s: "ConfirmarPublicacionSadaic" },
      }[this.language].s,
    };
  }

  get SignedEditionSadaicStateStateLabels(): Record<string, string> {
    return {
      contratosFirmados: {
        es: { s: "Documentación Firmada", p: "F" },
        en: { s: "Signed Documentation" },
        pt: { s: "Documentação Assinada", p: "F" },
      }[this.language].s,
      subiendoSadaic: {
        es: { s: "Entrega de Documentación (SADAIC)", p: "M" },
        en: { s: "Documentation Submission (SADAIC)" },
        pt: { s: "Entrega de Documentação (SADAIC)", p: "M" },
      }[this.language].s,
      sadaicEnTramite: {
        es: { s: "Documentación Entregada a SADAIC (vía SFTP)", p: "F" },
        en: { s: "Documentation Delivered to SADAIC (via SFTP)" },
        pt: { s: "Documentação Entregue à SADAIC (via SFTP)", p: "F" },
      }[this.language].s,
      sadaicPublicado: {
        es: { s: "Obras Ingresadas (SADAIC)", p: "M" },
        en: { s: "Works Registered (SADAIC)" },
        pt: { s: "Obras Ingressadas (SADAIC)", p: "M" },
      }[this.language].s,
      subidaSadaicFallida: {
        es: { s: "Error Subiendo a SADAIC", p: "M" },
        en: { s: "SADAIC Upload Failed" },
        pt: { s: "Falha no Envio para SADAIC", p: "M" },
      }[this.language].s,
    };
  }

  SignedEditionWorksLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Obras", p: "Obras incluidas en esta edición firmada", g: "F" },
      en: { s: "Works", p: "Works included in this signed edition" },
      pt: { s: "Obras", p: "Obras incluídas nesta edição assinada", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionWorksLabel() {
    return this.SignedEditionWorksLabelByLang();
  }

  SignedEditionSigningPublishersLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Editoriales Firmantes",
        p: "Editoriales firmantes congeladas de esta edición",
        g: "F",
      },
      en: {
        s: "Signing Publishers",
        p: "Frozen signing publishers of this signed edition",
      },
      pt: {
        s: "Editoras Signatárias",
        p: "Editoras signatárias congeladas desta edição assinada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSigningPublishersLabel() {
    return this.SignedEditionSigningPublishersLabelByLang();
  }

  SignedEditionSigningAuthorsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Autores Firmantes",
        p: "Autores firmantes congelados de esta edición",
        g: "M",
      },
      en: {
        s: "Signing Authors",
        p: "Frozen signing authors of this signed edition",
      },
      pt: {
        s: "Autores Signatários",
        p: "Autores signatários congelados desta edição assinada",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSigningAuthorsLabel() {
    return this.SignedEditionSigningAuthorsLabelByLang();
  }

  SignedEditionPublicationDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha de Publicación",
        p: "Fecha en que se publica la edición firmada",
        g: "F",
      },
      en: {
        s: "Publication Date",
        p: "Date when the signed edition is published",
      },
      pt: {
        s: "Data de Publicação",
        p: "Data em que a edição assinada é publicada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionPublicationDateLabel() {
    return this.SignedEditionPublicationDateLabelByLang();
  }

  get SignedEditionPublicationDateDescription() {
    return {
      es: {
        s: "Fecha en la que se considera publicada la edición: el día en que se completó la firma del último contrato. Es la misma fecha impresa en el ejemplar de edición y en la DJCT.",
      },
      en: {
        s: "Date when the edition is considered published: the day the last contract signature was completed. It is the same date printed on the edition exemplar and the DJCT.",
      },
      pt: {
        s: "Data em que a edição é considerada publicada: o dia em que a assinatura do último contrato foi concluída. É a mesma data impressa no exemplar da edição e na DJCT.",
      },
    }[this.language].s;
  }

  SignedEditionDndaExpedienteNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nº de Expediente DNDA",
        p: "Número de expediente del trámite DNDA",
        g: "M",
      },
      en: { s: "DNDA File Number", p: "DNDA procedure file number" },
      pt: {
        s: "Nº de Processo DNDA",
        p: "Número de processo do trâmite DNDA",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaExpedienteNumberLabel() {
    return this.SignedEditionDndaExpedienteNumberLabelByLang();
  }

  SignedEditionDndaDocumentsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Documentos DNDA",
        p: "Documentos descargados del trámite DNDA",
        g: "F",
      },
      en: {
        s: "DNDA Documents",
        p: "Documents downloaded from the DNDA procedure",
      },
      pt: {
        s: "Documentos DNDA",
        p: "Documentos baixados do trâmite DNDA",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaDocumentsLabel() {
    return this.SignedEditionDndaDocumentsLabelByLang();
  }

  SignedEditionEjemplarPdfLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Ejemplar de Edición",
        p: "PDF que se sube a DNDA y SADAIC",
        g: "M",
      },
      en: { s: "Edition Exemplar", p: "PDF uploaded to DNDA and SADAIC" },
      pt: { s: "Exemplar de Edição", p: "PDF enviado à DNDA e SADAIC", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionEjemplarPdfLabel() {
    return this.SignedEditionEjemplarPdfLabelByLang();
  }

  get SignedEditionEjemplarPdfDescription() {
    return {
      es: {
        s: "Documento único de la edición que combina la portada con las partituras firmadas. Se genera una sola vez al crear la SignedEdition y es el archivo que se sube tanto al repositorio DNDA como al FTP de SADAIC.",
      },
      en: {
        s: "Single document of the edition combining the cover page with the signed scores. Generated once at SignedEdition creation; it is the file uploaded both to the DNDA repository and the SADAIC FTP.",
      },
      pt: {
        s: "Documento único da edição que combina a capa com as partituras assinadas. Gerado uma única vez ao criar a SignedEdition; é o arquivo enviado tanto ao repositório da DNDA quanto ao FTP da SADAIC.",
      },
    }[this.language].s;
  }

  SignedEditionDndaComprobantePdfLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Comprobante DNDA",
        p: "PDF del comprobante de obra publicada en DNDA",
        g: "M",
      },
      en: {
        s: "DNDA Certificate",
        p: "PDF of the published-work certificate from DNDA",
      },
      pt: {
        s: "Comprovante DNDA",
        p: "PDF do comprovante de obra publicada na DNDA",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaComprobantePdfLabel() {
    return this.SignedEditionDndaComprobantePdfLabelByLang();
  }

  get SignedEditionDndaComprobantePdfDescription() {
    return {
      es: {
        s: "Comprobante final de obra publicada en DNDA. Lo baja el bot DndaDownloadComprobante que un scheduler ejecuta cada N días contra el portal hasta encontrarlo (puede tardar meses).",
      },
      en: {
        s: "Final published-work certificate from DNDA. Fetched by the DndaDownloadComprobante bot, scheduled to poll the portal every N days until found (may take months).",
      },
      pt: {
        s: "Comprovante final de obra publicada na DNDA. Baixado pelo bot DndaDownloadComprobante, executado por um scheduler a cada N dias até encontrá-lo (pode levar meses).",
      },
    }[this.language].s;
  }

  SignedEditionBlockchainCertificatesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Certificados Blockchain",
        p: "Certificados de anclaje en blockchain de los contratos",
        g: "F",
      },
      en: {
        s: "Blockchain Certificates",
        p: "Blockchain anchoring certificates of the contracts",
      },
      pt: {
        s: "Certificados Blockchain",
        p: "Certificados de ancoragem em blockchain dos contratos",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionBlockchainCertificatesLabel() {
    return this.SignedEditionBlockchainCertificatesLabelByLang();
  }

  SignedEditionDndaUploadLinkLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Link de Upload DNDA",
        p: "Link efímero del repositorio digital DNDA",
        g: "M",
      },
      en: {
        s: "DNDA Upload Link",
        p: "Ephemeral link to the DNDA digital repository",
      },
      pt: {
        s: "Link de Envio DNDA",
        p: "Link efêmero do repositório digital DNDA",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaUploadLinkLabel() {
    return this.SignedEditionDndaUploadLinkLabelByLang();
  }

  SignedEditionDndaUploadPasswordLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Contraseña de Upload DNDA",
        p: "Contraseña efímera del link DNDA",
        g: "F",
      },
      en: {
        s: "DNDA Upload Password",
        p: "Ephemeral password of the DNDA link",
      },
      pt: { s: "Senha de Envio DNDA", p: "Senha efêmera do link DNDA", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaUploadPasswordLabel() {
    return this.SignedEditionDndaUploadPasswordLabelByLang();
  }

  SignedEditionDndaUploadKeyReceivedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Clave de Upload Recibida",
        p: "Fecha y hora en que llegó el email DNDA con el link y la contraseña",
        g: "F",
      },
      en: {
        s: "Upload Key Received At",
        p: "Date and time when the DNDA email with the link and password arrived",
      },
      pt: {
        s: "Chave de Envio Recebida em",
        p: "Data e hora em que chegou o email DNDA com o link e a senha",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaUploadKeyReceivedAtLabel() {
    return this.SignedEditionDndaUploadKeyReceivedAtLabelByLang();
  }

  SignedEditionDndaUploadKeyExpiresAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Clave de Upload Expira",
        p: "Fecha y hora en que vence la clave de upload (72hs desde recepción)",
        g: "F",
      },
      en: {
        s: "Upload Key Expires At",
        p: "Date and time when the upload key expires (72h from receipt)",
      },
      pt: {
        s: "Chave de Envio Expira em",
        p: "Data e hora em que a chave de envio expira (72h após o recebimento)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaUploadKeyExpiresAtLabel() {
    return this.SignedEditionDndaUploadKeyExpiresAtLabelByLang();
  }

  SignedEditionDndaComprobanteReceivedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Comprobante de Registro (DNDA) Descargado el", p: "M" },
      en: { s: "Registration Certificate (DNDA) Downloaded At" },
      pt: { s: "Comprovante de Registro (DNDA) Baixado em", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaComprobanteReceivedAtLabel() {
    return this.SignedEditionDndaComprobanteReceivedAtLabelByLang();
  }

  SignedEditionDndaLastComprobantePollAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Último Polling de Comprobante",
        p: "Fecha y hora del último intento de descarga del comprobante DNDA",
        g: "F",
      },
      en: {
        s: "Last Certificate Poll",
        p: "Date and time of the last DNDA certificate download attempt",
      },
      pt: {
        s: "Último Polling de Comprovante",
        p: "Data e hora da última tentativa de download do comprovante DNDA",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaLastComprobantePollAtLabel() {
    return this.SignedEditionDndaLastComprobantePollAtLabelByLang();
  }

  SignedEditionEjemplarGenerationAttemptsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intentos de Generación de Ejemplar", p: "M" },
      en: { s: "Exemplar Generation Attempts" },
      pt: { s: "Tentativas de Geração de Exemplar", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionEjemplarGenerationAttemptsLabel() {
    return this.SignedEditionEjemplarGenerationAttemptsLabelByLang();
  }

  SignedEditionDndaRegistroAttemptsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intentos de Registro DNDA", p: "M" },
      en: { s: "DNDA Registration Attempts" },
      pt: { s: "Tentativas de Registro DNDA", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaRegistroAttemptsLabel() {
    return this.SignedEditionDndaRegistroAttemptsLabelByLang();
  }

  SignedEditionDndaUploadAttemptsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intentos de Upload de Ejemplar", p: "M" },
      en: { s: "Exemplar Upload Attempts" },
      pt: { s: "Tentativas de Envio de Exemplar", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaUploadAttemptsLabel() {
    return this.SignedEditionDndaUploadAttemptsLabelByLang();
  }

  SignedEditionDndaLastOperationalErrorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Último Error Operacional DNDA",
        p: "Mensaje del último error del proceso DNDA (debug)",
        g: "M",
      },
      en: {
        s: "DNDA Last Operational Error",
        p: "Last DNDA process error message (debug)",
      },
      pt: {
        s: "Último Erro Operacional DNDA",
        p: "Mensagem do último erro do processo DNDA (debug)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaLastOperationalErrorLabel() {
    return this.SignedEditionDndaLastOperationalErrorLabelByLang();
  }

  SignedEditionSadaicLastPolledAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Última Consulta SADAIC",
        p: "Fecha y hora de la última consulta de publicación",
        g: "F",
      },
      en: {
        s: "Last SADAIC Poll",
        p: "Date and time of the last publication poll",
      },
      pt: {
        s: "Última Consulta SADAIC",
        p: "Data e hora da última consulta de publicação",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSadaicLastPolledAtLabel() {
    return this.SignedEditionSadaicLastPolledAtLabelByLang();
  }

  SignedEditionSadaicDeliveredAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Entregado en SADAIC (SFTP)", p: "M" },
      en: { s: "Delivered to SADAIC (SFTP)" },
      pt: { s: "Entregue à SADAIC (SFTP)", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSadaicDeliveredAtLabel() {
    return this.SignedEditionSadaicDeliveredAtLabelByLang();
  }

  SignedEditionSadaicUploadAttemptsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Intentos de Subida SADAIC", p: "M" },
      en: { s: "SADAIC Upload Attempts" },
      pt: { s: "Tentativas de Envio SADAIC", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSadaicUploadAttemptsLabel() {
    return this.SignedEditionSadaicUploadAttemptsLabelByLang();
  }

  SignedEditionSadaicLastOperationalErrorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Último Error Operacional SADAIC",
        p: "Mensaje del último error del proceso SADAIC (debug)",
        g: "M",
      },
      en: {
        s: "SADAIC Last Operational Error",
        p: "Last SADAIC process error message (debug)",
      },
      pt: {
        s: "Último Erro Operacional SADAIC",
        p: "Mensagem do último erro do processo SADAIC (debug)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSadaicLastOperationalErrorLabel() {
    return this.SignedEditionSadaicLastOperationalErrorLabelByLang();
  }

  SignedEditionCreatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Firmada el", p: "F" },
      en: { s: "Signed on" },
      pt: { s: "Assinada em", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionCreatedAt_Label() {
    return this.SignedEditionCreatedAt_LabelByLang();
  }

  get SignedEditionCreatedAt_Description() {
    return {
      es: {
        s: "Fecha y hora en que todas las partes firmaron y se creó este snapshot inmutable.",
      },
      en: {
        s: "Date and time when all parties signed and this immutable snapshot was created.",
      },
      pt: {
        s: "Data e hora em que todas as partes assinaram e este snapshot imutável foi criado.",
      },
    }[this.language].s;
  }

  SignedEditionLastUpdatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Última Actualización", p: "F" },
      en: { s: "Last Updated" },
      pt: { s: "Última Atualização", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionLastUpdatedAt_Label() {
    return this.SignedEditionLastUpdatedAt_LabelByLang();
  }

  get SignedEditionLastUpdatedAt_Description() {
    return {
      es: {
        s: "Fecha y hora de la última actualización de esta edición firmada (estados DNDA/SADAIC, número de expediente, documentos descargados).",
      },
      en: {
        s: "Date and time of the last update of this signed edition (DNDA/SADAIC states, file number, downloaded documents).",
      },
      pt: {
        s: "Data e hora da última atualização desta edição assinada (estados DNDA/SADAIC, número de processo, documentos baixados).",
      },
    }[this.language].s;
  }

  SignedEditionData_LabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Datos Congelados", g: "f" },
      en: { s: "Frozen Data" },
      pt: { s: "Dados Congelados", g: "f" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionData_Label() {
    return this.SignedEditionData_LabelByLang();
  }

  get SignedEditionData_Description() {
    return {
      es: {
        s: "Snapshot estructurado de la entidad y su grafo al momento del congelamiento.",
      },
      en: {
        s: "Structured snapshot of the entity and its graph at freeze time.",
      },
      pt: {
        s: "Snapshot estruturado da entidade e seu grafo no momento do congelamento.",
      },
    }[this.language].s;
  }

  SignedEditionOriginalId_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID Original", g: "m" },
      en: { s: "Original ID" },
      pt: { s: "ID Original", g: "m" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionOriginalId_Label() {
    return this.SignedEditionOriginalId_LabelByLang();
  }

  get SignedEditionOriginalId_Description() {
    return {
      es: {
        s: "ID que tuvo la entidad viva al momento del snapshot. Auditoría — no es una FK.",
      },
      en: {
        s: "ID the live entity had at snapshot time. Audit only — not an FK.",
      },
      pt: {
        s: "ID que a entidade viva tinha no momento do snapshot. Auditoria apenas — não é uma FK.",
      },
    }[this.language].s;
  }

  SignedEditionNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre de edición", p: "Nombre de la edición", g: "M" },
      en: { s: "Edition name", p: "Edition name" },
      pt: { s: "Nome da edição", p: "Nome da edição", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionNameLabel() {
    return this.SignedEditionNameLabelByLang();
  }

  SignedEditionEditionNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nº Edición", p: "Nº Edición" },
      en: { s: "EditionNumber" },
      pt: { s: "EditionNumber" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionEditionNumberLabel() {
    return this.SignedEditionEditionNumberLabelByLang();
  }

  // EditionContractPublisher

  get EditionContractPublisherLabels() {
    return {
      es: {
        s: "Reparto Editorial del Contrato",
        p: "Repartos Editoriales del Contrato",
        g: "M",
      },
      en: {
        s: "Contract Publisher Share",
        p: "Contract Publisher Shares",
        g: undefined,
      },
      pt: {
        s: "Distribuição Editorial do Contrato",
        p: "Distribuições Editoriais do Contrato",
        g: "M",
      },
    }[this.language];
  }

  get EditionContractPublisherSingular() {
    return this.EditionContractPublisherLabels.s;
  }

  get EditionContractPublisherPlural() {
    return this.EditionContractPublisherLabels.p;
  }

  get EditionContractPublisherGender() {
    return this.EditionContractPublisherLabels.g;
  }

  get groupEditionContractPublisherLabel() {
    return {
      es: {
        s: "Reparto Editorial del Contrato",
        p: "Repartos Editoriales del Contrato",
        g: "M",
      },
      en: { s: "Contract Publisher Share", p: "Contract Publisher Shares" },
      pt: {
        s: "Distribuição Editorial do Contrato",
        p: "Distribuições Editoriais do Contrato",
        g: "M",
      },
    }[this.language].s;
  }

  EditionContractPublisherPublisherLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Editorial", p: "Editorial musical", g: "F" },
      en: { s: "Publisher", p: "Music publisher" },
      pt: { s: "Editora", p: "Editora musical", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionContractPublisherPublisherLabel() {
    return this.EditionContractPublisherPublisherLabelByLang();
  }

  EditionContractPublisherPercentageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Porcentaje",
        p: "Porcentaje editorial dentro del contrato",
        g: "M",
      },
      en: { s: "Percentage", p: "Publisher percentage within the contract" },
      pt: {
        s: "Porcentagem",
        p: "Porcentagem editorial dentro do contrato",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionContractPublisherPercentageLabel() {
    return this.EditionContractPublisherPercentageLabelByLang();
  }

  // EditionDocumentation

  get EditionDocumentationLabels() {
    return {
      es: {
        s: "Contrato de Edición Musical",
        p: "Contratos de Edición Musical",
        g: "M",
      },
      en: {
        s: "Musical Edition Contract",
        p: "Musical Edition Contracts",
        g: undefined,
      },
      pt: {
        s: "Contrato de Edição Musical",
        p: "Contratos de Edição Musical",
        g: "M",
      },
    }[this.language];
  }

  get EditionDocumentationSingular() {
    return this.EditionDocumentationLabels.s;
  }

  get EditionDocumentationPlural() {
    return this.EditionDocumentationLabels.p;
  }

  get EditionDocumentationGender() {
    return this.EditionDocumentationLabels.g;
  }

  get groupEditionDocumentationLabel() {
    return {
      es: {
        s: "Contrato de Edición Musical",
        p: "Contratos de Edición Musical",
        g: "M",
      },
      en: { s: "Musical Edition Contract", p: "Musical Edition Contracts" },
      pt: {
        s: "Contrato de Edição Musical",
        p: "Contratos de Edição Musical",
        g: "M",
      },
    }[this.language].s;
  }

  EditionDocumentationWorksLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Obras Incluidas", p: "F" },
      en: { s: "Included Works" },
      pt: { s: "Obras Incluídas", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationWorksLabel() {
    return this.EditionDocumentationWorksLabelByLang();
  }

  get EditionDocumentationWorksDescription() {
    return {
      es: {
        s: "Lista de obras musicales específicas incluídas en este contrato de edición musical.",
      },
      en: {
        s: "List of specific musical works included in this music publishing contract.",
      },
      pt: {
        s: "Lista de obras musicais específicas incluídas neste contrato de edição musical.",
      },
    }[this.language].s;
  }

  EditionDocumentationTerritoriesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Territorios", p: "Territorios del contrato", g: "M" },
      en: { s: "Territories", p: "Territories of the contract" },
      pt: { s: "Territórios", p: "Territórios do contrato", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationTerritoriesLabel() {
    return this.EditionDocumentationTerritoriesLabelByLang();
  }

  get EditionDocumentationTerritoriesDescription() {
    return {
      es: {
        s: "Territorios en los que aplica el Contrato de Edición Musical.",
      },
      en: { s: "Territories where the Music Publishing Contract applies." },
      pt: { s: "Territórios onde o Contrato de Edição Musical se aplica." },
    }[this.language].s;
  }

  EditionDocumentationAuthorsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Autores", p: "Autores/Compositores del contrato", g: "M" },
      en: { s: "Authors", p: "Authors/Composers of the contract" },
      pt: { s: "Autores", p: "Autores/Compositores do contrato", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationAuthorsLabel() {
    return this.EditionDocumentationAuthorsLabelByLang();
  }

  get EditionDocumentationAuthorsDescription() {
    return {
      es: {
        s: "Autores/Compositores incluídos en el Contrato de Edición Musical.",
      },
      en: { s: "Authors/Composers included in the Music Publishing Contract." },
      pt: {
        s: "Autores/Compositores incluídos no Contrato de Edição Musical.",
      },
    }[this.language].s;
  }

  EditionDocumentationAuthorsDisplayLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Autores", p: "Descripción de autores del grupo", g: "M" },
      en: { s: "Authors", p: "Authors in this group" },
      pt: { s: "Autores", p: "Autores deste grupo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationAuthorsDisplayLabel() {
    return this.EditionDocumentationAuthorsDisplayLabelByLang();
  }

  get EditionDocumentationAuthorsDisplayDescription() {
    return {
      es: {
        s: "Autores/Compositores incluídos en el Contrato de Edición Musical.",
      },
      en: { s: "Authors/Composers included in the Music Publishing Contract." },
      pt: {
        s: "Autores/Compositores incluídos no Contrato de Edição Musical.",
      },
    }[this.language].s;
  }

  EditionDocumentationPublishersLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Reparto Editorial",
        p: "Reparto editorial del contrato",
        g: "M",
      },
      en: {
        s: "Publisher Distribution",
        p: "Publisher distribution of the contract",
      },
      pt: {
        s: "Distribuição Editorial",
        p: "Distribuição editorial do contrato",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationPublishersLabel() {
    return this.EditionDocumentationPublishersLabelByLang();
  }

  get EditionDocumentationPublishersDescription() {
    return {
      es: {
        s: "Reparto editorial (editorial + porcentaje) del Contrato de Edición Musical.",
      },
      en: {
        s: "Publisher distribution (publisher + percentage) of the Music Publishing Contract.",
      },
      pt: {
        s: "Distribuição editorial (editora + porcentagem) do Contrato de Edição Musical.",
      },
    }[this.language].s;
  }

  EditionDocumentationPublisherConfigurationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Configuración Editorial",
        p: "Clave de configuración de editoriales",
        g: "F",
      },
      en: { s: "Publisher Configuration", p: "Publisher configuration key" },
      pt: {
        s: "Configuração Editorial",
        p: "Chave de configuração de editoras",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationPublisherConfigurationLabel() {
    return this.EditionDocumentationPublisherConfigurationLabelByLang();
  }

  get EditionDocumentationPublisherConfigurationDescription() {
    return {
      es: {
        s: "Reparto editorial del Contrato de Edición Musical y sus porcentajes asignados.",
      },
      en: {
        s: "Publishing distribution of the Music Publishing Contract and its assigned percentages.",
      },
      pt: {
        s: "Distribuição editorial do Contrato de Edição Musical e suas porcentagens atribuídas.",
      },
    }[this.language].s;
  }

  EditionDocumentationPublisherConfigurationDisplayLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Editoriales", p: "Descripción de editoriales", g: "M" },
      en: { s: "Publishers", p: "Publishers description" },
      pt: { s: "Editoras", p: "Descrição de editoras", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationPublisherConfigurationDisplayLabel() {
    return this.EditionDocumentationPublisherConfigurationDisplayLabelByLang();
  }

  get EditionDocumentationPublisherConfigurationDisplayDescription() {
    return {
      es: { s: "Reparto editorial del Contrato de Edición Musical." },
      en: { s: "Publishing distribution of the Music Publishing Contract." },
      pt: { s: "Distribuição editorial do Contrato de Edição Musical." },
    }[this.language].s;
  }

  EditionDocumentationGroupingKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Clave de Agrupación",
        p: "La clave única (JSON) que define este grupo",
        g: "F",
      },
      en: {
        s: "Grouping Key",
        p: "The unique key (JSON) that defines this group",
      },
      pt: {
        s: "Chave de Agrupamento",
        p: "A chave única (JSON) que define este grupo",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationGroupingKeyLabel() {
    return this.EditionDocumentationGroupingKeyLabelByLang();
  }

  get EditionDocumentationGroupingKeyDescription() {
    return {
      es: {
        s: "Clave interna utilizada para agrupar contratos que pertenecen a una misma negociación o conjunto, usualmente basada en los autores involucrados.",
      },
      en: {
        s: "Internal key used to group contracts belonging to the same negotiation or set, usually based on the involved authors.",
      },
      pt: {
        s: "Chave interna usada para agrupar contratos pertencentes à mesma negociação ou conjunto, geralmente baseada nos autores envolvidos.",
      },
    }[this.language].s;
  }

  EditionDocumentationTerritoriesDisplayLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Territorios", p: "Descripción de territorios", g: "M" },
      en: { s: "Territories", p: "Territories description" },
      pt: { s: "Territórios", p: "Descrição de territórios", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationTerritoriesDisplayLabel() {
    return this.EditionDocumentationTerritoriesDisplayLabelByLang();
  }

  get EditionDocumentationTerritoriesDisplayDescription() {
    return {
      es: {
        s: "Territorios en los que aplica el Contrato de Edición Musical.",
      },
      en: { s: "Territories where the Music Publishing Contract applies." },
      pt: { s: "Territórios onde o Contrato de Edição Musical se aplica." },
    }[this.language].s;
  }

  EditionDocumentationContractDurationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Duración", p: "Duración del contrato en años", g: "F" },
      en: { s: "Duration", p: "Contract duration in years" },
      pt: { s: "Duração", p: "Duração do contrato em anos", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationContractDurationLabel() {
    return this.EditionDocumentationContractDurationLabelByLang();
  }

  get EditionDocumentationContractDurationNullText() {
    return {
      es: {
        s: "Sin límite de duración",
        p: "Sin límite específico de duración del contrato",
      },
      en: { s: "No limit", p: "No specific duration limit for the contract" },
      pt: {
        s: "Sem limite",
        p: "Sem limite específico de duração do contrato",
      },
    }[this.language].s;
  }

  get EditionDocumentationContractDurationDescription() {
    return {
      es: {
        s: "Duración del Contrato de Edición Musical, en el caso de que la duración esté limitada en la sección 'Observaciones'.",
      },
      en: {
        s: "Duration of the Music Publishing Contract, if the duration is limited in the 'Observations' section.",
      },
      pt: {
        s: "Duração do Contrato de Edição Musical, caso a duração esteja limitada na seção 'Observações'.",
      },
    }[this.language].s;
  }

  EditionDocumentationContractRenewalLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Renovación",
        p: "Renovación automática del contrato en años",
        g: "F",
      },
      en: { s: "Renewal", p: "Contract auto-renewal in years" },
      pt: {
        s: "Renovação",
        p: "Renovação automática do contrato em anos",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationContractRenewalLabel() {
    return this.EditionDocumentationContractRenewalLabelByLang();
  }

  get EditionDocumentationContractRenewalNullText() {
    return {
      es: { s: "NO", p: "Sin renovación automática" },
      en: { s: "NO", p: "No automatic renewal" },
      pt: { s: "NÃO", p: "Sem renovação automática" },
    }[this.language].s;
  }

  get EditionDocumentationContractRenewalDescription() {
    return {
      es: {
        s: "Años de renovación automática del Contrato de Edición Musical. Un contrato no puede mezclar distintos años de renovación (segmenta como la duración).",
      },
      en: {
        s: "Auto-renewal years of the Music Publishing Contract. A contract cannot mix different renewal years (it segments like the duration).",
      },
      pt: {
        s: "Anos de renovação automática do Contrato de Edição Musical. Um contrato não pode misturar diferentes anos de renovação (segmenta como a duração).",
      },
    }[this.language].s;
  }

  EditionDocumentationContractNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Número de Contrato", p: "M" },
      en: { s: "Contract Number" },
      pt: { s: "Número do Contrato", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationContractNumberLabel() {
    return this.EditionDocumentationContractNumberLabelByLang();
  }

  get EditionDocumentationContractNumberDescription() {
    return {
      es: { s: "Número o código de identificación único para este contrato." },
      en: { s: "Unique identification number or code for this contract." },
      pt: {
        s: "Número ou código de identificação exclusivo para este contrato.",
      },
    }[this.language].s;
  }

  EditionDocumentationDocumentNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Documento", p: "Nombre del documento a firmar", g: "M" },
      en: { s: "Document", p: "Name of the document to sign" },
      pt: { s: "Documento", p: "Nome do documento a assinar", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationDocumentNameLabel() {
    return this.EditionDocumentationDocumentNameLabelByLang();
  }

  get EditionDocumentationDocumentNameDescription() {
    return {
      es: {
        s: "Nombre visible del documento: CTTO-<número> para contratos de edición y DJCT-<número> para la Declaración Jurada de Control de Tiraje.",
      },
      en: {
        s: "Visible document name: CTTO-<number> for publishing contracts and DJCT-<number> for the Sworn Print-Run Control Statement.",
      },
      pt: {
        s: "Nome visível do documento: CTTO-<número> para contratos de edição e DJCT-<número> para a Declaração Juramentada de Controle de Tiragem.",
      },
    }[this.language].s;
  }

  EditionDocumentationDocumentTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Documento",
        p: "Tipo de documento generado (contrato o DJCT)",
        g: "M",
      },
      en: {
        s: "Document Type",
        p: "Generated document type (contract or DJCT)",
      },
      pt: {
        s: "Tipo de Documento",
        p: "Tipo de documento gerado (contrato ou DJCT)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationDocumentTypeLabel() {
    return this.EditionDocumentationDocumentTypeLabelByLang();
  }

  get EditionDocumentationDocumentTypeDescription() {
    return {
      es: {
        s: "Discrimina el tipo de documento: 'contract' (contrato de edición por reparto) o 'djct' (Declaración Jurada de Control de Tiraje, una por edición).",
      },
      en: {
        s: "Discriminates the document type: 'contract' (per-distribution edition contract) or 'djct' (Sworn Print-Run Control Statement, one per edition).",
      },
      pt: {
        s: "Discrimina o tipo de documento: 'contract' (contrato de edição por distribuição) ou 'djct' (Declaração Juramentada de Controle de Tiragem, uma por edição).",
      },
    }[this.language].s;
  }

  EditionDocumentationFinalDocumentPdfLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Archivo PDF", p: "Documento final en formato PDF", g: "M" },
      en: { s: "PDF File", p: "Final document in PDF format" },
      pt: { s: "Arquivo PDF", p: "Documento final em formato PDF", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationFinalDocumentPdfLabel() {
    return this.EditionDocumentationFinalDocumentPdfLabelByLang();
  }

  get EditionDocumentationFinalDocumentPdfDescription() {
    return {
      es: {
        s: "La versión final del documento, generada como un PDF no editable, lista para su distribución o firma.",
      },
      en: {
        s: "The final version of the document, generated as a non-editable PDF, ready for distribution or signature.",
      },
      pt: {
        s: "A versão final do documento, gerada como um PDF não editável, pronta para distribuição ou assinatura.",
      },
    }[this.language].s;
  }

  // SignedContract

  get SignedContractLabels() {
    return {
      es: { s: "Contrato Firmado", p: "Contratos Firmados", g: "M" },
      en: { s: "Signed Contract", p: "Signed Contracts", g: undefined },
      pt: { s: "Contrato Assinado", p: "Contratos Assinados", g: "M" },
    }[this.language];
  }

  get SignedContractSingular() {
    return this.SignedContractLabels.s;
  }

  get SignedContractPlural() {
    return this.SignedContractLabels.p;
  }

  get SignedContractGender() {
    return this.SignedContractLabels.g;
  }

  get groupSignedContractLabel() {
    return {
      es: { s: "Contrato Firmado", p: "Contratos Firmados", g: "M" },
      en: { s: "Signed Contract", p: "Signed Contracts" },
      pt: { s: "Contrato Assinado", p: "Contratos Assinados", g: "M" },
    }[this.language].s;
  }

  SignedContractSigningPublishersLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Editoriales Firmantes",
        p: "Editoriales firmantes congeladas de este contrato",
        g: "F",
      },
      en: {
        s: "Signing Publishers",
        p: "Frozen signing publishers of this signed contract",
      },
      pt: {
        s: "Editoras Signatárias",
        p: "Editoras signatárias congeladas deste contrato assinado",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedContractSigningPublishersLabel() {
    return this.SignedContractSigningPublishersLabelByLang();
  }

  SignedContractSigningAuthorsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Autores Firmantes",
        p: "Autores firmantes congelados de este contrato",
        g: "M",
      },
      en: {
        s: "Signing Authors",
        p: "Frozen signing authors of this signed contract",
      },
      pt: {
        s: "Autores Signatários",
        p: "Autores signatários congelados deste contrato assinado",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedContractSigningAuthorsLabel() {
    return this.SignedContractSigningAuthorsLabelByLang();
  }

  SignedContractSigningWorksLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Obras del Contrato",
        p: "Obras congeladas de este contrato firmado",
        g: "F",
      },
      en: { s: "Contract Works", p: "Frozen works of this signed contract" },
      pt: {
        s: "Obras do Contrato",
        p: "Obras congeladas deste contrato assinado",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedContractSigningWorksLabel() {
    return this.SignedContractSigningWorksLabelByLang();
  }

  SignedContractData_LabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Datos Congelados", g: "f" },
      en: { s: "Frozen Data" },
      pt: { s: "Dados Congelados", g: "f" },
    }[lang ? lang : this.language].s;
  }

  get SignedContractData_Label() {
    return this.SignedContractData_LabelByLang();
  }

  get SignedContractData_Description() {
    return {
      es: {
        s: "Snapshot estructurado de la entidad y su grafo al momento del congelamiento.",
      },
      en: {
        s: "Structured snapshot of the entity and its graph at freeze time.",
      },
      pt: {
        s: "Snapshot estruturado da entidade e seu grafo no momento do congelamento.",
      },
    }[this.language].s;
  }

  SignedContractOriginalId_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID Original", g: "m" },
      en: { s: "Original ID" },
      pt: { s: "ID Original", g: "m" },
    }[lang ? lang : this.language].s;
  }

  get SignedContractOriginalId_Label() {
    return this.SignedContractOriginalId_LabelByLang();
  }

  get SignedContractOriginalId_Description() {
    return {
      es: {
        s: "ID que tuvo la entidad viva al momento del snapshot. Auditoría — no es una FK.",
      },
      en: {
        s: "ID the live entity had at snapshot time. Audit only — not an FK.",
      },
      pt: {
        s: "ID que a entidade viva tinha no momento do snapshot. Auditoria apenas — não é uma FK.",
      },
    }[this.language].s;
  }

  // EditionDocumentType

  get EditionDocumentTypeLabels() {
    return {
      es: {
        s: "Tipo de Documento de Edición",
        p: "Tipos de Documento de Edición",
        g: "M",
      },
      en: {
        s: "Edition Document Type",
        p: "Edition Document Types",
        g: undefined,
      },
      pt: {
        s: "Tipo de Documento de Edição",
        p: "Tipos de Documento de Edição",
        g: "M",
      },
    }[this.language];
  }

  get EditionDocumentTypeSingular() {
    return this.EditionDocumentTypeLabels.s;
  }

  get EditionDocumentTypePlural() {
    return this.EditionDocumentTypeLabels.p;
  }

  get EditionDocumentTypeGender() {
    return this.EditionDocumentTypeLabels.g;
  }

  get groupEditionDocumentTypeLabel() {
    return {
      es: {
        s: "Tipo de Documento de Edición",
        p: "Tipos de Documento de Edición",
        g: "M",
      },
      en: { s: "Edition Document Type", p: "Edition Document Types" },
      pt: {
        s: "Tipo de Documento de Edição",
        p: "Tipos de Documento de Edição",
        g: "M",
      },
    }[this.language].s;
  }

  EditionDocumentTypeNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del tipo de documento", g: "M" },
      en: { s: "Name", p: "Name of the document type" },
      pt: { s: "Nome", p: "Nome do tipo de documento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentTypeNameLabel() {
    return this.EditionDocumentTypeNameLabelByLang();
  }

  get EditionDocumentTypeNameDescription() {
    return {
      es: { s: "Nombre de la categoría (ej. 'Partitura', 'Libreto')." },
      en: { s: "Name of the category (e.g., 'Score', 'Libretto')." },
      pt: { s: "Nome da categoria (ex. 'Partitura', 'Libreto')." },
    }[this.language].s;
  }

  // AssociatedDocumentEdition

  get AssociatedDocumentEditionLabels() {
    return {
      es: {
        s: "Documento Adicional (Edición)",
        p: "Documentos Adicionales (Edición)",
        g: "M",
      },
      en: {
        s: "Additional Document (Edition)",
        p: "Additional Documents (Edition)",
        g: undefined,
      },
      pt: {
        s: "Documento Adicional (Edição)",
        p: "Documentos Adicionais (Edição)",
        g: "M",
      },
    }[this.language];
  }

  get AssociatedDocumentEditionSingular() {
    return this.AssociatedDocumentEditionLabels.s;
  }

  get AssociatedDocumentEditionPlural() {
    return this.AssociatedDocumentEditionLabels.p;
  }

  get AssociatedDocumentEditionGender() {
    return this.AssociatedDocumentEditionLabels.g;
  }

  get groupAssociatedDocumentEditionLabel() {
    return {
      es: {
        s: "Documento Adicional (Edición)",
        p: "Documentos Adicionales (Edición)",
        g: "M",
      },
      en: {
        s: "Additional Document (Edition)",
        p: "Additional Documents (Edition)",
      },
      pt: {
        s: "Documento Adicional (Edição)",
        p: "Documentos Adicionais (Edição)",
        g: "M",
      },
    }[this.language].s;
  }

  AssociatedDocumentEditionNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del documento adicional", g: "M" },
      en: { s: "Name", p: "Name of the additional document" },
      pt: { s: "Nome", p: "Nome do documento adicional", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentEditionNameLabel() {
    return this.AssociatedDocumentEditionNameLabelByLang();
  }

  get AssociatedDocumentEditionNameDescription() {
    return {
      es: { s: "Nombre o título del documento adicional." },
      en: { s: "Name or title of the additional document." },
      pt: { s: "Nome ou título do documento adicional." },
    }[this.language].s;
  }

  AssociatedDocumentEditionFileLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Archivo", p: "Archivo del documento adicional", g: "M" },
      en: { s: "File", p: "Additional document file" },
      pt: { s: "Arquivo", p: "Arquivo do documento adicional", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentEditionFileLabel() {
    return this.AssociatedDocumentEditionFileLabelByLang();
  }

  get AssociatedDocumentEditionFileDescription() {
    return {
      es: { s: "El archivo digital del documento adicional." },
      en: { s: "The digital file of the additional document." },
      pt: { s: "O arquivo digital do documento adicional." },
    }[this.language].s;
  }

  AssociatedDocumentEditionTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo", p: "Tipo de documento", g: "M" },
      en: { s: "Type", p: "Document type" },
      pt: { s: "Tipo", p: "Tipo de documento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentEditionTypeLabel() {
    return this.AssociatedDocumentEditionTypeLabelByLang();
  }

  get AssociatedDocumentEditionTypeDescription() {
    return {
      es: { s: "Categoría del documento adicional (ej. Partitura, Libreto)." },
      en: { s: "Category of the additional document (e.g., Score, Libretto)." },
      pt: { s: "Categoria do documento adicional (ex: Partitura, Libreto)." },
    }[this.language].s;
  }

  AssociatedDocumentEditionNotesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Notas",
        p: "Notas adicionales sobre el documento adicional",
        g: "F",
      },
      en: { s: "Notes", p: "Additional notes about the additional document" },
      pt: {
        s: "Notas",
        p: "Notas adicionais sobre o documento adicional",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentEditionNotesLabel() {
    return this.AssociatedDocumentEditionNotesLabelByLang();
  }

  get AssociatedDocumentEditionNotesDescription() {
    return {
      es: {
        s: "Contexto o información adicional sobre la relevancia de este documento para la edición.",
      },
      en: {
        s: "Context or additional information regarding this document's relevance to the edition.",
      },
      pt: {
        s: "Contexto ou informações adicionais sobre a relevância deste documento para a edição.",
      },
    }[this.language].s;
  }

  AssociatedDocumentEditionPublisherConfigurationDisplayLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Editoriales", p: "Descripción de editoriales", g: "M" },
      en: { s: "Publishers", p: "Publishers description" },
      pt: { s: "Editoras", p: "Descrição de editoras", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentEditionPublisherConfigurationDisplayLabel() {
    return this.AssociatedDocumentEditionPublisherConfigurationDisplayLabelByLang();
  }

  AssociatedDocumentEditionAuthorsDisplayLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Autores", p: "Descripción de autores del grupo", g: "M" },
      en: { s: "Authors", p: "Authors in this group" },
      pt: { s: "Autores", p: "Autores deste grupo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentEditionAuthorsDisplayLabel() {
    return this.AssociatedDocumentEditionAuthorsDisplayLabelByLang();
  }

  // AssociatedDocumentSignedEdition

  get AssociatedDocumentSignedEditionLabels() {
    return {
      es: {
        s: "Documento DNDA (Edición Firmada)",
        p: "Documentos DNDA (Edición Firmada)",
        g: "M",
      },
      en: {
        s: "DNDA Document (Signed Edition)",
        p: "DNDA Documents (Signed Edition)",
        g: undefined,
      },
      pt: {
        s: "Documento DNDA (Edição Assinada)",
        p: "Documentos DNDA (Edição Assinada)",
        g: "M",
      },
    }[this.language];
  }

  get AssociatedDocumentSignedEditionSingular() {
    return this.AssociatedDocumentSignedEditionLabels.s;
  }

  get AssociatedDocumentSignedEditionPlural() {
    return this.AssociatedDocumentSignedEditionLabels.p;
  }

  get AssociatedDocumentSignedEditionGender() {
    return this.AssociatedDocumentSignedEditionLabels.g;
  }

  get groupAssociatedDocumentSignedEditionLabel() {
    return {
      es: {
        s: "Documento DNDA (Edición Firmada)",
        p: "Documentos DNDA (Edición Firmada)",
        g: "M",
      },
      en: {
        s: "DNDA Document (Signed Edition)",
        p: "DNDA Documents (Signed Edition)",
      },
      pt: {
        s: "Documento DNDA (Edição Assinada)",
        p: "Documentos DNDA (Edição Assinada)",
        g: "M",
      },
    }[this.language].s;
  }

  AssociatedDocumentSignedEditionNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del documento DNDA", g: "M" },
      en: { s: "Name", p: "Name of the DNDA document" },
      pt: { s: "Nome", p: "Nome do documento DNDA", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentSignedEditionNameLabel() {
    return this.AssociatedDocumentSignedEditionNameLabelByLang();
  }

  AssociatedDocumentSignedEditionFileLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Archivo", p: "Archivo del documento DNDA", g: "M" },
      en: { s: "File", p: "DNDA document file" },
      pt: { s: "Arquivo", p: "Arquivo do documento DNDA", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AssociatedDocumentSignedEditionFileLabel() {
    return this.AssociatedDocumentSignedEditionFileLabelByLang();
  }

  // SignedEditionBlockchainCert

  get SignedEditionBlockchainCertLabels() {
    return {
      es: { s: "Certificado Blockchain", p: "Certificados Blockchain", g: "M" },
      en: {
        s: "Blockchain Certificate",
        p: "Blockchain Certificates",
        g: undefined,
      },
      pt: { s: "Certificado Blockchain", p: "Certificados Blockchain", g: "M" },
    }[this.language];
  }

  get SignedEditionBlockchainCertSingular() {
    return this.SignedEditionBlockchainCertLabels.s;
  }

  get SignedEditionBlockchainCertPlural() {
    return this.SignedEditionBlockchainCertLabels.p;
  }

  get SignedEditionBlockchainCertGender() {
    return this.SignedEditionBlockchainCertLabels.g;
  }

  get groupSignedEditionBlockchainCertLabel() {
    return {
      es: { s: "Certificado Blockchain", p: "Certificados Blockchain", g: "M" },
      en: { s: "Blockchain Certificate", p: "Blockchain Certificates" },
      pt: { s: "Certificado Blockchain", p: "Certificados Blockchain", g: "M" },
    }[this.language].s;
  }

  SignedEditionBlockchainCertNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del certificado blockchain", g: "M" },
      en: { s: "Name", p: "Name of the blockchain certificate" },
      pt: { s: "Nome", p: "Nome do certificado blockchain", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionBlockchainCertNameLabel() {
    return this.SignedEditionBlockchainCertNameLabelByLang();
  }

  SignedEditionBlockchainCertContractNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nº de Contrato",
        p: "Número del contrato que certifica",
        g: "M",
      },
      en: { s: "Contract Number", p: "Number of the contract it certifies" },
      pt: {
        s: "Nº do Contrato",
        p: "Número do contrato que certifica",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionBlockchainCertContractNumberLabel() {
    return this.SignedEditionBlockchainCertContractNumberLabelByLang();
  }

  SignedEditionBlockchainCertFileLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Archivo",
        p: "PDF del certificado de anclaje en blockchain",
        g: "M",
      },
      en: { s: "File", p: "Blockchain anchoring certificate PDF" },
      pt: {
        s: "Arquivo",
        p: "PDF do certificado de ancoragem em blockchain",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionBlockchainCertFileLabel() {
    return this.SignedEditionBlockchainCertFileLabelByLang();
  }

  SignedEditionBlockchainCertConfirmedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Confirmado el",
        p: "Fecha y hora en que se confirmó el anclaje en blockchain",
        g: "F",
      },
      en: {
        s: "Confirmed At",
        p: "Date and time the blockchain anchoring was confirmed",
      },
      pt: {
        s: "Confirmado em",
        p: "Data e hora em que a ancoragem em blockchain foi confirmada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionBlockchainCertConfirmedAtLabel() {
    return this.SignedEditionBlockchainCertConfirmedAtLabelByLang();
  }

  // UserAttachmentEdition

  get UserAttachmentEditionLabels() {
    return {
      es: {
        s: "Documento Adjunto del Usuario",
        p: "Documentos Adjuntos del Usuario",
        g: "M",
      },
      en: {
        s: "User Attached Document",
        p: "User Attached Documents",
        g: undefined,
      },
      pt: {
        s: "Documento Anexo do Usuário",
        p: "Documentos Anexos do Usuário",
        g: "M",
      },
    }[this.language];
  }

  get UserAttachmentEditionSingular() {
    return this.UserAttachmentEditionLabels.s;
  }

  get UserAttachmentEditionPlural() {
    return this.UserAttachmentEditionLabels.p;
  }

  get UserAttachmentEditionGender() {
    return this.UserAttachmentEditionLabels.g;
  }

  get groupUserAttachmentEditionLabel() {
    return {
      es: {
        s: "Documento Adjunto del Usuario",
        p: "Documentos Adjuntos del Usuario",
        g: "M",
      },
      en: { s: "User Attached Document", p: "User Attached Documents" },
      pt: {
        s: "Documento Anexo do Usuário",
        p: "Documentos Anexos do Usuário",
        g: "M",
      },
    }[this.language].s;
  }

  UserAttachmentEditionNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del documento adjunto", g: "M" },
      en: { s: "Name", p: "Name of the attached document" },
      pt: { s: "Nome", p: "Nome do documento anexo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserAttachmentEditionNameLabel() {
    return this.UserAttachmentEditionNameLabelByLang();
  }

  get UserAttachmentEditionNameDescription() {
    return {
      es: { s: "Nombre o título descriptivo del archivo adjunto." },
      en: { s: "Descriptive name or title of the attached file." },
      pt: { s: "Nome ou título descritivo do arquivo anexo." },
    }[this.language].s;
  }

  UserAttachmentEditionFileLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Archivo", p: "Archivo adjunto", g: "M" },
      en: { s: "File", p: "Attached file" },
      pt: { s: "Arquivo", p: "Arquivo anexo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get UserAttachmentEditionFileLabel() {
    return this.UserAttachmentEditionFileLabelByLang();
  }

  get UserAttachmentEditionFileDescription() {
    return {
      es: {
        s: "Archivo digital cargado por el usuario. Tipos permitidos: PDF, Word, Excel, CSV, texto e imágenes. Tamaño máximo: 10 MB.",
      },
      en: {
        s: "Digital file uploaded by the user. Allowed types: PDF, Word, Excel, CSV, text and images. Max size: 10 MB.",
      },
      pt: {
        s: "Arquivo digital carregado pelo usuário. Tipos permitidos: PDF, Word, Excel, CSV, texto e imagens. Tamanho máximo: 10 MB.",
      },
    }[this.language].s;
  }

  UserAttachmentEditionNotesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Notas",
        p: "Notas adicionales sobre el archivo adjunto",
        g: "F",
      },
      en: { s: "Notes", p: "Additional notes about the attached file" },
      pt: { s: "Notas", p: "Notas adicionais sobre o arquivo anexo", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get UserAttachmentEditionNotesLabel() {
    return this.UserAttachmentEditionNotesLabelByLang();
  }

  get UserAttachmentEditionNotesDescription() {
    return {
      es: { s: "Información de contexto sobre este archivo (opcional)." },
      en: { s: "Context information about this file (optional)." },
      pt: { s: "Informações de contexto sobre este arquivo (opcional)." },
    }[this.language].s;
  }

  // Interpreter

  get InterpreterLabels() {
    return {
      es: { s: "Intérprete", p: "Intérpretes", g: "M" },
      en: { s: "Interpreter", p: "Interpreters", g: undefined },
      pt: { s: "Intérprete", p: "Intérpretes", g: "M" },
    }[this.language];
  }

  get InterpreterSingular() {
    return this.InterpreterLabels.s;
  }

  get InterpreterPlural() {
    return this.InterpreterLabels.p;
  }

  get InterpreterGender() {
    return this.InterpreterLabels.g;
  }

  get groupInterpreterLabel() {
    return {
      es: { s: "Intérprete", p: "Intérpretes", g: "M" },
      en: { s: "Interpreter", p: "Interpreters" },
      pt: { s: "Intérprete", p: "Intérpretes", g: "M" },
    }[this.language].s;
  }

  get groupInterpreterSystemDatesLabel() {
    return {
      es: { s: "Fechas de sistema", p: "F" },
      en: { s: "System dates" },
      pt: { s: "Datas de sistema", p: "F" },
    }[this.language].s;
  }

  InterpreterNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del intérprete", g: "M" },
      en: { s: "Name", p: "Name of the interpreter" },
      pt: { s: "Nome", p: "Nome do intérprete", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get InterpreterNameLabel() {
    return this.InterpreterNameLabelByLang();
  }

  get InterpreterNameDescription() {
    return {
      es: { s: "Nombre artístico del intérprete." },
      en: { s: "Stage name of the performer." },
      pt: { s: "Nome artístico do intérprete." },
    }[this.language].s;
  }

  InterpreterCreatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Creación", p: "F" },
      en: { s: "Creation Date" },
      pt: { s: "Data de Criação", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get InterpreterCreatedAt_Label() {
    return this.InterpreterCreatedAt_LabelByLang();
  }

  get InterpreterCreatedAt_Description() {
    return {
      es: { s: "Fecha y hora del registro en el sistema de esta edición." },
      en: {
        s: "Date and time when this edtition was registered in the system.",
      },
      pt: { s: "Data e hora do registro no sistema desta edição." },
    }[this.language].s;
  }

  InterpreterLastUpdatedAt_LabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Última Actualización", p: "F" },
      en: { s: "Last Updated" },
      pt: { s: "Última Atualização", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get InterpreterLastUpdatedAt_Label() {
    return this.InterpreterLastUpdatedAt_LabelByLang();
  }

  get InterpreterLastUpdatedAt_Description() {
    return {
      es: { s: "Fecha y hora de la última modificación de esta edición." },
      en: { s: "Date and time of the last modification of this edition." },
      pt: { s: "Data e hora da última modificação desta edição." },
    }[this.language].s;
  }

  // MusicalGenre

  get MusicalGenreLabels() {
    return {
      es: { s: "Género Musical", p: "Géneros Musicales", g: "M" },
      en: { s: "Musical Genre", p: "Musical Genres", g: undefined },
      pt: { s: "Gênero Musical", p: "Gêneros Musicais", g: "M" },
    }[this.language];
  }

  get MusicalGenreSingular() {
    return this.MusicalGenreLabels.s;
  }

  get MusicalGenrePlural() {
    return this.MusicalGenreLabels.p;
  }

  get MusicalGenreGender() {
    return this.MusicalGenreLabels.g;
  }

  get groupMusicalGenreLabel() {
    return {
      es: { s: "Género Musical", p: "Géneros Musicales", g: "M" },
      en: { s: "Musical Genre", p: "Musical Genres" },
      pt: { s: "Gênero Musical", p: "Gêneros Musicais", g: "M" },
    }[this.language].s;
  }

  MusicalGenreNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del género musical", g: "M" },
      en: { s: "Name", p: "Name of the musical genre" },
      pt: { s: "Nome", p: "Nome do gênero musical", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get MusicalGenreNameLabel() {
    return this.MusicalGenreNameLabelByLang();
  }

  get MusicalGenreNameDescription() {
    return {
      es: { s: "Género Musical." },
      en: { s: "Musical Genre." },
      pt: { s: "Gênero Musical." },
    }[this.language].s;
  }

  // ContractObservation

  get ContractObservationLabels() {
    return {
      es: {
        s: "Observación de Contrato",
        p: "Observaciones de Contrato",
        g: "F",
      },
      en: {
        s: "Contract Observation",
        p: "Contract Observations",
        g: undefined,
      },
      pt: { s: "Observação de Contrato", p: "Observações de Contrato", g: "F" },
    }[this.language];
  }

  get ContractObservationSingular() {
    return this.ContractObservationLabels.s;
  }

  get ContractObservationPlural() {
    return this.ContractObservationLabels.p;
  }

  get ContractObservationGender() {
    return this.ContractObservationLabels.g;
  }

  get groupContractObservationLabel() {
    return {
      es: {
        s: "Observación de Contrato",
        p: "Observaciones de Contrato",
        g: "F",
      },
      en: { s: "Contract Observation", p: "Contract Observations" },
      pt: { s: "Observação de Contrato", p: "Observações de Contrato", g: "F" },
    }[this.language].s;
  }

  get groupContractObservationFiltersLabel() {
    return {
      es: { s: "Filtro de Partes del Contrato", p: "M" },
      en: { s: "Contract Parties Filter" },
      pt: { s: "Filtro de Partes do Contrato", p: "M" },
    }[this.language].s;
  }

  get groupContractObservationFiltersDescription() {
    return {
      en: {
        s: "Use these fields to filter the observation to specific contracts based on the scope selected above.",
      },
      es: {
        s: "Con estos campos se filtra la observación a contratos específicos según el alcance seleccionado arriba.",
      },
      pt: {
        s: "Use estes campos para filtrar a observação para contratos específicos de acordo com o alcance selecionado acima.",
      },
    }[this.language].s;
  }

  ContractObservationTitleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Título", p: "Título de la observación", g: "M" },
      en: { s: "Title", p: "Observation title" },
      pt: { s: "Título", p: "Título da observação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get ContractObservationTitleLabel() {
    return this.ContractObservationTitleLabelByLang();
  }

  get ContractObservationTitleDescription() {
    return {
      es: { s: "Título identificativo de la Observación Inteligente." },
      en: { s: "Identifying title of the Smart Observation." },
      pt: { s: "Título identificativo da Observação Inteligente." },
    }[this.language].s;
  }

  ContractObservationObservationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Observación", p: "Texto de observación en HTML", g: "F" },
      en: { s: "Observation", p: "Observation text in HTML" },
      pt: { s: "Observação", p: "Texto de observação em HTML", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get ContractObservationObservationLabel() {
    return this.ContractObservationObservationLabelByLang();
  }

  get ContractObservationObservationDescription() {
    return {
      es: {
        s: "Contenido de la observación que se incluirá en los contratos aplicables.",
      },
      en: {
        s: "Content of the observation that will be included in applicable contracts.",
      },
      pt: {
        s: "Conteúdo da observação que será incluído nos contratos aplicáveis.",
      },
    }[this.language].s;
  }

  ContractObservationRequireAllLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Requiere Todos (Reparto)", p: "M" },
      en: { s: "Require All (Distribution)" },
      pt: { s: "Requer Todos (Distribuição)", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get ContractObservationRequireAllLabel() {
    return this.ContractObservationRequireAllLabelByLang();
  }

  get ContractObservationRequireAllDescription() {
    return {
      en: {
        s: "When enabled, the observation will only appear in contracts where ALL listed authors and/or publishers are present. When disabled (default), the observation appears if at least one matches.",
      },
      es: {
        s: "Cuando está activado, la observación solo aparecerá en contratos donde estén presentes TODOS los autores y/o editoriales listados. Cuando está desactivado (por defecto), la observación aparece si al menos uno coincide.",
      },
      pt: {
        s: "Quando ativado, a observação só aparecerá em contratos onde TODOS os autores e/ou editoras listados estejam presentes. Quando desativado (padrão), a observação aparece se pelo menos um coincidir.",
      },
    }[this.language].s;
  }

  ContractObservationAuthorsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Autores",
        p: "Autores a cuyos contratos se aplica esta observación",
        g: "M",
      },
      en: {
        s: "Authors",
        p: "Authors whose contracts will include this observation",
      },
      pt: {
        s: "Autores",
        p: "Autores cujos contratos incluirão esta observação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get ContractObservationAuthorsLabel() {
    return this.ContractObservationAuthorsLabelByLang();
  }

  get ContractObservationAuthorsDescription() {
    return {
      es: {
        s: "Lista de autores/compositores en cuyos contratos se incluirá automáticamente esta observación.",
      },
      en: {
        s: "List of authors/composers whose contracts will automatically include this observation.",
      },
      pt: {
        s: "Lista de autores/compositores cujos contratos incluirão automaticamente esta observação.",
      },
    }[this.language].s;
  }

  ContractObservationPublishersLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Editoriales",
        p: "Editoriales a cuyos contratos se aplica esta observación",
        g: "M",
      },
      en: {
        s: "Publishers",
        p: "Publishers whose contracts will include this observation",
      },
      pt: {
        s: "Editoras",
        p: "Editoras cujos contratos incluirão esta observação",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get ContractObservationPublishersLabel() {
    return this.ContractObservationPublishersLabelByLang();
  }

  get ContractObservationPublishersDescription() {
    return {
      es: {
        s: "Lista de editoriales musicales en cuyos contratos se incluirá automáticamente esta observación.",
      },
      en: {
        s: "List of music publishers whose contracts will automatically include this observation.",
      },
      pt: {
        s: "Lista de editoras musicais cujos contratos incluirão automaticamente esta observação.",
      },
    }[this.language].s;
  }

  // PaymentReceiptType

  get PaymentReceiptTypeLabels() {
    return {
      es: {
        s: "Tipo de Comprobante de Pago",
        p: "Tipos de Comprobante de Pago",
        g: "M",
      },
      en: { s: "Receipt Type", p: "Receipt Types", g: undefined },
      pt: { s: "Tipo de Comprovante", p: "Tipos de Comprovante", g: "M" },
    }[this.language];
  }

  get PaymentReceiptTypeSingular() {
    return this.PaymentReceiptTypeLabels.s;
  }

  get PaymentReceiptTypePlural() {
    return this.PaymentReceiptTypeLabels.p;
  }

  get PaymentReceiptTypeGender() {
    return this.PaymentReceiptTypeLabels.g;
  }

  get groupPaymentReceiptTypeLabel() {
    return {
      es: {
        s: "Tipo de Comprobante de Pago",
        p: "Tipos de Comprobante de Pago",
        g: "M",
      },
      en: { s: "Receipt Type", p: "Receipt Types" },
      pt: { s: "Tipo de Comprovante", p: "Tipos de Comprovante", g: "M" },
    }[this.language].s;
  }

  PaymentReceiptTypeNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Nombre", p: "Nombre del tipo de comprobante", g: "M" },
      en: { s: "Name", p: "Name of the receipt type" },
      pt: { s: "Nome", p: "Nome do tipo de comprovante", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptTypeNameLabel() {
    return this.PaymentReceiptTypeNameLabelByLang();
  }

  get PaymentReceiptTypeNameDescription() {
    return {
      es: {
        s: "Nombre del tipo de trámite (ej. DNDA Obra Publicada, DNDA Fonograma).",
      },
      en: {
        s: "Name of the process type (e.g., DNDA Published Work, DNDA Phonogram).",
      },
      pt: {
        s: "Nome do tipo de trâmite (ex. DNDA Obra Publicada, DNDA Fonograma).",
      },
    }[this.language].s;
  }

  PaymentReceiptTypePriceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Precio", p: "Precio del trámite", g: "M" },
      en: { s: "Price", p: "Process price" },
      pt: { s: "Preço", p: "Preço do trâmite", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptTypePriceLabel() {
    return this.PaymentReceiptTypePriceLabelByLang();
  }

  get PaymentReceiptTypePriceDescription() {
    return {
      es: { s: "Monto a pagar por este tipo de trámite." },
      en: { s: "Amount to pay for this type of process." },
      pt: { s: "Valor a pagar por este tipo de trâmite." },
    }[this.language].s;
  }

  PaymentReceiptTypeCurrencyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Moneda", p: "Moneda del precio", g: "F" },
      en: { s: "Currency", p: "Price currency" },
      pt: { s: "Moeda", p: "Moeda do preço", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptTypeCurrencyLabel() {
    return this.PaymentReceiptTypeCurrencyLabelByLang();
  }

  get PaymentReceiptTypeCurrencyDescription() {
    return {
      es: { s: "Moneda en que se expresa el precio del trámite." },
      en: { s: "Currency in which the process price is expressed." },
      pt: { s: "Moeda na qual o preço do trâmite é expresso." },
    }[this.language].s;
  }

  // PaymentReceiptUse

  get PaymentReceiptUseLabels() {
    return {
      es: { s: "Uso de Comprobante", p: "Usos de Comprobante", g: "M" },
      en: { s: "Receipt Use", p: "Receipt Uses", g: undefined },
      pt: { s: "Uso de Comprovante", p: "Usos de Comprovante", g: "M" },
    }[this.language];
  }

  get PaymentReceiptUseSingular() {
    return this.PaymentReceiptUseLabels.s;
  }

  get PaymentReceiptUsePlural() {
    return this.PaymentReceiptUseLabels.p;
  }

  get PaymentReceiptUseGender() {
    return this.PaymentReceiptUseLabels.g;
  }

  get groupPaymentReceiptUseLabel() {
    return {
      es: { s: "Uso de Comprobante", p: "Usos de Comprobante", g: "M" },
      en: { s: "Receipt Use", p: "Receipt Uses" },
      pt: { s: "Uso de Comprovante", p: "Usos de Comprovante", g: "M" },
    }[this.language].s;
  }

  PaymentReceiptUseDetailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Edición",
        p: "Edición del registro DNDA que consumió el uso",
        g: "M",
      },
      en: {
        s: "Edition",
        p: "Edition of the DNDA registration that consumed the use",
      },
      pt: {
        s: "Edição",
        p: "Edição do registro DNDA que consumiu o uso",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptUseDetailLabel() {
    return this.PaymentReceiptUseDetailLabelByLang();
  }

  PaymentReceiptUseUsedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Uso", p: "Fecha y hora del uso", g: "F" },
      en: { s: "Used At", p: "Date and time of the use" },
      pt: { s: "Data de Uso", p: "Data e hora do uso", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptUseUsedAtLabel() {
    return this.PaymentReceiptUseUsedAtLabelByLang();
  }

  PaymentReceiptUseSignedEditionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Edición Firmada",
        p: "Edición firmada que aplicó el comprobante",
        g: "F",
      },
      en: { s: "Signed Edition", p: "Signed edition that applied the receipt" },
      pt: {
        s: "Edição Assinada",
        p: "Edição assinada que aplicou o comprovante",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptUseSignedEditionLabel() {
    return this.PaymentReceiptUseSignedEditionLabelByLang();
  }

  // PaymentReceipt

  get PaymentReceiptLabels() {
    return {
      es: { s: "Comprobante de Pago", p: "Comprobantes de Pago", g: "M" },
      en: { s: "Payment Receipt", p: "Payment Receipts", g: undefined },
      pt: {
        s: "Comprovante de Pagamento",
        p: "Comprovantes de Pagamento",
        g: "M",
      },
    }[this.language];
  }

  get PaymentReceiptSingular() {
    return this.PaymentReceiptLabels.s;
  }

  get PaymentReceiptPlural() {
    return this.PaymentReceiptLabels.p;
  }

  get PaymentReceiptGender() {
    return this.PaymentReceiptLabels.g;
  }

  get groupPaymentReceiptLabel() {
    return {
      es: { s: "Comprobante de Pago", p: "Comprobantes de Pago", g: "M" },
      en: { s: "Payment Receipt", p: "Payment Receipts" },
      pt: {
        s: "Comprovante de Pagamento",
        p: "Comprovantes de Pagamento",
        g: "M",
      },
    }[this.language].s;
  }

  PaymentReceiptPaymentReceiptLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Comprobante de Pago",
        p: "Archivo del comprobante de pago",
        g: "M",
      },
      en: { s: "Payment Receipt", p: "Payment receipt file" },
      pt: {
        s: "Comprovante de Pagamento",
        p: "Arquivo do comprovante de pagamento",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptPaymentReceiptLabel() {
    return this.PaymentReceiptPaymentReceiptLabelByLang();
  }

  get PaymentReceiptPaymentReceiptDescription() {
    return {
      es: { s: "Archivo del comprobante de pago." },
      en: { s: "Payment receipt file." },
      pt: { s: "Arquivo do comprovante de pagamento." },
    }[this.language].s;
  }

  PaymentReceiptCrcLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "CRC", p: "Código único del comprobante", g: "M" },
      en: { s: "CRC", p: "Unique receipt code" },
      pt: { s: "CRC", p: "Código único do comprovante", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptCrcLabel() {
    return this.PaymentReceiptCrcLabelByLang();
  }

  get PaymentReceiptCrcDescription() {
    return {
      es: {
        s: "Código de verificación único asignado automáticamente al comprobante.",
      },
      en: {
        s: "Unique verification code automatically assigned to the receipt.",
      },
      pt: {
        s: "Código de verificação único atribuído automaticamente ao comprovante.",
      },
    }[this.language].s;
  }

  PaymentReceiptTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo", p: "Tipo de comprobante", g: "M" },
      en: { s: "Type", p: "Receipt type" },
      pt: { s: "Tipo", p: "Tipo de comprovante", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptTypeLabel() {
    return this.PaymentReceiptTypeLabelByLang();
  }

  get PaymentReceiptTypeDescription() {
    return {
      es: {
        s: "Tipo de trámite al que corresponde este comprobante. No puede modificarse después de la creación: si es incorrecto, eliminá el comprobante y cargalo de nuevo.",
      },
      en: {
        s: "Type of process this receipt corresponds to. It cannot be changed after creation: if it is wrong, delete the receipt and upload it again.",
      },
      pt: {
        s: "Tipo de trâmite ao qual este comprovante corresponde. Não pode ser modificado após a criação: se estiver incorreto, exclua o comprovante e carregue-o novamente.",
      },
    }[this.language].s;
  }

  PaymentReceiptAmountLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Importe", p: "Monto del comprobante", g: "M" },
      en: { s: "Amount", p: "Receipt amount" },
      pt: { s: "Valor", p: "Valor do comprovante", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptAmountLabel() {
    return this.PaymentReceiptAmountLabelByLang();
  }

  get PaymentReceiptAmountDescription() {
    return {
      es: {
        s: "Monto del comprobante de pago. Debe cubrir al menos un uso: la cantidad de usos posibles es la división entera del importe por el precio vigente del tipo de trámite. Si el importe no es un múltiplo exacto, el excedente no se aplica. No puede modificarse después de la creación.",
      },
      en: {
        s: "Payment receipt amount. It must cover at least one use: the number of possible uses is the integer division of the amount by the current price of the process type. If the amount is not an exact multiple, the surplus is not applied. It cannot be changed after creation.",
      },
      pt: {
        s: "Valor do comprovante de pagamento. Deve cobrir pelo menos um uso: a quantidade de usos possíveis é a divisão inteira do valor pelo preço vigente do tipo de trâmite. Se o valor não for um múltiplo exato, o excedente não é aplicado. Não pode ser modificado após a criação.",
      },
    }[this.language].s;
  }

  PaymentReceiptIssueDateTimeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha y hora de Emisión",
        p: "Fecha y hora de emisión del comprobante",
        g: "F",
      },
      en: {
        s: "Issue Date/Time",
        p: "Date and time when the receipt was issued",
      },
      pt: {
        s: "Data de Emissão",
        p: "Data e hora de emissão do comprovante",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptIssueDateTimeLabel() {
    return this.PaymentReceiptIssueDateTimeLabelByLang();
  }

  get PaymentReceiptIssueDateTimeDescription() {
    return {
      es: { s: "Fecha y hora en que se emitió el comprobante de pago." },
      en: { s: "Date and time when the payment receipt was issued." },
      pt: { s: "Data e hora em que o comprovante de pagamento foi emitido." },
    }[this.language].s;
  }

  PaymentReceiptStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "Estado del comprobante", g: "M" },
      en: { s: "Status", p: "Receipt status" },
      pt: { s: "Estado", p: "Estado do comprovante", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptStatusLabel() {
    return this.PaymentReceiptStatusLabelByLang();
  }

  get PaymentReceiptStatusDescription() {
    return {
      es: {
        s: "Estado actual del comprobante: Disponible mientras queden usos restantes; Utilizado cuando el contador llega a 0.",
      },
      en: {
        s: "Current receipt status: Available while there are remaining uses; Used when the counter reaches 0.",
      },
      pt: {
        s: "Estado atual do comprovante: Disponível enquanto restarem usos; Utilizado quando o contador chega a 0.",
      },
    }[this.language].s;
  }

  PaymentReceiptTotalUsesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Cantidad de Usos Posibles",
        p: "Cantidad total de usos que cubre el comprobante",
        g: "F",
      },
      en: { s: "Total Uses", p: "Total number of uses the receipt covers" },
      pt: {
        s: "Quantidade de Usos Possíveis",
        p: "Quantidade total de usos que o comprovante cobre",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptTotalUsesLabel() {
    return this.PaymentReceiptTotalUsesLabelByLang();
  }

  get PaymentReceiptTotalUsesDescription() {
    return {
      es: {
        s: "Cantidad de registros DNDA que cubre el importe del comprobante. La calcula el sistema al crear el comprobante: división entera del importe por el precio vigente del tipo de trámite.",
      },
      en: {
        s: "Number of DNDA registrations covered by the receipt amount. Computed by the system when the receipt is created: integer division of the amount by the current price of the process type.",
      },
      pt: {
        s: "Quantidade de registros DNDA que o valor do comprovante cobre. Calculada pelo sistema ao criar o comprovante: divisão inteira do valor pelo preço vigente do tipo de trâmite.",
      },
    }[this.language].s;
  }

  PaymentReceiptRemainingUsesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Usos Restantes",
        p: "Cantidad de usos restantes del comprobante",
        g: "M",
      },
      en: { s: "Remaining Uses", p: "Number of remaining uses of the receipt" },
      pt: {
        s: "Usos Restantes",
        p: "Quantidade de usos restantes do comprovante",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptRemainingUsesLabel() {
    return this.PaymentReceiptRemainingUsesLabelByLang();
  }

  get PaymentReceiptRemainingUsesDescription() {
    return {
      es: {
        s: "Contador descendente mantenido por el sistema: cuántos registros DNDA puede pagar todavía este comprobante. Comienza en la Cantidad de Usos Posibles; cada registro descuenta un uso y al llegar a 0 el comprobante pasa a Utilizado.",
      },
      en: {
        s: "Descending counter maintained by the system: how many DNDA registrations this receipt can still pay for. It starts at the Total Uses; each registration consumes one use and when it reaches 0 the receipt becomes Used.",
      },
      pt: {
        s: "Contador decrescente mantido pelo sistema: quantos registros DNDA este comprovante ainda pode pagar. Começa na Quantidade de Usos Possíveis; cada registro desconta um uso e ao chegar a 0 o comprovante passa a Utilizado.",
      },
    }[this.language].s;
  }

  PaymentReceiptUsedDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Fecha de Uso",
        p: "Fecha y hora del último uso del comprobante",
        g: "F",
      },
      en: { s: "Used Date", p: "Date and time of the last use of the receipt" },
      pt: {
        s: "Data de Uso",
        p: "Data e hora do último uso do comprovante",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptUsedDateLabel() {
    return this.PaymentReceiptUsedDateLabelByLang();
  }

  get PaymentReceiptUsedDateDescription() {
    return {
      es: {
        s: "Fecha y hora del último uso del comprobante en un trámite de edición. El detalle de todos los usos está en la lista de Usos.",
      },
      en: {
        s: "Date and time of the last use of the receipt in an edition process. The detail of every use is in the Uses list.",
      },
      pt: {
        s: "Data e hora do último uso do comprovante em um trâmite de edição. O detalhe de todos os usos está na lista de Usos.",
      },
    }[this.language].s;
  }

  PaymentReceiptSignedEditionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Edición Firmada",
        p: "Última edición firmada que usó el comprobante",
        g: "F",
      },
      en: {
        s: "Signed Edition",
        p: "Last signed edition that used the receipt",
      },
      pt: {
        s: "Edição Assinada",
        p: "Última edição assinada que usou o comprovante",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptSignedEditionLabel() {
    return this.PaymentReceiptSignedEditionLabelByLang();
  }

  get PaymentReceiptSignedEditionDescription() {
    return {
      es: {
        s: "Última edición firmada en la que se aplicó este comprobante (registro DNDA). El historial completo está en la lista de Usos.",
      },
      en: {
        s: "Last signed edition to which this receipt was applied (DNDA registration). The full history is in the Uses list.",
      },
      pt: {
        s: "Última edição assinada na qual este comprovante foi aplicado (registro DNDA). O histórico completo está na lista de Usos.",
      },
    }[this.language].s;
  }

  PaymentReceiptUsesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Detalles sobre el uso",
        p: "Registros DNDA pagados con este comprobante",
        g: "M",
      },
      en: {
        s: "Details regarding the use",
        p: "DNDA registrations paid with this receipt",
      },
      pt: {
        s: "Detalhes sobre a utilização",
        p: "Registros DNDA pagos com este comprovante",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get PaymentReceiptUsesLabel() {
    return this.PaymentReceiptUsesLabelByLang();
  }

  get PaymentReceiptUsesDescription() {
    return {
      es: {
        s: "Historial de usos del comprobante: una entrada por cada registro DNDA que lo consumió. Lo escribe el sistema.",
      },
      en: {
        s: "Receipt usage history: one entry per DNDA registration that consumed it. Written by the system.",
      },
      pt: {
        s: "Histórico de usos do comprovante: uma entrada por cada registro DNDA que o consumiu. Escrito pelo sistema.",
      },
    }[this.language].s;
  }

  // TadManagerConfig

  get TadManagerConfigLabels() {
    return {
      es: { s: "Gestor TAD", p: "Gestores TAD", g: "M" },
      en: { s: "TAD Manager", p: "TAD Managers", g: undefined },
      pt: { s: "Gestor TAD", p: "Gestores TAD", g: "M" },
    }[this.language];
  }

  get TadManagerConfigSingular() {
    return this.TadManagerConfigLabels.s;
  }

  get TadManagerConfigPlural() {
    return this.TadManagerConfigLabels.p;
  }

  get TadManagerConfigGender() {
    return this.TadManagerConfigLabels.g;
  }

  get groupTadManagerConfigLabel() {
    return {
      es: { s: "Gestor TAD", p: "Gestores TAD", g: "M" },
      en: { s: "TAD Manager", p: "TAD Managers" },
      pt: { s: "Gestor TAD", p: "Gestores TAD", g: "M" },
    }[this.language].s;
  }

  TadManagerConfigAuthProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor de Identidad", p: "M" },
      en: { s: "Identity Provider" },
      pt: { s: "Provedor de Identidade", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigAuthProviderLabel() {
    return this.TadManagerConfigAuthProviderLabelByLang();
  }

  get TadManagerConfigAuthProviderDescription() {
    return {
      es: {
        s: "Servicio con el que el bot se autentica en TAD. Cargá la credencial que corresponda al proveedor elegido.",
      },
      en: {
        s: "Service the bot uses to authenticate with TAD. Fill in the credential matching the chosen provider.",
      },
      pt: {
        s: "Serviço com o qual o bot se autentica no TAD. Preencha a credencial correspondente ao provedor escolhido.",
      },
    }[this.language].s;
  }

  TadManagerConfigIdCardTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Documento", p: "M" },
      en: { s: "ID Type" },
      pt: { s: "Tipo de Documento", p: "M" },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigIdCardTypeLabel() {
    return this.TadManagerConfigIdCardTypeLabelByLang();
  }

  get TadManagerConfigIdCardTypeDescription() {
    return {
      es: { s: "Tipo de documento de identificación del gestor TAD." },
      en: { s: "ID document type for the TAD manager." },
      pt: { s: "Tipo de documento de identificação do gestor TAD." },
    }[this.language].s;
  }

  TadManagerConfigIdCardLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "DNI",
        p: "Documento Nacional de Identidad del gestor TAD",
        g: "M",
      },
      en: { s: "National ID", p: "TAD manager's National Identity Document" },
      pt: {
        s: "DNI",
        p: "Documento Nacional de Identidade do gestor TAD",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigIdCardLabel() {
    return this.TadManagerConfigIdCardLabelByLang();
  }

  get TadManagerConfigIdCardDescription() {
    return {
      es: { s: "Número de documento nacional de identidad del gestor TAD." },
      en: { s: "TAD manager's national identity document number." },
      pt: { s: "Número do documento nacional de identidade do gestor TAD." },
    }[this.language].s;
  }

  TadManagerConfigProcedureNumberLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Número de Trámite",
        p: "Número de Trámite del gestor TAD",
        g: "M",
      },
      en: { s: "Procedure Number", p: "TAD manager's Procedure Number" },
      pt: {
        s: "Número do Trâmite",
        p: "Número do Trâmite do gestor TAD",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigProcedureNumberLabel() {
    return this.TadManagerConfigProcedureNumberLabelByLang();
  }

  get TadManagerConfigProcedureNumberDescription() {
    return {
      es: {
        s: "Número de trámite AFIP asociado al documento de identidad del gestor TAD, dato sensible enmascarado.",
      },
      en: {
        s: "AFIP procedure number associated with the TAD manager's identity document, masked sensitive data.",
      },
      pt: {
        s: "Número de trâmite AFIP associado ao documento de identidade do gestor TAD, dado sensível mascarado.",
      },
    }[this.language].s;
  }

  TadManagerConfigMiArgentinaPasswordLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Clave Mi Argentina",
        p: "Clave de Mi Argentina del gestor TAD",
        g: "M",
      },
      en: {
        s: "Mi Argentina Password",
        p: "TAD manager's Mi Argentina password",
      },
      pt: {
        s: "Senha Mi Argentina",
        p: "Senha do Mi Argentina do gestor TAD",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigMiArgentinaPasswordLabel() {
    return this.TadManagerConfigMiArgentinaPasswordLabelByLang();
  }

  get TadManagerConfigMiArgentinaPasswordDescription() {
    return {
      es: {
        s: "Contraseña de Mi Argentina del gestor TAD, dato sensible enmascarado. Requiere que el Tipo de Documento sea CUIT/CUIL.",
      },
      en: {
        s: "TAD manager's Mi Argentina password, masked sensitive data. Requires the ID Type to be CUIT/CUIL.",
      },
      pt: {
        s: "Senha do Mi Argentina do gestor TAD, dado sensível mascarado. Requer que o Tipo de Documento seja CUIT/CUIL.",
      },
    }[this.language].s;
  }

  TadManagerConfigArcaPasswordLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Clave Fiscal ARCA",
        p: "Clave fiscal ARCA del gestor TAD",
        g: "M",
      },
      en: { s: "ARCA Tax Key", p: "TAD manager's ARCA tax key" },
      pt: {
        s: "Chave Fiscal ARCA",
        p: "Chave fiscal ARCA do gestor TAD",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigArcaPasswordLabel() {
    return this.TadManagerConfigArcaPasswordLabelByLang();
  }

  get TadManagerConfigArcaPasswordDescription() {
    return {
      es: {
        s: "Clave fiscal de ARCA del gestor TAD, dato sensible enmascarado. El ingreso por ARCA todavía no está automatizado.",
      },
      en: {
        s: "TAD manager's ARCA tax key, masked sensitive data. ARCA login is not automated yet.",
      },
      pt: {
        s: "Chave fiscal da ARCA do gestor TAD, dado sensível mascarado. O acesso via ARCA ainda não está automatizado.",
      },
    }[this.language].s;
  }

  TadManagerConfigAnsesPasswordLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Clave ANSES",
        p: "Clave de la Seguridad Social del gestor TAD",
        g: "M",
      },
      en: { s: "ANSES Key", p: "TAD manager's social security key" },
      pt: {
        s: "Chave ANSES",
        p: "Chave da Segurança Social do gestor TAD",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigAnsesPasswordLabel() {
    return this.TadManagerConfigAnsesPasswordLabelByLang();
  }

  get TadManagerConfigAnsesPasswordDescription() {
    return {
      es: {
        s: "Clave de la Seguridad Social del gestor TAD, dato sensible enmascarado. El ingreso por ANSES todavía no está automatizado.",
      },
      en: {
        s: "TAD manager's social security key, masked sensitive data. ANSES login is not automated yet.",
      },
      pt: {
        s: "Chave da Segurança Social do gestor TAD, dado sensível mascarado. O acesso via ANSES ainda não está automatizado.",
      },
    }[this.language].s;
  }

  TadManagerConfigGenderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Género", p: "Género del gestor TAD", g: "M" },
      en: { s: "Gender", p: "TAD manager's gender" },
      pt: { s: "Gênero", p: "Gênero do gestor TAD", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TadManagerConfigGenderLabel() {
    return this.TadManagerConfigGenderLabelByLang();
  }

  get TadManagerConfigGenderDescription() {
    return {
      es: { s: "Género del gestor TAD, requerido para el formulario de TAD." },
      en: { s: "TAD manager's gender, required for the TAD form." },
      pt: { s: "Gênero do gestor TAD, requerido para o formulário de TAD." },
    }[this.language].s;
  }

  // DocumentGenerationParams

  get DocumentGenerationParamsLabels() {
    return {
      es: {
        s: "Parámetros para Generación de Documentación",
        p: "Parámetros para Generación de Documentación",
        g: "M",
      },
      en: {
        s: "Documentation Generation Parameters",
        p: "Documentation Generation Parameters",
        g: undefined,
      },
      pt: {
        s: "Parâmetros para Geração de Documentação",
        p: "Parâmetros para Geração de Documentação",
        g: "M",
      },
    }[this.language];
  }

  get DocumentGenerationParamsSingular() {
    return this.DocumentGenerationParamsLabels.s;
  }

  get DocumentGenerationParamsPlural() {
    return this.DocumentGenerationParamsLabels.p;
  }

  get DocumentGenerationParamsGender() {
    return this.DocumentGenerationParamsLabels.g;
  }

  get groupDocumentGenerationParamsLabel() {
    return {
      es: {
        s: "Parámetros para Generación de Documentación",
        p: "Parámetros para Generación de Documentación",
        g: "M",
      },
      en: {
        s: "Documentation Generation Parameters",
        p: "Documentation Generation Parameters",
      },
      pt: {
        s: "Parâmetros para Geração de Documentação",
        p: "Parâmetros para Geração de Documentação",
        g: "M",
      },
    }[this.language].s;
  }

  DocumentGenerationParamsContractEffectiveDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Inicio de Vigencia del Contrato", p: "F" },
      en: { s: "Contract Effective Date" },
      pt: { s: "Data de Início de Vigência do Contrato", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get DocumentGenerationParamsContractEffectiveDateLabel() {
    return this.DocumentGenerationParamsContractEffectiveDateLabelByLang();
  }

  get DocumentGenerationParamsContractEffectiveDateDescription() {
    return {
      es: {
        s: "La fecha a partir de la cual los términos del contrato generado serán efectivos.",
      },
      en: {
        s: "The date from which the terms of the generated contract will be effective.",
      },
      pt: {
        s: "A data a partir da qual os termos do contrato gerado serão efetivos.",
      },
    }[this.language].s;
  }

  DocumentGenerationParamsContractEndDateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha de Fin de Vigencia del Contrato", p: "F" },
      en: { s: "Contract End Date" },
      pt: { s: "Data de Fim de Vigência do Contrato", p: "F" },
    }[lang ? lang : this.language].s;
  }

  get DocumentGenerationParamsContractEndDateLabel() {
    return this.DocumentGenerationParamsContractEndDateLabelByLang();
  }

  get DocumentGenerationParamsContractEndDateDescription() {
    return {
      es: {
        s: "La fecha en que los términos del contrato generado finalizarán. Si se deja en blanco, puede no tener fin o ser indefinido.",
      },
      en: {
        s: "The date on which the terms of the generated contract will end. If left blank, it may be open-ended or indefinite.",
      },
      pt: {
        s: "A data em que os termos do contrato gerado terminarão. Se deixado em branco, pode ser por tempo indeterminado.",
      },
    }[this.language].s;
  }

  // EditionDocumentationWorkflowState

  get EditionDocumentationWorkflowStateLabels() {
    return {
      es: {
        s: "Estado del Ciclo de Vida de Documentación",
        p: "Estados del Ciclo de Vida de Documentación",
        g: "M",
      },
      en: {
        s: "Documentation Lifecycle State",
        p: "Documentation Lifecycle States",
        g: undefined,
      },
      pt: {
        s: "Estado do Ciclo de Vida de Documentação",
        p: "Estados do Ciclo de Vida de Documentação",
        g: "M",
      },
    }[this.language];
  }

  get EditionDocumentationWorkflowStateSingular() {
    return this.EditionDocumentationWorkflowStateLabels.s;
  }

  get EditionDocumentationWorkflowStatePlural() {
    return this.EditionDocumentationWorkflowStateLabels.p;
  }

  get EditionDocumentationWorkflowStateGender() {
    return this.EditionDocumentationWorkflowStateLabels.g;
  }

  get groupEditionDocumentationWorkflowStateLabel() {
    return {
      es: {
        s: "Estado del Ciclo de Vida de Documentación",
        p: "Estados del Ciclo de Vida de Documentación",
        g: "M",
      },
      en: {
        s: "Documentation Lifecycle State",
        p: "Documentation Lifecycle States",
      },
      pt: {
        s: "Estado do Ciclo de Vida de Documentação",
        p: "Estados do Ciclo de Vida de Documentação",
        g: "M",
      },
    }[this.language].s;
  }

  EditionDocumentationWorkflowStateNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nombre del Estado",
        p: "Nombre descriptivo del estado en el workflow (ej. Borradores Creados, Enviado para Firmas)",
        g: "M",
      },
      en: {
        s: "State Name",
        p: "Descriptive name of the state in the workflow (e.g. Drafts Created, Sent for Signatures)",
      },
      pt: {
        s: "Nome do Estado",
        p: "Nome descritivo do estado no workflow (ex. Rascunhos Criados, Enviado para Assinaturas)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EditionDocumentationWorkflowStateNameLabel() {
    return this.EditionDocumentationWorkflowStateNameLabelByLang();
  }

  // SignedEditionDndaWorkflowState

  get SignedEditionDndaWorkflowStateLabels() {
    return {
      es: {
        s: "Estado de Registro DNDA",
        p: "Estados de Registro DNDA",
        g: "M",
      },
      en: {
        s: "DNDA Registration State",
        p: "DNDA Registration States",
        g: undefined,
      },
      pt: {
        s: "Estado de Registro DNDA",
        p: "Estados de Registro DNDA",
        g: "M",
      },
    }[this.language];
  }

  get SignedEditionDndaWorkflowStateSingular() {
    return this.SignedEditionDndaWorkflowStateLabels.s;
  }

  get SignedEditionDndaWorkflowStatePlural() {
    return this.SignedEditionDndaWorkflowStateLabels.p;
  }

  get SignedEditionDndaWorkflowStateGender() {
    return this.SignedEditionDndaWorkflowStateLabels.g;
  }

  get groupSignedEditionDndaWorkflowStateLabel() {
    return {
      es: {
        s: "Estado de Registro DNDA",
        p: "Estados de Registro DNDA",
        g: "M",
      },
      en: { s: "DNDA Registration State", p: "DNDA Registration States" },
      pt: {
        s: "Estado de Registro DNDA",
        p: "Estados de Registro DNDA",
        g: "M",
      },
    }[this.language].s;
  }

  SignedEditionDndaWorkflowStateNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nombre del Estado",
        p: "Nombre descriptivo del estado de registro DNDA",
        g: "M",
      },
      en: {
        s: "State Name",
        p: "Descriptive name of the DNDA registration state",
      },
      pt: {
        s: "Nome do Estado",
        p: "Nome descritivo do estado de registro DNDA",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionDndaWorkflowStateNameLabel() {
    return this.SignedEditionDndaWorkflowStateNameLabelByLang();
  }

  // SignedEditionSadaicWorkflowState

  get SignedEditionSadaicWorkflowStateLabels() {
    return {
      es: {
        s: "Estado de Registro SADAIC",
        p: "Estados de Registro SADAIC",
        g: "M",
      },
      en: {
        s: "SADAIC Registration State",
        p: "SADAIC Registration States",
        g: undefined,
      },
      pt: {
        s: "Estado de Registro SADAIC",
        p: "Estados de Registro SADAIC",
        g: "M",
      },
    }[this.language];
  }

  get SignedEditionSadaicWorkflowStateSingular() {
    return this.SignedEditionSadaicWorkflowStateLabels.s;
  }

  get SignedEditionSadaicWorkflowStatePlural() {
    return this.SignedEditionSadaicWorkflowStateLabels.p;
  }

  get SignedEditionSadaicWorkflowStateGender() {
    return this.SignedEditionSadaicWorkflowStateLabels.g;
  }

  get groupSignedEditionSadaicWorkflowStateLabel() {
    return {
      es: {
        s: "Estado de Registro SADAIC",
        p: "Estados de Registro SADAIC",
        g: "M",
      },
      en: { s: "SADAIC Registration State", p: "SADAIC Registration States" },
      pt: {
        s: "Estado de Registro SADAIC",
        p: "Estados de Registro SADAIC",
        g: "M",
      },
    }[this.language].s;
  }

  SignedEditionSadaicWorkflowStateNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Nombre del Estado",
        p: "Nombre descriptivo del estado de registro SADAIC",
        g: "M",
      },
      en: {
        s: "State Name",
        p: "Descriptive name of the SADAIC registration state",
      },
      pt: {
        s: "Nome do Estado",
        p: "Nome descritivo do estado de registro SADAIC",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get SignedEditionSadaicWorkflowStateNameLabel() {
    return this.SignedEditionSadaicWorkflowStateNameLabelByLang();
  }

  // LlmProvider

  get LlmProviderLabels() {
    return {
      es: { s: "Proveedor LLM", p: "Proveedores LLM", g: "M" },
      en: { s: "LLM Provider", p: "LLM Providers", g: undefined },
      pt: { s: "Provedor LLM", p: "Provedores LLM", g: "M" },
    }[this.language];
  }

  get LlmProviderSingular() {
    return this.LlmProviderLabels.s;
  }

  get LlmProviderPlural() {
    return this.LlmProviderLabels.p;
  }

  get LlmProviderGender() {
    return this.LlmProviderLabels.g;
  }

  get groupLlmProviderLabel() {
    return {
      es: { s: "Proveedor LLM", p: "Proveedores LLM", g: "M" },
      en: { s: "LLM Provider", p: "LLM Providers" },
      pt: { s: "Provedor LLM", p: "Provedores LLM", g: "M" },
    }[this.language].s;
  }

  LlmProviderNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del proveedor", g: "M" },
      en: { s: "Name", p: "Provider name" },
      pt: { s: "Nome", p: "Nome do provedor", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get LlmProviderNameLabel() {
    return this.LlmProviderNameLabelByLang();
  }

  get LlmProviderNameDescription() {
    return {
      es: { s: "Nombre comercial del proveedor de LLM." },
      en: { s: "Commercial name of the LLM provider." },
      pt: { s: "Nome comercial do provedor de LLM." },
    }[this.language].s;
  }

  LlmProviderCodeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Código",
        p: "Código identificador (openai, anthropic, groq, google, ollama)",
        g: "M",
      },
      en: {
        s: "Code",
        p: "Identifier code (openai, anthropic, groq, google, ollama)",
      },
      pt: {
        s: "Código",
        p: "Código identificador (openai, anthropic, groq, google, ollama)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get LlmProviderCodeLabel() {
    return this.LlmProviderCodeLabelByLang();
  }

  get LlmProviderCodeDescription() {
    return {
      es: {
        s: "Código único que identifica el proveedor en el sistema (openai, anthropic, groq, google, ollama).",
      },
      en: {
        s: "Unique code that identifies the provider in the system (openai, anthropic, groq, google, ollama).",
      },
      pt: {
        s: "Código único que identifica o provedor no sistema (openai, anthropic, groq, google, ollama).",
      },
    }[this.language].s;
  }

  LlmProviderRequiresApiKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Requiere API Key",
        p: "Si el proveedor requiere una API key",
        g: "F",
      },
      en: {
        s: "Requires API Key",
        p: "Whether the provider requires an API key",
      },
      pt: {
        s: "Requer API Key",
        p: "Se o provedor requer uma API key",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get LlmProviderRequiresApiKeyLabel() {
    return this.LlmProviderRequiresApiKeyLabelByLang();
  }

  get LlmProviderRequiresApiKeyDescription() {
    return {
      es: {
        s: "Indica si el proveedor necesita una clave de API para autenticación.",
      },
      en: {
        s: "Indicates whether the provider needs an API key for authentication.",
      },
      pt: {
        s: "Indica se o provedor precisa de uma chave de API para autenticação.",
      },
    }[this.language].s;
  }

  LlmProviderRequiresBaseUrlLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Requiere URL Base",
        p: "Si el proveedor requiere una URL base personalizada",
        g: "F",
      },
      en: {
        s: "Requires Base URL",
        p: "Whether the provider requires a custom base URL",
      },
      pt: {
        s: "Requer URL Base",
        p: "Se o provedor requer uma URL base personalizada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get LlmProviderRequiresBaseUrlLabel() {
    return this.LlmProviderRequiresBaseUrlLabelByLang();
  }

  get LlmProviderRequiresBaseUrlDescription() {
    return {
      es: {
        s: "Indica si el proveedor necesita una URL base personalizada (ej. Ollama local).",
      },
      en: {
        s: "Indicates whether the provider needs a custom base URL (e.g., local Ollama).",
      },
      pt: {
        s: "Indica se o provedor precisa de uma URL base personalizada (ex. Ollama local).",
      },
    }[this.language].s;
  }

  LlmProviderDefaultModelsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Modelos Disponibles",
        p: "Lista de modelos disponibles para este proveedor",
        g: "M",
      },
      en: {
        s: "Available Models",
        p: "List of available models for this provider",
      },
      pt: {
        s: "Modelos Disponíveis",
        p: "Lista de modelos disponíveis para este provedor",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get LlmProviderDefaultModelsLabel() {
    return this.LlmProviderDefaultModelsLabelByLang();
  }

  get LlmProviderDefaultModelsDescription() {
    return {
      es: { s: "Lista de modelos predeterminados disponibles para selección." },
      en: { s: "List of default models available for selection." },
      pt: { s: "Lista de modelos padrão disponíveis para seleção." },
    }[this.language].s;
  }

  LlmProviderLogoUrlLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "URL del Logo", p: "URL del logo del proveedor", g: "F" },
      en: { s: "Logo URL", p: "Provider logo URL" },
      pt: { s: "URL do Logo", p: "URL do logo do provedor", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get LlmProviderLogoUrlLabel() {
    return this.LlmProviderLogoUrlLabelByLang();
  }

  get LlmProviderLogoUrlDescription() {
    return {
      es: {
        s: "URL de la imagen del logo del proveedor para mostrar en la interfaz.",
      },
      en: {
        s: "URL of the provider's logo image for display in the interface.",
      },
      pt: {
        s: "URL da imagem do logo do provedor para exibição na interface.",
      },
    }[this.language].s;
  }

  // TenantLlmConfig

  get TenantLlmConfigLabels() {
    return {
      es: { s: "Configuración LLM", p: "Configuraciones LLM", g: "F" },
      en: { s: "LLM Configuration", p: "LLM Configurations", g: undefined },
      pt: { s: "Configuração LLM", p: "Configurações LLM", g: "F" },
    }[this.language];
  }

  get TenantLlmConfigSingular() {
    return this.TenantLlmConfigLabels.s;
  }

  get TenantLlmConfigPlural() {
    return this.TenantLlmConfigLabels.p;
  }

  get TenantLlmConfigGender() {
    return this.TenantLlmConfigLabels.g;
  }

  get groupTenantLlmConfigLabel() {
    return {
      es: { s: "Configuración LLM", p: "Configuraciones LLM", g: "F" },
      en: { s: "LLM Configuration", p: "LLM Configurations" },
      pt: { s: "Configuração LLM", p: "Configurações LLM", g: "F" },
    }[this.language].s;
  }

  TenantLlmConfigProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor", p: "Proveedor LLM seleccionado", g: "M" },
      en: { s: "Provider", p: "Selected LLM provider" },
      pt: { s: "Provedor", p: "Provedor LLM selecionado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigProviderLabel() {
    return this.TenantLlmConfigProviderLabelByLang();
  }

  get TenantLlmConfigProviderDescription() {
    return {
      es: {
        s: "Proveedor de modelos de lenguaje seleccionado para este tenant.",
      },
      en: { s: "Language model provider selected for this tenant." },
      pt: {
        s: "Provedor de modelos de linguagem selecionado para este tenant.",
      },
    }[this.language].s;
  }

  TenantLlmConfigModelLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Modelo", p: "Modelo a utilizar (ej: gpt-4o-mini)", g: "M" },
      en: { s: "Model", p: "Model to use (e.g., gpt-4o-mini)" },
      pt: { s: "Modelo", p: "Modelo a utilizar (ex: gpt-4o-mini)", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigModelLabel() {
    return this.TenantLlmConfigModelLabelByLang();
  }

  get TenantLlmConfigModelDescription() {
    return {
      es: {
        s: "Identificador del modelo específico a usar (ej: gpt-4o-mini, claude-3-sonnet).",
      },
      en: {
        s: "Specific model identifier to use (e.g., gpt-4o-mini, claude-3-sonnet).",
      },
      pt: {
        s: "Identificador do modelo específico a usar (ex: gpt-4o-mini, claude-3-sonnet).",
      },
    }[this.language].s;
  }

  TenantLlmConfigApiKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "API Key", p: "Clave de API del proveedor", g: "F" },
      en: { s: "API Key", p: "Provider API key" },
      pt: { s: "API Key", p: "Chave de API do provedor", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigApiKeyLabel() {
    return this.TenantLlmConfigApiKeyLabelByLang();
  }

  get TenantLlmConfigApiKeyDescription() {
    return {
      es: {
        s: "Clave de autenticación para el proveedor LLM, almacenada de forma segura.",
      },
      en: { s: "Authentication key for the LLM provider, stored securely." },
      pt: {
        s: "Chave de autenticação para o provedor LLM, armazenada de forma segura.",
      },
    }[this.language].s;
  }

  TenantLlmConfigBaseUrlLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "URL Base",
        p: "URL base para proveedores locales (Ollama)",
        g: "F",
      },
      en: { s: "Base URL", p: "Base URL for local providers (Ollama)" },
      pt: {
        s: "URL Base",
        p: "URL base para provedores locais (Ollama)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigBaseUrlLabel() {
    return this.TenantLlmConfigBaseUrlLabelByLang();
  }

  get TenantLlmConfigBaseUrlDescription() {
    return {
      es: {
        s: "URL base personalizada para proveedores que lo requieran (ej: Ollama local).",
      },
      en: {
        s: "Custom base URL for providers that require it (e.g., local Ollama).",
      },
      pt: {
        s: "URL base personalizada para provedores que o requerem (ex: Ollama local).",
      },
    }[this.language].s;
  }

  TenantLlmConfigEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Habilitado", p: "Si la configuración está activa", g: "M" },
      en: { s: "Enabled", p: "Whether the configuration is active" },
      pt: { s: "Habilitado", p: "Se a configuração está ativa", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigEnabledLabel() {
    return this.TenantLlmConfigEnabledLabelByLang();
  }

  get TenantLlmConfigEnabledDescription() {
    return {
      es: {
        s: "Indica si esta configuración LLM está activa y disponible para uso.",
      },
      en: {
        s: "Indicates whether this LLM configuration is active and available for use.",
      },
      pt: {
        s: "Indica se esta configuração LLM está ativa e disponível para uso.",
      },
    }[this.language].s;
  }

  TenantLlmConfigPriorityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Prioridad",
        p: "Orden de prioridad (menor = más prioritario)",
        g: "F",
      },
      en: { s: "Priority", p: "Priority order (lower = higher priority)" },
      pt: {
        s: "Prioridade",
        p: "Ordem de prioridade (menor = mais prioritário)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigPriorityLabel() {
    return this.TenantLlmConfigPriorityLabelByLang();
  }

  get TenantLlmConfigPriorityDescription() {
    return {
      es: {
        s: "Orden de prioridad para fallback. El sistema intenta primero con prioridad más baja (1 antes que 2).",
      },
      en: {
        s: "Priority order for fallback. System tries lower priority first (1 before 2).",
      },
      pt: {
        s: "Ordem de prioridade para fallback. O sistema tenta primeiro com prioridade mais baixa (1 antes de 2).",
      },
    }[this.language].s;
  }

  TenantLlmConfigMaxTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tokens Máximos", p: "Límite de tokens por respuesta", g: "M" },
      en: { s: "Max Tokens", p: "Token limit per response" },
      pt: { s: "Tokens Máximos", p: "Limite de tokens por resposta", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigMaxTokensLabel() {
    return this.TenantLlmConfigMaxTokensLabelByLang();
  }

  get TenantLlmConfigMaxTokensDescription() {
    return {
      es: {
        s: "Límite máximo de tokens que el modelo puede generar en una respuesta.",
      },
      en: { s: "Maximum token limit the model can generate in a response." },
      pt: {
        s: "Limite máximo de tokens que o modelo pode gerar em uma resposta.",
      },
    }[this.language].s;
  }

  TenantLlmConfigTemperatureLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Temperatura", p: "Temperatura del modelo (0-2)", g: "F" },
      en: { s: "Temperature", p: "Model temperature (0-2)" },
      pt: { s: "Temperatura", p: "Temperatura do modelo (0-2)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigTemperatureLabel() {
    return this.TenantLlmConfigTemperatureLabelByLang();
  }

  get TenantLlmConfigTemperatureDescription() {
    return {
      es: {
        s: "Controla la aleatoriedad de las respuestas (0=determinista, 2=muy creativo).",
      },
      en: {
        s: "Controls response randomness (0=deterministic, 2=very creative).",
      },
      pt: {
        s: "Controla a aleatoriedade das respostas (0=determinístico, 2=muito criativo).",
      },
    }[this.language].s;
  }

  TenantLlmConfigDebugFullModeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Debug Completo",
        p: "Activar registro detallado de tool calls",
        g: "M",
      },
      en: { s: "Full Debug", p: "Enable detailed tool call logging" },
      pt: {
        s: "Debug Completo",
        p: "Ativar registro detalhado de tool calls",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantLlmConfigDebugFullModeLabel() {
    return this.TenantLlmConfigDebugFullModeLabelByLang();
  }

  get TenantLlmConfigDebugFullModeDescription() {
    return {
      es: {
        s: "Cuando está activo, guarda todos los tool calls intermedios con argumentos y resultados en AiSearchToolCall.",
      },
      en: {
        s: "When active, saves all intermediate tool calls with arguments and results to AiSearchToolCall.",
      },
      pt: {
        s: "Quando ativo, salva todas as tool calls intermediárias com argumentos e resultados em AiSearchToolCall.",
      },
    }[this.language].s;
  }

  // TenantDocument

  get TenantDocumentLabels() {
    return {
      es: { s: "Documento", p: "Documentos", g: "M" },
      en: { s: "Document", p: "Documents", g: undefined },
      pt: { s: "Documento", p: "Documentos", g: "M" },
    }[this.language];
  }

  get TenantDocumentSingular() {
    return this.TenantDocumentLabels.s;
  }

  get TenantDocumentPlural() {
    return this.TenantDocumentLabels.p;
  }

  get TenantDocumentGender() {
    return this.TenantDocumentLabels.g;
  }

  get groupTenantDocumentLabel() {
    return {
      es: { s: "Documento", p: "Documentos", g: "M" },
      en: { s: "Document", p: "Documents" },
      pt: { s: "Documento", p: "Documentos", g: "M" },
    }[this.language].s;
  }

  TenantDocumentNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre del documento", g: "M" },
      en: { s: "Name", p: "Document name" },
      pt: { s: "Nome", p: "Nome do documento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentNameLabel() {
    return this.TenantDocumentNameLabelByLang();
  }

  get TenantDocumentNameDescription() {
    return {
      es: { s: "Nombre descriptivo del documento para identificación." },
      en: { s: "Descriptive name of the document for identification." },
      pt: { s: "Nome descritivo do documento para identificação." },
    }[this.language].s;
  }

  TenantDocumentDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Descripción", p: "Descripción del contenido", g: "F" },
      en: { s: "Description", p: "Content description" },
      pt: { s: "Descrição", p: "Descrição do conteúdo", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentDescriptionLabel() {
    return this.TenantDocumentDescriptionLabelByLang();
  }

  get TenantDocumentDescriptionDescription() {
    return {
      es: {
        s: "Descripción opcional del contenido y propósito del documento.",
      },
      en: { s: "Optional description of the document's content and purpose." },
      pt: { s: "Descrição opcional do conteúdo e propósito do documento." },
    }[this.language].s;
  }

  TenantDocumentFileLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Archivo", p: "Archivo del documento", g: "M" },
      en: { s: "File", p: "Document file" },
      pt: { s: "Arquivo", p: "Arquivo do documento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentFileLabel() {
    return this.TenantDocumentFileLabelByLang();
  }

  get TenantDocumentFileDescription() {
    return {
      es: { s: "Archivo del documento subido para procesamiento y búsqueda." },
      en: { s: "Uploaded document file for processing and search." },
      pt: { s: "Arquivo do documento carregado para processamento e busca." },
    }[this.language].s;
  }

  TenantDocumentFileTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Archivo",
        p: "Tipo MIME del archivo (application/pdf, etc.)",
        g: "M",
      },
      en: { s: "File Type", p: "File MIME type (application/pdf, etc.)" },
      pt: {
        s: "Tipo de Arquivo",
        p: "Tipo MIME do arquivo (application/pdf, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentFileTypeLabel() {
    return this.TenantDocumentFileTypeLabelByLang();
  }

  get TenantDocumentFileTypeDescription() {
    return {
      es: {
        s: "Tipo MIME del archivo que determina cómo se procesará (application/pdf, text/plain).",
      },
      en: {
        s: "File MIME type that determines how it will be processed (application/pdf, text/plain).",
      },
      pt: {
        s: "Tipo MIME do arquivo que determina como será processado (application/pdf, text/plain).",
      },
    }[this.language].s;
  }

  TenantDocumentFileSizeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tamaño", p: "Tamaño del archivo en bytes", g: "M" },
      en: { s: "Size", p: "File size in bytes" },
      pt: { s: "Tamanho", p: "Tamanho do arquivo em bytes", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentFileSizeLabel() {
    return this.TenantDocumentFileSizeLabelByLang();
  }

  get TenantDocumentFileSizeDescription() {
    return {
      es: {
        s: "Tamaño del archivo en bytes, usado para validación y estadísticas.",
      },
      en: { s: "File size in bytes, used for validation and statistics." },
      pt: {
        s: "Tamanho do arquivo em bytes, usado para validação e estatísticas.",
      },
    }[this.language].s;
  }

  TenantDocumentStatusLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Estado", p: "Estado de procesamiento", g: "M" },
      en: { s: "Status", p: "Processing status" },
      pt: { s: "Estado", p: "Estado de processamento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentStatusLabel() {
    return this.TenantDocumentStatusLabelByLang();
  }

  get TenantDocumentStatusDescription() {
    return {
      es: {
        s: "Estado actual del procesamiento del documento (pendiente, procesando, listo, error).",
      },
      en: {
        s: "Current processing status of the document (pending, processing, ready, error).",
      },
      pt: {
        s: "Estado atual do processamento do documento (pendente, processando, pronto, erro).",
      },
    }[this.language].s;
  }

  TenantDocumentChunkCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fragmentos", p: "Número de fragmentos generados", g: "M" },
      en: { s: "Chunks", p: "Number of generated chunks" },
      pt: { s: "Fragmentos", p: "Número de fragmentos gerados", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentChunkCountLabel() {
    return this.TenantDocumentChunkCountLabelByLang();
  }

  get TenantDocumentChunkCountDescription() {
    return {
      es: {
        s: "Cantidad de fragmentos de texto generados tras el procesamiento.",
      },
      en: { s: "Number of text chunks generated after processing." },
      pt: {
        s: "Quantidade de fragmentos de texto gerados após o processamento.",
      },
    }[this.language].s;
  }

  TenantDocumentUploadedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Subido", p: "Fecha y hora de subida", g: "M" },
      en: { s: "Uploaded At", p: "Upload date and time" },
      pt: { s: "Carregado", p: "Data e hora de upload", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentUploadedAtLabel() {
    return this.TenantDocumentUploadedAtLabelByLang();
  }

  get TenantDocumentUploadedAtDescription() {
    return {
      es: { s: "Fecha y hora en que se subió el documento al sistema." },
      en: { s: "Date and time when the document was uploaded to the system." },
      pt: { s: "Data e hora em que o documento foi carregado no sistema." },
    }[this.language].s;
  }

  TenantDocumentProcessedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Procesado", p: "Fecha y hora de procesamiento", g: "M" },
      en: { s: "Processed At", p: "Processing date and time" },
      pt: { s: "Processado", p: "Data e hora de processamento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentProcessedAtLabel() {
    return this.TenantDocumentProcessedAtLabelByLang();
  }

  get TenantDocumentProcessedAtDescription() {
    return {
      es: {
        s: "Fecha y hora en que se completó el procesamiento del documento.",
      },
      en: { s: "Date and time when document processing was completed." },
      pt: {
        s: "Data e hora em que o processamento do documento foi concluído.",
      },
    }[this.language].s;
  }

  TenantDocumentProcessingErrorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Error de Procesamiento",
        p: "Mensaje de error si falló el procesamiento",
        g: "M",
      },
      en: { s: "Processing Error", p: "Error message if processing failed" },
      pt: {
        s: "Erro de Processamento",
        p: "Mensagem de erro se o processamento falhou",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentProcessingErrorLabel() {
    return this.TenantDocumentProcessingErrorLabelByLang();
  }

  get TenantDocumentProcessingErrorDescription() {
    return {
      es: {
        s: "Mensaje de error si el procesamiento falló, útil para diagnóstico.",
      },
      en: { s: "Error message if processing failed, useful for diagnosis." },
      pt: {
        s: "Mensagem de erro se o processamento falhou, útil para diagnóstico.",
      },
    }[this.language].s;
  }

  TenantDocumentContentTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Contenido",
        p: "Categoría semántica del documento (legal, técnico, contrato, etc.)",
        g: "M",
      },
      en: {
        s: "Content Type",
        p: "Semantic category of the document (legal, technical, contract, etc.)",
      },
      pt: {
        s: "Tipo de Conteúdo",
        p: "Categoria semântica do documento (legal, técnico, contrato, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentContentTypeLabel() {
    return this.TenantDocumentContentTypeLabelByLang();
  }

  get TenantDocumentContentTypeDescription() {
    return {
      es: {
        s: "Categoría del contenido para optimizar el procesamiento. Si está vacío, se detecta automáticamente. Valores: legal, technical, contract, manual, general.",
      },
      en: {
        s: "Content category to optimize processing. If empty, auto-detected. Values: legal, technical, contract, manual, general.",
      },
      pt: {
        s: "Categoria do conteúdo para otimizar o processamento. Se vazio, é detectado automaticamente. Valores: legal, technical, contract, manual, general.",
      },
    }[this.language].s;
  }

  TenantDocumentChunkingStrategyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Estrategia de Fragmentación",
        p: "Método para dividir el documento en fragmentos",
        g: "F",
      },
      en: {
        s: "Chunking Strategy",
        p: "Method for splitting the document into chunks",
      },
      pt: {
        s: "Estratégia de Fragmentação",
        p: "Método para dividir o documento em fragmentos",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentChunkingStrategyLabel() {
    return this.TenantDocumentChunkingStrategyLabelByLang();
  }

  TenantDocumentChunkingSeparatorRegexLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Regex Separador",
        p: "Expresión regular para separar secciones",
        g: "M",
      },
      en: {
        s: "Separator Regex",
        p: "Regular expression to separate sections",
      },
      pt: {
        s: "Regex Separador",
        p: "Expressão regular para separar seções",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentChunkingSeparatorRegexLabel() {
    return this.TenantDocumentChunkingSeparatorRegexLabelByLang();
  }

  get TenantDocumentChunkingSeparatorRegexDescription() {
    return {
      es: {
        s: "Patrón regex para identificar separadores de sección. Ejemplos: '^Art.?s*d+' para artículos, '^d+.d+' para secciones numeradas, '^#{1,3}s' para headings markdown.",
      },
      en: {
        s: "Regex pattern to identify section separators. Examples: '^Art.?s*d+' for articles, '^d+.d+' for numbered sections, '^#{1,3}s' for markdown headings.",
      },
      pt: {
        s: "Padrão regex para identificar separadores de seção. Exemplos: '^Art.?s*d+' para artigos, '^d+.d+' para seções numeradas, '^#{1,3}s' para headings markdown.",
      },
    }[this.language].s;
  }

  TenantDocumentChunkingMaxTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tokens Máximos por Fragmento",
        p: "Límite de tokens por fragmento",
        g: "M",
      },
      en: { s: "Max Tokens per Chunk", p: "Token limit per chunk" },
      pt: {
        s: "Tokens Máximos por Fragmento",
        p: "Limite de tokens por fragmento",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentChunkingMaxTokensLabel() {
    return this.TenantDocumentChunkingMaxTokensLabelByLang();
  }

  get TenantDocumentChunkingMaxTokensDescription() {
    return {
      es: {
        s: "Cantidad máxima de tokens por fragmento. Valor por defecto: 500.",
      },
      en: { s: "Maximum number of tokens per chunk. Default value: 500." },
      pt: {
        s: "Quantidade máxima de tokens por fragmento. Valor padrão: 500.",
      },
    }[this.language].s;
  }

  TenantDocumentChunkingOverlapTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tokens de Overlap",
        p: "Tokens de solapamiento entre fragmentos",
        g: "M",
      },
      en: { s: "Overlap Tokens", p: "Overlap tokens between chunks" },
      pt: {
        s: "Tokens de Overlap",
        p: "Tokens de sobreposição entre fragmentos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get TenantDocumentChunkingOverlapTokensLabel() {
    return this.TenantDocumentChunkingOverlapTokensLabelByLang();
  }

  get TenantDocumentChunkingOverlapTokensDescription() {
    return {
      es: {
        s: "Cantidad de tokens que se solapan entre fragmentos consecutivos para mantener contexto. Valor por defecto: 50.",
      },
      en: {
        s: "Number of tokens that overlap between consecutive chunks to maintain context. Default value: 50.",
      },
      pt: {
        s: "Quantidade de tokens que se sobrepõem entre fragmentos consecutivos para manter contexto. Valor padrão: 50.",
      },
    }[this.language].s;
  }

  // AiSearchConversation

  get AiSearchConversationLabels() {
    return {
      es: {
        s: "Conversación de Búsqueda",
        p: "Conversaciones de Búsqueda",
        g: "F",
      },
      en: { s: "Search Conversation", p: "Search Conversations", g: undefined },
      pt: { s: "Conversa de Busca", p: "Conversas de Busca", g: "F" },
    }[this.language];
  }

  get AiSearchConversationSingular() {
    return this.AiSearchConversationLabels.s;
  }

  get AiSearchConversationPlural() {
    return this.AiSearchConversationLabels.p;
  }

  get AiSearchConversationGender() {
    return this.AiSearchConversationLabels.g;
  }

  get groupAiSearchConversationLabel() {
    return {
      es: {
        s: "Conversación de Búsqueda",
        p: "Conversaciones de Búsqueda",
        g: "F",
      },
      en: { s: "Search Conversation", p: "Search Conversations" },
      pt: { s: "Conversa de Busca", p: "Conversas de Busca", g: "F" },
    }[this.language].s;
  }

  AiSearchConversationStartedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Iniciada", p: "Fecha y hora de inicio", g: "F" },
      en: { s: "Started At", p: "Start date and time" },
      pt: { s: "Iniciada", p: "Data e hora de início", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchConversationStartedAtLabel() {
    return this.AiSearchConversationStartedAtLabelByLang();
  }

  get AiSearchConversationStartedAtDescription() {
    return {
      es: { s: "Fecha y hora en que se inició la conversación de búsqueda." },
      en: { s: "Date and time when the search conversation was started." },
      pt: { s: "Data e hora em que a conversa de busca foi iniciada." },
    }[this.language].s;
  }

  AiSearchConversationLastMessageAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Último Mensaje", p: "Fecha y hora del último mensaje", g: "M" },
      en: { s: "Last Message At", p: "Last message date and time" },
      pt: { s: "Última Mensagem", p: "Data e hora da última mensagem", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchConversationLastMessageAtLabel() {
    return this.AiSearchConversationLastMessageAtLabelByLang();
  }

  get AiSearchConversationLastMessageAtDescription() {
    return {
      es: { s: "Fecha y hora del último mensaje en la conversación." },
      en: { s: "Date and time of the last message in the conversation." },
      pt: { s: "Data e hora da última mensagem na conversa." },
    }[this.language].s;
  }

  AiSearchConversationMessageCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Mensajes",
        p: "Cantidad de mensajes en la conversación",
        g: "M",
      },
      en: { s: "Messages", p: "Number of messages in the conversation" },
      pt: { s: "Mensagens", p: "Quantidade de mensagens na conversa" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchConversationMessageCountLabel() {
    return this.AiSearchConversationMessageCountLabelByLang();
  }

  get AiSearchConversationMessageCountDescription() {
    return {
      es: {
        s: "Contador total de mensajes intercambiados en la conversación.",
      },
      en: { s: "Total count of messages exchanged in the conversation." },
      pt: { s: "Contador total de mensagens trocadas na conversa." },
    }[this.language].s;
  }

  AiSearchConversationMessagesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Historial", p: "Historial de mensajes en formato JSON" },
      en: { s: "History", p: "Message history in JSON format" },
      pt: {
        s: "Histórico",
        p: "Histórico de mensagens em formato JSON",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchConversationMessagesLabel() {
    return this.AiSearchConversationMessagesLabelByLang();
  }

  get AiSearchConversationMessagesDescription() {
    return {
      es: {
        s: "Array JSON con todos los mensajes de usuario y asistente de la conversación.",
      },
      en: {
        s: "JSON array with all user and assistant messages in the conversation.",
      },
      pt: {
        s: "Array JSON com todas as mensagens de usuário e assistente na conversa.",
      },
    }[this.language].s;
  }

  AiSearchConversationContextLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Contexto",
        p: "Contexto de la conversación (entidades visitadas, etc.)",
        g: "M",
      },
      en: { s: "Context", p: "Conversation context (visited entities, etc.)" },
      pt: {
        s: "Contexto",
        p: "Contexto da conversa (entidades visitadas, etc.)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchConversationContextLabel() {
    return this.AiSearchConversationContextLabelByLang();
  }

  get AiSearchConversationContextDescription() {
    return {
      es: {
        s: "Información contextual de la conversación: entidades visitadas, filtros activos, etc.",
      },
      en: {
        s: "Contextual information of the conversation: visited entities, active filters, etc.",
      },
      pt: {
        s: "Informação contextual da conversa: entidades visitadas, filtros ativos, etc.",
      },
    }[this.language].s;
  }

  // AiSearchResponse

  get AiSearchResponseLabels() {
    return {
      es: { s: "Respuesta AI", p: "Respuestas AI", g: "F" },
      en: { s: "AI Response", p: "AI Responses", g: undefined },
      pt: { s: "Resposta AI", p: "Respostas AI", g: "F" },
    }[this.language];
  }

  get AiSearchResponseSingular() {
    return this.AiSearchResponseLabels.s;
  }

  get AiSearchResponsePlural() {
    return this.AiSearchResponseLabels.p;
  }

  get AiSearchResponseGender() {
    return this.AiSearchResponseLabels.g;
  }

  get groupAiSearchResponseLabel() {
    return {
      es: { s: "Respuesta AI", p: "Respuestas AI", g: "F" },
      en: { s: "AI Response", p: "AI Responses" },
      pt: { s: "Resposta AI", p: "Respostas AI", g: "F" },
    }[this.language].s;
  }

  AiSearchResponseConversationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Conversación", p: "Conversación padre", g: "F" },
      en: { s: "Conversation", p: "Parent conversation" },
      pt: { s: "Conversa", p: "Conversa pai", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseConversationLabel() {
    return this.AiSearchResponseConversationLabelByLang();
  }

  AiSearchResponseQueryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Consulta", p: "Pregunta del usuario", g: "F" },
      en: { s: "Query", p: "User question" },
      pt: { s: "Consulta", p: "Pergunta do usuário", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseQueryLabel() {
    return this.AiSearchResponseQueryLabelByLang();
  }

  AiSearchResponseResponseLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Respuesta", p: "Respuesta del asistente", g: "F" },
      en: { s: "Response", p: "Assistant response" },
      pt: { s: "Resposta", p: "Resposta do assistente", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseResponseLabel() {
    return this.AiSearchResponseResponseLabelByLang();
  }

  AiSearchResponseSourcesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fuentes", p: "Fuentes RAG utilizadas", g: "M" },
      en: { s: "Sources", p: "RAG sources used" },
      pt: { s: "Fontes", p: "Fontes RAG utilizadas", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseSourcesLabel() {
    return this.AiSearchResponseSourcesLabelByLang();
  }

  AiSearchResponseExplanationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Explicación", p: "Tool calls realizadas", g: "F" },
      en: { s: "Explanation", p: "Tool calls made" },
      pt: { s: "Explicação", p: "Tool calls realizadas", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseExplanationLabel() {
    return this.AiSearchResponseExplanationLabelByLang();
  }

  AiSearchResponseTokenUsageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tokens", p: "Uso de tokens", g: "M" },
      en: { s: "Tokens", p: "Token usage" },
      pt: { s: "Tokens", p: "Uso de tokens", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseTokenUsageLabel() {
    return this.AiSearchResponseTokenUsageLabelByLang();
  }

  AiSearchResponseCreatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Creado", p: "Fecha de creación", g: "M" },
      en: { s: "Created", p: "Creation date" },
      pt: { s: "Criado", p: "Data de criação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseCreatedAtLabel() {
    return this.AiSearchResponseCreatedAtLabelByLang();
  }

  AiSearchResponseRatingLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Valoración", p: "Feedback del usuario", g: "F" },
      en: { s: "Rating", p: "User feedback" },
      pt: { s: "Avaliação", p: "Feedback do usuário", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseRatingLabel() {
    return this.AiSearchResponseRatingLabelByLang();
  }

  AiSearchResponseEvaluatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Evaluado", p: "Fecha de evaluación", g: "M" },
      en: { s: "Evaluated At", p: "Evaluation date" },
      pt: { s: "Avaliado", p: "Data de avaliação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseEvaluatedAtLabel() {
    return this.AiSearchResponseEvaluatedAtLabelByLang();
  }

  AiSearchResponseEvaluatedByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Evaluado Por", p: "Usuario que evaluó", g: "M" },
      en: { s: "Evaluated By", p: "User who evaluated" },
      pt: { s: "Avaliado Por", p: "Usuário que avaliou", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseEvaluatedByLabel() {
    return this.AiSearchResponseEvaluatedByLabelByLang();
  }

  AiSearchResponseToolCallsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tool Calls", p: "Llamadas a herramientas", g: "F" },
      en: { s: "Tool Calls", p: "Tool invocations" },
      pt: { s: "Tool Calls", p: "Chamadas de ferramentas", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchResponseToolCallsLabel() {
    return this.AiSearchResponseToolCallsLabelByLang();
  }

  // AiSearchToolCall

  get AiSearchToolCallLabels() {
    return {
      es: { s: "Tool Call", p: "Tool Calls", g: "F" },
      en: { s: "Tool Call", p: "Tool Calls", g: undefined },
      pt: { s: "Tool Call", p: "Tool Calls", g: "F" },
    }[this.language];
  }

  get AiSearchToolCallSingular() {
    return this.AiSearchToolCallLabels.s;
  }

  get AiSearchToolCallPlural() {
    return this.AiSearchToolCallLabels.p;
  }

  get AiSearchToolCallGender() {
    return this.AiSearchToolCallLabels.g;
  }

  get groupAiSearchToolCallLabel() {
    return {
      es: { s: "Tool Call", p: "Tool Calls", g: "F" },
      en: { s: "Tool Call", p: "Tool Calls" },
      pt: { s: "Tool Call", p: "Tool Calls", g: "F" },
    }[this.language].s;
  }

  AiSearchToolCallSequenceLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "#", p: "Número de secuencia", g: "M" },
      en: { s: "#", p: "Sequence number" },
      pt: { s: "#", p: "Número de sequência", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallSequenceLabel() {
    return this.AiSearchToolCallSequenceLabelByLang();
  }

  get AiSearchToolCallSequenceDescription() {
    return {
      es: { s: "Orden de la llamada dentro de la respuesta (1, 2, 3...)" },
      en: { s: "Order of the call within the response (1, 2, 3...)" },
      pt: { s: "Ordem da chamada dentro da resposta (1, 2, 3...)" },
    }[this.language].s;
  }

  AiSearchToolCallToolNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Herramienta", p: "Nombre de la herramienta", g: "F" },
      en: { s: "Tool", p: "Tool name" },
      pt: { s: "Ferramenta", p: "Nome da ferramenta", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallToolNameLabel() {
    return this.AiSearchToolCallToolNameLabelByLang();
  }

  get AiSearchToolCallToolNameDescription() {
    return {
      es: {
        s: "Nombre de la herramienta llamada (query_entities, count_entities, etc.)",
      },
      en: {
        s: "Name of the tool called (query_entities, count_entities, etc.)",
      },
      pt: {
        s: "Nome da ferramenta chamada (query_entities, count_entities, etc.)",
      },
    }[this.language].s;
  }

  AiSearchToolCallPatternLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Pattern", p: "Pattern ID usado", g: "M" },
      en: { s: "Pattern", p: "Pattern ID used" },
      pt: { s: "Pattern", p: "Pattern ID usado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallPatternLabel() {
    return this.AiSearchToolCallPatternLabelByLang();
  }

  get AiSearchToolCallPatternDescription() {
    return {
      es: {
        s: "ID del pattern de navegación elegido por el LLM (nested-set, groupby, etc.)",
      },
      en: {
        s: "Navigation pattern ID chosen by the LLM (nested-set, groupby, etc.)",
      },
      pt: {
        s: "ID do pattern de navegação escolhido pelo LLM (nested-set, groupby, etc.)",
      },
    }[this.language].s;
  }

  AiSearchToolCallNavigationPathLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Path", p: "Path de navegación", g: "M" },
      en: { s: "Path", p: "Navigation path" },
      pt: { s: "Path", p: "Path de navegação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallNavigationPathLabel() {
    return this.AiSearchToolCallNavigationPathLabelByLang();
  }

  get AiSearchToolCallNavigationPathDescription() {
    return {
      es: {
        s: "Path de navegación declarado por el LLM (ej: Edition → works → authors → author → nationality)",
      },
      en: {
        s: "Navigation path declared by the LLM (e.g., Edition → works → authors → author → nationality)",
      },
      pt: {
        s: "Path de navegação declarado pelo LLM (ex: Edition → works → authors → author → nationality)",
      },
    }[this.language].s;
  }

  AiSearchToolCallToolArgumentsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Argumentos", p: "Argumentos de la llamada", g: "M" },
      en: { s: "Arguments", p: "Call arguments" },
      pt: { s: "Argumentos", p: "Argumentos da chamada", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallToolArgumentsLabel() {
    return this.AiSearchToolCallToolArgumentsLabelByLang();
  }

  get AiSearchToolCallToolArgumentsDescription() {
    return {
      es: {
        s: "Argumentos completos pasados a la herramienta en formato JSON",
      },
      en: { s: "Complete arguments passed to the tool in JSON format" },
      pt: {
        s: "Argumentos completos passados para a ferramenta em formato JSON",
      },
    }[this.language].s;
  }

  AiSearchToolCallResultLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Resultado", p: "Resultado de la llamada", g: "M" },
      en: { s: "Result", p: "Call result" },
      pt: { s: "Resultado", p: "Resultado da chamada", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallResultLabel() {
    return this.AiSearchToolCallResultLabelByLang();
  }

  get AiSearchToolCallResultDescription() {
    return {
      es: {
        s: "Resultado devuelto por la herramienta (puede ser grande, solo en modo DEBUG_FULL)",
      },
      en: {
        s: "Result returned by the tool (can be large, only in DEBUG_FULL mode)",
      },
      pt: {
        s: "Resultado retornado pela ferramenta (pode ser grande, apenas em modo DEBUG_FULL)",
      },
    }[this.language].s;
  }

  AiSearchToolCallDurationMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Duración (ms)", p: "Tiempo de ejecución", g: "F" },
      en: { s: "Duration (ms)", p: "Execution time" },
      pt: { s: "Duração (ms)", p: "Tempo de execução", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallDurationMsLabel() {
    return this.AiSearchToolCallDurationMsLabelByLang();
  }

  get AiSearchToolCallDurationMsDescription() {
    return {
      es: { s: "Tiempo de ejecución de la llamada en milisegundos" },
      en: { s: "Call execution time in milliseconds" },
      pt: { s: "Tempo de execução da chamada em milissegundos" },
    }[this.language].s;
  }

  AiSearchToolCallSuccessLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Éxito", p: "Si la llamada fue exitosa", g: "M" },
      en: { s: "Success", p: "Whether the call succeeded" },
      pt: { s: "Sucesso", p: "Se a chamada foi bem sucedida", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallSuccessLabel() {
    return this.AiSearchToolCallSuccessLabelByLang();
  }

  AiSearchToolCallErrorMessageLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Error", p: "Mensaje de error si falló", g: "M" },
      en: { s: "Error", p: "Error message if failed" },
      pt: { s: "Erro", p: "Mensagem de erro se falhou", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchToolCallErrorMessageLabel() {
    return this.AiSearchToolCallErrorMessageLabelByLang();
  }

  // TenantTokenUsage

  get TenantTokenUsageLabels() {
    return {
      es: { s: "Uso de Tokens", p: "Uso de Tokens", g: "M" },
      en: { s: "Token Usage", p: "Token Usage", g: undefined },
      pt: { s: "Uso de Tokens", p: "Uso de Tokens", g: "M" },
    }[this.language];
  }

  get TenantTokenUsageSingular() {
    return this.TenantTokenUsageLabels.s;
  }

  get TenantTokenUsagePlural() {
    return this.TenantTokenUsageLabels.p;
  }

  get TenantTokenUsageGender() {
    return this.TenantTokenUsageLabels.g;
  }

  get groupTenantTokenUsageLabel() {
    return {
      es: { s: "Uso de Tokens", p: "Uso de Tokens", g: "M" },
      en: { s: "Token Usage", p: "Token Usage" },
      pt: { s: "Uso de Tokens", p: "Uso de Tokens", g: "M" },
    }[this.language].s;
  }

  TenantTokenUsageProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor", p: "Código del proveedor LLM", g: "M" },
      en: { s: "Provider", p: "LLM provider code" },
      pt: { s: "Provedor", p: "Código do provedor LLM", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageProviderLabel() {
    return this.TenantTokenUsageProviderLabelByLang();
  }

  get TenantTokenUsageProviderDescription() {
    return {
      es: { s: "Código del proveedor utilizado (openai, anthropic, etc.)" },
      en: { s: "Provider code used (openai, anthropic, etc.)" },
      pt: { s: "Código do provedor utilizado (openai, anthropic, etc.)" },
    }[this.language].s;
  }

  TenantTokenUsageModelLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Modelo", p: "Modelo LLM utilizado", g: "M" },
      en: { s: "Model", p: "LLM model used" },
      pt: { s: "Modelo", p: "Modelo LLM utilizado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageModelLabel() {
    return this.TenantTokenUsageModelLabelByLang();
  }

  get TenantTokenUsageModelDescription() {
    return {
      es: {
        s: "Identificador del modelo utilizado (gpt-4o-mini, claude-3-sonnet, etc.)",
      },
      en: { s: "Model identifier used (gpt-4o-mini, claude-3-sonnet, etc.)" },
      pt: {
        s: "Identificador do modelo utilizado (gpt-4o-mini, claude-3-sonnet, etc.)",
      },
    }[this.language].s;
  }

  TenantTokenUsagePromptTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tokens de Prompt",
        p: "Tokens consumidos en el prompt",
        g: "M",
      },
      en: { s: "Prompt Tokens", p: "Tokens consumed in prompt" },
      pt: { s: "Tokens de Prompt", p: "Tokens consumidos no prompt", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsagePromptTokensLabel() {
    return this.TenantTokenUsagePromptTokensLabelByLang();
  }

  get TenantTokenUsagePromptTokensDescription() {
    return {
      es: { s: "Cantidad de tokens utilizados en el prompt de entrada." },
      en: { s: "Number of tokens used in the input prompt." },
      pt: { s: "Quantidade de tokens utilizados no prompt de entrada." },
    }[this.language].s;
  }

  TenantTokenUsageCompletionTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tokens de Respuesta",
        p: "Tokens generados en la respuesta",
        g: "M",
      },
      en: { s: "Completion Tokens", p: "Tokens generated in response" },
      pt: { s: "Tokens de Resposta", p: "Tokens gerados na resposta", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageCompletionTokensLabel() {
    return this.TenantTokenUsageCompletionTokensLabelByLang();
  }

  get TenantTokenUsageCompletionTokensDescription() {
    return {
      es: { s: "Cantidad de tokens generados en la respuesta del modelo." },
      en: { s: "Number of tokens generated in the model response." },
      pt: { s: "Quantidade de tokens gerados na resposta do modelo." },
    }[this.language].s;
  }

  TenantTokenUsageTotalTokensLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tokens Totales", p: "Total de tokens consumidos", g: "M" },
      en: { s: "Total Tokens", p: "Total tokens consumed" },
      pt: { s: "Tokens Totais", p: "Total de tokens consumidos", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageTotalTokensLabel() {
    return this.TenantTokenUsageTotalTokensLabelByLang();
  }

  get TenantTokenUsageTotalTokensDescription() {
    return {
      es: { s: "Suma de tokens de prompt + tokens de respuesta." },
      en: { s: "Sum of prompt tokens + completion tokens." },
      pt: { s: "Soma de tokens de prompt + tokens de resposta." },
    }[this.language].s;
  }

  TenantTokenUsageConversationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Conversación",
        p: "Conversación de AI Search asociada",
        g: "F",
      },
      en: { s: "Conversation", p: "Associated AI Search conversation" },
      pt: { s: "Conversa", p: "Conversa de AI Search associada", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageConversationLabel() {
    return this.TenantTokenUsageConversationLabelByLang();
  }

  get TenantTokenUsageConversationDescription() {
    return {
      es: {
        s: "Referencia a la conversación de AI Search asociada, si aplica.",
      },
      en: {
        s: "Reference to the associated AI Search conversation, if applicable.",
      },
      pt: { s: "Referência à conversa de AI Search associada, se aplicável." },
    }[this.language].s;
  }

  TenantTokenUsageConversationUserLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Usuario", p: "Usuario que realizó la conversación", g: "M" },
      en: { s: "User", p: "User who performed the conversation" },
      pt: { s: "Usuário", p: "Usuário que realizou a conversa", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageConversationUserLabel() {
    return this.TenantTokenUsageConversationUserLabelByLang();
  }

  get TenantTokenUsageConversationUserDescription() {
    return {
      es: { s: "Usuario que realizó esta conversación." },
      en: { s: "User who performed this conversation." },
      pt: { s: "Usuário que realizou esta conversa." },
    }[this.language].s;
  }

  TenantTokenUsageTimestampLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha", p: "Fecha y hora del consumo", g: "F" },
      en: { s: "Timestamp", p: "Consumption date and time" },
      pt: { s: "Data", p: "Data e hora do consumo", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageTimestampLabel() {
    return this.TenantTokenUsageTimestampLabelByLang();
  }

  get TenantTokenUsageTimestampDescription() {
    return {
      es: { s: "Momento exacto en que se realizó el consumo de tokens." },
      en: { s: "Exact moment when token consumption occurred." },
      pt: { s: "Momento exato em que o consumo de tokens ocorreu." },
    }[this.language].s;
  }

  TenantTokenUsageTotalTimeMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tiempo Total (ms)",
        p: "Tiempo total de procesamiento",
        g: "M",
      },
      en: { s: "Total Time (ms)", p: "Total processing time" },
      pt: { s: "Tempo Total (ms)", p: "Tempo total de processamento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageTotalTimeMsLabel() {
    return this.TenantTokenUsageTotalTimeMsLabelByLang();
  }

  get TenantTokenUsageTotalTimeMsDescription() {
    return {
      es: {
        s: "Tiempo total wall clock desde el inicio hasta el fin del request.",
      },
      en: { s: "Total wall clock time from request start to end." },
      pt: { s: "Tempo total wall clock desde o início até o fim do request." },
    }[this.language].s;
  }

  TenantTokenUsageLlmTimeMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tiempo LLM (ms)", p: "Tiempo en llamadas LLM", g: "M" },
      en: { s: "LLM Time (ms)", p: "Time spent in LLM calls" },
      pt: { s: "Tempo LLM (ms)", p: "Tempo em chamadas LLM", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageLlmTimeMsLabel() {
    return this.TenantTokenUsageLlmTimeMsLabelByLang();
  }

  get TenantTokenUsageLlmTimeMsDescription() {
    return {
      es: {
        s: "Tiempo acumulado en todas las llamadas al modelo de lenguaje.",
      },
      en: { s: "Cumulative time spent in all language model calls." },
      pt: { s: "Tempo acumulado em todas as chamadas ao modelo de linguagem." },
    }[this.language].s;
  }

  TenantTokenUsageToolsTimeMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tiempo Tools (ms)", p: "Tiempo en herramientas", g: "M" },
      en: { s: "Tools Time (ms)", p: "Time spent in tools" },
      pt: { s: "Tempo Tools (ms)", p: "Tempo em ferramentas", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageToolsTimeMsLabel() {
    return this.TenantTokenUsageToolsTimeMsLabelByLang();
  }

  get TenantTokenUsageToolsTimeMsDescription() {
    return {
      es: { s: "Tiempo acumulado ejecutando herramientas MCP." },
      en: { s: "Cumulative time spent executing MCP tools." },
      pt: { s: "Tempo acumulado executando ferramentas MCP." },
    }[this.language].s;
  }

  TenantTokenUsageRagTimeMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tiempo RAG (ms)", p: "Tiempo en búsqueda semántica", g: "M" },
      en: { s: "RAG Time (ms)", p: "Time spent in semantic search" },
      pt: { s: "Tempo RAG (ms)", p: "Tempo em busca semântica", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageRagTimeMsLabel() {
    return this.TenantTokenUsageRagTimeMsLabelByLang();
  }

  get TenantTokenUsageRagTimeMsDescription() {
    return {
      es: { s: "Tiempo en búsqueda y recuperación de documentos (si aplica)." },
      en: { s: "Time spent in document search and retrieval (if applicable)." },
      pt: { s: "Tempo em busca e recuperação de documentos (se aplicável)." },
    }[this.language].s;
  }

  TenantTokenUsageFirstTokenMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Primer Token (ms)", p: "Tiempo al primer token", g: "M" },
      en: { s: "First Token (ms)", p: "Time to first token" },
      pt: { s: "Primeiro Token (ms)", p: "Tempo até o primeiro token", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageFirstTokenMsLabel() {
    return this.TenantTokenUsageFirstTokenMsLabelByLang();
  }

  get TenantTokenUsageFirstTokenMsDescription() {
    return {
      es: { s: "Tiempo desde el inicio hasta recibir el primer token (TTFT)." },
      en: { s: "Time from start until receiving the first token (TTFT)." },
      pt: { s: "Tempo desde o início até receber o primeiro token (TTFT)." },
    }[this.language].s;
  }

  TenantTokenUsageLlmCallCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Llamadas LLM", p: "Número de llamadas al LLM", g: "F" },
      en: { s: "LLM Calls", p: "Number of LLM calls" },
      pt: { s: "Chamadas LLM", p: "Número de chamadas ao LLM", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageLlmCallCountLabel() {
    return this.TenantTokenUsageLlmCallCountLabelByLang();
  }

  get TenantTokenUsageLlmCallCountDescription() {
    return {
      es: {
        s: "Cantidad de llamadas al modelo (puede ser >1 en loops agentic).",
      },
      en: { s: "Number of model calls (can be >1 in agentic loops)." },
      pt: {
        s: "Quantidade de chamadas ao modelo (pode ser >1 em loops agentic).",
      },
    }[this.language].s;
  }

  TenantTokenUsageToolCallCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Llamadas Tools", p: "Número de tool calls", g: "F" },
      en: { s: "Tool Calls", p: "Number of tool calls" },
      pt: { s: "Chamadas Tools", p: "Número de tool calls", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageToolCallCountLabel() {
    return this.TenantTokenUsageToolCallCountLabelByLang();
  }

  get TenantTokenUsageToolCallCountDescription() {
    return {
      es: { s: "Cantidad de herramientas ejecutadas en el request." },
      en: { s: "Number of tools executed in the request." },
      pt: { s: "Quantidade de ferramentas executadas no request." },
    }[this.language].s;
  }

  TenantTokenUsageRagChunksCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Chunks RAG", p: "Chunks recuperados", g: "M" },
      en: { s: "RAG Chunks", p: "Retrieved chunks" },
      pt: { s: "Chunks RAG", p: "Chunks recuperados", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageRagChunksCountLabel() {
    return this.TenantTokenUsageRagChunksCountLabelByLang();
  }

  get TenantTokenUsageRagChunksCountDescription() {
    return {
      es: {
        s: "Cantidad de chunks de documentos recuperados por RAG (si aplica).",
      },
      en: { s: "Number of document chunks retrieved by RAG (if applicable)." },
      pt: {
        s: "Quantidade de chunks de documentos recuperados por RAG (se aplicável).",
      },
    }[this.language].s;
  }

  TenantTokenUsageAbTestConditionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Condición A/B", p: "Condición del test A/B", g: "F" },
      en: { s: "A/B Condition", p: "A/B test condition" },
      pt: { s: "Condição A/B", p: "Condição do teste A/B", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantTokenUsageAbTestConditionLabel() {
    return this.TenantTokenUsageAbTestConditionLabelByLang();
  }

  get TenantTokenUsageAbTestConditionDescription() {
    return {
      es: {
        s: "Condición asignada en el test A/B: with_examples o without_examples.",
      },
      en: {
        s: "Assigned condition in A/B test: with_examples or without_examples.",
      },
      pt: {
        s: "Condição atribuída no teste A/B: with_examples ou without_examples.",
      },
    }[this.language].s;
  }

  // TenantStorageUsage

  get TenantStorageUsageLabels() {
    return {
      es: { s: "Uso de Almacenamiento", p: "Uso de Almacenamiento", g: "M" },
      en: { s: "Storage Usage", p: "Storage Usage", g: undefined },
      pt: { s: "Uso de Armazenamento", p: "Uso de Armazenamento", g: "M" },
    }[this.language];
  }

  get TenantStorageUsageSingular() {
    return this.TenantStorageUsageLabels.s;
  }

  get TenantStorageUsagePlural() {
    return this.TenantStorageUsageLabels.p;
  }

  get TenantStorageUsageGender() {
    return this.TenantStorageUsageLabels.g;
  }

  get groupTenantStorageUsageLabel() {
    return {
      es: { s: "Uso de Almacenamiento", p: "Uso de Almacenamiento", g: "M" },
      en: { s: "Storage Usage", p: "Storage Usage" },
      pt: { s: "Uso de Armazenamento", p: "Uso de Armazenamento", g: "M" },
    }[this.language].s;
  }

  TenantStorageUsageOperationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Operación", p: "Tipo de operación (Upload/Delete)", g: "F" },
      en: { s: "Operation", p: "Operation type (Upload/Delete)" },
      pt: { s: "Operação", p: "Tipo de operação (Upload/Delete)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageOperationLabel() {
    return this.TenantStorageUsageOperationLabelByLang();
  }

  get TenantStorageUsageOperationDescription() {
    return {
      es: { s: "Tipo de operación realizada sobre el documento." },
      en: { s: "Type of operation performed on the document." },
      pt: { s: "Tipo de operação realizada no documento." },
    }[this.language].s;
  }

  TenantStorageUsageBytesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Bytes", p: "Cantidad de bytes", g: "M" },
      en: { s: "Bytes", p: "Number of bytes" },
      pt: { s: "Bytes", p: "Quantidade de bytes", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageBytesLabel() {
    return this.TenantStorageUsageBytesLabelByLang();
  }

  get TenantStorageUsageBytesDescription() {
    return {
      es: {
        s: "Cantidad de bytes involucrados. Positivo para upload, negativo para delete.",
      },
      en: {
        s: "Number of bytes involved. Positive for upload, negative for delete.",
      },
      pt: {
        s: "Quantidade de bytes envolvidos. Positivo para upload, negativo para delete.",
      },
    }[this.language].s;
  }

  TenantStorageUsageDocumentNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Documento", p: "Nombre del documento", g: "M" },
      en: { s: "Document", p: "Document name" },
      pt: { s: "Documento", p: "Nome do documento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageDocumentNameLabel() {
    return this.TenantStorageUsageDocumentNameLabelByLang();
  }

  get TenantStorageUsageDocumentNameDescription() {
    return {
      es: { s: "Nombre del documento involucrado en la operación." },
      en: { s: "Name of the document involved in the operation." },
      pt: { s: "Nome do documento envolvido na operação." },
    }[this.language].s;
  }

  TenantStorageUsageDocumentIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID Documento", p: "Identificador del documento", g: "M" },
      en: { s: "Document ID", p: "Document identifier" },
      pt: { s: "ID Documento", p: "Identificador do documento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageDocumentIdLabel() {
    return this.TenantStorageUsageDocumentIdLabelByLang();
  }

  get TenantStorageUsageDocumentIdDescription() {
    return {
      es: { s: "Identificador único del documento involucrado." },
      en: { s: "Unique identifier of the document involved." },
      pt: { s: "Identificador único do documento envolvido." },
    }[this.language].s;
  }

  TenantStorageUsageFileTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Archivo", p: "Tipo MIME del archivo", g: "M" },
      en: { s: "File Type", p: "File MIME type" },
      pt: { s: "Tipo de Arquivo", p: "Tipo MIME do arquivo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageFileTypeLabel() {
    return this.TenantStorageUsageFileTypeLabelByLang();
  }

  get TenantStorageUsageFileTypeDescription() {
    return {
      es: { s: "Tipo MIME del archivo del documento." },
      en: { s: "MIME type of the document file." },
      pt: { s: "Tipo MIME do arquivo do documento." },
    }[this.language].s;
  }

  TenantStorageUsageTimestampLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Fecha", p: "Fecha y hora de la operación", g: "F" },
      en: { s: "Timestamp", p: "Operation date and time" },
      pt: { s: "Data", p: "Data e hora da operação", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageTimestampLabel() {
    return this.TenantStorageUsageTimestampLabelByLang();
  }

  get TenantStorageUsageTimestampDescription() {
    return {
      es: {
        s: "Momento exacto en que se realizó la operación de almacenamiento.",
      },
      en: { s: "Exact moment when the storage operation occurred." },
      pt: { s: "Momento exato em que a operação de armazenamento ocorreu." },
    }[this.language].s;
  }

  TenantStorageUsageBalanceAfterLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Balance", p: "Balance después de la operación", g: "M" },
      en: { s: "Balance", p: "Balance after operation" },
      pt: { s: "Saldo", p: "Saldo após a operação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get TenantStorageUsageBalanceAfterLabel() {
    return this.TenantStorageUsageBalanceAfterLabelByLang();
  }

  get TenantStorageUsageBalanceAfterDescription() {
    return {
      es: {
        s: "Total de bytes almacenados del tenant después de esta operación.",
      },
      en: { s: "Total stored bytes of the tenant after this operation." },
      pt: { s: "Total de bytes armazenados do tenant após esta operação." },
    }[this.language].s;
  }

  // AiSearchAutoEval

  get AiSearchAutoEvalLabels() {
    return {
      es: {
        s: "Evaluación Automatizada AI",
        p: "Evaluaciones Automatizadas AI",
        g: "F",
      },
      en: {
        s: "AI Automated Evaluation",
        p: "AI Automated Evaluations",
        g: undefined,
      },
      pt: {
        s: "Avaliação Automatizada AI",
        p: "Avaliações Automatizadas AI",
        g: "F",
      },
    }[this.language];
  }

  get AiSearchAutoEvalSingular() {
    return this.AiSearchAutoEvalLabels.s;
  }

  get AiSearchAutoEvalPlural() {
    return this.AiSearchAutoEvalLabels.p;
  }

  get AiSearchAutoEvalGender() {
    return this.AiSearchAutoEvalLabels.g;
  }

  get groupAiSearchAutoEvalLabel() {
    return {
      es: {
        s: "Evaluación Automatizada AI",
        p: "Evaluaciones Automatizadas AI",
        g: "F",
      },
      en: { s: "AI Automated Evaluation", p: "AI Automated Evaluations" },
      pt: {
        s: "Avaliação Automatizada AI",
        p: "Avaliações Automatizadas AI",
        g: "F",
      },
    }[this.language].s;
  }

  AiSearchAutoEvalRunIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "ID de Corrida",
        p: "Identificador único de la corrida de evaluación",
        g: "M",
      },
      en: { s: "Run ID", p: "Unique identifier for the evaluation run" },
      pt: {
        s: "ID da Corrida",
        p: "Identificador único da corrida de avaliação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalRunIdLabel() {
    return this.AiSearchAutoEvalRunIdLabelByLang();
  }

  get AiSearchAutoEvalRunIdDescription() {
    return {
      es: { s: "Identificador de la corrida (ej: 2026-01-16-C1_schema)" },
      en: { s: "Run identifier (e.g., 2026-01-16-C1_schema)" },
      pt: { s: "Identificador da corrida (ex: 2026-01-16-C1_schema)" },
    }[this.language].s;
  }

  AiSearchAutoEvalQueryIndexLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Índice de Query",
        p: "Posición de la query en el dataset",
        g: "M",
      },
      en: { s: "Query Index", p: "Query position in the dataset" },
      pt: { s: "Índice de Query", p: "Posição da query no dataset", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalQueryIndexLabel() {
    return this.AiSearchAutoEvalQueryIndexLabelByLang();
  }

  get AiSearchAutoEvalQueryIndexDescription() {
    return {
      es: { s: "Índice de la query dentro del dataset de evaluación" },
      en: { s: "Query index within the evaluation dataset" },
      pt: { s: "Índice da query dentro do dataset de avaliação" },
    }[this.language].s;
  }

  AiSearchAutoEvalConditionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Condición", p: "Condición experimental (C0, C1, C2)", g: "F" },
      en: { s: "Condition", p: "Experimental condition (C0, C1, C2)" },
      pt: { s: "Condição", p: "Condição experimental (C0, C1, C2)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalConditionLabel() {
    return this.AiSearchAutoEvalConditionLabelByLang();
  }

  get AiSearchAutoEvalConditionDescription() {
    return {
      es: { s: "Condición experimental: C0_baseline, C1_schema, C2_full" },
      en: { s: "Experimental condition: C0_baseline, C1_schema, C2_full" },
      pt: { s: "Condição experimental: C0_baseline, C1_schema, C2_full" },
    }[this.language].s;
  }

  AiSearchAutoEvalIncludeSchemaLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Incluye Schema",
        p: "Si el prompt incluía el modelo de datos",
        g: "M",
      },
      en: {
        s: "Include Schema",
        p: "Whether the prompt included the data model",
      },
      pt: {
        s: "Inclui Schema",
        p: "Se o prompt incluía o modelo de dados",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalIncludeSchemaLabel() {
    return this.AiSearchAutoEvalIncludeSchemaLabelByLang();
  }

  AiSearchAutoEvalIncludeExamplesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Incluye Ejemplos",
        p: "Si el prompt incluía ejemplos BFS",
        g: "M",
      },
      en: {
        s: "Include Examples",
        p: "Whether the prompt included BFS examples",
      },
      pt: {
        s: "Inclui Exemplos",
        p: "Se o prompt incluía exemplos BFS",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalIncludeExamplesLabel() {
    return this.AiSearchAutoEvalIncludeExamplesLabelByLang();
  }

  AiSearchAutoEvalQueryLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Query", p: "Pregunta del usuario", g: "F" },
      en: { s: "Query", p: "User question" },
      pt: { s: "Query", p: "Pergunta do usuário", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalQueryLabel() {
    return this.AiSearchAutoEvalQueryLabelByLang();
  }

  get AiSearchAutoEvalQueryDescription() {
    return {
      es: { s: "La pregunta original enviada al sistema" },
      en: { s: "The original question sent to the system" },
      pt: { s: "A pergunta original enviada ao sistema" },
    }[this.language].s;
  }

  AiSearchAutoEvalPathTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Path",
        p: "Clasificación del patrón de navegación",
        g: "M",
      },
      en: { s: "Path Type", p: "Navigation pattern classification" },
      pt: {
        s: "Tipo de Path",
        p: "Classificação do padrão de navegação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalPathTypeLabel() {
    return this.AiSearchAutoEvalPathTypeLabelByLang();
  }

  get AiSearchAutoEvalPathTypeDescription() {
    return {
      es: {
        s: "Tipo de path: entity_only, direct_ref, direct_set, junction, etc.",
      },
      en: {
        s: "Path type: entity_only, direct_ref, direct_set, junction, etc.",
      },
      pt: {
        s: "Tipo de path: entity_only, direct_ref, direct_set, junction, etc.",
      },
    }[this.language].s;
  }

  AiSearchAutoEvalExpectedToolLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tool Esperado",
        p: "Herramienta que debería haberse llamado",
        g: "F",
      },
      en: { s: "Expected Tool", p: "Tool that should have been called" },
      pt: {
        s: "Tool Esperado",
        p: "Ferramenta que deveria ter sido chamada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalExpectedToolLabel() {
    return this.AiSearchAutoEvalExpectedToolLabelByLang();
  }

  AiSearchAutoEvalExpectedEntityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Entidad Esperada",
        p: "Entidad que debería haberse pasado",
        g: "F",
      },
      en: { s: "Expected Entity", p: "Entity that should have been passed" },
      pt: {
        s: "Entidade Esperada",
        p: "Entidade que deveria ter sido passada",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalExpectedEntityLabel() {
    return this.AiSearchAutoEvalExpectedEntityLabelByLang();
  }

  AiSearchAutoEvalActualToolLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tool Actual", p: "Herramienta que llamó el LLM", g: "F" },
      en: { s: "Actual Tool", p: "Tool that the LLM called" },
      pt: { s: "Tool Atual", p: "Ferramenta que o LLM chamou", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalActualToolLabel() {
    return this.AiSearchAutoEvalActualToolLabelByLang();
  }

  AiSearchAutoEvalActualEntityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Entidad Actual", p: "Entidad que pasó el LLM", g: "F" },
      en: { s: "Actual Entity", p: "Entity that the LLM passed" },
      pt: { s: "Entidade Atual", p: "Entidade que o LLM passou", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalActualEntityLabel() {
    return this.AiSearchAutoEvalActualEntityLabelByLang();
  }

  AiSearchAutoEvalActualArgsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Args Actuales",
        p: "Argumentos completos del tool call",
        g: "M",
      },
      en: { s: "Actual Args", p: "Complete tool call arguments" },
      pt: { s: "Args Atuais", p: "Argumentos completos do tool call" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalActualArgsLabel() {
    return this.AiSearchAutoEvalActualArgsLabelByLang();
  }

  AiSearchAutoEvalL1PassLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "L1 Pass", p: "¿Tool correcto?", g: "M" },
      en: { s: "L1 Pass", p: "Tool correct?" },
      pt: { s: "L1 Pass", p: "Tool correto?", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalL1PassLabel() {
    return this.AiSearchAutoEvalL1PassLabelByLang();
  }

  get AiSearchAutoEvalL1PassDescription() {
    return {
      es: { s: "Nivel 1: ¿El LLM llamó al tool correcto?" },
      en: { s: "Level 1: Did the LLM call the correct tool?" },
      pt: { s: "Nível 1: O LLM chamou o tool correto?" },
    }[this.language].s;
  }

  AiSearchAutoEvalL2PassLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "L2 Pass", p: "¿Entidad correcta?", g: "M" },
      en: { s: "L2 Pass", p: "Entity correct?" },
      pt: { s: "L2 Pass", p: "Entidade correta?", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalL2PassLabel() {
    return this.AiSearchAutoEvalL2PassLabelByLang();
  }

  get AiSearchAutoEvalL2PassDescription() {
    return {
      es: {
        s: "Nivel 2: ¿El LLM pasó la entidad correcta? (null si L1 falló)",
      },
      en: {
        s: "Level 2: Did the LLM pass the correct entity? (null if L1 failed)",
      },
      pt: {
        s: "Nível 2: O LLM passou a entidade correta? (null se L1 falhou)",
      },
    }[this.language].s;
  }

  AiSearchAutoEvalErrorTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Error", p: "Categoría del error", g: "M" },
      en: { s: "Error Type", p: "Error category" },
      pt: { s: "Tipo de Erro", p: "Categoria do erro", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalErrorTypeLabel() {
    return this.AiSearchAutoEvalErrorTypeLabelByLang();
  }

  get AiSearchAutoEvalErrorTypeDescription() {
    return {
      es: { s: "Tipo de error: wrong_tool, wrong_entity, no_tool_call, etc." },
      en: { s: "Error type: wrong_tool, wrong_entity, no_tool_call, etc." },
      pt: { s: "Tipo de erro: wrong_tool, wrong_entity, no_tool_call, etc." },
    }[this.language].s;
  }

  AiSearchAutoEvalTotalTimeMsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tiempo Total (ms)",
        p: "Tiempo total de procesamiento",
        g: "M",
      },
      en: { s: "Total Time (ms)", p: "Total processing time" },
      pt: { s: "Tempo Total (ms)", p: "Tempo total de processamento", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalTotalTimeMsLabel() {
    return this.AiSearchAutoEvalTotalTimeMsLabelByLang();
  }

  AiSearchAutoEvalLlmCallCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Llamadas LLM", p: "Número de llamadas al LLM", g: "F" },
      en: { s: "LLM Calls", p: "Number of LLM calls" },
      pt: { s: "Chamadas LLM", p: "Número de chamadas ao LLM", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalLlmCallCountLabel() {
    return this.AiSearchAutoEvalLlmCallCountLabelByLang();
  }

  AiSearchAutoEvalToolCallCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tool Calls", p: "Número de tool calls", g: "F" },
      en: { s: "Tool Calls", p: "Number of tool calls" },
      pt: { s: "Tool Calls", p: "Número de tool calls", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalToolCallCountLabel() {
    return this.AiSearchAutoEvalToolCallCountLabelByLang();
  }

  AiSearchAutoEvalTokenCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tokens", p: "Tokens consumidos", g: "M" },
      en: { s: "Tokens", p: "Tokens consumed" },
      pt: { s: "Tokens", p: "Tokens consumidos", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalTokenCountLabel() {
    return this.AiSearchAutoEvalTokenCountLabelByLang();
  }

  AiSearchAutoEvalLlmResponseLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Respuesta LLM", p: "Respuesta completa del LLM", g: "F" },
      en: { s: "LLM Response", p: "Complete LLM response" },
      pt: { s: "Resposta LLM", p: "Resposta completa do LLM", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalLlmResponseLabel() {
    return this.AiSearchAutoEvalLlmResponseLabelByLang();
  }

  AiSearchAutoEvalToolCallsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tool Calls", p: "Array de tool calls ejecutados", g: "F" },
      en: { s: "Tool Calls", p: "Array of executed tool calls" },
      pt: { s: "Tool Calls", p: "Array de tool calls executados", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalToolCallsLabel() {
    return this.AiSearchAutoEvalToolCallsLabelByLang();
  }

  AiSearchAutoEvalEvaluatedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Evaluado", p: "Fecha y hora de evaluación", g: "M" },
      en: { s: "Evaluated At", p: "Evaluation date and time" },
      pt: { s: "Avaliado", p: "Data e hora de avaliação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalEvaluatedAtLabel() {
    return this.AiSearchAutoEvalEvaluatedAtLabelByLang();
  }

  AiSearchAutoEvalHadDisambiguationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tuvo Desambiguación",
        p: "¿El clasificador detectó ambigüedad?",
        g: "M",
      },
      en: {
        s: "Had Disambiguation",
        p: "Did the classifier detect ambiguity?",
      },
      pt: {
        s: "Teve Desambiguação",
        p: "O classificador detectou ambiguidade?",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalHadDisambiguationLabel() {
    return this.AiSearchAutoEvalHadDisambiguationLabelByLang();
  }

  AiSearchAutoEvalDisambiguationTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Desambiguación",
        p: "entity_ambiguity, scope_ambiguity, etc.",
        g: "M",
      },
      en: {
        s: "Disambiguation Type",
        p: "entity_ambiguity, scope_ambiguity, etc.",
      },
      pt: {
        s: "Tipo de Desambiguação",
        p: "entity_ambiguity, scope_ambiguity, etc.",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalDisambiguationTypeLabel() {
    return this.AiSearchAutoEvalDisambiguationTypeLabelByLang();
  }

  AiSearchAutoEvalDisambiguationResponseLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Respuesta Desambiguación",
        p: "Respuesta del usuario a la desambiguación",
        g: "F",
      },
      en: {
        s: "Disambiguation Response",
        p: "User response to disambiguation",
      },
      pt: {
        s: "Resposta Desambiguação",
        p: "Resposta do usuário à desambiguação",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalDisambiguationResponseLabel() {
    return this.AiSearchAutoEvalDisambiguationResponseLabelByLang();
  }

  AiSearchAutoEvalPatternsAppliedLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Patrones Aplicados",
        p: "IDs de patrones que estaban activos",
        g: "M",
      },
      en: { s: "Patterns Applied", p: "IDs of patterns that were active" },
      pt: {
        s: "Padrões Aplicados",
        p: "IDs de padrões que estavam ativos",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalPatternsAppliedLabel() {
    return this.AiSearchAutoEvalPatternsAppliedLabelByLang();
  }

  AiSearchAutoEvalL3EfficiencyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "L3: Eficiencia", p: "Puntuación de eficiencia (1-5)", g: "F" },
      en: { s: "L3: Efficiency", p: "Efficiency score (1-5)" },
      pt: { s: "L3: Eficiência", p: "Pontuação de eficiência (1-5)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalL3EfficiencyLabel() {
    return this.AiSearchAutoEvalL3EfficiencyLabelByLang();
  }

  get AiSearchAutoEvalL3EfficiencyDescription() {
    return {
      es: {
        s: "¿Cuántas llamadas hizo vs. óptimo? 5=óptimo, 1=muy ineficiente",
      },
      en: { s: "How many calls vs optimal? 5=optimal, 1=very inefficient" },
      pt: { s: "Quantas chamadas fez vs. ótimo? 5=ótimo, 1=muito ineficiente" },
    }[this.language].s;
  }

  AiSearchAutoEvalL4RedundancyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "L4: Redundancia",
        p: "Puntuación de redundancia (1-5)",
        g: "F",
      },
      en: { s: "L4: Redundancy", p: "Redundancy score (1-5)" },
      pt: { s: "L4: Redundância", p: "Pontuação de redundância (1-5)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalL4RedundancyLabel() {
    return this.AiSearchAutoEvalL4RedundancyLabelByLang();
  }

  get AiSearchAutoEvalL4RedundancyDescription() {
    return {
      es: { s: "¿Repitió queries? 5=ninguna redundancia, 1=muy redundante" },
      en: { s: "Did it repeat queries? 5=no redundancy, 1=very redundant" },
      pt: { s: "Repetiu queries? 5=nenhuma redundância, 1=muito redundante" },
    }[this.language].s;
  }

  AiSearchAutoEvalL5DisambiguationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "L5: Desambiguación",
        p: "Puntuación de desambiguación (1-5)",
        g: "F",
      },
      en: { s: "L5: Disambiguation", p: "Disambiguation score (1-5)" },
      pt: {
        s: "L5: Desambiguação",
        p: "Pontuação de desambiguação (1-5)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalL5DisambiguationLabel() {
    return this.AiSearchAutoEvalL5DisambiguationLabelByLang();
  }

  get AiSearchAutoEvalL5DisambiguationDescription() {
    return {
      es: {
        s: "¿Desambiguó correctamente? 5=perfecto, 1=desambiguación innecesaria o faltante",
      },
      en: {
        s: "Did it disambiguate correctly? 5=perfect, 1=unnecessary or missing disambiguation",
      },
      pt: {
        s: "Desambiguou corretamente? 5=perfeito, 1=desambiguação desnecessária ou faltante",
      },
    }[this.language].s;
  }

  AiSearchAutoEvalL6ResponseQualityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "L6: Calidad Respuesta",
        p: "Puntuación de calidad de respuesta (1-5)",
        g: "F",
      },
      en: { s: "L6: Response Quality", p: "Response quality score (1-5)" },
      pt: {
        s: "L6: Qualidade Resposta",
        p: "Pontuação de qualidade da resposta (1-5)",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalL6ResponseQualityLabel() {
    return this.AiSearchAutoEvalL6ResponseQualityLabelByLang();
  }

  get AiSearchAutoEvalL6ResponseQualityDescription() {
    return {
      es: {
        s: "¿La respuesta fue correcta y completa? 5=perfecta, 1=incorrecta",
      },
      en: {
        s: "Was the response correct and complete? 5=perfect, 1=incorrect",
      },
      pt: { s: "A resposta foi correta e completa? 5=perfeita, 1=incorreta" },
    }[this.language].s;
  }

  AiSearchAutoEvalOverallScoreLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Puntuación Global", p: "Puntuación global (1-5)", g: "F" },
      en: { s: "Overall Score", p: "Overall score (1-5)" },
      pt: { s: "Pontuação Global", p: "Pontuação global (1-5)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalOverallScoreLabel() {
    return this.AiSearchAutoEvalOverallScoreLabelByLang();
  }

  AiSearchAutoEvalJudgeEvaluationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Evaluación Judge",
        p: "Respuesta completa del LLM-as-Judge",
        g: "F",
      },
      en: { s: "Judge Evaluation", p: "Complete LLM-as-Judge response" },
      pt: {
        s: "Avaliação Judge",
        p: "Resposta completa do LLM-as-Judge",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalJudgeEvaluationLabel() {
    return this.AiSearchAutoEvalJudgeEvaluationLabelByLang();
  }

  AiSearchAutoEvalRedundantCallsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Llamadas Redundantes",
        p: "Número de llamadas redundantes detectadas",
        g: "F",
      },
      en: { s: "Redundant Calls", p: "Number of redundant calls detected" },
      pt: {
        s: "Chamadas Redundantes",
        p: "Número de chamadas redundantes detectadas",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalRedundantCallsLabel() {
    return this.AiSearchAutoEvalRedundantCallsLabelByLang();
  }

  AiSearchAutoEvalActualCallCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Llamadas Actuales",
        p: "Número total de llamadas API realizadas",
        g: "F",
      },
      en: { s: "Actual Call Count", p: "Total number of API calls made" },
      pt: {
        s: "Chamadas Atuais",
        p: "Número total de chamadas API realizadas",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalActualCallCountLabel() {
    return this.AiSearchAutoEvalActualCallCountLabelByLang();
  }

  AiSearchAutoEvalOptimalCallCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Llamadas Óptimas",
        p: "Número óptimo de llamadas según dataset",
        g: "F",
      },
      en: {
        s: "Optimal Call Count",
        p: "Optimal number of calls according to dataset",
      },
      pt: {
        s: "Chamadas Ótimas",
        p: "Número ótimo de chamadas segundo dataset",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalOptimalCallCountLabel() {
    return this.AiSearchAutoEvalOptimalCallCountLabelByLang();
  }

  AiSearchAutoEvalSequenceMatchedOptimalLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Secuencia Óptima",
        p: "¿Siguió la secuencia óptima de llamadas?",
        g: "F",
      },
      en: {
        s: "Optimal Sequence",
        p: "Did it follow the optimal call sequence?",
      },
      pt: {
        s: "Sequência Ótima",
        p: "Seguiu a sequência ótima de chamadas?",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalSequenceMatchedOptimalLabel() {
    return this.AiSearchAutoEvalSequenceMatchedOptimalLabelByLang();
  }

  get AiSearchAutoEvalSequenceMatchedOptimalDescription() {
    return {
      es: {
        s: "Campo diagnóstico: true si las llamadas siguieron el orden óptimo esperado",
      },
      en: {
        s: "Diagnostic field: true if calls followed the expected optimal order",
      },
      pt: {
        s: "Campo diagnóstico: true se as chamadas seguiram a ordem ótima esperada",
      },
    }[this.language].s;
  }

  AiSearchAutoEvalResponseLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Respuesta", p: "Respuesta final del asistente", g: "F" },
      en: { s: "Response", p: "Final assistant response" },
      pt: { s: "Resposta", p: "Resposta final do assistente", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchAutoEvalResponseLabel() {
    return this.AiSearchAutoEvalResponseLabelByLang();
  }

  // AiSearchPattern

  get AiSearchPatternLabels() {
    return {
      es: { s: "Patrón AI Search", p: "Patrones AI Search", g: "M" },
      en: { s: "AI Search Pattern", p: "AI Search Patterns", g: undefined },
      pt: { s: "Padrão AI Search", p: "Padrões AI Search", g: "M" },
    }[this.language];
  }

  get AiSearchPatternSingular() {
    return this.AiSearchPatternLabels.s;
  }

  get AiSearchPatternPlural() {
    return this.AiSearchPatternLabels.p;
  }

  get AiSearchPatternGender() {
    return this.AiSearchPatternLabels.g;
  }

  get groupAiSearchPatternLabel() {
    return {
      es: { s: "Patrón AI Search", p: "Patrones AI Search", g: "M" },
      en: { s: "AI Search Pattern", p: "AI Search Patterns" },
      pt: { s: "Padrão AI Search", p: "Padrões AI Search", g: "M" },
    }[this.language].s;
  }

  AiSearchPatternNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Nombre corto del patrón", g: "M" },
      en: { s: "Name", p: "Short pattern name" },
      pt: { s: "Nome", p: "Nome curto do padrão", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternNameLabel() {
    return this.AiSearchPatternNameLabelByLang();
  }

  get AiSearchPatternNameDescription() {
    return {
      es: {
        s: "Identificador único: inverse_navigation, count_vs_query, etc.",
      },
      en: { s: "Unique identifier: inverse_navigation, count_vs_query, etc." },
      pt: {
        s: "Identificador único: inverse_navigation, count_vs_query, etc.",
      },
    }[this.language].s;
  }

  AiSearchPatternDescriptionLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Descripción", p: "Descripción del patrón", g: "F" },
      en: { s: "Description", p: "Pattern description" },
      pt: { s: "Descrição", p: "Descrição do padrão", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternDescriptionLabel() {
    return this.AiSearchPatternDescriptionLabelByLang();
  }

  AiSearchPatternPatternLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Patrón", p: "El texto que se inyecta al prompt", g: "M" },
      en: { s: "Pattern", p: "The text injected into the prompt" },
      pt: { s: "Padrão", p: "O texto que é injetado no prompt", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternPatternLabel() {
    return this.AiSearchPatternPatternLabelByLang();
  }

  get AiSearchPatternPatternDescription() {
    return {
      es: { s: "Texto markdown que se agrega al system prompt del LLM" },
      en: { s: "Markdown text added to the LLM system prompt" },
      pt: { s: "Texto markdown que é adicionado ao system prompt do LLM" },
    }[this.language].s;
  }

  AiSearchPatternPathTypesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Path Types", p: "A qué tipos de path aplica", g: "M" },
      en: { s: "Path Types", p: "Which path types it applies to" },
      pt: { s: "Path Types", p: "A quais tipos de path se aplica", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternPathTypesLabel() {
    return this.AiSearchPatternPathTypesLabelByLang();
  }

  get AiSearchPatternPathTypesDescription() {
    return {
      es: {
        s: "inverse_ref, junction, entity_only, etc. Vacío = aplica a todos",
      },
      en: {
        s: "inverse_ref, junction, entity_only, etc. Empty = applies to all",
      },
      pt: {
        s: "inverse_ref, junction, entity_only, etc. Vazio = aplica-se a todos",
      },
    }[this.language].s;
  }

  AiSearchPatternToolsLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tools", p: "A qué herramientas aplica", g: "F" },
      en: { s: "Tools", p: "Which tools it applies to" },
      pt: { s: "Tools", p: "A quais ferramentas se aplica", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternToolsLabel() {
    return this.AiSearchPatternToolsLabelByLang();
  }

  get AiSearchPatternToolsDescription() {
    return {
      es: { s: "query_entities, count_entities, etc. Vacío = aplica a todos" },
      en: { s: "query_entities, count_entities, etc. Empty = applies to all" },
      pt: {
        s: "query_entities, count_entities, etc. Vazio = aplica-se a todos",
      },
    }[this.language].s;
  }

  AiSearchPatternEffectivenessLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Efectividad", p: "% de mejora observada", g: "F" },
      en: { s: "Effectiveness", p: "% improvement observed" },
      pt: { s: "Efetividade", p: "% de melhora observada", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternEffectivenessLabel() {
    return this.AiSearchPatternEffectivenessLabelByLang();
  }

  AiSearchPatternEvaluationCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Evaluaciones", p: "Cuántas veces se ha evaluado", g: "M" },
      en: { s: "Evaluations", p: "How many times evaluated" },
      pt: { s: "Avaliações", p: "Quantas vezes foi avaliado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternEvaluationCountLabel() {
    return this.AiSearchPatternEvaluationCountLabelByLang();
  }

  AiSearchPatternSuccessCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Éxitos", p: "Cuántas veces mejoró el resultado", g: "M" },
      en: { s: "Successes", p: "How many times it improved results" },
      pt: { s: "Sucessos", p: "Quantas vezes melhorou o resultado", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternSuccessCountLabel() {
    return this.AiSearchPatternSuccessCountLabelByLang();
  }

  AiSearchPatternSourceEvalIdsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "IDs de Evaluaciones",
        p: "IDs de evaluaciones que motivaron este patrón",
        g: "M",
      },
      en: {
        s: "Evaluation IDs",
        p: "IDs of evaluations that motivated this pattern",
      },
      pt: {
        s: "IDs de Avaliações",
        p: "IDs de avaliações que motivaram este padrão",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternSourceEvalIdsLabel() {
    return this.AiSearchPatternSourceEvalIdsLabelByLang();
  }

  AiSearchPatternProposedByLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Propuesto Por", p: "claude o human", g: "M" },
      en: { s: "Proposed By", p: "claude or human" },
      pt: { s: "Proposto Por", p: "claude ou human", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternProposedByLabel() {
    return this.AiSearchPatternProposedByLabelByLang();
  }

  AiSearchPatternActiveLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Activo", p: "Si el patrón está activo", g: "M" },
      en: { s: "Active", p: "If the pattern is active" },
      pt: { s: "Ativo", p: "Se o padrão está ativo", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternActiveLabel() {
    return this.AiSearchPatternActiveLabelByLang();
  }

  AiSearchPatternPriorityLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Prioridad", p: "Orden en el prompt (menor = primero)", g: "F" },
      en: { s: "Priority", p: "Order in prompt (lower = first)" },
      pt: { s: "Prioridade", p: "Ordem no prompt (menor = primeiro)", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get AiSearchPatternPriorityLabel() {
    return this.AiSearchPatternPriorityLabelByLang();
  }

  // EvaluationQuery

  get EvaluationQueryLabels() {
    return {
      es: { s: "Query de Evaluación", p: "Queries de Evaluación", g: "F" },
      en: { s: "Evaluation Query", p: "Evaluation Queries", g: undefined },
      pt: { s: "Query de Avaliação", p: "Queries de Avaliação", g: "F" },
    }[this.language];
  }

  get EvaluationQuerySingular() {
    return this.EvaluationQueryLabels.s;
  }

  get EvaluationQueryPlural() {
    return this.EvaluationQueryLabels.p;
  }

  get EvaluationQueryGender() {
    return this.EvaluationQueryLabels.g;
  }

  get groupEvaluationQueryLabel() {
    return {
      es: { s: "Query de Evaluación", p: "Queries de Evaluación", g: "F" },
      en: { s: "Evaluation Query", p: "Evaluation Queries" },
      pt: { s: "Query de Avaliação", p: "Queries de Avaliação", g: "F" },
    }[this.language].s;
  }

  EvaluationQueryQueryLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Query", p: "Pregunta en lenguaje natural", g: "F" },
      en: { s: "Query", p: "Natural language question" },
      pt: { s: "Query", p: "Pergunta em linguagem natural", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryQueryLabel() {
    return this.EvaluationQueryQueryLabelByLang();
  }

  get EvaluationQueryQueryDescription() {
    return {
      es: { s: "La pregunta que el usuario haría al sistema" },
      en: { s: "The question a user would ask the system" },
      pt: { s: "A pergunta que o usuário faria ao sistema" },
    }[this.language].s;
  }

  EvaluationQueryPathTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Path",
        p: "Clasificación del tipo de navegación",
        g: "M",
      },
      en: { s: "Path Type", p: "Navigation type classification" },
      pt: {
        s: "Tipo de Path",
        p: "Classificação do tipo de navegação",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryPathTypeLabel() {
    return this.EvaluationQueryPathTypeLabelByLang();
  }

  EvaluationQueryDifficultyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Dificultad", p: "Nivel de dificultad del query" },
      en: { s: "Difficulty", p: "Query difficulty level" },
      pt: { s: "Dificuldade", p: "Nível de dificuldade do query", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryDifficultyLabel() {
    return this.EvaluationQueryDifficultyLabelByLang();
  }

  EvaluationQueryExpectedToolLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tool Esperado",
        p: "Herramienta que debería seleccionar el LLM",
        g: "F",
      },
      en: { s: "Expected Tool", p: "Tool the LLM should select" },
      pt: {
        s: "Tool Esperado",
        p: "Ferramenta que o LLM deveria selecionar",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryExpectedToolLabel() {
    return this.EvaluationQueryExpectedToolLabelByLang();
  }

  EvaluationQueryExpectedArgsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Args Esperados",
        p: "Argumentos correctos para el tool",
        g: "M",
      },
      en: { s: "Expected Args", p: "Correct arguments for the tool" },
      pt: { s: "Args Esperados", p: "Argumentos corretos para o tool", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryExpectedArgsLabel() {
    return this.EvaluationQueryExpectedArgsLabelByLang();
  }

  EvaluationQueryAcceptableToolsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tools Aceptables",
        p: "Lista de tools que se consideran correctos",
        g: "F",
      },
      en: { s: "Acceptable Tools", p: "List of tools considered correct" },
      pt: {
        s: "Tools Aceitáveis",
        p: "Lista de tools considerados corretos",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryAcceptableToolsLabel() {
    return this.EvaluationQueryAcceptableToolsLabelByLang();
  }

  EvaluationQueryExpectedResponsePatternLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Patrón de Respuesta",
        p: "Descripción de lo que debería responder",
        g: "M",
      },
      en: { s: "Response Pattern", p: "Description of expected response" },
      pt: {
        s: "Padrão de Resposta",
        p: "Descrição do que deveria responder",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryExpectedResponsePatternLabel() {
    return this.EvaluationQueryExpectedResponsePatternLabelByLang();
  }

  EvaluationQueryGeneratedFromLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Generado Desde",
        p: "Metadata de generación (entity, field, template)",
        g: "M",
      },
      en: {
        s: "Generated From",
        p: "Generation metadata (entity, field, template)",
      },
      pt: {
        s: "Gerado De",
        p: "Metadata de geração (entity, field, template)",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryGeneratedFromLabel() {
    return this.EvaluationQueryGeneratedFromLabelByLang();
  }

  EvaluationQueryIsAmbiguousLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Es Ambiguo",
        p: "Si el query es intencionalmente ambiguo",
        g: "M",
      },
      en: {
        s: "Is Ambiguous",
        p: "Whether the query is intentionally ambiguous",
      },
      pt: {
        s: "É Ambíguo",
        p: "Se o query é intencionalmente ambíguo",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryIsAmbiguousLabel() {
    return this.EvaluationQueryIsAmbiguousLabelByLang();
  }

  EvaluationQueryAmbiguityTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Tipo de Ambigüedad",
        p: "Clasificación de la ambigüedad",
        g: "M",
      },
      en: { s: "Ambiguity Type", p: "Ambiguity classification" },
      pt: {
        s: "Tipo de Ambiguidade",
        p: "Classificação da ambiguidade",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryAmbiguityTypeLabel() {
    return this.EvaluationQueryAmbiguityTypeLabelByLang();
  }

  EvaluationQueryDisambiguationResponseLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Respuesta de Desambiguación",
        p: "Lo que el usuario respondería para aclarar",
        g: "F",
      },
      en: {
        s: "Disambiguation Response",
        p: "What the user would respond to clarify",
      },
      pt: {
        s: "Resposta de Desambiguação",
        p: "O que o usuário responderia para esclarecer",
        g: "F",
      },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryDisambiguationResponseLabel() {
    return this.EvaluationQueryDisambiguationResponseLabelByLang();
  }

  EvaluationQueryNotesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Notas", p: "Observaciones adicionales", g: "F" },
      en: { s: "Notes", p: "Additional observations" },
      pt: { s: "Notas", p: "Observações adicionais", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get EvaluationQueryNotesLabel() {
    return this.EvaluationQueryNotesLabelByLang();
  }

  // QueryPattern

  get QueryPatternLabels() {
    return {
      es: { s: "Patrón de Query", p: "Patrones de Query", g: "M" },
      en: { s: "Query Pattern", p: "Query Patterns", g: undefined },
      pt: { s: "Padrão de Query", p: "Padrões de Query", g: "M" },
    }[this.language];
  }

  get QueryPatternSingular() {
    return this.QueryPatternLabels.s;
  }

  get QueryPatternPlural() {
    return this.QueryPatternLabels.p;
  }

  get QueryPatternGender() {
    return this.QueryPatternLabels.g;
  }

  get groupQueryPatternLabel() {
    return {
      es: { s: "Patrón de Query", p: "Patrones de Query", g: "M" },
      en: { s: "Query Pattern", p: "Query Patterns" },
      pt: { s: "Padrão de Query", p: "Padrões de Query", g: "M" },
    }[this.language].s;
  }

  QueryPatternNameLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Nombre", p: "Identificador único del patrón", g: "M" },
      en: { s: "Name", p: "Unique pattern identifier" },
      pt: { s: "Nome", p: "Identificador único do padrão", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternNameLabel() {
    return this.QueryPatternNameLabelByLang();
  }

  get QueryPatternNameDescription() {
    return {
      es: {
        s: "Nombre corto y descriptivo, ej: 'count_by_ref', 'list_with_filter'",
      },
      en: {
        s: "Short descriptive name, e.g.: 'count_by_ref', 'list_with_filter'",
      },
      pt: {
        s: "Nome curto e descritivo, ex: 'count_by_ref', 'list_with_filter'",
      },
    }[this.language].s;
  }

  QueryPatternPatternLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Patrón", p: "Template con variables", g: "M" },
      en: { s: "Pattern", p: "Template with variables" },
      pt: { s: "Padrão", p: "Template com variáveis", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternPatternLabel() {
    return this.QueryPatternPatternLabelByLang();
  }

  get QueryPatternPatternDescription() {
    return {
      es: {
        s: "Plantilla con variables: {entity.plural}, {ref.singular}, {g:os/as} para género. Ej: '¿Cuánt{g:os/as} {entity.plural} hay de {ref.value}?'",
      },
      en: {
        s: "Template with variables: {entity.plural}, {ref.singular}, {g:os/as} for gender. E.g.: 'How many {entity.plural} are from {ref.value}?'",
      },
      pt: {
        s: "Template com variáveis: {entity.plural}, {ref.singular}, {g:os/as} para gênero. Ex: 'Quantos {entity.plural} são de {ref.value}?'",
      },
    }[this.language].s;
  }

  QueryPatternPathTypeLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Tipo de Path", p: "Clasificación según navegación", g: "M" },
      en: { s: "Path Type", p: "Classification by navigation" },
      pt: { s: "Tipo de Path", p: "Classificação por navegação", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternPathTypeLabel() {
    return this.QueryPatternPathTypeLabelByLang();
  }

  QueryPatternVariablesLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Variables", p: "Definición de variables del patrón", g: "M" },
      en: { s: "Variables", p: "Pattern variable definitions" },
      pt: { s: "Variáveis", p: "Definições de variáveis do padrão" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternVariablesLabel() {
    return this.QueryPatternVariablesLabelByLang();
  }

  get QueryPatternVariablesDescription() {
    return {
      es: {
        s: "Array de {name, type, constraints}. Ej: [{name:'entity', type:'entity'}, {name:'ref', type:'ref_field', constraints:{targetCacheable:true}}]",
      },
      en: {
        s: "Array of {name, type, constraints}. E.g.: [{name:'entity', type:'entity'}, {name:'ref', type:'ref_field', constraints:{targetCacheable:true}}]",
      },
      pt: {
        s: "Array de {name, type, constraints}. Ex: [{name:'entity', type:'entity'}, {name:'ref', type:'ref_field', constraints:{targetCacheable:true}}]",
      },
    }[this.language].s;
  }

  QueryPatternToolTemplateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Template de Tool",
        p: "Herramienta esperada con placeholders",
        g: "M",
      },
      en: { s: "Tool Template", p: "Expected tool with placeholders" },
      pt: {
        s: "Template de Tool",
        p: "Ferramenta esperada com placeholders",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternToolTemplateLabel() {
    return this.QueryPatternToolTemplateLabelByLang();
  }

  get QueryPatternToolTemplateDescription() {
    return {
      es: { s: "Ej: 'count_entities' o 'query_entities'" },
      en: { s: "E.g.: 'count_entities' or 'query_entities'" },
      pt: { s: "Ex: 'count_entities' ou 'query_entities'" },
    }[this.language].s;
  }

  QueryPatternArgsTemplateLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Template de Args",
        p: "Argumentos esperados con placeholders",
        g: "M",
      },
      en: { s: "Args Template", p: "Expected arguments with placeholders" },
      pt: {
        s: "Template de Args",
        p: "Argumentos esperados com placeholders",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternArgsTemplateLabel() {
    return this.QueryPatternArgsTemplateLabelByLang();
  }

  get QueryPatternArgsTemplateDescription() {
    return {
      es: { s: "Ej: {entity: '{entity.name}', groupBy: '{ref.name}'}" },
      en: { s: "E.g.: {entity: '{entity.name}', groupBy: '{ref.name}'}" },
      pt: { s: "Ex: {entity: '{entity.name}', groupBy: '{ref.name}'}" },
    }[this.language].s;
  }

  QueryPatternVariationsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Variaciones",
        p: "Formas alternativas de expresar el mismo patrón",
        g: "M",
      },
      en: {
        s: "Variations",
        p: "Alternative ways to express the same pattern",
      },
      pt: {
        s: "Variações",
        p: "Formas alternativas de expressar o mesmo padrão",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternVariationsLabel() {
    return this.QueryPatternVariationsLabelByLang();
  }

  get QueryPatternVariationsDescription() {
    return {
      es: {
        s: "Array de strings con patrones alternativos que significan lo mismo",
      },
      en: {
        s: "Array of strings with alternative patterns meaning the same thing",
      },
      pt: {
        s: "Array de strings com padrões alternativos que significam o mesmo",
      },
    }[this.language].s;
  }

  QueryPatternExampleInstantiationLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Ejemplo", p: "Instanciación concreta del patrón", g: "M" },
      en: { s: "Example", p: "Concrete pattern instantiation" },
      pt: { s: "Exemplo", p: "Instanciação concreta do padrão", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternExampleInstantiationLabel() {
    return this.QueryPatternExampleInstantiationLabelByLang();
  }

  get QueryPatternExampleInstantiationDescription() {
    return {
      es: {
        s: "Un ejemplo real para entender el patrón, ej: '¿Cuántos autores hay de Argentina?'",
      },
      en: {
        s: "A real example to understand the pattern, e.g.: 'How many authors are from Argentina?'",
      },
      pt: {
        s: "Um exemplo real para entender o padrão, ex: 'Quantos autores são da Argentina?'",
      },
    }[this.language].s;
  }

  QueryPatternSourceLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Origen", p: "Quién creó este patrón", g: "M" },
      en: { s: "Source", p: "Who created this pattern" },
      pt: { s: "Origem", p: "Quem criou este padrão" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternSourceLabel() {
    return this.QueryPatternSourceLabelByLang();
  }

  QueryPatternQualityScoreLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Calidad", p: "Puntuación de calidad 1-5", g: "F" },
      en: { s: "Quality", p: "Quality score 1-5" },
      pt: { s: "Qualidade", p: "Pontuação de qualidade 1-5", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternQualityScoreLabel() {
    return this.QueryPatternQualityScoreLabelByLang();
  }

  get QueryPatternQualityScoreDescription() {
    return {
      es: {
        s: "1=bajo, 5=excelente. Patrones humanos empiezan en 4, LLM en 3, auto en 2",
      },
      en: {
        s: "1=low, 5=excellent. Human patterns start at 4, LLM at 3, auto at 2",
      },
      pt: {
        s: "1=baixo, 5=excelente. Padrões humanos começam em 4, LLM em 3, auto em 2",
      },
    }[this.language].s;
  }

  QueryPatternUsageCountLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: {
        s: "Usos",
        p: "Cantidad de queries generados con este patrón",
        g: "M",
      },
      en: { s: "Uses", p: "Number of queries generated with this pattern" },
      pt: {
        s: "Usos",
        p: "Quantidade de queries gerados com este padrão",
        g: "M",
      },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternUsageCountLabel() {
    return this.QueryPatternUsageCountLabelByLang();
  }

  QueryPatternIsActiveLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: {
        s: "Activo",
        p: "Si el patrón está habilitado para generación",
        g: "M",
      },
      en: { s: "Active", p: "Whether the pattern is enabled for generation" },
      pt: { s: "Ativo", p: "Se o padrão está habilitado para geração", g: "M" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternIsActiveLabel() {
    return this.QueryPatternIsActiveLabelByLang();
  }

  QueryPatternNotesLabelByLang(lang: LanguageCodes | undefined = undefined) {
    return {
      es: { s: "Notas", p: "Observaciones sobre el patrón", g: "F" },
      en: { s: "Notes", p: "Pattern observations" },
      pt: { s: "Notas", p: "Observações sobre o padrão", g: "F" },
    }[lang ? lang : this.language].s;
  }

  get QueryPatternNotesLabel() {
    return this.QueryPatternNotesLabelByLang();
  }

  // EsignatureConfig

  get EsignatureConfigLabels() {
    return {
      es: {
        s: "Configuracion de Firma",
        p: "Configuraciones de Firma",
        g: undefined,
      },
      en: { s: "E-Signature Config", p: "E-Signature Configs", g: undefined },
      pt: {
        s: "Configuracao de Assinatura",
        p: "Configuracoes de Assinatura",
        g: undefined,
      },
    }[this.language];
  }

  get EsignatureConfigSingular() {
    return this.EsignatureConfigLabels.s;
  }

  get EsignatureConfigPlural() {
    return this.EsignatureConfigLabels.p;
  }

  get EsignatureConfigGender() {
    return this.EsignatureConfigLabels.g;
  }

  get groupEsignatureConfigLabel() {
    return {
      es: { s: "Configuracion de Firma", p: "Configuraciones de Firma" },
      en: { s: "E-Signature Config", p: "E-Signature Configs" },
      pt: { s: "Configuracao de Assinatura", p: "Configuracoes de Assinatura" },
    }[this.language].s;
  }

  EsignatureConfigProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor" },
      en: { s: "Provider" },
      pt: { s: "Provedor" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureConfigProviderLabel() {
    return this.EsignatureConfigProviderLabelByLang();
  }

  EsignatureConfigCredentialsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Credenciales" },
      en: { s: "Credentials" },
      pt: { s: "Credenciais" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureConfigCredentialsLabel() {
    return this.EsignatureConfigCredentialsLabelByLang();
  }

  get EsignatureConfigCredentialsDescription() {
    return {
      es: {
        s: "JSON con las credenciales del proveedor. AgilSign: {apiKey, baseUrl}. DocuSign: {integrationKey, userId, accountId, rsaPrivateKey}. PandaDoc: {apiKey}. SignNow: {clientId, clientSecret}.",
      },
      en: {
        s: "JSON with provider credentials. AgilSign: {apiKey, baseUrl}. DocuSign: {integrationKey, userId, accountId, rsaPrivateKey}. PandaDoc: {apiKey}. SignNow: {clientId, clientSecret}.",
      },
      pt: {
        s: "JSON com as credenciais do provedor. AgilSign: {apiKey, baseUrl}. DocuSign: {integrationKey, userId, accountId, rsaPrivateKey}. PandaDoc: {apiKey}. SignNow: {clientId, clientSecret}.",
      },
    }[this.language].s;
  }

  EsignatureConfigSandboxModeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Modo Sandbox" },
      en: { s: "Sandbox Mode" },
      pt: { s: "Modo Sandbox" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureConfigSandboxModeLabel() {
    return this.EsignatureConfigSandboxModeLabelByLang();
  }

  EsignatureConfigWebhookSecretLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Secreto Webhook" },
      en: { s: "Webhook Secret" },
      pt: { s: "Segredo Webhook" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureConfigWebhookSecretLabel() {
    return this.EsignatureConfigWebhookSecretLabelByLang();
  }

  EsignatureConfigEnabledLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Habilitado" },
      en: { s: "Enabled" },
      pt: { s: "Habilitado" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureConfigEnabledLabel() {
    return this.EsignatureConfigEnabledLabelByLang();
  }

  // EsignatureDocument

  get EsignatureDocumentLabels() {
    return {
      es: { s: "Documento de Firma", p: "Documentos de Firma", g: undefined },
      en: {
        s: "E-Signature Document",
        p: "E-Signature Documents",
        g: undefined,
      },
      pt: {
        s: "Documento de Assinatura",
        p: "Documentos de Assinatura",
        g: undefined,
      },
    }[this.language];
  }

  get EsignatureDocumentSingular() {
    return this.EsignatureDocumentLabels.s;
  }

  get EsignatureDocumentPlural() {
    return this.EsignatureDocumentLabels.p;
  }

  get EsignatureDocumentGender() {
    return this.EsignatureDocumentLabels.g;
  }

  get groupEsignatureDocumentLabel() {
    return {
      es: { s: "Documento de Firma", p: "Documentos de Firma" },
      en: { s: "E-Signature Document", p: "E-Signature Documents" },
      pt: { s: "Documento de Assinatura", p: "Documentos de Assinatura" },
    }[this.language].s;
  }

  EsignatureDocumentExternalIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID Externo" },
      en: { s: "External ID" },
      pt: { s: "ID Externo" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentExternalIdLabel() {
    return this.EsignatureDocumentExternalIdLabelByLang();
  }

  EsignatureDocumentProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor" },
      en: { s: "Provider" },
      pt: { s: "Provedor" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentProviderLabel() {
    return this.EsignatureDocumentProviderLabelByLang();
  }

  EsignatureDocumentNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Nombre" }, en: { s: "Name" }, pt: { s: "Nome" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureDocumentNameLabel() {
    return this.EsignatureDocumentNameLabelByLang();
  }

  EsignatureDocumentStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Estado" }, en: { s: "Status" }, pt: { s: "Status" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureDocumentStatusLabel() {
    return this.EsignatureDocumentStatusLabelByLang();
  }

  EsignatureDocumentSentAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Enviado" },
      en: { s: "Sent At" },
      pt: { s: "Enviado Em" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentSentAtLabel() {
    return this.EsignatureDocumentSentAtLabelByLang();
  }

  EsignatureDocumentCompletedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Completado" },
      en: { s: "Completed At" },
      pt: { s: "Completado Em" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentCompletedAtLabel() {
    return this.EsignatureDocumentCompletedAtLabelByLang();
  }

  EsignatureDocumentSignedDocumentLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Documento Firmado" },
      en: { s: "Signed Document" },
      pt: { s: "Documento Assinado" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentSignedDocumentLabel() {
    return this.EsignatureDocumentSignedDocumentLabelByLang();
  }

  EsignatureDocumentMetadataLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Metadatos" },
      en: { s: "Metadata" },
      pt: { s: "Metadados" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentMetadataLabel() {
    return this.EsignatureDocumentMetadataLabelByLang();
  }

  EsignatureDocumentSourceEntityTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo Entidad Origen" },
      en: { s: "Source Entity Type" },
      pt: { s: "Tipo Entidade Origem" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentSourceEntityTypeLabel() {
    return this.EsignatureDocumentSourceEntityTypeLabelByLang();
  }

  EsignatureDocumentSourceEntityIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID Entidad Origen" },
      en: { s: "Source Entity ID" },
      pt: { s: "ID Entidade Origem" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentSourceEntityIdLabel() {
    return this.EsignatureDocumentSourceEntityIdLabelByLang();
  }

  EsignatureDocumentActiveSourceKeyLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Clave de Origen Activa" },
      en: { s: "Active Source Key" },
      pt: { s: "Chave de Origem Ativa" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentActiveSourceKeyLabel() {
    return this.EsignatureDocumentActiveSourceKeyLabelByLang();
  }

  EsignatureDocumentRecipientsLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Recipients" },
      en: { s: "Recipients" },
      pt: { s: "Recipients" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureDocumentRecipientsLabel() {
    return this.EsignatureDocumentRecipientsLabelByLang();
  }

  // EsignatureRecipient

  get EsignatureRecipientLabels() {
    return {
      es: { s: "Firmante", p: "Firmantes", g: undefined },
      en: { s: "Recipient", p: "Recipients", g: undefined },
      pt: { s: "Signatario", p: "Signatarios", g: undefined },
    }[this.language];
  }

  get EsignatureRecipientSingular() {
    return this.EsignatureRecipientLabels.s;
  }

  get EsignatureRecipientPlural() {
    return this.EsignatureRecipientLabels.p;
  }

  get EsignatureRecipientGender() {
    return this.EsignatureRecipientLabels.g;
  }

  get groupEsignatureRecipientLabel() {
    return {
      es: { s: "Firmante", p: "Firmantes" },
      en: { s: "Recipient", p: "Recipients" },
      pt: { s: "Signatario", p: "Signatarios" },
    }[this.language].s;
  }

  EsignatureRecipientDocumentLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Documento" },
      en: { s: "Document" },
      pt: { s: "Documento" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureRecipientDocumentLabel() {
    return this.EsignatureRecipientDocumentLabelByLang();
  }

  EsignatureRecipientEmailLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Email" }, en: { s: "Email" }, pt: { s: "Email" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureRecipientEmailLabel() {
    return this.EsignatureRecipientEmailLabelByLang();
  }

  EsignatureRecipientNameLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Nombre" }, en: { s: "Name" }, pt: { s: "Nome" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureRecipientNameLabel() {
    return this.EsignatureRecipientNameLabelByLang();
  }

  EsignatureRecipientRoleLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Rol" }, en: { s: "Role" }, pt: { s: "Papel" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureRecipientRoleLabel() {
    return this.EsignatureRecipientRoleLabelByLang();
  }

  EsignatureRecipientOrderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Orden" }, en: { s: "Order" }, pt: { s: "Ordem" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureRecipientOrderLabel() {
    return this.EsignatureRecipientOrderLabelByLang();
  }

  EsignatureRecipientStatusLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Estado" }, en: { s: "Status" }, pt: { s: "Status" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureRecipientStatusLabel() {
    return this.EsignatureRecipientStatusLabelByLang();
  }

  EsignatureRecipientSignedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Firmado" },
      en: { s: "Signed At" },
      pt: { s: "Assinado Em" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureRecipientSignedAtLabel() {
    return this.EsignatureRecipientSignedAtLabelByLang();
  }

  // EsignatureWebhookEvent

  get EsignatureWebhookEventLabels() {
    return {
      es: {
        s: "Evento de Webhook de Firma",
        p: "Eventos de Webhook de Firma",
        g: undefined,
      },
      en: {
        s: "E-Signature Webhook Event",
        p: "E-Signature Webhook Events",
        g: undefined,
      },
      pt: {
        s: "Evento de Webhook de Assinatura",
        p: "Eventos de Webhook de Assinatura",
        g: undefined,
      },
    }[this.language];
  }

  get EsignatureWebhookEventSingular() {
    return this.EsignatureWebhookEventLabels.s;
  }

  get EsignatureWebhookEventPlural() {
    return this.EsignatureWebhookEventLabels.p;
  }

  get EsignatureWebhookEventGender() {
    return this.EsignatureWebhookEventLabels.g;
  }

  get groupEsignatureWebhookEventLabel() {
    return {
      es: { s: "Evento de Webhook de Firma", p: "Eventos de Webhook de Firma" },
      en: { s: "E-Signature Webhook Event", p: "E-Signature Webhook Events" },
      pt: {
        s: "Evento de Webhook de Assinatura",
        p: "Eventos de Webhook de Assinatura",
      },
    }[this.language].s;
  }

  EsignatureWebhookEventProviderLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Proveedor" },
      en: { s: "Provider" },
      pt: { s: "Provedor" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureWebhookEventProviderLabel() {
    return this.EsignatureWebhookEventProviderLabelByLang();
  }

  EsignatureWebhookEventExternalEventIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Evento" },
      en: { s: "Event ID" },
      pt: { s: "ID do Evento" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureWebhookEventExternalEventIdLabel() {
    return this.EsignatureWebhookEventExternalEventIdLabelByLang();
  }

  EsignatureWebhookEventEventTypeLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Tipo de Evento" },
      en: { s: "Event Type" },
      pt: { s: "Tipo de Evento" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureWebhookEventEventTypeLabel() {
    return this.EsignatureWebhookEventEventTypeLabelByLang();
  }

  EsignatureWebhookEventDocumentExternalIdLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "ID de Documento" },
      en: { s: "Document ID" },
      pt: { s: "ID do Documento" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureWebhookEventDocumentExternalIdLabel() {
    return this.EsignatureWebhookEventDocumentExternalIdLabelByLang();
  }

  EsignatureWebhookEventPayloadLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Payload" }, en: { s: "Payload" }, pt: { s: "Payload" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureWebhookEventPayloadLabel() {
    return this.EsignatureWebhookEventPayloadLabelByLang();
  }

  EsignatureWebhookEventProcessedAtLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return {
      es: { s: "Procesado" },
      en: { s: "Processed At" },
      pt: { s: "Processado Em" },
    }[lang ? lang : this.language].s;
  }

  get EsignatureWebhookEventProcessedAtLabel() {
    return this.EsignatureWebhookEventProcessedAtLabelByLang();
  }

  EsignatureWebhookEventErrorLabelByLang(
    lang: LanguageCodes | undefined = undefined
  ) {
    return { es: { s: "Error" }, en: { s: "Error" }, pt: { s: "Erro" } }[
      lang ? lang : this.language
    ].s;
  }

  get EsignatureWebhookEventErrorLabel() {
    return this.EsignatureWebhookEventErrorLabelByLang();
  }

  // SiteUser projection labels

  get groupSiteUserMainLabel() {
    return {
      es: { s: "Datos principales", p: "M" },
      en: { s: "Main" },
      pt: { s: "Dados principais", p: "M" },
    }[this.language].s;
  }

  get groupSiteUserDetailsLabel() {
    return {
      es: { s: "Datos personales", p: "M" },
      en: { s: "Personal data" },
      pt: { s: "Dados pessoais", p: "M" },
    }[this.language].s;
  }

  // MyCreditPurchases projection labels

  get groupMyCreditPurchasesCompraLabel() {
    return {
      es: { s: "Compra", p: "F" },
      en: { s: "Purchase" },
      pt: { s: "Compra", p: "F" },
    }[this.language].s;
  }

  // AuthorComposerPublic projection labels

  get groupAuthorComposerPublicPersonalInformationLabel() {
    return {
      es: { s: "Información Personal" },
      en: { s: "Personal Information" },
      pt: { s: "Informação Pessoal" },
    }[this.language].s;
  }

  get groupAuthorComposerPublicAddressLabel() {
    return {
      es: { s: "Domicilio" },
      en: { s: "Address" },
      pt: { s: "Endereço" },
    }[this.language].s;
  }

  get groupAuthorComposerPublicProfessionalInformationLabel() {
    return {
      es: { s: "Información Profesional" },
      en: { s: "Professional Information" },
      pt: { s: "Informação Profissional" },
    }[this.language].s;
  }

  // IdCardType enum labels

  get IdCardTypeCuitCuilLabel() {
    return {
      es: { s: "CUIT/CUIL" },
      en: { s: "CUIT/CUIL" },
      pt: { s: "CUIT/CUIL" },
    }[this.language].s;
  }

  get IdCardTypeDniLabel() {
    return { es: { s: "DNI" }, en: { s: "National ID" }, pt: { s: "DNI" } }[
      this.language
    ].s;
  }

  get IdCardTypeAnyIdCardLabel() {
    return {
      es: { s: "Otro ID" },
      en: { s: "Other ID" },
      pt: { s: "Outro ID" },
    }[this.language].s;
  }

  get IdCardTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "cuitCuil", displayId: this.IdCardTypeCuitCuilLabel },
      { id: "dni", displayId: this.IdCardTypeDniLabel },
      { id: "anyIdCard", displayId: this.IdCardTypeAnyIdCardLabel },
    ];
  }

  // TadAuthProvider enum labels

  get TadAuthProviderRenaperLabel() {
    return {
      es: { s: "DNI y número de trámite" },
      en: { s: "National ID and procedure number" },
      pt: { s: "DNI e número do trâmite" },
    }[this.language].s;
  }

  get TadAuthProviderMiArgentinaLabel() {
    return {
      es: { s: "Mi Argentina" },
      en: { s: "Mi Argentina" },
      pt: { s: "Mi Argentina" },
    }[this.language].s;
  }

  get TadAuthProviderArcaLabel() {
    return {
      es: { s: "ARCA (clave fiscal)" },
      en: { s: "ARCA (tax key)" },
      pt: { s: "ARCA (chave fiscal)" },
    }[this.language].s;
  }

  get TadAuthProviderAnsesLabel() {
    return {
      es: { s: "Clave de seguridad social ANSES" },
      en: { s: "ANSES social security key" },
      pt: { s: "Chave de segurança social ANSES" },
    }[this.language].s;
  }

  get TadAuthProviderOptions(): { id: string; displayId: string }[] {
    return [
      { id: "renaper", displayId: this.TadAuthProviderRenaperLabel },
      { id: "miArgentina", displayId: this.TadAuthProviderMiArgentinaLabel },
      { id: "arca", displayId: this.TadAuthProviderArcaLabel },
      { id: "anses", displayId: this.TadAuthProviderAnsesLabel },
    ];
  }

  // PersonType enum labels

  get PersonTypeNaturalPersonLabel() {
    return {
      es: { s: "Persona física" },
      en: { s: "Natural person" },
      pt: { s: "Pessoa física" },
    }[this.language].s;
  }

  get PersonTypeLegalEntityLabel() {
    return {
      es: { s: "Persona jurídica" },
      en: { s: "Legal entity" },
      pt: { s: "Pessoa jurídica" },
    }[this.language].s;
  }

  get PersonTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "naturalPerson", displayId: this.PersonTypeNaturalPersonLabel },
      { id: "legalEntity", displayId: this.PersonTypeLegalEntityLabel },
    ];
  }

  // MaritalStatus enum labels

  get MaritalStatusWidowedLabel() {
    return { es: { s: "Viudo/a" }, en: { s: "Widowed" }, pt: { s: "Viúvo/a" } }[
      this.language
    ].s;
  }

  get MaritalStatusSingleLabel() {
    return {
      es: { s: "Soltero/a" },
      en: { s: "Single" },
      pt: { s: "Solteiro/a" },
    }[this.language].s;
  }

  get MaritalStatusCommonLawUnionLabel() {
    return {
      es: { s: "Unión libre o de hecho" },
      en: { s: "Common-law union" },
      pt: { s: "União estável ou de fato" },
    }[this.language].s;
  }

  get MaritalStatusMarriedLabel() {
    return {
      es: { s: "Casado/a" },
      en: { s: "Married" },
      pt: { s: "Casado/a" },
    }[this.language].s;
  }

  get MaritalStatusSeparatedLabel() {
    return {
      es: { s: "Separado/a" },
      en: { s: "Separated" },
      pt: { s: "Separado/a" },
    }[this.language].s;
  }

  get MaritalStatusDivorcedLabel() {
    return {
      es: { s: "Divorciado/a" },
      en: { s: "Divorced" },
      pt: { s: "Divorciado/a" },
    }[this.language].s;
  }

  get MaritalStatusCivilUnionLabel() {
    return {
      es: { s: "Unión civil" },
      en: { s: "Civil union" },
      pt: { s: "União civil" },
    }[this.language].s;
  }

  get MaritalStatusNotDeclaredLabel() {
    return {
      es: { s: "No declara" },
      en: { s: "Not declared" },
      pt: { s: "Não declara" },
    }[this.language].s;
  }

  get MaritalStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "widowed", displayId: this.MaritalStatusWidowedLabel },
      { id: "single", displayId: this.MaritalStatusSingleLabel },
      {
        id: "commonLawUnion",
        displayId: this.MaritalStatusCommonLawUnionLabel,
      },
      { id: "married", displayId: this.MaritalStatusMarriedLabel },
      { id: "separated", displayId: this.MaritalStatusSeparatedLabel },
      { id: "divorced", displayId: this.MaritalStatusDivorcedLabel },
      { id: "civilUnion", displayId: this.MaritalStatusCivilUnionLabel },
      { id: "notDeclared", displayId: this.MaritalStatusNotDeclaredLabel },
    ];
  }

  // Gender enum labels

  get GenderFemaleLabel() {
    return {
      es: { s: "Femenino" },
      en: { s: "Female" },
      pt: { s: "Feminino" },
    }[this.language].s;
  }

  get GenderMaleLabel() {
    return {
      es: { s: "Masculino" },
      en: { s: "Male" },
      pt: { s: "Masculino" },
    }[this.language].s;
  }

  get GenderNonBinaryLabel() {
    return {
      es: { s: "No binario" },
      en: { s: "Non-binary" },
      pt: { s: "Não binário" },
    }[this.language].s;
  }

  get GenderOptions(): { id: string; displayId: string }[] {
    return [
      { id: "female", displayId: this.GenderFemaleLabel },
      { id: "male", displayId: this.GenderMaleLabel },
      { id: "nonBinary", displayId: this.GenderNonBinaryLabel },
    ];
  }

  // Language enum labels

  get LanguageEsLabel() {
    return {
      es: { s: "Español" },
      en: { s: "Spanish" },
      pt: { s: "Espanhol" },
    }[this.language].s;
  }

  get LanguageEnLabel() {
    return { es: { s: "Inglés" }, en: { s: "English" }, pt: { s: "Inglês" } }[
      this.language
    ].s;
  }

  get LanguagePtLabel() {
    return {
      es: { s: "Portugués" },
      en: { s: "Portuguese" },
      pt: { s: "Português" },
    }[this.language].s;
  }

  get LanguageOptions(): { id: string; displayId: string }[] {
    return [
      { id: "es", displayId: this.LanguageEsLabel },
      { id: "en", displayId: this.LanguageEnLabel },
      { id: "pt", displayId: this.LanguagePtLabel },
    ];
  }

  // EsignatureProvider enum labels

  get EsignatureProviderAgilSignLabel() {
    return {
      es: { s: "AgilSign" },
      en: { s: "AgilSign" },
      pt: { s: "AgilSign" },
    }[this.language].s;
  }

  get EsignatureProviderPandaDocLabel() {
    return {
      es: { s: "PandaDoc" },
      en: { s: "PandaDoc" },
      pt: { s: "PandaDoc" },
    }[this.language].s;
  }

  get EsignatureProviderDocuSignLabel() {
    return {
      es: { s: "DocuSign" },
      en: { s: "DocuSign" },
      pt: { s: "DocuSign" },
    }[this.language].s;
  }

  get EsignatureProviderSignNowLabel() {
    return { es: { s: "SignNow" }, en: { s: "SignNow" }, pt: { s: "SignNow" } }[
      this.language
    ].s;
  }

  get EsignatureProviderOptions(): { id: string; displayId: string }[] {
    return [
      { id: "agilSign", displayId: this.EsignatureProviderAgilSignLabel },
      { id: "pandaDoc", displayId: this.EsignatureProviderPandaDocLabel },
      { id: "docuSign", displayId: this.EsignatureProviderDocuSignLabel },
      { id: "signNow", displayId: this.EsignatureProviderSignNowLabel },
    ];
  }

  // CreditMovementType enum labels

  get CreditMovementTypePurchaseLabel() {
    return {
      es: { s: "Compra", p: "F" },
      en: { s: "Purchase" },
      pt: { s: "Compra", p: "F" },
    }[this.language].s;
  }

  get CreditMovementTypeConsumptionLabel() {
    return {
      es: { s: "Consumo", p: "M" },
      en: { s: "Consumption" },
      pt: { s: "Consumo", p: "M" },
    }[this.language].s;
  }

  get CreditMovementTypeRefundLabel() {
    return {
      es: { s: "Devolución", p: "F" },
      en: { s: "Refund" },
      pt: { s: "Devolução", p: "F" },
    }[this.language].s;
  }

  get CreditMovementTypeAdjustmentLabel() {
    return {
      es: { s: "Ajuste", p: "M" },
      en: { s: "Adjustment" },
      pt: { s: "Ajuste", p: "M" },
    }[this.language].s;
  }

  get CreditMovementTypeExpirationLabel() {
    return {
      es: { s: "Vencimiento", p: "M" },
      en: { s: "Expiration" },
      pt: { s: "Vencimento", p: "M" },
    }[this.language].s;
  }

  get CreditMovementTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "purchase", displayId: this.CreditMovementTypePurchaseLabel },
      { id: "consumption", displayId: this.CreditMovementTypeConsumptionLabel },
      { id: "refund", displayId: this.CreditMovementTypeRefundLabel },
      { id: "adjustment", displayId: this.CreditMovementTypeAdjustmentLabel },
      { id: "expiration", displayId: this.CreditMovementTypeExpirationLabel },
    ];
  }

  // BotType enum labels

  get BotTypeRegistroDndaLabel() {
    return {
      es: { s: "Registro DNDA" },
      en: { s: "DNDA Registration" },
      pt: { s: "Registro DNDA" },
    }[this.language].s;
  }

  get BotTypeEmailDndaLabel() {
    return {
      es: { s: "Email Habilitación DNDA" },
      en: { s: "DNDA Authorization Email" },
      pt: { s: "Email Habilitação DNDA" },
    }[this.language].s;
  }

  get BotTypeUploadDndaLabel() {
    return {
      es: { s: "Subida Ejemplar DNDA" },
      en: { s: "DNDA Copy Upload" },
      pt: { s: "Envio Exemplar DNDA" },
    }[this.language].s;
  }

  get BotTypeUploadSadaicLabel() {
    return {
      es: { s: "Subida SADAIC" },
      en: { s: "SADAIC Upload" },
      pt: { s: "Envio SADAIC" },
    }[this.language].s;
  }

  get BotTypeComprobanteDndaLabel() {
    return {
      es: { s: "Comprobante DNDA" },
      en: { s: "DNDA Receipt" },
      pt: { s: "Comprovante DNDA" },
    }[this.language].s;
  }

  get BotTypeConstanciaCuilLabel() {
    return {
      es: { s: "Constancia de CUIL" },
      en: { s: "CUIL Certificate" },
      pt: { s: "Constância de CUIL" },
    }[this.language].s;
  }

  get BotTypeDniSadaicLabel() {
    return {
      es: { s: "Envío de DNI a SADAIC" },
      en: { s: "ID Upload to SADAIC" },
      pt: { s: "Envio de documento à SADAIC" },
    }[this.language].s;
  }

  get BotTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "registroDnda", displayId: this.BotTypeRegistroDndaLabel },
      { id: "emailDnda", displayId: this.BotTypeEmailDndaLabel },
      { id: "uploadDnda", displayId: this.BotTypeUploadDndaLabel },
      { id: "uploadSadaic", displayId: this.BotTypeUploadSadaicLabel },
      { id: "comprobanteDnda", displayId: this.BotTypeComprobanteDndaLabel },
      { id: "constanciaCuil", displayId: this.BotTypeConstanciaCuilLabel },
      { id: "dniSadaic", displayId: this.BotTypeDniSadaicLabel },
    ];
  }

  // NotificationType enum labels

  get NotificationTypeMentionLabel() {
    return {
      es: { s: "Mención", p: "F" },
      en: { s: "Mention" },
      pt: { s: "Menção", p: "F" },
    }[this.language].s;
  }

  get NotificationTypeAssignmentLabel() {
    return {
      es: { s: "Asignación", p: "F" },
      en: { s: "Assignment" },
      pt: { s: "Atribuição", p: "F" },
    }[this.language].s;
  }

  get NotificationTypeCommentLabel() {
    return {
      es: { s: "Comentario", p: "M" },
      en: { s: "Comment" },
      pt: { s: "Comentário", p: "M" },
    }[this.language].s;
  }

  get NotificationTypeAlertLabel() {
    return {
      es: { s: "Alerta", p: "F" },
      en: { s: "Alert" },
      pt: { s: "Alerta", p: "F" },
    }[this.language].s;
  }

  get NotificationTypeSystemLabel() {
    return {
      es: { s: "Sistema", p: "M" },
      en: { s: "System" },
      pt: { s: "Sistema", p: "M" },
    }[this.language].s;
  }

  get NotificationTypeToken_limit_reachedLabel() {
    return {
      es: { s: "Límite de Tokens", p: "M" },
      en: { s: "Token Limit" },
      pt: { s: "Limite de Tokens", p: "M" },
    }[this.language].s;
  }

  get NotificationTypeCredits_insufficientLabel() {
    return {
      es: { s: "Créditos Insuficientes", p: "M" },
      en: { s: "Insufficient Credits" },
      pt: { s: "Créditos Insuficientes", p: "M" },
    }[this.language].s;
  }

  get NotificationTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "mention", displayId: this.NotificationTypeMentionLabel },
      { id: "assignment", displayId: this.NotificationTypeAssignmentLabel },
      { id: "comment", displayId: this.NotificationTypeCommentLabel },
      { id: "alert", displayId: this.NotificationTypeAlertLabel },
      { id: "system", displayId: this.NotificationTypeSystemLabel },
      {
        id: "token_limit_reached",
        displayId: this.NotificationTypeToken_limit_reachedLabel,
      },
      {
        id: "credits_insufficient",
        displayId: this.NotificationTypeCredits_insufficientLabel,
      },
    ];
  }

  // NotificationPriority enum labels

  get NotificationPriorityHighLabel() {
    return {
      es: { s: "Alta", p: "F" },
      en: { s: "High" },
      pt: { s: "Alta", p: "F" },
    }[this.language].s;
  }

  get NotificationPriorityMediumLabel() {
    return {
      es: { s: "Media", p: "F" },
      en: { s: "Medium" },
      pt: { s: "Média", p: "F" },
    }[this.language].s;
  }

  get NotificationPriorityLowLabel() {
    return {
      es: { s: "Baja", p: "F" },
      en: { s: "Low" },
      pt: { s: "Baixa", p: "F" },
    }[this.language].s;
  }

  get NotificationPriorityOptions(): { id: string; displayId: string }[] {
    return [
      { id: "high", displayId: this.NotificationPriorityHighLabel },
      { id: "medium", displayId: this.NotificationPriorityMediumLabel },
      { id: "low", displayId: this.NotificationPriorityLowLabel },
    ];
  }

  // EmailFrequency enum labels

  get EmailFrequencyImmediateLabel() {
    return {
      es: { s: "Inmediato", p: "M" },
      en: { s: "Immediate" },
      pt: { s: "Imediato", p: "M" },
    }[this.language].s;
  }

  get EmailFrequencyDaily_digestLabel() {
    return {
      es: { s: "Resumen Diario", p: "M" },
      en: { s: "Daily Digest" },
      pt: { s: "Resumo Diário", p: "M" },
    }[this.language].s;
  }

  get EmailFrequencyWeekly_digestLabel() {
    return {
      es: { s: "Resumen Semanal", p: "M" },
      en: { s: "Weekly Digest" },
      pt: { s: "Resumo Semanal", p: "M" },
    }[this.language].s;
  }

  get EmailFrequencyOptions(): { id: string; displayId: string }[] {
    return [
      { id: "immediate", displayId: this.EmailFrequencyImmediateLabel },
      { id: "daily_digest", displayId: this.EmailFrequencyDaily_digestLabel },
      { id: "weekly_digest", displayId: this.EmailFrequencyWeekly_digestLabel },
    ];
  }

  // EmailVerificationStatus enum labels

  get EmailVerificationStatusPendingLabel() {
    return {
      es: { s: "Pendiente" },
      en: { s: "Pending" },
      pt: { s: "Pendente" },
    }[this.language].s;
  }

  get EmailVerificationStatusVerifiedLabel() {
    return {
      es: { s: "Verificado" },
      en: { s: "Verified" },
      pt: { s: "Verificado" },
    }[this.language].s;
  }

  get EmailVerificationStatusExpiredLabel() {
    return {
      es: { s: "Expirado" },
      en: { s: "Expired" },
      pt: { s: "Expirado" },
    }[this.language].s;
  }

  get EmailVerificationStatusRevokedLabel() {
    return {
      es: { s: "Revocado" },
      en: { s: "Revoked" },
      pt: { s: "Revogado" },
    }[this.language].s;
  }

  get EmailVerificationStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "pending", displayId: this.EmailVerificationStatusPendingLabel },
      { id: "verified", displayId: this.EmailVerificationStatusVerifiedLabel },
      { id: "expired", displayId: this.EmailVerificationStatusExpiredLabel },
      { id: "revoked", displayId: this.EmailVerificationStatusRevokedLabel },
    ];
  }

  // EmailVerificationScope enum labels

  get EmailVerificationScopeEmailLabel() {
    return {
      es: { s: "Solo email" },
      en: { s: "Email only" },
      pt: { s: "Somente e-mail" },
    }[this.language].s;
  }

  get EmailVerificationScopeDataLabel() {
    return {
      es: { s: "Email y datos" },
      en: { s: "Email and data" },
      pt: { s: "E-mail e dados" },
    }[this.language].s;
  }

  get EmailVerificationScopeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "email", displayId: this.EmailVerificationScopeEmailLabel },
      { id: "data", displayId: this.EmailVerificationScopeDataLabel },
    ];
  }

  // ShowcaseStatus enum labels

  get ShowcaseStatusDraftLabel() {
    return { es: { s: "Borrador" }, en: { s: "Draft" }, pt: { s: "Rascunho" } }[
      this.language
    ].s;
  }

  get ShowcaseStatusActiveLabel() {
    return { es: { s: "Activo" }, en: { s: "Active" }, pt: { s: "Ativo" } }[
      this.language
    ].s;
  }

  get ShowcaseStatusArchivedLabel() {
    return {
      es: { s: "Archivado" },
      en: { s: "Archived" },
      pt: { s: "Arquivado" },
    }[this.language].s;
  }

  get ShowcaseStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "draft", displayId: this.ShowcaseStatusDraftLabel },
      { id: "active", displayId: this.ShowcaseStatusActiveLabel },
      { id: "archived", displayId: this.ShowcaseStatusArchivedLabel },
    ];
  }

  // EditionContentType enum labels

  get EditionContentTypeScoresLabel() {
    return {
      es: { s: "Edición de PARTITURAS" },
      en: { s: "SCORE edition" },
      pt: { s: "Edição de PARTITURAS" },
    }[this.language].s;
  }

  get EditionContentTypeAudiosLabel() {
    return {
      es: { s: "Edición de audios" },
      en: { s: "Audio edition" },
      pt: { s: "Edição de áudios" },
    }[this.language].s;
  }

  get EditionContentTypeScoresAndAudiosLabel() {
    return {
      es: { s: "Edición de PARTITURAS + Audio de Registro a SADAIC" },
      en: { s: "SCORE edition + SADAIC registration audio" },
      pt: { s: "Edição de PARTITURAS + Áudio de Registro na SADAIC" },
    }[this.language].s;
  }

  get EditionContentTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "scores", displayId: this.EditionContentTypeScoresLabel },
      { id: "audios", displayId: this.EditionContentTypeAudiosLabel },
      {
        id: "scoresAndAudios",
        displayId: this.EditionContentTypeScoresAndAudiosLabel,
      },
    ];
  }

  // TaxIdType enum labels

  get TaxIdTypeCuitCuilLabel() {
    return {
      es: { s: "CUIT/CUIL" },
      en: { s: "CUIT/CUIL" },
      pt: { s: "CUIT/CUIL" },
    }[this.language].s;
  }

  get TaxIdTypeDuLabel() {
    return {
      es: { s: "DU (DNI)" },
      en: { s: "National ID (DNI)" },
      pt: { s: "DU (DNI)" },
    }[this.language].s;
  }

  get TaxIdTypeTaxIdLabel() {
    return {
      es: { s: "Otro ID" },
      en: { s: "Other ID" },
      pt: { s: "Outro ID" },
    }[this.language].s;
  }

  get TaxIdTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "cuitCuil", displayId: this.TaxIdTypeCuitCuilLabel },
      { id: "du", displayId: this.TaxIdTypeDuLabel },
      { id: "taxId", displayId: this.TaxIdTypeTaxIdLabel },
    ];
  }

  // CuitCertificateErrorType enum labels

  get CuitCertificateErrorTypeInvalidDataLabel() {
    return {
      es: { s: "Datos personales no válidos" },
      en: { s: "Invalid personal data" },
      pt: { s: "Dados pessoais inválidos" },
    }[this.language].s;
  }

  get CuitCertificateErrorTypeTechnicalLabel() {
    return {
      es: { s: "Error técnico (reintentando)" },
      en: { s: "Technical error (retrying)" },
      pt: { s: "Erro técnico (tentando novamente)" },
    }[this.language].s;
  }

  get CuitCertificateErrorTypeIncompleteDataLabel() {
    return {
      es: { s: "Datos incompletos (faltan datos del autor)" },
      en: { s: "Incomplete data (author details missing)" },
      pt: { s: "Dados incompletos (faltam dados do autor)" },
    }[this.language].s;
  }

  get CuitCertificateErrorTypeOptions(): { id: string; displayId: string }[] {
    return [
      {
        id: "invalidData",
        displayId: this.CuitCertificateErrorTypeInvalidDataLabel,
      },
      {
        id: "technical",
        displayId: this.CuitCertificateErrorTypeTechnicalLabel,
      },
      {
        id: "incompleteData",
        displayId: this.CuitCertificateErrorTypeIncompleteDataLabel,
      },
    ];
  }

  // PaymentReceiptStatus enum labels

  get PaymentReceiptStatusAvailableLabel() {
    return {
      es: { s: "Disponible", p: "M" },
      en: { s: "Available" },
      pt: { s: "Disponível", p: "M" },
    }[this.language].s;
  }

  get PaymentReceiptStatusUsedLabel() {
    return {
      es: { s: "Utilizado", p: "M" },
      en: { s: "Used" },
      pt: { s: "Utilizado", p: "M" },
    }[this.language].s;
  }

  get PaymentReceiptStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "available", displayId: this.PaymentReceiptStatusAvailableLabel },
      { id: "used", displayId: this.PaymentReceiptStatusUsedLabel },
    ];
  }

  // ChunkingStrategy enum labels

  get ChunkingStrategyAutoLabel() {
    return {
      es: { s: "Automático", p: "M" },
      en: { s: "Auto" },
      pt: { s: "Automático", p: "M" },
    }[this.language].s;
  }

  get ChunkingStrategyBy_tokensLabel() {
    return {
      es: { s: "Por Tokens", p: "M" },
      en: { s: "By Tokens" },
      pt: { s: "Por Tokens", p: "M" },
    }[this.language].s;
  }

  get ChunkingStrategyBy_regexLabel() {
    return {
      es: { s: "Por Expresión Regular", p: "F" },
      en: { s: "By Regex" },
      pt: { s: "Por Expressão Regular", p: "F" },
    }[this.language].s;
  }

  get ChunkingStrategyBy_structureLabel() {
    return {
      es: { s: "Por Estructura", p: "F" },
      en: { s: "By Structure" },
      pt: { s: "Por Estrutura", p: "F" },
    }[this.language].s;
  }

  get ChunkingStrategyBy_legalLabel() {
    return {
      es: { s: "Por Estructura Legal", p: "F" },
      en: { s: "By Legal Structure" },
      pt: { s: "Por Estrutura Legal", p: "F" },
    }[this.language].s;
  }

  get ChunkingStrategyOptions(): { id: string; displayId: string }[] {
    return [
      { id: "auto", displayId: this.ChunkingStrategyAutoLabel },
      { id: "by_tokens", displayId: this.ChunkingStrategyBy_tokensLabel },
      { id: "by_regex", displayId: this.ChunkingStrategyBy_regexLabel },
      { id: "by_structure", displayId: this.ChunkingStrategyBy_structureLabel },
      { id: "by_legal", displayId: this.ChunkingStrategyBy_legalLabel },
    ];
  }

  // DocumentProcessingStatus enum labels

  get DocumentProcessingStatusPendingLabel() {
    return {
      es: { s: "Pendiente", p: "M" },
      en: { s: "Pending" },
      pt: { s: "Pendente", p: "M" },
    }[this.language].s;
  }

  get DocumentProcessingStatusProcessingLabel() {
    return {
      es: { s: "Procesando", p: "M" },
      en: { s: "Processing" },
      pt: { s: "Processando", p: "M" },
    }[this.language].s;
  }

  get DocumentProcessingStatusReadyLabel() {
    return {
      es: { s: "Listo", p: "M" },
      en: { s: "Ready" },
      pt: { s: "Pronto", p: "M" },
    }[this.language].s;
  }

  get DocumentProcessingStatusErrorLabel() {
    return {
      es: { s: "Error", p: "M" },
      en: { s: "Error" },
      pt: { s: "Erro", p: "M" },
    }[this.language].s;
  }

  get DocumentProcessingStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "pending", displayId: this.DocumentProcessingStatusPendingLabel },
      {
        id: "processing",
        displayId: this.DocumentProcessingStatusProcessingLabel,
      },
      { id: "ready", displayId: this.DocumentProcessingStatusReadyLabel },
      { id: "error", displayId: this.DocumentProcessingStatusErrorLabel },
    ];
  }

  // StorageOperation enum labels

  get StorageOperationUploadLabel() {
    return {
      es: { s: "Subida", p: "F" },
      en: { s: "Upload" },
      pt: { s: "Upload" },
    }[this.language].s;
  }

  get StorageOperationDeleteLabel() {
    return {
      es: { s: "Eliminación", p: "F" },
      en: { s: "Delete" },
      pt: { s: "Exclusão", p: "F" },
    }[this.language].s;
  }

  get StorageOperationOptions(): { id: string; displayId: string }[] {
    return [
      { id: "Upload", displayId: this.StorageOperationUploadLabel },
      { id: "Delete", displayId: this.StorageOperationDeleteLabel },
    ];
  }

  // EvaluationRating enum labels

  get EvaluationRatingThumbs_upLabel() {
    return {
      es: { s: "Correcto", p: "M" },
      en: { s: "Correct" },
      pt: { s: "Correto", p: "M" },
    }[this.language].s;
  }

  get EvaluationRatingThumbs_downLabel() {
    return {
      es: { s: "Incorrecto", p: "M" },
      en: { s: "Incorrect" },
      pt: { s: "Incorreto", p: "M" },
    }[this.language].s;
  }

  get EvaluationRatingPartialLabel() {
    return {
      es: { s: "Parcial", p: "M" },
      en: { s: "Partial" },
      pt: { s: "Parcial", p: "M" },
    }[this.language].s;
  }

  get EvaluationRatingOptions(): { id: string; displayId: string }[] {
    return [
      { id: "thumbs_up", displayId: this.EvaluationRatingThumbs_upLabel },
      { id: "thumbs_down", displayId: this.EvaluationRatingThumbs_downLabel },
      { id: "partial", displayId: this.EvaluationRatingPartialLabel },
    ];
  }

  // EvaluationPathType enum labels

  get EvaluationPathTypeEntity_onlyLabel() {
    return {
      es: { s: "Solo Entidad", p: "M" },
      en: { s: "Entity Only" },
      pt: { s: "Somente Entidade", p: "F" },
    }[this.language].s;
  }

  get EvaluationPathTypeDirect_refLabel() {
    return {
      es: { s: "Referencia Directa", p: "F" },
      en: { s: "Direct Ref" },
      pt: { s: "Referência Direta", p: "F" },
    }[this.language].s;
  }

  get EvaluationPathTypeDirect_setLabel() {
    return {
      es: { s: "Set Directo", p: "M" },
      en: { s: "Direct Set" },
      pt: { s: "Set Direto", p: "M" },
    }[this.language].s;
  }

  get EvaluationPathTypeJunctionLabel() {
    return {
      es: { s: "Junction", p: "F" },
      en: { s: "Junction" },
      pt: { s: "Junction", p: "F" },
    }[this.language].s;
  }

  get EvaluationPathTypeNested_setLabel() {
    return {
      es: { s: "Set Anidado", p: "M" },
      en: { s: "Nested Set" },
      pt: { s: "Set Aninhado", p: "M" },
    }[this.language].s;
  }

  get EvaluationPathTypeInverse_refLabel() {
    return {
      es: { s: "Referencia Inversa", p: "F" },
      en: { s: "Inverse Ref" },
      pt: { s: "Referência Inversa", p: "F" },
    }[this.language].s;
  }

  get EvaluationPathTypeInverse_junctionLabel() {
    return {
      es: { s: "Junction Inversa", p: "F" },
      en: { s: "Inverse Junction" },
      pt: { s: "Junction Inversa", p: "F" },
    }[this.language].s;
  }

  get EvaluationPathTypeInverse_parentLabel() {
    return {
      es: { s: "Parent Inverso", p: "M" },
      en: { s: "Inverse Parent" },
      pt: { s: "Parent Inverso", p: "M" },
    }[this.language].s;
  }

  get EvaluationPathTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "entity_only", displayId: this.EvaluationPathTypeEntity_onlyLabel },
      { id: "direct_ref", displayId: this.EvaluationPathTypeDirect_refLabel },
      { id: "direct_set", displayId: this.EvaluationPathTypeDirect_setLabel },
      { id: "junction", displayId: this.EvaluationPathTypeJunctionLabel },
      { id: "nested_set", displayId: this.EvaluationPathTypeNested_setLabel },
      { id: "inverse_ref", displayId: this.EvaluationPathTypeInverse_refLabel },
      {
        id: "inverse_junction",
        displayId: this.EvaluationPathTypeInverse_junctionLabel,
      },
      {
        id: "inverse_parent",
        displayId: this.EvaluationPathTypeInverse_parentLabel,
      },
    ];
  }

  // EvaluationDifficulty enum labels

  get EvaluationDifficultyEasyLabel() {
    return {
      es: { s: "Fácil", p: "M" },
      en: { s: "Easy" },
      pt: { s: "Fácil", p: "M" },
    }[this.language].s;
  }

  get EvaluationDifficultyMediumLabel() {
    return {
      es: { s: "Media", p: "F" },
      en: { s: "Medium" },
      pt: { s: "Média", p: "F" },
    }[this.language].s;
  }

  get EvaluationDifficultyHardLabel() {
    return {
      es: { s: "Difícil", p: "M" },
      en: { s: "Hard" },
      pt: { s: "Difícil", p: "M" },
    }[this.language].s;
  }

  get EvaluationDifficultyOptions(): { id: string; displayId: string }[] {
    return [
      { id: "easy", displayId: this.EvaluationDifficultyEasyLabel },
      { id: "medium", displayId: this.EvaluationDifficultyMediumLabel },
      { id: "hard", displayId: this.EvaluationDifficultyHardLabel },
    ];
  }

  // EvaluationAmbiguityType enum labels

  get EvaluationAmbiguityTypeUnknown_termLabel() {
    return {
      es: { s: "Término Desconocido", p: "M" },
      en: { s: "Unknown Term" },
      pt: { s: "Termo Desconhecido", p: "M" },
    }[this.language].s;
  }

  get EvaluationAmbiguityTypeChart_groupingLabel() {
    return {
      es: { s: "Agrupación de Gráfico", p: "F" },
      en: { s: "Chart Grouping" },
      pt: { s: "Agrupamento de Gráfico", p: "M" },
    }[this.language].s;
  }

  get EvaluationAmbiguityTypeColumn_ambiguityLabel() {
    return {
      es: { s: "Ambigüedad de Columna", p: "F" },
      en: { s: "Column Ambiguity" },
      pt: { s: "Ambiguidade de Coluna", p: "F" },
    }[this.language].s;
  }

  get EvaluationAmbiguityTypeValue_ambiguityLabel() {
    return {
      es: { s: "Ambigüedad de Valor", p: "F" },
      en: { s: "Value Ambiguity" },
      pt: { s: "Ambiguidade de Valor", p: "F" },
    }[this.language].s;
  }

  get EvaluationAmbiguityTypeOperation_ambiguityLabel() {
    return {
      es: { s: "Ambigüedad de Operación", p: "F" },
      en: { s: "Operation Ambiguity" },
      pt: { s: "Ambiguidade de Operação", p: "F" },
    }[this.language].s;
  }

  get EvaluationAmbiguityTypeOptions(): { id: string; displayId: string }[] {
    return [
      {
        id: "unknown_term",
        displayId: this.EvaluationAmbiguityTypeUnknown_termLabel,
      },
      {
        id: "chart_grouping",
        displayId: this.EvaluationAmbiguityTypeChart_groupingLabel,
      },
      {
        id: "column_ambiguity",
        displayId: this.EvaluationAmbiguityTypeColumn_ambiguityLabel,
      },
      {
        id: "value_ambiguity",
        displayId: this.EvaluationAmbiguityTypeValue_ambiguityLabel,
      },
      {
        id: "operation_ambiguity",
        displayId: this.EvaluationAmbiguityTypeOperation_ambiguityLabel,
      },
    ];
  }

  // QueryPatternSource enum labels

  get QueryPatternSourceHuman_curatedLabel() {
    return {
      es: { s: "Curado por Humano", p: "M" },
      en: { s: "Human Curated" },
      pt: { s: "Curado por Humano", p: "M" },
    }[this.language].s;
  }

  get QueryPatternSourceLlm_suggestedLabel() {
    return {
      es: { s: "Sugerido por LLM", p: "M" },
      en: { s: "LLM Suggested" },
      pt: { s: "Sugerido por LLM", p: "M" },
    }[this.language].s;
  }

  get QueryPatternSourceAuto_generatedLabel() {
    return {
      es: { s: "Generado Automáticamente", p: "M" },
      en: { s: "Auto Generated" },
      pt: { s: "Gerado Automaticamente", p: "M" },
    }[this.language].s;
  }

  get QueryPatternSourceOptions(): { id: string; displayId: string }[] {
    return [
      {
        id: "human_curated",
        displayId: this.QueryPatternSourceHuman_curatedLabel,
      },
      {
        id: "llm_suggested",
        displayId: this.QueryPatternSourceLlm_suggestedLabel,
      },
      {
        id: "auto_generated",
        displayId: this.QueryPatternSourceAuto_generatedLabel,
      },
    ];
  }

  // QueryPatternVariableType enum labels

  get QueryPatternVariableTypeEntityLabel() {
    return {
      es: { s: "Entidad", p: "F" },
      en: { s: "Entity" },
      pt: { s: "Entidade", p: "F" },
    }[this.language].s;
  }

  get QueryPatternVariableTypeRef_fieldLabel() {
    return {
      es: { s: "Campo Referencia", p: "M" },
      en: { s: "Ref Field" },
      pt: { s: "Campo Referência", p: "M" },
    }[this.language].s;
  }

  get QueryPatternVariableTypeSet_fieldLabel() {
    return {
      es: { s: "Campo Set", p: "M" },
      en: { s: "Set Field" },
      pt: { s: "Campo Set", p: "M" },
    }[this.language].s;
  }

  get QueryPatternVariableTypeCatalog_valueLabel() {
    return {
      es: { s: "Valor de Catálogo", p: "M" },
      en: { s: "Catalog Value" },
      pt: { s: "Valor de Catálogo", p: "M" },
    }[this.language].s;
  }

  get QueryPatternVariableTypeScalar_fieldLabel() {
    return {
      es: { s: "Campo Escalar", p: "M" },
      en: { s: "Scalar Field" },
      pt: { s: "Campo Escalar", p: "M" },
    }[this.language].s;
  }

  get QueryPatternVariableTypeNumberLabel() {
    return {
      es: { s: "Número", p: "M" },
      en: { s: "Number" },
      pt: { s: "Número", p: "M" },
    }[this.language].s;
  }

  get QueryPatternVariableTypeOptions(): { id: string; displayId: string }[] {
    return [
      { id: "entity", displayId: this.QueryPatternVariableTypeEntityLabel },
      {
        id: "ref_field",
        displayId: this.QueryPatternVariableTypeRef_fieldLabel,
      },
      {
        id: "set_field",
        displayId: this.QueryPatternVariableTypeSet_fieldLabel,
      },
      {
        id: "catalog_value",
        displayId: this.QueryPatternVariableTypeCatalog_valueLabel,
      },
      {
        id: "scalar_field",
        displayId: this.QueryPatternVariableTypeScalar_fieldLabel,
      },
      { id: "number", displayId: this.QueryPatternVariableTypeNumberLabel },
    ];
  }

  // EsignatureDocumentStatus enum labels

  get EsignatureDocumentStatusDraftLabel() {
    return { es: { s: "Borrador" }, en: { s: "Draft" }, pt: { s: "Rascunho" } }[
      this.language
    ].s;
  }

  get EsignatureDocumentStatusSentLabel() {
    return { es: { s: "Enviado" }, en: { s: "Sent" }, pt: { s: "Enviado" } }[
      this.language
    ].s;
  }

  get EsignatureDocumentStatusDeliveredLabel() {
    return {
      es: { s: "Entregado" },
      en: { s: "Delivered" },
      pt: { s: "Entregue" },
    }[this.language].s;
  }

  get EsignatureDocumentStatusCompletedLabel() {
    return {
      es: { s: "Completado" },
      en: { s: "Completed" },
      pt: { s: "Completado" },
    }[this.language].s;
  }

  get EsignatureDocumentStatusDeclinedLabel() {
    return {
      es: { s: "Rechazado" },
      en: { s: "Declined" },
      pt: { s: "Recusado" },
    }[this.language].s;
  }

  get EsignatureDocumentStatusVoidedLabel() {
    return { es: { s: "Anulado" }, en: { s: "Voided" }, pt: { s: "Anulado" } }[
      this.language
    ].s;
  }

  get EsignatureDocumentStatusExpiredLabel() {
    return {
      es: { s: "Expirado" },
      en: { s: "Expired" },
      pt: { s: "Expirado" },
    }[this.language].s;
  }

  get EsignatureDocumentStatusErrorLabel() {
    return { es: { s: "Error" }, en: { s: "Error" }, pt: { s: "Erro" } }[
      this.language
    ].s;
  }

  get EsignatureDocumentStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "draft", displayId: this.EsignatureDocumentStatusDraftLabel },
      { id: "sent", displayId: this.EsignatureDocumentStatusSentLabel },
      {
        id: "delivered",
        displayId: this.EsignatureDocumentStatusDeliveredLabel,
      },
      {
        id: "completed",
        displayId: this.EsignatureDocumentStatusCompletedLabel,
      },
      { id: "declined", displayId: this.EsignatureDocumentStatusDeclinedLabel },
      { id: "voided", displayId: this.EsignatureDocumentStatusVoidedLabel },
      { id: "expired", displayId: this.EsignatureDocumentStatusExpiredLabel },
      { id: "error", displayId: this.EsignatureDocumentStatusErrorLabel },
    ];
  }

  // EsignatureRecipientStatus enum labels

  get EsignatureRecipientStatusPendingLabel() {
    return {
      es: { s: "Pendiente" },
      en: { s: "Pending" },
      pt: { s: "Pendente" },
    }[this.language].s;
  }

  get EsignatureRecipientStatusSentLabel() {
    return { es: { s: "Enviado" }, en: { s: "Sent" }, pt: { s: "Enviado" } }[
      this.language
    ].s;
  }

  get EsignatureRecipientStatusDeliveredLabel() {
    return {
      es: { s: "Entregado" },
      en: { s: "Delivered" },
      pt: { s: "Entregue" },
    }[this.language].s;
  }

  get EsignatureRecipientStatusSignedLabel() {
    return { es: { s: "Firmado" }, en: { s: "Signed" }, pt: { s: "Assinado" } }[
      this.language
    ].s;
  }

  get EsignatureRecipientStatusDeclinedLabel() {
    return {
      es: { s: "Rechazado" },
      en: { s: "Declined" },
      pt: { s: "Recusado" },
    }[this.language].s;
  }

  get EsignatureRecipientStatusAuthFailedLabel() {
    return {
      es: { s: "Fallo de Autenticacion" },
      en: { s: "Auth Failed" },
      pt: { s: "Falha de Autenticacao" },
    }[this.language].s;
  }

  get EsignatureRecipientStatusBouncedLabel() {
    return {
      es: { s: "Rebotado" },
      en: { s: "Bounced" },
      pt: { s: "Devolvido" },
    }[this.language].s;
  }

  get EsignatureRecipientStatusOptions(): { id: string; displayId: string }[] {
    return [
      { id: "pending", displayId: this.EsignatureRecipientStatusPendingLabel },
      { id: "sent", displayId: this.EsignatureRecipientStatusSentLabel },
      {
        id: "delivered",
        displayId: this.EsignatureRecipientStatusDeliveredLabel,
      },
      { id: "signed", displayId: this.EsignatureRecipientStatusSignedLabel },
      {
        id: "declined",
        displayId: this.EsignatureRecipientStatusDeclinedLabel,
      },
      {
        id: "authFailed",
        displayId: this.EsignatureRecipientStatusAuthFailedLabel,
      },
      { id: "bounced", displayId: this.EsignatureRecipientStatusBouncedLabel },
    ];
  }
}
